// DispatchDeck - CRM Module
// Contact Relationship Management: Customers, Subcontractors, Sites, Contacts, Contracts, Opportunities, Activities
// Founder account only (trippin-trucking)

import {
  db,
  auth,
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  writeBatch,
  storage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  functions,
  httpsCallable
} from '../firebase-config.js?v=1868';
import { runGuardedSave, withWriteTimeout } from '../saveGuard.js?v=2';
// TASK-339 (Lacey 2026-05-20) — vendor logo upload. Same image-processing
// helper roster.js uses for asset photos.
import { processPhoto } from '../utils/imageProcessor.js?v=1955';
import { toDrawablePhoto, photoErrorMessage, detectHeic, createPhotoPrepTracker, settlePhotoPrepBeforeSave } from '../utils/heicConvert.js?v=3';
// TASK-9843 — the registered-states rule (`window.RegisteredStates`), a
// side-effect module GENERATED from functions/src/shared/registeredStates.js by
// scripts/build-shared.cjs. compliance.js imports the same file; neither ever
// imports the canonical one or each other (Pitfall #1).
import '../shared/registered-states.js?v=9843001';
// TASK-4705 — the ONE people model (`window.ProjectPeople`): side-effect module,
// read through window only (Pitfall #1). projects.js imports the same ?v=.
import '../projectPeople.js?v=4705001';
// TASK-9646 S3b-5 — the revenue card's ticket money resolves against the
// server-written ticketMoney mirror (loadRevenueIntelligence). Generated mirror
// of functions/src/shared/ticketMoneyView.js; pure, SAME ?v= as billing.js / clock.js.
// LAZY chunk (perf budget, _crmLoadTmv): loaded only when the card got a mirror.
// Until then it is the helper's own no-mirror answer (the ticket unchanged).
let _tmvWithTicketMoney = (t) => t;
let _crmTmvLoading = null;
function _crmLoadTmv() {
  if (!_crmTmvLoading) {
    _crmTmvLoading = __ddChunkWrap(import('../shared/ticket-money-view.js?v=9646310')).then((m) => {
      _tmvWithTicketMoney = m.withTicketMoney;
      return true;
    }, () => { _crmTmvLoading = null; return false; });
  }
  return _crmTmvLoading;
}
// TASK-9797 item 3 — the contract checklist card / editor / setting
// (`window.CrmContractChecklist`) is a LAZY chunk (perf budget): loaded only
// when the company has the setting on, or a CRM editor opens its settings.
let _ccLoading = null;
function _ccLoad() {
  if (window.CrmContractChecklist) return Promise.resolve(window.CrmContractChecklist);
  if (!_ccLoading) _ccLoading = __ddChunkWrap(import('./crmContractChecklist.js?v=10297974')).then(() => window.CrmContractChecklist).catch((e) => { _ccLoading = null; throw e; });
  return _ccLoading;
}
function _ccOn() { return window.AppState?.settings?.contractChecklist?.enabled === true; }
// UI gate for the settings button only (the module re-checks; rules decide):
// CRM module, not view-only / read-only; owner by ownerId; legacy login. Modules, never roles.
function _ccCanEdit() {
  const S = window.AppState || {};
  if (S.isDemo === true) return true;
  if (typeof window.isComplianceOnlyTenant === 'function' && window.isComplianceOnlyTenant()) return false;
  if (S.user?.uid && S.tenantData?.ownerId === S.user.uid) return true;
  const m = S.memberAccess;
  if (!m) return true;
  if ((m.status && m.status !== 'active') || m.viewUploadOnly === true || m.readOnly === true) return false;
  return m.modules == null || (Array.isArray(m.modules) && m.modules.includes('crm'));
}
function _ccCard(c) {
  // One line per contract: the customer's price books (this detail's live
  // contracts snapshot) and projects; the customer itself only as a fallback.
  if (window.CrmContractChecklist) {
    if (_ccOn() && !CRMState._detailContractsLoaded) return '<div class="crm-overview-card" id="crm-contract-checklist-card"><div class="crm-overview-card-header"><span class="material-icons" style="font-size:16px;color:var(--brand-blue);">fact_check</span><span>Contract checklist</span></div><div style="font-size:13px;color:var(--text-secondary);">Loading…</div></div>';
    return window.CrmContractChecklist.renderCard(c, { priceBooks: CRMState.contracts, tenantId: CRMState.tenantId }) || '';
  }
  if (_ccOn()) _ccLoad().then(() => window.CRMModule?.refreshContractChecklist?.()).catch((e) => console.warn('[CRM] contract checklist load failed', e));
  return '';
}

// ============================================
// CRM STATE
// ============================================
const CRMState = {
  tenantId: null,
  customers: [],
  itemCodes: [],
  unsubscribers: [],
  // TASK-339 (Lacey 2026-05-20) — pending vendor logo upload state, mirrors
  // roster.js asset/equipment photo pattern. Reset on every modal open.
  vendorLogo: { pending: null, existing: null },
  filters: {
    search: '',
    activeOnly: false,
    hasContracts: false,
    hasSites: false,
    typeFilter: 'all'
  },
  // Main view tabs
  currentTab: 'dashboard',
  // Detail modal state
  selectedCustomer: null,
  detailTab: 'overview',
  sites: [],
  contacts: [],
  contracts: [],
  opportunities: [],
  activities: [],
  documents: [],
  detailUnsubscribers: [],
  // Cross-customer data (for kanban/dashboard)
  allOpportunities: [],
  allOpportunitiesLoaded: false,
  // Roster data for asset selection
  trucks: [],
  trailers: [],
  rosterAssets: [],
  people: [],
  // Opportunity editing state
  editingOpp: null,
  editingOppBidLines: [],
  editingOppPortal: null,
  oppComments: [],
  oppCommentsUnsub: null,
  // Opportunity holds for dispatch integration
  opportunityHolds: [],
  // Deal Desk bot state
  botVisible: false,
  botHistory: [],
  pendingBotAction: null,
  // CRM Tasks state
  crmTasks: [],
  tasksUnsubscriber: null,
  taskFilter: 'due',
  pendingBotActions: null,
  // CRM Profiles (multi-rep support)
  profiles: [],
  profilesUnsubscriber: null,
  activeProfile: null,   // { id, name, role, email } — set after PIN entered
  profilesLoaded: false,
  // Raw unfiltered lists (kept separate so we can re-scope when profile changes)
  _allCustomers: [],
  _allCrmTasks: [],
  // TASK-368 pattern: first-snapshot-landed flags so renders show a skeleton
  // loader instead of a lying empty state during the ~200ms-2s cold subscribe.
  // Set true in BOTH the success and error onSnapshot callbacks; reset in initCRM
  // (top-level) and openCustomerDetail (per-customer detail subcollections).
  _customersLoaded: false,
  _tasksLoaded: false,
  _detailContactsLoaded: false,
  _detailContractsLoaded: false,
  // TASK-352 Slice 2 lazy-init bridge — re-entrancy guard for the server-side
  // provisionCrmProfileForMember callable. onSnapshot fires repeatedly and
  // maybeShowCrmPinGate is fire-and-forget; this flag prevents stacked
  // callable round-trips. Cross-tab races are defeated server-side by the
  // Firestore transaction inside the callable.
  _lazyBridgeInFlight: false
};

// ============================================
// CONSTANTS
// ============================================

// Opportunity stages with probability, color, and icon
const OPPORTUNITY_STAGES = [
  { key: 'initiating',   label: 'Initiating',   probability: 10,  color: '#94A3B8', icon: 'radio_button_unchecked' },
  { key: 'qualifying',   label: 'Qualifying',   probability: 25,  color: '#F59E0B', icon: 'search' },
  { key: 'prospecting',  label: 'Prospecting',  probability: 50,  color: '#3B82F6', icon: 'explore' },
  { key: 'negotiating',  label: 'Negotiating',  probability: 75,  color: '#06B6D4', icon: 'handshake' },
  { key: 'closing',      label: 'Closing',      probability: 90,  color: '#10B981', icon: 'pending' },
  { key: 'on_hold',      label: 'On Hold',      probability: 85,  color: '#F97316', icon: 'pause_circle' },
  { key: 'won',          label: 'Won',          probability: 100, color: '#8B5CF6', icon: 'check_circle' },
  { key: 'lost',         label: 'Lost',         probability: 0,   color: '#EF4444', icon: 'cancel' }
];

const STAGE_MAP = {};
OPPORTUNITY_STAGES.forEach(s => { STAGE_MAP[s.key] = s; });

const LOST_REASONS = ['Price', 'Competitor', 'Timing', 'No Budget', 'No Response', 'Other'];

const ACTIVITY_TYPES = [
  { key: 'call',     label: 'Phone Call', icon: 'phone',     color: '#3B82F6' },
  { key: 'email',    label: 'Email',      icon: 'email',     color: '#8B5CF6' },
  { key: 'meeting',  label: 'Meeting',    icon: 'groups',    color: '#10B981' },
  { key: 'note',     label: 'Note',       icon: 'edit_note', color: '#F59E0B' },
  { key: 'followup', label: 'Follow-up',  icon: 'event',     color: '#EF4444' }
];
const LINE_ITEM_CATEGORIES = [
  'Personnel',
  'Heavy Haul',
  'Hydrovac',
  'Trucks & Trailer',
  'Equipment',
  'Other'
];
const KIND_LABELS = { person: 'People', truck: 'Vehicles', trailer: 'Trailers', asset: 'Assets' };
const KIND_TO_CATEGORY = { person: 'Personnel', truck: 'Trucks & Trailer', trailer: 'Trucks & Trailer', asset: 'Equipment' };
const CATEGORY_KIND_MAP = { 'Personnel': 'person', 'Heavy Haul': 'truck', 'Hydrovac': 'truck', 'Trucks & Trailer': 'truck', 'Equipment': 'asset' };

// TASK-9621 S0 #3 — the canonical price-book units. The server copy is
// functions/src/services/docIntakeCore.js RATE_UNITS (tests pin the two equal);
// a line stored with any other string still loads and shows unchanged.
const RATE_UNITS = ['Hourly', 'Daily', 'Monthly', 'Per Load', 'Per Ton', 'Per Yard', 'Per Mile', 'Per Day', 'Each', 'Per Delivery', 'Flat'];

const PAYMENT_TERMS = [
  'Due on Receipt',
  'NET15',
  'NET30',
  'NET45',
  'NET60'
];

const CUSTOMER_STATUSES = ['Active', 'Inactive', 'Prospect'];

// ============================================
// INITIALIZE CRM VIEW
// ============================================
export function initCRM(container, tenantId) {
  console.log('[CRM] Initializing for tenant:', tenantId);

  if (!tenantId) {
    container.innerHTML = `
      <div class="empty-state" style="padding:40px;text-align:center;">
        <span class="material-icons" style="font-size:64px;color:var(--text-muted);">error_outline</span>
        <div class="empty-state-text">Unable to load CRM. Please try again.</div>
      </div>
    `;
    return () => {};
  }

  CRMState.tenantId = tenantId;
  CRMState.customers = [];
  CRMState.itemCodes = [];
  CRMState.selectedCustomer = null;
  CRMState.currentTab = 'dashboard';
  CRMState.allOpportunities = [];
  CRMState.allOpportunitiesLoaded = false;
  CRMState.botVisible = false;
  CRMState.botHistory = [];
  CRMState.pendingBotAction = null;
  CRMState.crmTasks = [];
  CRMState.taskFilter = 'due';
  CRMState.pendingBotActions = null;
  _botConfirmGen++; // TASK-9585 S10b: a re-init invalidates any in-flight Deal Desk batch
  // TASK-368 pattern — reset onSnapshot-landed flags so the first view-open
  // shows skeleton loaders instead of "No customers yet" before data lands.
  CRMState._customersLoaded = false;
  CRMState._tasksLoaded = false;
  // TASK-9684 — per-tenant payee-view state (bill-vendor ids, folded list, demo-only view).
  CRMState.billVendorIds = {};
  CRMState.billVendorsAt = 0;
  CRMState.billVendorsLoading = false;
  CRMState.payeesOpen = false;
  CRMState.payeeViewSaving = false;
  CRMState.demoPayeeView = null;
  // Until bill activity loads, nothing is folded (COD round 2).
  CRMState.billVendorsIncomplete = true;

  removeVendorEditorHost(); // TASK-9793 — never two #crm-customer-modal nodes
  container.innerHTML = getCRMHTML();
  subscribeToCustomers();
  subscribeToItemCodes();
  subscribeToRosterData();
  subscribeToCrmTasks();
  subscribeToCrmProfiles();
  updateManageTeamButtonVisibility();

  // Auto-open Tasks tab if requested (from login banner)
  if (window.AppState?.crmOpenToTasks) {
    window.AppState.crmOpenToTasks = false;
    setTimeout(() => switchCRMTab('tasks'), 300);
  }

  // TASK-9446 — "Open it" on the New Job form's Draft Price Book hint.
  if (window.AppState?.crmOpenContract) {
    const want = window.AppState.crmOpenContract;
    window.AppState.crmOpenContract = null;
    _openPendingCrmContract(want);
  }
  _drainCrmOpenHint(); // TASK-9671 S2 — a Claude lookup link (one customer)

  return () => {
    CRMState.unsubscribers.forEach(u => u());
    CRMState.unsubscribers = [];
    CRMState.detailUnsubscribers.forEach(u => u());
    CRMState.detailUnsubscribers = [];
    if (CRMState.tasksUnsubscriber) { CRMState.tasksUnsubscriber(); CRMState.tasksUnsubscriber = null; }
    ['crm-cust-name','crm-cust-ach-bank','crm-cust-notes','crm-contact-name','crm-contact-title','crm-contact-phone','crm-contact-email','crm-contact-notes','crm-site-name','crm-site-address','crm-site-contact','crm-site-phone','crm-site-notes','crm-contract-title']
      .forEach(id => window.SmartAssistant?.unregister(id));
    ['.poc-name','.poc-phone','.poc-email','.addr-value']
      .forEach(s => window.SmartAssistant?.unregisterClass(s));
  };
}

// TASK-9671 S2 — #/crm?dd_open=customer&customer=<id> (app.js parks it as
// AppState.pendingFocus, window.OpenLinks). takeHint consumes it once (stale /
// other company = null); a driver, link or PIN session drops it. Access
// re-check + wait + open: the LAZY openLinkRecordDrain.js. Opens only a
// customer in THIS login's CRM list (scopedCustomers), else says so.
// TASK-9671 S3 — #/crm?dd_open=price_book&customer=<id>&contract=<id> opens
// that customer's Price Book (same list rule; only a contract of that customer).
function _drainCrmOpenHint() {
  const OL = window.OpenLinks;
  if (!OL || window.AppState?.pendingFocus?.view !== 'crm') return;
  const hint = OL.takeHint('crm');
  if (!hint) return;
  if (window.AppState?.driverMode || window.AppState?.isPublicAccess || window.AppState?.driverModePin) {
    window.showToast?.('Links from Claude open from the office login.', 'info');
    return;
  }
  const tenant = window.AppState?.tenant || null;
  const tid = CRMState.tenantId;
  const host = document.getElementById('crm-detail-modal'); // a re-mount replaces it
  // COD r2 (S3): the mount check carries the CRM gate too, so a paint after a read re-checks access.
  const still = () => CRMState.tenantId === tid && !!host && document.getElementById('crm-detail-modal') === host
    && window.checkModuleAccess?.('crm', true) === true;
  const findCustomer = (id) => (CRMState._customersLoaded ? (CRMState.customers.find(c => c.id === id) || null) : undefined);
  // TASK-9671 S3 — a Price Book link: the TASK-9446 open path, told to report
  // back (true only once that customer's own contract view opened).
  const openBook = (customerId, contractId) => new Promise((resolve) => {
    _openPendingCrmContract({ customerId, contractId, fromLink: true, still, onDone: resolve });
  });
  const open = hint.kind === 'price_book' ? 'openPriceBookHint' : 'openCustomerHint';
  __ddChunkWrap(import('./openLinkRecordDrain.js?v=2'))
    .then((m) => m[open](hint, { tenant, still, findCustomer, openDetail: openCustomerDetail, openBook }))
    .catch((e) => { console.warn('[CRM] open link failed', e?.message); window.showToast?.('Could not open that from the link. Find it in CRM.', 'error'); });
}

// TASK-9446 — open a customer's Price Book once the CRM data it needs has
// landed (customers first, then that customer's contracts). Polls briefly and
// gives up quietly; stops if the dispatcher navigates away or opens something
// else in the meantime.
// TASK-9671 S3 — a Claude Price Book link passes `fromLink`, `still` (its CRM
// mount) and `onDone(opened)`: every way out reports once, and a contract that
// is not in THIS customer's contracts is said out loud instead of dropped.
function _openPendingCrmContract(want) {
  let reported = false;
  const done = (opened) => {
    if (reported) return;
    reported = true;
    if (typeof want?.onDone === 'function') { try { want.onDone(opened === true); } catch (_) { /* reporting only */ } }
  };
  const cid = want && want.customerId;
  if (!cid) { done(false); return; }
  const deadline = Date.now() + 10000;
  const mounted = () => !!document.getElementById('crm-detail-modal') && (typeof want.still !== 'function' || want.still() === true);
  const stillHere = () => mounted() && Date.now() < deadline;
  // A link's paint re-checks mount + access + customer AFTER viewContract's read.
  const paint = typeof want.still === 'function' ? { still: () => mounted() && CRMState.selectedCustomer?.id === cid } : undefined;
  let loadedAt = 0;
  const waitCustomer = () => {
    if (!stillHere()) { done(false); return; }
    if (!CRMState._customersLoaded || !CRMState.customers.find(c => c.id === cid)) {
      // Loaded but not in this viewer's list (deleted, or a CRM rep scoped to
      // other customers — COD Edge Case r1). Allow ~2s for the server snapshot
      // to follow the cache one, then say so instead of polling silently.
      if (CRMState._customersLoaded) {
        if (!loadedAt) loadedAt = Date.now();
        if (Date.now() - loadedAt > 2000) {
          window.showToast?.('That customer isn’t in your CRM list, so its Price Book can’t be opened here.', 'info');
          done(false);
          return;
        }
      }
      setTimeout(waitCustomer, 150);
      return;
    }
    openCustomerDetail(cid);
    CRMState.detailTab = want.tab === 'overview' ? 'overview' : 'contracts'; // TASK-10212 Roster "Open in CRM"
    renderDetailBody({ force: true });
    if (want.contractId) waitContract();
    else done(true);
  };
  const waitContract = async () => {
    if (!stillHere() || CRMState.selectedCustomer?.id !== cid) { done(false); return; }
    if (!CRMState._detailContractsLoaded) { setTimeout(waitContract, 150); return; }
    if (!CRMState.contracts.find(c => c.id === want.contractId)) {
      // TASK-9671 S3 — only a contract of THIS customer opens; a link's is said.
      if (want.fromLink) window.showToast?.('That Price Book isn’t on this customer any more.', 'info');
      done(false);
      return;
    }
    try { await viewContract(cid, want.contractId, paint); } catch (_) { done(false); return; }
    // Opened only if it is still this customer, on this mount, once painted.
    if (!mounted() || CRMState.selectedCustomer?.id !== cid) { done(false); return; }
    done(true);
    // The other detail snapshots (sites, contacts, opps, activity, docs) land
    // after this and each one re-renders the Contracts LIST over the book
    // (renderDetailBody has no guard for the contract view). Re-open it once
    // or twice while they settle; stop if the dispatcher moved on.
    [900, 2200].forEach(ms => setTimeout(() => {
      if (CRMState.selectedCustomer?.id !== cid || CRMState.detailTab !== 'contracts') return;
      if (typeof want.still === 'function' && want.still() !== true) return; // TASK-9671 S3
      if (!document.getElementById('crm-detail-modal') || document.getElementById('crm-detail-modal').classList.contains('hidden')) return;
      if (!document.getElementById('crm-line-items-list')) viewContract(cid, want.contractId, paint);
    }, ms));
  };
  waitCustomer();
}

// ============================================
// MAIN VIEW HTML
// ============================================
function getCRMHTML() {
  return `
    <div class="crm-view">
      <!-- Header -->
      <div class="view-header" style="justify-content:center;margin-bottom:var(--spacing-sm);">
        <div class="view-title" style="flex-direction:column;align-items:center;gap:2px;">
          <h1 style="background:linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-blue) 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;">CRM</h1>
          <p class="crm-subtitle">Contact Relationship Management</p>
        </div>
      </div>
      <div class="crm-header" style="justify-content:center;">
        <div style="display:none;"></div>
        <div class="crm-header-actions">
          <button class="crm-action-btn crm-action-secondary" onclick="window.CRMModule.exportCustomerList()">
            <span class="material-icons">download</span> Export List
          </button>
          <button class="crm-action-btn crm-action-secondary" onclick="window.BillingModule?.showLineItemsModal()">
            <span class="material-icons">list_alt</span> Line Items
          </button>
          <button id="crm-manage-team-btn" class="crm-action-btn crm-action-secondary" onclick="window.CRMModule.showManageCrmProfiles()" style="display:none;">
            <span class="material-icons">group</span> Team
          </button>
          <button class="crm-action-btn crm-action-accent ai-entry" onclick="window.CRMModule.toggleDealDesk()">
            <span class="material-icons">support_agent</span> Deal Desk
          </button>
          ${_ccCanEdit() ? `<button id="crm-cc-settings-btn" class="crm-action-btn crm-action-secondary" title="Contract checklist settings" aria-label="Contract checklist settings" onclick="window.CRMModule.openContractChecklistSettings()">
            <span class="material-icons">tune</span>
          </button>` : ''}
          <button class="crm-action-btn crm-action-primary" onclick="window.CRMModule.showAddCustomer()">
            <span class="material-icons">add</span> Add Customer
          </button>
        </div>
      </div>

      <!-- Tabs -->
      <div class="crm-tabs">
        <button class="crm-tab active" data-tab="dashboard" onclick="window.CRMModule.switchCRMTab('dashboard')">
          <span class="material-icons">dashboard</span> Dashboard
        </button>
        <button class="crm-tab" data-tab="customers" onclick="window.CRMModule.switchCRMTab('customers')">
          <span class="material-icons">people</span> Customers
        </button>
        <button class="crm-tab" data-tab="vendors" onclick="window.CRMModule.switchCRMTab('vendors')">
          <span class="material-icons">engineering</span> Vendors <span id="crm-vendors-compliance-badge" style="display:none;width:8px;height:8px;border-radius:50%;background:#dc2626;flex-shrink:0;"></span>
        </button>
        <button class="crm-tab" data-tab="opportunities" onclick="window.CRMModule.switchCRMTab('opportunities')">
          <span class="material-icons">developer_board</span> Opportunities
        </button>
        <button class="crm-tab" data-tab="revenue" onclick="window.CRMModule.switchCRMTab('revenue')">
          <span class="material-icons">attach_money</span> Revenue
        </button>
        <button class="crm-tab" data-tab="tasks" onclick="window.CRMModule.switchCRMTab('tasks')">
          <span class="material-icons">task_alt</span> Tasks <span id="crm-tasks-badge" class="crm-task-badge" style="display:none;"></span>
        </button>
        <button class="crm-tab" data-tab="marketing" onclick="window.CRMModule.switchCRMTab('marketing')">
          <span class="material-icons">campaign</span> Marketing
        </button>
      </div>

      <!-- Tab: Dashboard -->
      <div id="crm-tab-dashboard" class="crm-tab-content active">
        <!-- Quick Actions -->
        <div class="crm-quick-actions">
          <button class="crm-quick-action-btn" onclick="window.CRMModule.quickLogActivity('call')" style="--qa-color:#3B82F6;">
            <div class="crm-qa-icon"><span class="material-icons">phone</span></div>
            <span class="crm-qa-label">Log Call</span>
          </button>
          <button class="crm-quick-action-btn" onclick="window.CRMModule.quickLogActivity('meeting')" style="--qa-color:#10B981;">
            <div class="crm-qa-icon"><span class="material-icons">groups</span></div>
            <span class="crm-qa-label">Face to Face</span>
          </button>
          <button class="crm-quick-action-btn" onclick="window.CRMModule.quickLogActivity('email')" style="--qa-color:#8B5CF6;">
            <div class="crm-qa-icon"><span class="material-icons">email</span></div>
            <span class="crm-qa-label">Email</span>
          </button>
          <button class="crm-quick-action-btn" onclick="window.CRMModule.quickNewOpportunity()" style="--qa-color:#F59E0B;">
            <div class="crm-qa-icon"><span class="material-icons">trending_up</span></div>
            <span class="crm-qa-label">New Opportunity</span>
          </button>
        </div>
        <div id="crm-dashboard-content">
          <div class="loading-screen"><div class="loading-spinner"></div><p>Loading dashboard...</p></div>
        </div>
      </div>

      <!-- Tab: Customers -->
      <div id="crm-tab-customers" class="crm-tab-content">
        <!-- Filter Bar -->
        <div class="crm-filter-bar">
          <input type="search" id="crm-search" placeholder="Search customers..." autocomplete="off" oninput="window.CRMModule.applyFilters()">
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
            <input type="checkbox" id="crm-filter-active" onchange="window.CRMModule.applyFilters()">
            <label for="crm-filter-active" style="cursor:pointer;margin:0;font-size:13px;">Active Only</label>
          </div>
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
            <input type="checkbox" id="crm-filter-contracts" onchange="window.CRMModule.applyFilters()">
            <label for="crm-filter-contracts" style="cursor:pointer;margin:0;font-size:13px;">Has Contracts</label>
          </div>
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
            <input type="checkbox" id="crm-filter-sites" onchange="window.CRMModule.applyFilters()">
            <label for="crm-filter-sites" style="cursor:pointer;margin:0;font-size:13px;">Has Sites</label>
          </div>
        </div>

        <!-- Customer List -->
        <div id="crm-customer-list">
          <div class="loading-screen"><div class="loading-spinner"></div><p>Loading customers...</p></div>
        </div>
      </div>

      <!-- Tab: Vendors -->
      <div id="crm-tab-vendors" class="crm-tab-content">
        <!-- Filter Bar -->
        <div class="crm-filter-bar">
          <input type="search" id="crm-vendor-search" placeholder="Search vendors..." autocomplete="off" oninput="window.CRMModule.applyVendorFilters()">
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
            <input type="checkbox" id="crm-vendor-filter-active" onchange="window.CRMModule.applyVendorFilters()">
            <label for="crm-vendor-filter-active" style="cursor:pointer;margin:0;font-size:13px;">Active Only</label>
          </div>
          <!-- TASK-9684 — "Show all payees as vendors" (a VIEW setting; records never change). -->
          <div id="crm-payee-showall-wrap" style="display:flex;align-items:center;gap:var(--spacing-sm);">
            <input type="checkbox" id="crm-payee-showall" onchange="window.CRMModule.setShowAllPayees(this.checked)">
            <label for="crm-payee-showall" style="cursor:pointer;margin:0;font-size:13px;color:var(--text-primary);">Show all payees as vendors</label>
            <button type="button" class="btn-icon" id="crm-payee-info-btn" aria-label="What does Show all payees as vendors do?" aria-expanded="false" aria-controls="crm-payee-info"
              title="${escAttr(PAYEE_VIEW_INFO)}" onclick="window.CRMModule.togglePayeeInfo()" style="padding:0 2px;min-width:32px;min-height:32px;">
              <span class="material-icons" style="font-size:18px;color:var(--text-secondary);">info_outline</span>
            </button>
          </div>
          <div id="crm-payee-info" role="note" style="display:none;flex-basis:100%;font-size:13px;line-height:1.45;color:var(--text-primary);background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:8px 10px;">${escHTML(PAYEE_VIEW_INFO)}</div>
        </div>
        <div id="crm-vendor-list">
          <div class="loading-screen"><div class="loading-spinner"></div><p>Loading vendors...</p></div>
        </div>
      </div>

      <!-- Tab: Opportunities (Kanban) -->
      <div id="crm-tab-opportunities" class="crm-tab-content">
        <div id="crm-opportunities-content"></div>
      </div>

      <!-- Tab: Revenue -->
      <div id="crm-tab-revenue" class="crm-tab-content">
        <div id="crm-revenue-content"></div>
      </div>

      <!-- Tab: Tasks -->
      <div id="crm-tab-tasks" class="crm-tab-content">
        <div id="crm-tasks-content"></div>
      </div>

      <!-- Tab: Marketing -->
      <div id="crm-tab-marketing" class="crm-tab-content">
        <div id="crm-marketing-content">
          <div class="loading-screen"><div class="loading-spinner"></div><p>Loading...</p></div>
        </div>
      </div>
    </div>

    <!-- Deal Desk Bot Panel -->
    <div id="crm-dealdesk-panel" class="crm-dealdesk-panel hidden">
      <div class="crm-dealdesk-header">
        <div style="display:flex;align-items:center;gap:8px;">
          <span class="material-icons" style="color:var(--brand-blue);">support_agent</span>
          <strong>Deal Desk</strong>
        </div>
        <button class="modal-close" onclick="window.CRMModule.toggleDealDesk()">&times;</button>
      </div>
      <div class="crm-dealdesk-messages" id="crm-dealdesk-messages">
        <div class="crm-dealdesk-msg bot">What do you need? I can create opportunities, log activities, update customers, pull pipeline stats, or generate reports. To update a price book from a rate sheet, tap the rate sheet button below.</div>
      </div>
      <div id="crm-dealdesk-confirm" class="crm-dealdesk-confirm hidden"></div>
      <div class="crm-dealdesk-input">
        <!-- TASK-9622 — rate sheet → price book (same flow as Ask DispatchDeck). -->
        <button type="button" title="Update a price book from a rate sheet" aria-label="Update a price book from a rate sheet" style="background:var(--bg-elevated);color:var(--text-primary);"
                onclick="window.RateSheetIntake && window.RateSheetIntake.open({ surface: 'deal_desk', customerId: (window.CRMState && window.CRMState.selectedCustomer && window.CRMState.selectedCustomer.id) || '' })"><span class="material-icons">request_quote</span></button>
        <button type="button" title="File a contract or COI" aria-label="File a contract or COI" style="background:var(--bg-elevated);color:var(--text-primary);"
                onclick="window.DocIntake.open({ surface: 'deal_desk', customerId: (window.CRMState && window.CRMState.selectedCustomer && window.CRMState.selectedCustomer.id) || '' })"><span class="material-icons">note_add</span></button>
        <input type="text" id="crm-dealdesk-input" data-maxlen="50000" placeholder="Ask Deal Desk..." onkeydown="if(event.key==='Enter')window.CRMModule.sendBotMessage()">
        <button onclick="window.CRMModule.sendBotMessage()"><span class="material-icons">send</span></button>
      </div>
      <div id="crm-dealdesk-transcript-notice" class="crm-dealdesk-transcript-notice" role="note" style="display:none;padding:0 var(--spacing-md) var(--spacing-sm);font-size:0.72rem;line-height:1.4;color:var(--text-secondary);background:var(--bg-card);"></div>
    </div>

    <!-- Customer Add/Edit Modal (z-index above detail modal) -->
    ${getCustomerModalHTML()}

    <!-- Customer Detail Modal -->
    <div id="crm-detail-modal" class="modal hidden" onclick="if(event.target===this)window.CRMModule.closeDetailModal()">
      <div class="modal-content" style="max-width:900px;max-height:90vh;max-height:90dvh;display:flex;flex-direction:column;">
        <div class="crm-detail-header">
          <div class="crm-detail-header-info">
            <div class="crm-detail-header-icon">
              <span class="material-icons">business</span>
            </div>
            <div>
              <h3 id="crm-detail-title" style="margin:0;font-size:1.125rem;">Customer Details</h3>
              <div id="crm-detail-subtitle" style="font-size:12px;color:rgba(255,255,255,0.7);margin-top:2px;"></div>
            </div>
          </div>
          <button class="modal-close" style="color:#fff;opacity:0.8;" onclick="window.CRMModule.closeDetailModal()">&times;</button>
        </div>
        <div id="crm-detail-body" style="flex:1;overflow-y:auto;padding:0;"></div>
      </div>
    </div>


    <!-- Opportunity Form Modal -->
    <div id="crm-opportunity-modal" class="modal hidden" style="z-index:1060;" onclick="if(event.target===this)window.CRMModule.closeOpportunityModal()">
      <div class="modal-content" style="max-width:600px;max-height:90vh;max-height:90dvh;display:flex;flex-direction:column;">
        <div class="modal-header">
          <h3 id="crm-opp-modal-title">Add Opportunity</h3>
          <button class="modal-close" onclick="window.CRMModule.closeOpportunityModal()">&times;</button>
        </div>
        <div class="modal-body" id="crm-opp-form" style="flex:1;overflow-y:auto;"></div>
        <div class="form-actions" style="padding:16px 24px 20px;border-top:1px solid var(--border-light);background:linear-gradient(to bottom,#F8FAFC,#fff);box-shadow:0 -4px 12px rgba(0,0,0,0.04);">
          <button class="btn btn-secondary" onclick="window.CRMModule.closeOpportunityModal()" style="border-radius:8px;">Cancel</button>
          <button class="btn btn-primary" id="crm-save-opp-btn" onclick="window.CRMModule.saveOpportunity()" style="border-radius:8px;">Save Opportunity</button>
        </div>
      </div>
    </div>

    <!-- Activity Log Modal -->
    <div id="crm-activity-modal" class="modal hidden" style="z-index:1060;" onclick="if(event.target===this)window.CRMModule.closeActivityModal()">
      <div class="modal-content" style="max-width:560px;max-height:90vh;max-height:90dvh;display:flex;flex-direction:column;">
        <div class="modal-header">
          <h3 id="crm-activity-modal-title">Log Activity</h3>
          <button class="modal-close" onclick="window.CRMModule.closeActivityModal()">&times;</button>
        </div>
        <div class="modal-body" id="crm-activity-form" style="flex:1;overflow-y:auto;"></div>
        <div class="form-actions" style="padding:16px 24px 20px;border-top:1px solid var(--border-light);background:linear-gradient(to bottom,#F8FAFC,#fff);box-shadow:0 -4px 12px rgba(0,0,0,0.04);">
          <button class="btn btn-secondary" onclick="window.CRMModule.closeActivityModal()" style="border-radius:8px;">Cancel</button>
          <button class="btn btn-primary" id="crm-save-activity-btn" onclick="window.CRMModule.saveActivity()" style="border-radius:8px;">Log Activity</button>
        </div>
      </div>
    </div>

    <!-- Won/Lost Prompt Modal -->
    <div id="crm-wonlost-modal" class="modal hidden" style="z-index:1070;" onclick="if(event.target===this)document.getElementById('crm-wonlost-modal').classList.add('hidden')">
      <div class="modal-content" style="max-width:420px;">
        <div class="modal-header">
          <h3 id="crm-wonlost-title">Deal Outcome</h3>
          <button class="modal-close" onclick="document.getElementById('crm-wonlost-modal').classList.add('hidden')">&times;</button>
        </div>
        <div class="modal-body" id="crm-wonlost-form"></div>
        <div class="form-actions" style="padding:16px 24px 20px;border-top:1px solid var(--border-light);background:linear-gradient(to bottom,#F8FAFC,#fff);box-shadow:0 -4px 12px rgba(0,0,0,0.04);">
          <button class="btn btn-secondary" onclick="document.getElementById('crm-wonlost-modal').classList.add('hidden')" style="border-radius:8px;">Cancel</button>
          <button class="btn btn-primary" id="crm-wonlost-save-btn" onclick="window.CRMModule.confirmWonLost()" style="border-radius:8px;">Confirm</button>
        </div>
      </div>
    </div>
  `;
}

// ============================================
// REAL-TIME SUBSCRIPTIONS
// ============================================
function subscribeToCustomers() {
  const q = query(
    collection(db, 'tenants', CRMState.tenantId, 'customers'),
    orderBy('name')
  );
  const unsub = onSnapshot(q, snap => {
    CRMState._allCustomers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    CRMState.customers = scopedCustomers(CRMState._allCustomers);
    CRMState._customersLoaded = true; // TASK-368 pattern — flip flag in success callback
    updateComplianceBadges();
    renderActiveTab();
    // Refresh detail modal if open for a customer that just changed
    if (CRMState.selectedCustomer) {
      const updated = CRMState.customers.find(c => c.id === CRMState.selectedCustomer.id);
      if (updated) {
        CRMState.selectedCustomer = updated;
        document.getElementById('crm-detail-title').textContent = updated.name;
        if (CRMState.detailTab === 'overview') renderDetailBody();
      }
    }
  }, err => {
    console.error('[CRM] Customer subscription error:', err);
    CRMState._customersLoaded = true; // TASK-368 pattern — flip on error so skeleton doesn't hang forever
    window.showToast?.('Error loading customers', 'error');
    renderActiveTab();
  });
  CRMState.unsubscribers.push(unsub);

  // Register with Smart Assistant for data quality warnings
  if (window.SmartAssistant) {
    const SA = window.SmartAssistant;
    SA.register('crm-cust-name', () => CRMState.customers.map(c => ({ value: c.name || '', id: c.id })), 'Customers', { excludeIdField: 'crm-cust-id' });
    SA.register('crm-cust-ach-bank', () => [], 'Customers', { formatOnly: true });
    SA.register('crm-cust-notes', () => [], 'Customers', { formatOnly: true });
    // Contacts
    SA.register('crm-contact-name', () => {
      // TASK-823 — contacts live in the subcollection (loaded into CRMState.contacts),
      // not embedded on the customer. Prefer the loaded subcollection; fall back to
      // any legacy embedded array so non-managed customers still autocomplete.
      const sub = Array.isArray(CRMState.contacts) ? CRMState.contacts : [];
      const emb = Array.isArray(CRMState.selectedCustomer?.contacts) ? CRMState.selectedCustomer.contacts : [];
      const src = sub.length ? sub : emb;
      return src.map((c, i) => ({ value: c.name || '', id: String(c.id || i) }));
    }, 'Contacts');
    SA.register('crm-contact-title', () => [], 'Contacts', { formatOnly: true });
    SA.register('crm-contact-phone', () => [], 'Contacts', { phoneFormat: true });
    SA.register('crm-contact-email', () => [], 'Contacts', { emailFormat: true });
    SA.register('crm-contact-notes', () => [], 'Contacts', { formatOnly: true });
    // Sites
    SA.register('crm-site-name', () => {
      const cust = CRMState.selectedCustomer;
      if (!cust?.sites) return [];
      return cust.sites.map((s, i) => ({ value: s.name || '', id: String(i) }));
    }, 'Sites');
    // Follow-up (task-smartassist-names-2) — addresses are identifier mode:
    // whitespace-only fix, never case. cleanFormat('123 MAIN ST NE, WILLISTON ND
    // 58801') === '123 Main St Ne, Williston Nd 58801' — state code, NE/SW, APT,
    // STE, PO BOX, HWY, CR all get mangled. A smarter title-caser was rejected:
    // any word list misses a token; identifier mode cannot corrupt.
    SA.register('crm-site-address', () => [], 'Sites', { identifier: true });
    SA.register('crm-site-contact', () => [], 'Sites', { formatOnly: true });
    SA.register('crm-site-phone', () => [], 'Sites', { phoneFormat: true });
    SA.register('crm-site-notes', () => [], 'Sites', { formatOnly: true });
    // Contracts
    SA.register('crm-contract-title', () => {
      const cust = CRMState.selectedCustomer;
      if (!cust?.contracts) return [];
      return cust.contracts.map((c, i) => ({ value: c.title || '', id: String(i) }));
    }, 'Contracts');
    // Dynamic class-based fields
    SA.registerClass('.poc-name', { formatOnly: true });
    SA.registerClass('.poc-phone', { phoneFormat: true });
    SA.registerClass('.poc-email', { emailFormat: true });
    SA.registerClass('.addr-value', { identifier: true });
  }
}

function renderActiveTab() {
  const tab = CRMState.currentTab;
  if (tab === 'dashboard') renderDashboardTab();
  else if (tab === 'customers') { CRMState.filters.typeFilter = 'customer'; renderCustomerList(); }
  else if (tab === 'vendors') renderVendorList();
  else if (tab === 'opportunities') renderOpportunitiesKanban();
  // Revenue tab renders on demand
}

function subscribeToItemCodes() {
  const q = query(
    collection(db, 'tenants', CRMState.tenantId, 'itemCodes'),
    orderBy('code')
  );
  const unsub = onSnapshot(q, snap => {
    CRMState.itemCodes = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }, err => {
    console.error('[CRM] Item codes subscription error:', err);
  });
  CRMState.unsubscribers.push(unsub);
}

// ============================================
// CUSTOMER LIST RENDERING
// ============================================
function applyFilters() {
  CRMState.filters.search = (document.getElementById('crm-search')?.value || '').trim().toLowerCase();
  CRMState.filters.activeOnly = document.getElementById('crm-filter-active')?.checked || false;
  CRMState.filters.hasContracts = document.getElementById('crm-filter-contracts')?.checked || false;
  CRMState.filters.hasSites = document.getElementById('crm-filter-sites')?.checked || false;
  CRMState.filters.statusFilter = null; // Clear dashboard drill-down
  renderCustomerList();
}

function filterByType(type) {
  CRMState.filters.typeFilter = type;
  // Update toggle UI
  document.querySelectorAll('.crm-type-toggle').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.type === type);
  });
  // Update search placeholder
  const searchEl = document.getElementById('crm-search');
  if (searchEl) {
    searchEl.placeholder = type === 'subcontractor' ? 'Search subcontractors...' : type === 'customer' ? 'Search customers...' : 'Search...';
  }
  renderCustomerList();
}

function renderCustomerList() {
  const container = document.getElementById('crm-customer-list');
  if (!container) return;

  // TASK-368 pattern — show skeleton loader until first customers snapshot lands,
  // so the user doesn't see "No customers yet" during the ~200ms-2s cold fetch.
  if (!CRMState._customersLoaded) {
    container.innerHTML = `<div style="text-align:center;padding:var(--spacing-xl);"><div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div><div style="color:var(--text-secondary);font-size:0.875rem;">Loading customers…</div></div>`;
    return;
  }

  // Background-load opportunities to populate next-action chips
  if (!CRMState.allOpportunitiesLoaded && !CRMState._customerListOppLoadInFlight) {
    CRMState._customerListOppLoadInFlight = true;
    loadAllOpportunities().then(() => {
      CRMState._customerListOppLoadInFlight = false;
      if (document.getElementById('crm-customer-list')) renderCustomerList();
    }).catch(() => { CRMState._customerListOppLoadInFlight = false; });
  }

  let filtered = [...CRMState.customers];

  // Type filter
  if (CRMState.filters.typeFilter && CRMState.filters.typeFilter !== 'all') {
    filtered = filtered.filter(c => (c.type || 'customer') === CRMState.filters.typeFilter);
  }

  // Apply filters
  if (CRMState.filters.statusFilter) {
    filtered = filtered.filter(c => (c.status || 'Active') === CRMState.filters.statusFilter);
  } else if (CRMState.filters.activeOnly) {
    filtered = filtered.filter(c => (c.status || 'Active') === 'Active');
  }
  if (CRMState.filters.hasContracts) {
    filtered = filtered.filter(c => c._contractCount > 0);
  }
  if (CRMState.filters.hasSites) {
    filtered = filtered.filter(c => c._siteCount > 0);
  }
  if (CRMState.filters.search) {
    const s = CRMState.filters.search;
    filtered = filtered.filter(c =>
      (c.name || '').toLowerCase().includes(s) ||
      (c.email || '').toLowerCase().includes(s) ||
      (c.emails || []).some(e => (e.email || '').toLowerCase().includes(s)) ||
      (c.phone || '').includes(s) ||
      (c.phones || []).some(p => (p.phone || '').includes(s)) ||
      (c.address || '').toLowerCase().includes(s) ||
      (c.addresses || []).some(a => (a.address || '').toLowerCase().includes(s))
    );
  }

  if (filtered.length === 0) {
    // TASK-352 Slice 1: distinguish "no customers" from "no CRM profile yet".
    // Restricted TASK-319 members without an activeProfile see scoped-to-empty
    // output from scopedCustomers — surface that explicitly so they don't
    // think the pipeline is empty (and so they know who to ask for access).
    const noProfileForRestrictedMember = !CRMState.activeProfile && !_isUnrestrictedCrmCaller();
    // TASK-375 ACCEPTANCE GATE — a member with `crm` opening CRM for the first
    // time is being provisioned async (autoCreateCrmProfileForMember, guarded by
    // _lazyBridgeInFlight, OR before profiles have loaded). Show a LOADING state
    // during that window, never the "access not set up yet" empty-state flash.
    const provisioningInFlight = noProfileForRestrictedMember
      && (CRMState._lazyBridgeInFlight || !CRMState.profilesLoaded);
    if (provisioningInFlight) {
      container.innerHTML = `<div style="text-align:center;padding:var(--spacing-xl);"><div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div><div style="color:var(--text-secondary);font-size:0.875rem;">Setting up your CRM access…</div></div>`;
      return;
    }
    let title, text;
    if (noProfileForRestrictedMember) {
      title = 'CRM access not set up yet';
      text = 'Ask an admin to finish setting up your CRM profile.';
    } else if (CRMState.customers.length === 0) {
      title = 'No customers yet';
      text = 'Add your first customer to get started.';
    } else {
      title = 'No matches';
      text = 'Try adjusting your filters.';
    }
    container.innerHTML = `
      <div class="empty-state" style="padding:40px;text-align:center;">
        <span class="material-icons" style="font-size:48px;color:var(--text-muted);">${noProfileForRestrictedMember ? 'lock_outline' : 'business'}</span>
        <div class="empty-state-title" style="color:var(--text-primary);">${title}</div>
        <div class="empty-state-text">${text}</div>
      </div>
    `;
    return;
  }

  container.innerHTML = `
    <div class="crm-customer-grid">
      ${filtered.map(c => renderCustomerCard(c)).join('')}
    </div>
  `;
}

// ============================================
// VENDOR LIST (subcontractors)
// ============================================
function applyVendorFilters() {
  renderVendorList();
}

function renderVendorList() {
  const container = document.getElementById('crm-vendor-list');
  if (!container) return;

  // TASK-368 pattern — vendors are a subset of customers; show skeleton until
  // first customers snapshot lands instead of "No vendors yet".
  if (!CRMState._customersLoaded) {
    container.innerHTML = `<div style="text-align:center;padding:var(--spacing-xl);"><div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div><div style="color:var(--text-secondary);font-size:0.875rem;">Loading vendors…</div></div>`;
    return;
  }

  // TASK-9684 — split by how they are paid (shared rule window.BillableCustomer,
  // never a local definition): account vendors are the default list, everyone
  // else folds into "Other payees (N)". Search reaches both. View only.
  _ensureBillVendorIds();
  const opts = _payeeViewOpts();
  _syncPayeeViewControls(opts);
  const _BC = window.BillableCustomer;
  const allVendorLike = CRMState.customers.filter(c => _BC.isVendorLikeCustomer(c));
  let filtered = allVendorLike;

  // Active only
  const activeOnly = document.getElementById('crm-vendor-filter-active')?.checked;
  if (activeOnly) {
    filtered = filtered.filter(c => (c.status || 'Active') === 'Active');
  }

  // Search
  const searchEl = document.getElementById('crm-vendor-search');
  const search = (searchEl?.value || '').toLowerCase().trim();
  if (search) {
    filtered = filtered.filter(c =>
      (c.name || '').toLowerCase().includes(search) ||
      (c.email || '').toLowerCase().includes(search) ||
      (c.emails || []).some(e => (e.email || '').toLowerCase().includes(search)) ||
      (c.phone || '').includes(search) ||
      (c.phones || []).some(p => (p.phone || '').includes(search))
    );
  }

  if (filtered.length === 0) {
    container.innerHTML = `
      <div class="empty-state" style="padding:40px;text-align:center;">
        <span class="material-icons" style="font-size:48px;color:var(--text-muted);">engineering</span>
        <div class="empty-state-title" style="color:var(--text-primary);">${allVendorLike.length ? 'No matches' : 'No vendors yet'}</div>
        <div class="empty-state-text">${allVendorLike.length ? 'Try adjusting your filters.' : 'Add a contact with type "Subcontractor" or "Vendor" to see them here.'}</div>
      </div>
    `;
    return;
  }

  const { vendors, payees } = _BC.splitVendorsAndPayees(filtered, opts);
  const canEdit = _payeeViewCanEdit();
  const pinSet = _BC.vendorIdSet(opts.pins);
  const cardFor = (c) => renderCustomerCard(c, { payeeView: { kind: _BC.vendorPayeeKind(c, opts), pinned: pinSet[c.id] === true, canEdit, showAll: opts.showAll } });
  // A search opens the folded list so a payee is never hidden behind a click.
  const payeesOpen = !!search || CRMState.payeesOpen === true;
  // Imported from QuickBooks before the import stamped bill activity (the 10/05
  // TITS run): how they are paid is unknown, so the shared rule keeps them on
  // the vendor list (never folded) until QuickBooks setup -> Vendors re-runs.
  // COD Domain round 2: a folded payee's compliance flag must not hide behind the fold.
  const payeesFlagged = payees.filter(c => c._complianceExpired || c.complianceStatus === 'expired' || c._complianceExpiringSoon || c.complianceStatus === 'expiring').length;
  const unknownQbo = opts.showAll ? 0 : vendors.filter(c => _BC.vendorActivityUnknown(c)).length;
  const unknownHint = unknownQbo ? `<div style="margin:0 0 var(--spacing-md);padding:8px 10px;font-size:13px;line-height:1.45;color:var(--text-primary);background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:var(--radius-md);">${unknownQbo} vendor${unknownQbo === 1 ? '' : 's'} came from QuickBooks before DispatchDeck tracked how ${unknownQbo === 1 ? 'it is' : 'they are'} paid, so ${unknownQbo === 1 ? 'it stays' : 'they all stay'} on this list. Run <strong>Billing Settings → QuickBooks setup → Vendors</strong> again and the card-only ones move under Other payees on their own.</div>` : '';

  const vendorsHtml = unknownHint + (vendors.length
    ? `<div class="crm-customer-grid">${vendors.map(cardFor).join('')}</div>`
    : `<div style="padding:16px;border:1px dashed var(--border-medium);border-radius:var(--radius-md);color:var(--text-secondary);font-size:14px;background:var(--bg-card);">
        ${search ? 'No account vendors match.' : 'No account vendors yet. Vendors you get bills from show here — they move up on their own when their first bill is entered.'}
      </div>`);
  const payeesHtml = payees.length ? `
    <div style="margin-top:var(--spacing-lg);">
      <button type="button" id="crm-payees-toggle" onclick="window.CRMModule.togglePayees()" aria-expanded="${payeesOpen}" aria-controls="crm-payees-list"
        style="display:flex;align-items:center;gap:6px;width:100%;min-height:44px;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-md);background:var(--bg-elevated);color:var(--text-primary);font-size:14px;font-weight:600;font-family:inherit;cursor:pointer;text-align:left;">
        <span class="material-icons" style="font-size:20px;">${payeesOpen ? 'expand_more' : 'chevron_right'}</span>
        Other payees (${payees.length})
        <span style="font-weight:400;color:var(--text-secondary);font-size:13px;">— paid by card or bank, no bills yet</span>
        ${payeesFlagged ? `<span style="margin-left:auto;font-size:11px;font-weight:700;padding:2px 8px;border-radius:10px;background:#FEE2E2;color:#991B1B;" title="Insurance / documents expired or expiring">${payeesFlagged} need${payeesFlagged === 1 ? 's' : ''} attention</span>` : ''}
      </button>
      ${payeesOpen ? `
        <div id="crm-payees-list" class="crm-customer-grid" style="margin-top:var(--spacing-md);">${payees.map(cardFor).join('')}</div>` : ''}
    </div>` : '';

  container.innerHTML = vendorsHtml + payeesHtml;
}

// ============================================
// TASK-9684 — ACCOUNT VENDORS vs OTHER PAYEES (view split)
// ============================================
// Chris 2026-10-05. The RULE is window.BillableCustomer.vendorPayeeKind (shared
// with the server and the AP picker). This section only feeds it: which
// vendors have a DD AP bill, and the tenant VIEW setting settings.payeeView
// { showAll, pins } (written by the vendorPayeeSetView callable — records are
// never changed). Copy is Chris's, verbatim.
const PAYEE_VIEW_INFO = "QuickBooks counts everyone you've ever paid as a vendor — including card swipes like gas stations. "
  + 'DispatchDeck shows the vendors you get bills from, and keeps the rest under Other payees. Turn this on to see everyone in one list.';

function _crmIsDemoTenant() {
  return CRMState.tenantId === 'demo' || CRMState.tenantId === 'demo-tenant' || window.AppState?.isDemo === true;
}

function _payeeViewOpts() {
  const pv = CRMState.demoPayeeView || window.AppState?.settings?.payeeView || {};
  return {
    billVendorIds: CRMState.billVendorIds || {},
    showAll: pv.showAll === true || CRMState.billVendorsIncomplete === true,
    pins: Array.isArray(pv.pins) ? pv.pins : [],
  };
}

// UI hint only — the callable re-checks (owner via ownerId, else crm/billing
// module, not view-only / read-only). Module access, never role (Chris 9/24).
function _payeeViewCanEdit() {
  if (_crmIsDemoTenant()) return true;
  const m = window.AppState?.memberAccess;
  if (!m) return true; // owner / legacy single login
  if (m.status && m.status !== 'active') return false;
  if (m.viewUploadOnly === true || m.readOnly === true) return false;
  if (m.role === 'owner') return true;
  return Array.isArray(m.modules) && (m.modules.includes('crm') || m.modules.includes('billing'));
}

function _syncPayeeViewControls(opts) {
  const cb = document.getElementById('crm-payee-showall');
  if (cb) {
    // The SETTING, not the effective view (a not-yet-loaded bill list also shows everyone).
    const pv = CRMState.demoPayeeView || window.AppState?.settings?.payeeView || {};
    cb.checked = pv.showAll === true;
    cb.disabled = !_payeeViewCanEdit() || CRMState.payeeViewSaving === true;
  }
}

// Which vendors have a DD AP bill. The callable returns ids only and works for
// crm-only members (apInvoices reads are billing/bi/admin in firestore.rules);
// the demo and a not-yet-deployed callable fall back to a direct read. Until it
// lands the split uses QuickBooks activity alone, then re-renders.
const BILL_VENDOR_TTL_MS = 60 * 1000;
async function _ensureBillVendorIds(force) {
  const now = Date.now();
  if (CRMState.billVendorsLoading) return;
  if (!force && CRMState.billVendorsAt && now - CRMState.billVendorsAt < BILL_VENDOR_TTL_MS) return;
  CRMState.billVendorsLoading = true;
  const tid = CRMState.tenantId;
  let ids = null;
  let truncated = false;
  try {
    if (!_crmIsDemoTenant()) {
      const fn = httpsCallable(functions, 'vendorPayeeBillVendors');
      const res = await fn({ tenantId: tid }); // COD 10/13: server refuses (expectTenantId) if this screen's tenant is not the caller's
      const list = res?.data?.billVendorIds;
      if (Array.isArray(list)) { ids = window.BillableCustomer.vendorIdSet(list); truncated = res?.data?.truncated === true; }
    }
  } catch (e) {
    console.warn('[CRM] bill-vendor lookup (callable) failed, trying a direct read:', e?.code || e?.message || e);
  }
  if (!ids) {
    try {
      const tenant = tid === 'demo-tenant' ? 'demo' : tid;
      const snap = await getDocs(collection(db, 'tenants', tenant, 'apInvoices'));
      ids = window.BillableCustomer.billVendorIdsFromInvoices(snap.docs.map(d => d.data()));
    } catch (e) {
      console.warn('[CRM] bill-vendor lookup (direct) failed — showing every vendor:', e?.code || e?.message || e);
    }
  }
  // COD round 2: every mutation happens only for the tenant that asked.
  if (CRMState.tenantId !== tid) return; // tenant switched while waiting (its own load resets the flags)
  CRMState.billVendorsLoading = false;
  CRMState.billVendorsAt = Date.now();
  // Bill activity unknown (both reads failed) or incomplete -> fold nothing:
  // a real bill sender must never hide behind Other payees on missing data.
  const incomplete = !ids || truncated;
  const flagChanged = incomplete !== (CRMState.billVendorsIncomplete === true);
  CRMState.billVendorsIncomplete = incomplete;
  const idsChanged = !!ids && JSON.stringify(Object.keys(ids).sort()) !== JSON.stringify(Object.keys(CRMState.billVendorIds || {}).sort());
  if (ids) CRMState.billVendorIds = ids;
  if ((idsChanged || flagChanged) && CRMState.currentTab === 'vendors') renderVendorList();
}

function togglePayees() {
  CRMState.payeesOpen = !(CRMState.payeesOpen === true);
  renderVendorList();
}

function togglePayeeInfo() {
  const box = document.getElementById('crm-payee-info');
  const btn = document.getElementById('crm-payee-info-btn');
  if (!box) return;
  const open = box.style.display === 'none';
  box.style.display = open ? 'block' : 'none';
  if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
}

async function _savePayeeView(payload, okMsg) {
  if (!_payeeViewCanEdit()) { window.showToast?.('View only — ask the owner to change how vendors are listed.', 'error'); renderVendorList(); return; }
  // The shared demo tenant: this browser only (never written to the shared doc).
  if (_crmIsDemoTenant()) {
    const cur = _payeeViewOpts();
    const pins = cur.pins.slice();
    if (payload.pin) {
      const at = pins.indexOf(payload.pin.vendorId);
      if (payload.pin.pinned && at < 0) pins.push(payload.pin.vendorId);
      if (!payload.pin.pinned && at >= 0) pins.splice(at, 1);
    }
    CRMState.demoPayeeView = { showAll: payload.showAll !== undefined ? payload.showAll : cur.showAll, pins };
    renderVendorList();
    if (okMsg) window.showToast?.(okMsg, 'success');
    return;
  }
  if (CRMState.payeeViewSaving) return;
  CRMState.payeeViewSaving = true;
  const saveTid = CRMState.tenantId; // COD Code Reviewer: never apply a reply to a tenant switched to mid-call
  _syncPayeeViewControls(_payeeViewOpts());
  try {
    const fn = httpsCallable(functions, 'vendorPayeeSetView');
    const res = await fn({ ...payload, tenantId: saveTid }); // server refuses if this screen's tenant is not the caller's (expectTenantId)
    const pv = res?.data?.payeeView;
    if (CRMState.tenantId !== saveTid) return;
    // The tenant-doc listener (app.js subscribeTenantSettings) will deliver the
    // same value; set it now so the list moves without waiting.
    if (pv && window.AppState) {
      window.AppState.settings = window.AppState.settings || {};
      window.AppState.settings.payeeView = { ...(window.AppState.settings.payeeView || {}), showAll: pv.showAll === true, pins: Array.isArray(pv.pins) ? pv.pins : [] };
    }
    if (okMsg) window.showToast?.(okMsg, 'success');
  } catch (e) {
    console.error('[CRM] payee view save failed:', e);
    window.showToast?.(e?.message && e?.code !== 'internal' ? e.message : 'Could not save — try again.', 'error');
  } finally {
    if (CRMState.tenantId === saveTid) { CRMState.payeeViewSaving = false;
    renderVendorList(); }
  }
}

function setShowAllPayees(on) {
  return _savePayeeView({ showAll: on === true }, on === true ? 'Showing all payees as vendors' : 'Showing account vendors — other payees are folded below');
}

function setPayeePin(vendorId, pinned) {
  if (typeof vendorId !== 'string' || !vendorId) return;
  return _savePayeeView({ pin: { vendorId, pinned: pinned === true } }, pinned === true ? 'Pinned to vendors' : 'Unpinned — back under Other payees');
}

// ─────────────────────────────────────────────────────────────────────────────
// TASK-10141 — vendor CHARGE ACCOUNTS (Chris 10/27). customers.chargeAccount is
// the ONE list the driver's "Paid with → Charge account" picker offers. Only the
// vendorChargeAccountSet callable writes it (firestore.rules deny the field to
// clients), audited with who turned it on or off. The setup answers ("Which
// vendors do you run charge accounts with?") only SUGGEST it here — nothing is
// set without a click.
// ─────────────────────────────────────────────────────────────────────────────
function _crmVendorKey(name) {
  return String(name || '').normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim(); // = billerQuestions.vendorKey
}
function _crmIsVendorLike(c) {
  const t = String((c && c.type) || '').trim().toLowerCase();
  return t === 'vendor' || t === 'subcontractor';
}
function _chargeAccountChipHtml(c) {
  return c && c.chargeAccount === true && _crmIsVendorLike(c)
    ? '<span style="font-size:10px;padding:1px 6px;border-radius:4px;background:#CCFBF1;color:#115E59;font-weight:600;" title="Drivers can put purchases here on the company account">CHARGE ACCOUNT</span>'
    : '';
}
function _loadChargeSuggestions() {
  const tid = CRMState.tenantId;
  if (!tid || _crmIsDemoTenant() || !_payeeViewCanEdit()) return;
  if (CRMState._chargeSugTid === tid) return; // loaded or loading for this company
  CRMState._chargeSugTid = tid;
  CRMState.chargeSuggestionKeys = null;
  httpsCallable(functions, 'vendorChargeAccountSuggestions')({ tenantId: tid }).then((res) => {
    if (CRMState.tenantId !== tid) return;
    const list = Array.isArray(res?.data?.suggestions) ? res.data.suggestions : [];
    CRMState.chargeSuggestionKeys = new Set(list.map(s => String(s.key || '')).filter(Boolean));
    if (CRMState.chargeSuggestionKeys.size && CRMState.detailTab === 'overview' && _crmIsVendorLike(CRMState.selectedCustomer)) renderDetailBody();
  }).catch((e) => { console.warn('[CRM] charge-account suggestions', e?.message); });
}
function _chargeAccountCardHtml(c) {
  if (!_crmIsVendorLike(c)) return '';
  const on = c.chargeAccount === true;
  const canEdit = _payeeViewCanEdit() && !_crmIsDemoTenant();
  if (!on && canEdit) _loadChargeSuggestions();
  const suggested = !on && CRMState.chargeSuggestionKeys instanceof Set && CRMState.chargeSuggestionKeys.has(_crmVendorKey(c.name));
  const jsId = escJsAttr(c.id);
  return `
    <div class="crm-overview-card" id="crm-charge-card" style="margin-bottom:12px;">
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:10px 14px;">
        <span class="material-icons" style="color:#0F766E;">storefront</span>
        <div style="flex:1;min-width:180px;">
          <div style="font-weight:600;font-size:14px;color:var(--text-primary);">${on ? 'Charge account' : 'Not a charge account'}</div>
          <div style="font-size:12px;color:var(--text-secondary);">${on
            ? 'Drivers can pick this vendor when they put a purchase on the company account.'
            : 'Add it if the crew buys here on the company account and the vendor bills you later.'}</div>
          ${suggested ? '<div style="font-size:12px;color:#92400E;margin-top:4px;"><span class="material-icons" style="font-size:14px;vertical-align:-2px;">lightbulb</span> Your setup answers said you have a charge account here. Add it?</div>' : ''}
        </div>
        ${canEdit ? `<button type="button" class="btn ${on ? 'btn-secondary' : 'btn-primary'} btn-sm" style="min-height:36px;" onclick="window.CRMModule.setVendorChargeAccount('${jsId}', ${on ? 'false' : 'true'})">${on ? 'Remove from charge accounts' : 'Add to charge accounts'}</button>` : ''}
      </div>
    </div>`;
}
// TASK-10212 — units rented FROM this vendor: the LAZY crmVendorRentals.js.
let _vrP = null;
function _crmVendorRentalsSlot(c) {
  if (!_crmIsVendorLike(c) || !c.id) return '';
  const tid = CRMState.tenantId;
  setTimeout(() => (_vrP = _vrP || __ddChunkWrap(import('./crmVendorRentals.js?v=1')).catch((e) => { _vrP = null; console.warn('[CRM] vendor rentals', e?.message); }))
    .then((m) => m && m.render(document.getElementById('crm-vendor-rentals'), c, tid, () => CRMState.tenantId === tid && CRMState.selectedCustomer?.id === c.id)), 0);
  return '<div id="crm-vendor-rentals"></div>';
}
async function setVendorChargeAccount(vendorId, on) {
  if (typeof vendorId !== 'string' || !vendorId || typeof on !== 'boolean') return;
  if (_crmIsDemoTenant()) { window.showToast?.('Not available on the demo', 'info'); return; }
  if (CRMState._chargeSaving) return;
  CRMState._chargeSaving = true;
  const tid = CRMState.tenantId;
  try {
    await httpsCallable(functions, 'vendorChargeAccountSet')({ tenantId: tid, vendorId, on });
    if (CRMState.tenantId !== tid) return;
    // The customers listener delivers the same value; set it now so the screen moves.
    const hit = (CRMState.customers || []).find(c => c.id === vendorId);
    if (hit) hit.chargeAccount = on;
    if (CRMState.selectedCustomer && CRMState.selectedCustomer.id === vendorId) CRMState.selectedCustomer.chargeAccount = on;
    window.showToast?.(on ? 'Added to charge accounts — drivers can pick it now' : 'Removed from charge accounts', 'success');
  } catch (e) {
    console.error('[CRM] charge account save failed:', e);
    window.showToast?.(e?.message && e?.code !== 'internal' ? e.message : 'Could not save — try again.', 'error');
  } finally {
    CRMState._chargeSaving = false;
    if (CRMState.tenantId === tid) renderDetailBody();
  }
}

function renderCustomerCard(customer, cardOpts) {
  const isSub = customer.type === 'subcontractor';
  // TASK-9684 — Vendors tab only: "Other payee" chip + pin to / unpin from the vendor list.
  const pv = cardOpts && cardOpts.payeeView;
  let payeeChip = '';
  let pinBtn = '';
  if (pv && pv.kind === 'payee') {
    payeeChip = `<span style="font-size:10px;padding:1px 6px;border-radius:4px;background:#F1F5F9;color:#334155;font-weight:600;" title="Paid by card or bank, no bills yet">OTHER PAYEE</span>`;
    if (pv.pinned) payeeChip += `<span class="material-icons" style="font-size:15px;color:var(--brand-blue);" title="Pinned to the vendor list">push_pin</span>`;
    if (pv.canEdit && !pv.showAll) {
      const jsId = window.escJsAttr ? window.escJsAttr(customer.id) : String(customer.id).replace(/[^A-Za-z0-9_-]/g, '');
      pinBtn = `<button type="button" class="btn btn-secondary btn-sm crm-payee-pin" data-payee-pin="${escAttr(customer.id)}"
        onclick="event.stopPropagation();window.CRMModule.setPayeePin('${jsId}', ${pv.pinned ? 'false' : 'true'})"
        style="min-height:36px;font-size:12px;padding:4px 10px;display:inline-flex;align-items:center;gap:4px;margin-top:8px;">
        <span class="material-icons" style="font-size:15px;">${pv.pinned ? 'push_pin' : 'vertical_align_top'}</span>${pv.pinned ? 'Unpin' : 'Pin to vendors'}
      </button>`;
    }
  }
  const status = customer.status || 'Active';
  const statusBg = status === 'Active' ? '#DBEAFE' : status === 'Prospect' ? '#FEF3C7' : '#F1F5F9';
  const statusText = status === 'Active' ? '#1E40AF' : status === 'Prospect' ? '#92400E' : '#475569';
  const dotColor = status === 'Active' ? '#3B82F6' : status === 'Prospect' ? '#F59E0B' : '#94A3B8';
  const phoneList = customer.phones && customer.phones.length > 0 ? customer.phones : (customer.phone ? [{ label: 'Main', phone: customer.phone }] : []);
  const emailList = customer.emails && customer.emails.length > 0 ? customer.emails : (customer.email ? [{ label: 'General', email: customer.email }] : []);
  const contracts = customer._contractCount || 0;
  const sites = customer._siteCount || 0;
  const compExpired = customer._complianceExpired || customer.complianceStatus === 'expired';
  const compExpiring = !compExpired && (customer._complianceExpiringSoon || customer.complianceStatus === 'expiring');
  const compFlag = compExpired ? '<span class="material-icons" style="font-size:16px;color:#dc2626;" title="Compliance expired">flag</span>' : compExpiring ? '<span class="material-icons" style="font-size:16px;color:#F59E0B;" title="Compliance expiring soon">flag</span>' : '';
  // W-9 gap: subcontractor with no W-9 on file (24% IRS backup-withholding risk at pay-time).
  const needsW9 = isSub && !customer.w9OnFile;
  const w9Badge = needsW9 ? '<span style="font-size:10px;padding:1px 6px;border-radius:4px;background:#FEE2E2;color:#991B1B;font-weight:700;" title="No W-9 on file — collect before paying (IRS backup withholding)">W-9 needed</span>' : '';

  // Next-action chip: soonest due/overdue action across this customer's open opps
  let nextActionChip = '';
  if (CRMState.allOpportunitiesLoaded) {
    const today = new Date(); today.setHours(0,0,0,0);
    const soonCutoff = new Date(today); soonCutoff.setDate(soonCutoff.getDate() + 7);
    let soonest = null;
    (CRMState.allOpportunities || []).forEach(o => {
      if (o.customerId !== customer.id) return;
      if (o.stage === 'won' || o.stage === 'lost') return;
      if (!o._nextActionDate) return;
      const d = o._nextActionDate.toDate ? o._nextActionDate.toDate() : new Date(o._nextActionDate);
      if (isNaN(d.getTime())) return;
      if (d > soonCutoff) return;
      if (!soonest || d < soonest) soonest = d;
    });
    if (soonest) {
      const due = new Date(soonest); due.setHours(0,0,0,0);
      const days = Math.round((due - today) / 86400000);
      let bg, fg, label;
      if (days < 0) { bg = '#FEE2E2'; fg = '#991B1B'; label = days === -1 ? 'overdue 1d' : `overdue ${-days}d`; }
      else if (days === 0) { bg = '#FEF3C7'; fg = '#92400E'; label = 'due today'; }
      else if (days === 1) { bg = '#FEF3C7'; fg = '#92400E'; label = 'due tomorrow'; }
      else { bg = '#DBEAFE'; fg = '#1E40AF'; label = `due in ${days}d`; }
      nextActionChip = `<span style="font-size:10px;padding:1px 6px;border-radius:4px;background:${bg};color:${fg};font-weight:600;" title="Next action on an open opportunity">${label}</span>`;
    }
  }

  return `
    <div class="crm-customer-card" onclick="window.CRMModule.openCustomerDetail('${escJsAttr(customer.id)}')" style="${(compExpired || needsW9) ? 'border-left:4px solid #dc2626;' : compExpiring ? 'border-left:4px solid #F59E0B;' : ''}">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:6px;">
        <div style="font-weight:600;font-size:15px;color:var(--text-primary);display:flex;align-items:center;gap:6px;flex-wrap:wrap;">${customer.logoUrl ? `<img src="${escAttr(customer.logoUrl)}" alt="" class="crm-customer-logo" loading="lazy">` : ''}${escHTML(customer.name)}${isSub ? '<span style="font-size:10px;padding:1px 6px;border-radius:4px;background:#FEF3C7;color:#92400E;font-weight:600;">SUB</span>' : customer.type === 'vendor' ? '<span style="font-size:10px;padding:1px 6px;border-radius:4px;background:#EDE9FE;color:#6D28D9;font-weight:600;">VENDOR</span>' : ''}${_chargeAccountChipHtml(customer)}${payeeChip}${w9Badge}${compFlag}${nextActionChip}</div>
        <span style="display:flex;align-items:center;gap:4px;font-size:11px;padding:2px 8px;border-radius:var(--radius-sm);background:${statusBg};color:${statusText};font-weight:600;">
          <span style="width:6px;height:6px;border-radius:50%;background:${dotColor};display:inline-block;"></span>
          ${status}
        </span>
      </div>
      ${phoneList.length > 0 ? `<div style="font-size:13px;color:var(--text-secondary);margin-bottom:2px;"><span class="material-icons" style="font-size:14px;vertical-align:-2px;">phone</span> ${escHTML(phoneList[0].phone)}${phoneList.length > 1 ? ` <span style="font-size:11px;color:var(--text-secondary);">+${phoneList.length - 1} more</span>` : ''}</div>` : ''}
      ${emailList.length > 0 ? `<div style="font-size:13px;color:var(--text-secondary);margin-bottom:2px;"><span class="material-icons" style="font-size:14px;vertical-align:-2px;">email</span> ${escHTML(emailList[0].email)}${emailList.length > 1 ? ` <span style="font-size:11px;color:var(--text-secondary);">+${emailList.length - 1} more</span>` : ''}</div>` : ''}
      ${(() => {
        const addrList = customer.addresses && customer.addresses.length > 0 ? customer.addresses : (customer.address ? [{ label: 'Main', address: customer.address }] : []);
        if (addrList.length === 0) return '';
        return `<div style="font-size:13px;color:var(--text-secondary);margin-bottom:4px;"><span class="material-icons" style="font-size:14px;vertical-align:-2px;">location_on</span> ${escHTML(addrList[0].address)}${addrList.length > 1 ? ` <span style="font-size:11px;color:var(--text-secondary);">+${addrList.length - 1} more</span>` : ''}</div>${_crmRegisteredStateFlag(customer)}`;
      })()}
      <div style="display:flex;gap:12px;margin-top:8px;font-size:12px;color:var(--text-secondary);flex-wrap:wrap;align-items:center;">
        ${contracts > 0 ? `<span>${contracts} contract${contracts !== 1 ? 's' : ''}</span>` : ''}
        ${sites > 0 ? `<span>${sites} site${sites !== 1 ? 's' : ''}</span>` : ''}
        ${customer.billingTerms ? `<span>${escHTML(customer.billingTerms)}</span>` : ''}
        ${(customer.requiredFields || []).length > 0 ? `<span class="customer-req-badge"><span class="material-icons" style="font-size:12px;">checklist</span>${customer.requiredFields.length} req'd</span>` : ''}
      </div>
      ${pinBtn}
    </div>
  `;
}

// ============================================
// EXPORT CUSTOMER LIST
// ============================================
function exportCustomerList() {
  // Use same filter logic as renderCustomerList to export what's on screen
  let filtered = [...CRMState.customers];
  if (CRMState.filters.statusFilter) {
    filtered = filtered.filter(c => (c.status || 'Active') === CRMState.filters.statusFilter);
  } else if (CRMState.filters.activeOnly) {
    filtered = filtered.filter(c => (c.status || 'Active') === 'Active');
  }
  if (CRMState.filters.hasContracts) {
    filtered = filtered.filter(c => c._contractCount > 0);
  }
  if (CRMState.filters.hasSites) {
    filtered = filtered.filter(c => c._siteCount > 0);
  }
  if (CRMState.filters.search) {
    const s = CRMState.filters.search;
    filtered = filtered.filter(c =>
      (c.name || '').toLowerCase().includes(s) ||
      (c.email || '').toLowerCase().includes(s) ||
      (c.emails || []).some(e => (e.email || '').toLowerCase().includes(s)) ||
      (c.phone || '').includes(s) ||
      (c.phones || []).some(p => (p.phone || '').includes(s)) ||
      (c.address || '').toLowerCase().includes(s) ||
      (c.addresses || []).some(a => (a.address || '').toLowerCase().includes(s))
    );
  }

  if (filtered.length === 0) {
    window.showToast?.('No customers to export', 'info');
    return;
  }

  // Build CSV
  const headers = ['Name', 'Status', 'Phones', 'Emails', 'Addresses', 'Billing Terms', 'Contracts', 'Sites'];
  const csvEsc = (val) => {
    const s = String(val || '').replace(/"/g, '""');
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s}"` : s;
  };
  const rows = filtered.map(c => {
    const allPhones = c.phones && c.phones.length > 0
      ? c.phones.map(p => `${p.label}: ${p.phone}`).join('; ')
      : (c.phone || '');
    const allEmails = c.emails && c.emails.length > 0
      ? c.emails.map(e => `${e.label}: ${e.email}`).join('; ')
      : (c.email || '');
    const allAddrs = c.addresses && c.addresses.length > 0
      ? c.addresses.map(a => `${a.label}: ${a.address}`).join('; ')
      : (c.address || '');
    return [
      csvEsc(c.name),
      csvEsc(c.status || 'Active'),
      csvEsc(allPhones),
      csvEsc(allEmails),
      csvEsc(allAddrs),
      csvEsc(c.billingTerms),
      c._contractCount || 0,
      c._siteCount || 0
    ].join(',');
  });

  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `customers-${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  window.showToast?.(`Exported ${filtered.length} customers`, 'success');
}

// ============================================
// TAB SWITCHING
// ============================================
function switchCRMTab(tab) {
  CRMState.currentTab = tab;

  // Update tab buttons
  document.querySelectorAll('.crm-tab').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.tab === tab);
  });

  // Update tab content
  document.querySelectorAll('.crm-tab-content').forEach(el => {
    el.classList.toggle('active', el.id === `crm-tab-${tab}`);
  });

  // Render tab content
  if (tab === 'dashboard') renderDashboardTab();
  else if (tab === 'customers') { CRMState.filters.typeFilter = 'customer'; renderCustomerList(); }
  else if (tab === 'vendors') { renderVendorList(); }
  else if (tab === 'opportunities') renderOpportunitiesKanban();
  else if (tab === 'revenue') renderRevenueTab();
  else if (tab === 'tasks') renderTasksTab();
  else if (tab === 'marketing') renderMarketingTab();
}

// ============================================
// MARKETING TAB (Slice 1 — list shell + new-brochure stub)
// Full builder lands in Slice 2 (window.CRMBrochure.openBuilder)
// ============================================
function renderMarketingTab() {
  const el = document.getElementById('crm-marketing-content');
  if (!el) return;
  el.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-md);">
      <div>
        <h3 style="margin:0;">Marketing Brochures</h3>
        <div style="font-size:12px;color:var(--text-secondary);margin-top:2px;">
          Trifold marketing brochures you can print and hand out. Reusable across prospects.
        </div>
      </div>
      <button class="btn btn-primary" onclick="window.CRMBrochure?.openBuilder ? window.CRMBrochure.openBuilder() : window.showToast?.('Builder ships in Slice 2','info')">
        <span class="material-icons" style="font-size:18px;vertical-align:middle;">add</span>
        New Brochure
      </button>
    </div>
    <div id="crm-brochure-list">
      ${window.CRMBrochure?.renderList ? window.CRMBrochure.renderList() : `
        <div style="background:var(--surface-elevated);border:1px dashed var(--border);border-radius:8px;padding:32px;text-align:center;color:var(--text-secondary);">
          <span class="material-icons" style="font-size:48px;color:var(--text-muted);">campaign</span>
          <div style="margin-top:12px;font-size:14px;">No brochures yet — builder ships in the next slice.</div>
        </div>
      `}
    </div>
  `;
}

function dashboardFilterCustomers(filter) {
  CRMState.filters = { search: '', activeOnly: false, hasContracts: false, hasSites: false, statusFilter: null, typeFilter: 'all' };
  if (filter === 'active') CRMState.filters.activeOnly = true;
  else if (filter === 'contracts') CRMState.filters.hasContracts = true;
  else if (filter === 'prospects') CRMState.filters.statusFilter = 'Prospect';
  switchCRMTab('customers');
}

// ============================================
// DASHBOARD TAB
// ============================================
// Computes attention-needed items from already-loaded CRMState.
// Uses CRMState.allOpportunities when loaded; otherwise silently skips opps (async fills in on reload).
function renderAttentionNeededWidget() {
  const now = new Date();
  const todayEnd = new Date(now); todayEnd.setHours(23, 59, 59, 999);

  // Tasks due today or overdue, pending, not snoozed past now
  const dueTasks = (CRMState.crmTasks || []).filter(t => {
    if (t.status !== 'pending') return false;
    if (t.snoozedUntil) {
      const s = t.snoozedUntil.toDate ? t.snoozedUntil.toDate() : new Date(t.snoozedUntil);
      if (s > now) return false;
    }
    const due = t.dueDate?.toDate ? t.dueDate.toDate() : (t.dueDate ? new Date(t.dueDate) : null);
    return due && due <= todayEnd;
  });

  // Opps with overdue _nextActionDate (open stages only)
  const overdueOpps = (CRMState.allOpportunities || []).filter(o => {
    if (o.stage === 'won' || o.stage === 'lost') return false;
    const d = o._nextActionDate?.toDate ? o._nextActionDate.toDate() : (o._nextActionDate ? new Date(o._nextActionDate) : null);
    return d && d <= todayEnd;
  });

  const taskCount = dueTasks.length;
  const oppCount = overdueOpps.length;

  if (taskCount === 0 && oppCount === 0) {
    // Clean state — small affirming bar so the widget doesn't feel empty
    if ((CRMState.allOpportunities || []).length === 0 && (CRMState.crmTasks || []).length === 0) return '';
    return `
      <div style="display:flex;align-items:center;gap:10px;background:linear-gradient(135deg,#F0FDF4,#DCFCE7);border:1px solid #BBF7D0;border-radius:10px;padding:10px 14px;margin-bottom:var(--spacing-md);">
        <span class="material-icons" style="color:#059669;">check_circle</span>
        <div style="font-size:13px;color:#065F46;font-weight:600;">All caught up — nothing due today.</div>
      </div>
    `;
  }

  // Pick top 3 items across both sources, ranked by due date (overdue first)
  const items = [];
  dueTasks.forEach(t => {
    const d = t.dueDate?.toDate ? t.dueDate.toDate() : new Date(t.dueDate);
    items.push({ sortDate: d, kind: 'task', title: t.title || 'Untitled task', subtitle: t.customerName || '', date: d, onclick: `window.CRMModule.switchCRMTab('tasks')` });
  });
  overdueOpps.forEach(o => {
    const d = o._nextActionDate?.toDate ? o._nextActionDate.toDate() : new Date(o._nextActionDate);
    const action = o._nextAction ? escHTML(String(o._nextAction).slice(0, 50)) : 'Follow up';
    items.push({ sortDate: d, kind: 'opp', title: `${action} — ${escHTML(o.title || '')}`, subtitle: o.customerName || '', date: d, onclick: `window.CRMModule.showOpportunityForm('${escJsAttr(o.customerId)}','${escJsAttr(o.id)}')` });
  });
  items.sort((a, b) => a.sortDate - b.sortDate);
  const top = items.slice(0, 3);

  return `
    <div style="background:linear-gradient(135deg,#FFF7ED,#FFEDD5);border:1px solid #FDBA74;border-radius:12px;padding:14px 16px;margin-bottom:var(--spacing-md);">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
        <span class="material-icons" style="color:#C2410C;">notifications_active</span>
        <div style="font-weight:700;color:#9A3412;">Attention Needed</div>
        <div style="display:flex;gap:8px;margin-left:auto;flex-wrap:wrap;">
          ${taskCount > 0 ? `<button class="btn btn-sm" style="background:#FED7AA;color:#9A3412;border:none;padding:4px 10px;font-weight:600;" onclick="window.CRMModule.switchCRMTab('tasks')">${taskCount} task${taskCount !== 1 ? 's' : ''} due</button>` : ''}
          ${oppCount > 0 ? `<button class="btn btn-sm" style="background:#FECACA;color:#991B1B;border:none;padding:4px 10px;font-weight:600;" onclick="window.CRMModule.switchCRMTab('opportunities')">${oppCount} opp${oppCount !== 1 ? 's' : ''} overdue</button>` : ''}
        </div>
      </div>
      <div style="display:flex;flex-direction:column;gap:4px;">
        ${top.map(it => {
          const badge = renderNextActionBadge(it.date, null);
          const icon = it.kind === 'task' ? 'task_alt' : 'developer_board';
          return `
            <div onclick="${it.onclick}" style="display:flex;align-items:center;gap:10px;background:rgba(255,255,255,0.7);border-radius:8px;padding:8px 12px;cursor:pointer;transition:background 0.15s;" onmouseenter="this.style.background='rgba(255,255,255,1)'" onmouseleave="this.style.background='rgba(255,255,255,0.7)'">
              <span class="material-icons" style="font-size:16px;color:#9A3412;">${icon}</span>
              <div style="flex:1;min-width:0;">
                <div style="font-size:13px;font-weight:600;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escHTML(it.title)}</div>
                ${it.subtitle ? `<div style="font-size:11px;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escHTML(it.subtitle)}</div>` : ''}
              </div>
              ${badge}
            </div>
          `;
        }).join('')}
      </div>
    </div>
  `;
}

function renderDashboardTab() {
  const container = document.getElementById('crm-dashboard-content');
  if (!container) return;

  // P0 2026-10-06 — the dashboard counts CUSTOMERS; vendors/subcontractors (incl. the
  // QBO vendor import) live in the same collection and have their own tab.
  const customers = window.BillableCustomer.billableCustomers(CRMState.customers);
  const total = customers.length;
  const active = customers.filter(c => (c.status || 'Active') === 'Active').length;
  const prospects = customers.filter(c => c.status === 'Prospect').length;
  const withContracts = customers.filter(c => (c._contractCount || 0) > 0).length;
  const attentionWidget = renderAttentionNeededWidget();

  // Recent activity - last 5 touched customers.
  // Fall back to createdAt: a customer that was added but never edited has NO
  // updatedAt, and filtering on updatedAt alone dropped it forever — so any
  // tenant whose customers were imported/created and never touched saw an empty
  // "Recent Activity" panel (found auditing the demo, which shows exactly that).
  const _touchedAt = (c) => {
    const ts = c.updatedAt || c.createdAt;
    return ts?.toDate ? ts.toDate().getTime() : 0;
  };
  const recent = [...customers]
    .filter(c => c.updatedAt || c.createdAt)
    .sort((a, b) => _touchedAt(b) - _touchedAt(a))
    .slice(0, 5);

  const inactive = total - active - prospects;

  container.innerHTML = `
    ${attentionWidget}
    <!-- Stats Row -->
    <div class="crm-stats-grid">
      <div class="crm-stat-card crm-stat-clickable" onclick="window.CRMModule.switchCRMTab('customers')">
        <div class="crm-stat-icon" style="background:linear-gradient(135deg,#3B82F6,#2563EB);">
          <span class="material-icons">groups</span>
        </div>
        <div>
          <div class="crm-stat-value">${total}</div>
          <div class="crm-stat-label">Total Customers</div>
        </div>
      </div>
      <div class="crm-stat-card crm-stat-clickable" onclick="window.CRMModule.dashboardFilterCustomers('active')">
        <div class="crm-stat-icon" style="background:linear-gradient(135deg,#059669,#047857);">
          <span class="material-icons">check_circle</span>
        </div>
        <div>
          <div class="crm-stat-value">${active}</div>
          <div class="crm-stat-label">Active</div>
        </div>
      </div>
      <div class="crm-stat-card crm-stat-clickable" onclick="window.CRMModule.dashboardFilterCustomers('prospects')">
        <div class="crm-stat-icon" style="background:linear-gradient(135deg,#F59E0B,#D97706);">
          <span class="material-icons">trending_up</span>
        </div>
        <div>
          <div class="crm-stat-value">${prospects}</div>
          <div class="crm-stat-label">Prospects</div>
        </div>
      </div>
      <div class="crm-stat-card crm-stat-clickable" onclick="window.CRMModule.dashboardFilterCustomers('contracts')">
        <div class="crm-stat-icon" style="background:linear-gradient(135deg,#06B6D4,#0891B2);">
          <span class="material-icons">description</span>
        </div>
        <div>
          <div class="crm-stat-value">${withContracts}</div>
          <div class="crm-stat-label">With Contracts</div>
        </div>
      </div>
    </div>

    <!-- Pipeline Snapshot + Recent Activity -->
    <div class="crm-dashboard-grid" style="display:grid;grid-template-columns:1fr 1fr;gap:var(--spacing-md);">
      <!-- Pipeline Snapshot -->
      <div class="crm-section-card">
        <div class="crm-section-title">
          <span class="material-icons">view_kanban</span> Pipeline Snapshot
        </div>
        ${total === 0 ? '<div class="empty-state-small" style="color:var(--text-secondary);font-size:13px;padding:8px 0;"><span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;opacity:0.55;">people_outline</span>No customers yet.</div>' : `
          <div class="crm-pipeline-snapshot" style="margin-bottom:var(--spacing-md);">
            ${prospects > 0 ? `<div class="crm-pipeline-snapshot-segment" style="flex:${prospects};background:#F59E0B;" title="${prospects} Prospect${prospects !== 1 ? 's' : ''}">${prospects}</div>` : ''}
            ${active > 0 ? `<div class="crm-pipeline-snapshot-segment" style="flex:${active};background:#3B82F6;" title="${active} Active">${active}</div>` : ''}
            ${inactive > 0 ? `<div class="crm-pipeline-snapshot-segment" style="flex:${inactive};background:#94A3B8;" title="${inactive} Inactive">${inactive}</div>` : ''}
          </div>
          <div style="display:flex;gap:var(--spacing-lg);font-size:0.8125rem;">
            <span style="display:flex;align-items:center;gap:4px;"><span style="width:10px;height:10px;border-radius:2px;background:#F59E0B;display:inline-block;"></span> Prospect (${prospects})</span>
            <span style="display:flex;align-items:center;gap:4px;"><span style="width:10px;height:10px;border-radius:2px;background:#3B82F6;display:inline-block;"></span> Active (${active})</span>
            <span style="display:flex;align-items:center;gap:4px;"><span style="width:10px;height:10px;border-radius:2px;background:#94A3B8;display:inline-block;"></span> Inactive (${inactive})</span>
          </div>
        `}
      </div>

      <!-- Recent Activity -->
      <div class="crm-section-card">
        <div class="crm-section-title">
          <span class="material-icons">history</span> Recent Activity
        </div>
        ${recent.length === 0 ? '<div style="color:var(--text-secondary);font-size:13px;">No recent activity.</div>' :
          recent.map(c => {
            const status = c.status || 'Active';
            const dotColor = status === 'Active' ? '#3B82F6' : status === 'Prospect' ? '#F59E0B' : '#94A3B8';
            // Same updatedAt-or-createdAt fallback the list is built on — reading
            // updatedAt alone here would render a blank time for the never-edited
            // customers we now (correctly) include.
            const _ts = c.updatedAt || c.createdAt;
            const time = _ts?.toDate ? formatRelativeTime(_ts.toDate()) : '';
            return `
              <div class="crm-recent-item" style="cursor:pointer;" onclick="window.CRMModule.openCustomerDetail('${escJsAttr(c.id)}')">
                <span class="crm-recent-dot" style="background:${dotColor};"></span>
                <span class="crm-recent-name">${escHTML(c.name)}</span>
                <span class="crm-recent-time">${time}</span>
              </div>
            `;
          }).join('')
        }
      </div>
    </div>

    <!-- Opportunity Pipeline Stats (loads async) -->
    <div id="crm-dashboard-opp-stats" style="margin-top:var(--spacing-md);">
      <div style="color:var(--text-secondary);font-size:0.8125rem;">Loading opportunity data...</div>
    </div>

    <!-- Sales Accountability Metrics (loads async) -->
    <div id="crm-accountability-stats" style="margin-top:var(--spacing-lg);">
      <div style="color:var(--text-secondary);font-size:0.8125rem;">Loading sales activity data...</div>
    </div>
  `;

  // Load opportunity stats async
  loadAllOpportunities().then(() => {
    const statsContainer = document.getElementById('crm-dashboard-opp-stats');
    if (!statsContainer) return;
    const stats = renderDashboardOpportunityStats();
    const _subIds = new Set(CRMState.customers.filter(c => window.BillableCustomer.isVendorLikeCustomer(c)).map(c => c.id));
    const openOpps = CRMState.allOpportunities.filter(o => o.stage !== 'won' && o.stage !== 'lost' && !_subIds.has(o.customerId));

    // Build pipeline snapshot bar by stage
    const stageCountsHTML = OPPORTUNITY_STAGES.filter(s => s.key !== 'won' && s.key !== 'lost').map(s => {
      const count = openOpps.filter(o => o.stage === s.key).length;
      return count > 0 ? `<div class="crm-pipeline-snapshot-segment" style="flex:${count};background:${s.color};" title="${s.label} (${count}) — ${s.probability}% likelihood">${count}</div>` : '';
    }).join('');

    statsContainer.innerHTML = `
      <!-- Opportunity Stats Row -->
      <div class="crm-stats-grid" style="margin-bottom:var(--spacing-md);">
        <div class="crm-stat-card crm-stat-clickable" onclick="window.CRMModule.switchCRMTab('opportunities')">
          <div class="crm-stat-icon" style="background:linear-gradient(135deg,#8B5CF6,#7C3AED);">
            <span class="material-icons">developer_board</span>
          </div>
          <div>
            <div class="crm-stat-value">$${formatDollars(stats.totalPipeline)}</div>
            <div class="crm-stat-label">Pipeline Value</div>
          </div>
        </div>
        <div class="crm-stat-card crm-stat-clickable" onclick="window.CRMModule.switchCRMTab('opportunities')">
          <div class="crm-stat-icon" style="background:linear-gradient(135deg,#10B981,#059669);">
            <span class="material-icons">payments</span>
          </div>
          <div>
            <div class="crm-stat-value">$${formatDollars(stats.weightedRevenue)}</div>
            <div class="crm-stat-label">Weighted Revenue</div>
          </div>
        </div>
        <div class="crm-stat-card crm-stat-clickable" onclick="window.CRMModule.switchCRMTab('opportunities')">
          <div class="crm-stat-icon" style="background:linear-gradient(135deg,#3B82F6,#2563EB);">
            <span class="material-icons">pending_actions</span>
          </div>
          <div>
            <div class="crm-stat-value">${stats.openCount}</div>
            <div class="crm-stat-label">Open Opportunities</div>
          </div>
        </div>
        <div class="crm-stat-card crm-stat-clickable" onclick="window.CRMModule.switchCRMTab('opportunities')">
          <div class="crm-stat-icon" style="background:linear-gradient(135deg,#F59E0B,#D97706);">
            <span class="material-icons">event</span>
          </div>
          <div>
            <div class="crm-stat-value">${stats.closingThisMonth}</div>
            <div class="crm-stat-label">Closing This Month</div>
          </div>
        </div>
      </div>

      ${openOpps.length > 0 ? `
        <div class="crm-section-card" style="margin-bottom:var(--spacing-md);">
          <div class="crm-section-title"><span class="material-icons">developer_board</span> Opportunity Pipeline</div>
          <div class="crm-pipeline-snapshot" style="margin-bottom:var(--spacing-md);">
            ${stageCountsHTML || '<div style="flex:1;background:var(--bg-elevated);border-radius:var(--radius-md);height:32px;"></div>'}
          </div>
          <div style="display:flex;gap:var(--spacing-md);flex-wrap:wrap;font-size:0.75rem;">
            ${OPPORTUNITY_STAGES.filter(s => s.key !== 'won' && s.key !== 'lost').map(s => {
              const ct = openOpps.filter(o => o.stage === s.key).length;
              return ct > 0 ? `<span style="display:flex;align-items:center;gap:4px;"><span style="width:10px;height:10px;border-radius:2px;background:${s.color};display:inline-block;"></span> ${s.label} (${ct}) <span style="color:var(--text-secondary);font-size:11px;">${s.probability}%</span></span>` : '';
            }).join('')}
          </div>
        </div>
      ` : ''}

      ${stats.staleDeals.length > 0 ? `
        <div class="crm-section-card" style="border-left:3px solid #EF4444;">
          <div class="crm-section-title"><span class="material-icons" style="color:#EF4444;">warning</span> Stale Deals (14+ days idle)</div>
          ${stats.staleDeals.slice(0, 5).map(o => {
            const stage = STAGE_MAP[o.stage] || STAGE_MAP['initiating'];
            return `
              <div class="crm-recent-item" style="cursor:pointer;" onclick="window.CRMModule.openCustomerDetail('${escJsAttr(o.customerId)}')">
                <span class="crm-recent-dot" style="background:${stage.color};"></span>
                <span class="crm-recent-name">${escHTML(o.customerName)} — ${escHTML(o.title)}</span>
                <span style="font-size:0.75rem;color:var(--text-secondary);">$${formatDollars(o.revenue || 0)}</span>
              </div>
            `;
          }).join('')}
        </div>
      ` : ''}

      ${(stats.wonCount + stats.lostCount) > 0 ? `
        <div class="crm-section-card">
          <div class="crm-section-title"><span class="material-icons" style="color:#8B5CF6;">insights</span> Performance</div>
          <div class="crm-stats-grid" style="grid-template-columns:repeat(auto-fit,minmax(130px,1fr));">
            <div class="crm-stat-card" style="padding:12px;">
              <div>
                <div class="crm-stat-value" style="font-size:1.25rem;">${stats.winRate !== null ? stats.winRate + '%' : '—'}</div>
                <div class="crm-stat-label">Win Rate</div>
                <div style="font-size:0.7rem;color:var(--text-secondary);">${stats.wonCount}W / ${stats.lostCount}L</div>
              </div>
            </div>
            <div class="crm-stat-card" style="padding:12px;">
              <div>
                <div class="crm-stat-value" style="font-size:1.25rem;">${stats.avgDealSize !== null ? '$' + formatDollars(stats.avgDealSize) : '—'}</div>
                <div class="crm-stat-label">Avg Deal Size</div>
                <div style="font-size:0.7rem;color:var(--text-secondary);">${stats.wonCount} won deal${stats.wonCount !== 1 ? 's' : ''}</div>
              </div>
            </div>
            <div class="crm-stat-card" style="padding:12px;">
              <div>
                <div class="crm-stat-value" style="font-size:1.25rem;">${stats.avgSalesCycle !== null ? stats.avgSalesCycle + 'd' : '—'}</div>
                <div class="crm-stat-label">Avg Sales Cycle</div>
                <div style="font-size:0.7rem;color:var(--text-secondary);">days to close</div>
              </div>
            </div>
            <div class="crm-stat-card" style="padding:12px;">
              <div>
                <div class="crm-stat-value" style="font-size:1.25rem;">$${formatDollars(stats.pipelineVelocity)}</div>
                <div class="crm-stat-label">Monthly Velocity</div>
                <div style="font-size:0.7rem;color:var(--text-secondary);">$ closed/mo (90d)</div>
              </div>
            </div>
          </div>
        </div>
      ` : ''}
    `;
  }).catch(err => {
    console.error('[CRM] Dashboard opp stats error:', err);
    const c = document.getElementById('crm-dashboard-opp-stats');
    if (c) c.innerHTML = '';
  });

  // Load sales accountability metrics async
  loadAccountabilityMetrics().then(metrics => {
    const acctContainer = document.getElementById('crm-accountability-stats');
    if (!acctContainer) return;

    const meetingClick = metrics.latestMeetingCust && metrics.meetings > 0
      ? `window.CRMModule.openCustomerDetail('${escJsAttr(metrics.latestMeetingCust)}')`
      : `window.CRMModule.switchCRMTab('customers')`;
    const callClick = metrics.latestCallCust && metrics.calls > 0
      ? `window.CRMModule.openCustomerDetail('${escJsAttr(metrics.latestCallCust)}')`
      : `window.CRMModule.switchCRMTab('customers')`;
    const emailClick = metrics.latestEmailCust && metrics.emails > 0
      ? `window.CRMModule.openCustomerDetail('${escJsAttr(metrics.latestEmailCust)}')`
      : `window.CRMModule.switchCRMTab('customers')`;
    const newOppClick = metrics.latestNewOppCust && metrics.newOpps > 0
      ? `window.CRMModule.openCustomerDetail('${escJsAttr(metrics.latestNewOppCust)}')`
      : `window.CRMModule.switchCRMTab('opportunities')`;
    const wonClick = metrics.latestWonCust && metrics.dealsWon > 0
      ? `window.CRMModule.openCustomerDetail('${escJsAttr(metrics.latestWonCust)}')`
      : `window.CRMModule.switchCRMTab('revenue')`;
    const revenueClick = metrics.latestWonCust && metrics.revenueWon > 0
      ? `window.CRMModule.openCustomerDetail('${escJsAttr(metrics.latestWonCust)}')`
      : `window.CRMModule.switchCRMTab('revenue')`;

    acctContainer.innerHTML = `
      <div class="crm-section-card">
        <div class="crm-section-title"><span class="material-icons">leaderboard</span> Sales Activity (Last 30 Days)</div>
        <div class="crm-stats-grid" style="grid-template-columns:repeat(3,1fr);">
          <div class="crm-stat-card crm-stat-clickable" onclick="${meetingClick}">
            <div class="crm-stat-icon" style="background:linear-gradient(135deg,#10B981,#059669);">
              <span class="material-icons">handshake</span>
            </div>
            <div>
              <div class="crm-stat-value">${metrics.meetings}</div>
              <div class="crm-stat-label">Face-to-Face</div>
            </div>
          </div>
          <div class="crm-stat-card crm-stat-clickable" onclick="${callClick}">
            <div class="crm-stat-icon" style="background:linear-gradient(135deg,#3B82F6,#2563EB);">
              <span class="material-icons">phone</span>
            </div>
            <div>
              <div class="crm-stat-value">${metrics.calls}</div>
              <div class="crm-stat-label">Phone Calls</div>
            </div>
          </div>
          <div class="crm-stat-card crm-stat-clickable" onclick="${emailClick}">
            <div class="crm-stat-icon" style="background:linear-gradient(135deg,#8B5CF6,#7C3AED);">
              <span class="material-icons">email</span>
            </div>
            <div>
              <div class="crm-stat-value">${metrics.emails}</div>
              <div class="crm-stat-label">Emails</div>
            </div>
          </div>
          <div class="crm-stat-card crm-stat-clickable" onclick="${newOppClick}">
            <div class="crm-stat-icon" style="background:linear-gradient(135deg,#06B6D4,#0891B2);">
              <span class="material-icons">add_circle</span>
            </div>
            <div>
              <div class="crm-stat-value">${metrics.newOpps}</div>
              <div class="crm-stat-label">New Opportunities</div>
            </div>
          </div>
          <div class="crm-stat-card crm-stat-clickable" onclick="${wonClick}">
            <div class="crm-stat-icon" style="background:linear-gradient(135deg,#F59E0B,#D97706);">
              <span class="material-icons">emoji_events</span>
            </div>
            <div>
              <div class="crm-stat-value">${metrics.dealsWon}</div>
              <div class="crm-stat-label">Deals Won</div>
            </div>
          </div>
          <div class="crm-stat-card crm-stat-clickable" onclick="${revenueClick}">
            <div class="crm-stat-icon" style="background:linear-gradient(135deg,#059669,#047857);">
              <span class="material-icons">payments</span>
            </div>
            <div>
              <div class="crm-stat-value">$${formatDollars(metrics.revenueWon)}</div>
              <div class="crm-stat-label">Revenue Won</div>
            </div>
          </div>
        </div>
      </div>
    `;
  }).catch(err => {
    console.error('[CRM] Accountability metrics error:', err);
    const c = document.getElementById('crm-accountability-stats');
    if (c) c.innerHTML = '';
  });
}

function formatRelativeTime(date) {
  const now = new Date();
  const diff = now - date;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Renders a compact "Next: <action> <when>" pill. Red = overdue, amber = ≤2 days out, green = future.
function renderNextActionBadge(rawDate, actionText) {
  if (!rawDate) return '';
  const d = rawDate.toDate ? rawDate.toDate() : new Date(rawDate);
  if (isNaN(d.getTime())) return '';
  const today = new Date(); today.setHours(0,0,0,0);
  const due = new Date(d); due.setHours(0,0,0,0);
  const days = Math.round((due - today) / 86400000);
  let bg, fg, when;
  if (days < 0) { bg = '#FEE2E2'; fg = '#991B1B'; when = days === -1 ? 'overdue 1d' : `overdue ${-days}d`; }
  else if (days === 0) { bg = '#FEF3C7'; fg = '#92400E'; when = 'today'; }
  else if (days <= 2) { bg = '#FEF3C7'; fg = '#92400E'; when = days === 1 ? 'tomorrow' : `in ${days}d`; }
  else if (days <= 7) { bg = '#DBEAFE'; fg = '#1E40AF'; when = `in ${days}d`; }
  else { bg = '#D1FAE5'; fg = '#065F46'; when = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  const label = actionText ? `Next: ${escHTML(String(actionText).slice(0, 40))} · ${when}` : `Next action · ${when}`;
  return `<span style="display:inline-flex;align-items:center;gap:4px;background:${bg};color:${fg};font-size:0.6875rem;font-weight:600;padding:2px 8px;border-radius:999px;white-space:nowrap;"><span class="material-icons" style="font-size:12px;">schedule</span>${label}</span>`;
}

// ============================================
// REVENUE / SALES FORECAST TAB
// ============================================

// Color for closing percentage: red → amber → green
function closingColor(pct) {
  if (pct >= 75) return { bg: '#D1FAE5', text: '#065F46', bar: '#059669' };
  if (pct >= 50) return { bg: '#FEF9C3', text: '#854D0E', bar: '#CA8A04' };
  if (pct >= 25) return { bg: '#FFEDD5', text: '#9A3412', bar: '#EA580C' };
  return { bg: '#FEE2E2', text: '#991B1B', bar: '#DC2626' };
}

function renderRevenueTab() {
  const container = document.getElementById('crm-revenue-content');
  if (!container) return;

  // Get customers with estimated revenue
  const prospects = CRMState.customers
    .filter(c => (c.estRevenue || 0) > 0)
    .map(c => ({
      id: c.id,
      name: c.name,
      estRevenue: c.estRevenue || 0,
      closingPct: c.closingPct ?? 50,
      estCloseDate: c.estCloseDate?.toDate ? c.estCloseDate.toDate() : (c.estCloseDate ? new Date(c.estCloseDate) : null),
      weighted: (c.estRevenue || 0) * ((c.closingPct ?? 50) / 100)
    }));

  const totalPipeline = prospects.reduce((s, c) => s + c.estRevenue, 0);
  const totalWeighted = prospects.reduce((s, c) => s + c.weighted, 0);
  const avgClose = prospects.length > 0 ? prospects.reduce((s, c) => s + c.closingPct, 0) / prospects.length : 0;

  // Group by time period based on estCloseDate
  const now = new Date();
  const endOfWeek = new Date(now); endOfWeek.setDate(now.getDate() + (7 - now.getDay()));
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const endOfQuarter = new Date(now.getFullYear(), Math.ceil((now.getMonth() + 1) / 3) * 3, 0);

  const buckets = [
    { label: 'This Week', end: endOfWeek, items: [] },
    { label: 'This Month', end: endOfMonth, items: [] },
    { label: 'This Quarter', end: endOfQuarter, items: [] },
    { label: 'Later', end: null, items: [] },
    { label: 'No Close Date', end: null, items: [] }
  ];

  prospects.forEach(p => {
    if (!p.estCloseDate) { buckets[4].items.push(p); return; }
    if (p.estCloseDate <= endOfWeek) { buckets[0].items.push(p); return; }
    if (p.estCloseDate <= endOfMonth) { buckets[1].items.push(p); return; }
    if (p.estCloseDate <= endOfQuarter) { buckets[2].items.push(p); return; }
    buckets[3].items.push(p);
  });

  // Sort each bucket by weighted revenue desc
  buckets.forEach(b => b.items.sort((a, c) => c.weighted - a.weighted));

  const maxWeighted = Math.max(...prospects.map(p => p.weighted), 1);

  container.innerHTML = `
    <!-- Forecast Summary -->
    <div class="crm-stats-grid" style="grid-template-columns:repeat(3,1fr);margin-bottom:var(--spacing-lg);">
      <div class="crm-stat-card">
        <div class="crm-stat-icon" style="background:linear-gradient(135deg,#3B82F6,#2563EB);">
          <span class="material-icons">trending_up</span>
        </div>
        <div>
          <div class="crm-stat-value">$${formatDollars(totalPipeline)}</div>
          <div class="crm-stat-label">Total Pipeline</div>
        </div>
      </div>
      <div class="crm-stat-card">
        <div class="crm-stat-icon" style="background:linear-gradient(135deg,#059669,#047857);">
          <span class="material-icons">payments</span>
        </div>
        <div>
          <div class="crm-stat-value">$${formatDollars(totalWeighted)}</div>
          <div class="crm-stat-label">Weighted Revenue</div>
        </div>
      </div>
      <div class="crm-stat-card">
        <div class="crm-stat-icon" style="background:linear-gradient(135deg,#F59E0B,#D97706);">
          <span class="material-icons">speed</span>
        </div>
        <div>
          <div class="crm-stat-value">${Math.round(avgClose)}%</div>
          <div class="crm-stat-label">Est. Close %</div>
        </div>
      </div>
    </div>

    ${prospects.length === 0 ? `
      <div class="crm-section-card" style="text-align:center;padding:var(--spacing-xl);">
        <span class="material-icons" style="font-size:48px;color:var(--text-muted);">attach_money</span>
        <div style="font-weight:600;color:var(--text-primary);margin:var(--spacing-md) 0 var(--spacing-sm);">No revenue estimates yet</div>
        <div style="color:var(--text-secondary);font-size:0.875rem;max-width:400px;margin:0 auto;">
          Edit a customer and enter their <strong>Est. Revenue</strong>, <strong>Close %</strong>, and <strong>Est. Close Date</strong> to build your sales forecast.
        </div>
      </div>
    ` : `
      <!-- Forecast by Time Period -->
      ${buckets.filter(b => b.items.length > 0).map(bucket => {
        const bucketTotal = bucket.items.reduce((s, p) => s + p.estRevenue, 0);
        const bucketWeighted = bucket.items.reduce((s, p) => s + p.weighted, 0);
        return `
          <div class="crm-section-card" style="margin-bottom:var(--spacing-md);">
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-md);">
              <div class="crm-section-title" style="margin-bottom:0;">
                <span class="material-icons">event</span> ${bucket.label}
              </div>
              <div style="display:flex;gap:var(--spacing-lg);font-size:0.8125rem;">
                <span style="color:var(--text-secondary);">Pipeline: <strong style="color:var(--text-primary);">$${formatDollars(bucketTotal)}</strong></span>
                <span style="color:var(--text-secondary);">Weighted: <strong style="color:#059669;">$${formatDollars(bucketWeighted)}</strong></span>
              </div>
            </div>

            <!-- Forecast Table -->
            <table style="width:100%;border-collapse:collapse;font-size:0.875rem;">
              <thead>
                <tr style="border-bottom:2px solid var(--border-light);">
                  <th style="text-align:left;padding:8px;color:var(--text-secondary);font-weight:600;">Customer</th>
                  <th style="text-align:right;padding:8px;color:var(--text-secondary);font-weight:600;">Est. Revenue</th>
                  <th style="text-align:center;padding:8px;color:var(--text-secondary);font-weight:600;width:80px;">Close %</th>
                  <th style="text-align:right;padding:8px;color:var(--text-secondary);font-weight:600;">Weighted</th>
                  <th style="text-align:left;padding:8px;color:var(--text-secondary);font-weight:600;width:100px;">Close Date</th>
                  <th style="padding:8px;width:40%;"></th>
                </tr>
              </thead>
              <tbody>
                ${bucket.items.map(p => {
                  const clr = closingColor(p.closingPct);
                  const closeStr = p.estCloseDate ? p.estCloseDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
                  const barWidth = Math.max(2, (p.weighted / maxWeighted) * 100);
                  return `
                    <tr style="border-bottom:1px solid var(--border-light);cursor:pointer;" onclick="window.CRMModule.openCustomerDetail('${escJsAttr(p.id)}')">
                      <td style="padding:8px 8px;font-weight:500;color:var(--text-primary);">${escHTML(p.name)}</td>
                      <td style="padding:8px;text-align:right;font-weight:600;color:var(--text-primary);">$${formatDollars(p.estRevenue)}</td>
                      <td style="padding:8px;text-align:center;">
                        <span style="display:inline-block;padding:2px 8px;border-radius:var(--radius-sm);background:${clr.bg};color:${clr.text};font-weight:700;font-size:0.8125rem;">${p.closingPct}%</span>
                      </td>
                      <td style="padding:8px;text-align:right;font-weight:600;color:#059669;">$${formatDollars(p.weighted)}</td>
                      <td style="padding:8px;color:var(--text-secondary);font-size:0.8125rem;">${closeStr}</td>
                      <td style="padding:8px;">
                        <div style="height:20px;background:var(--bg-elevated);border-radius:var(--radius-sm);overflow:hidden;">
                          <div style="height:100%;width:${barWidth}%;background:${clr.bar};border-radius:var(--radius-sm);transition:width 0.3s;"></div>
                        </div>
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
        `;
      }).join('')}

      <!-- Grand Totals -->
      <div class="crm-section-card" style="background:var(--bg-page);">
        <table style="width:100%;border-collapse:collapse;font-size:0.9375rem;">
          <tr style="font-weight:700;">
            <td style="padding:10px 8px;color:var(--text-primary);">Grand Total (${prospects.length} account${prospects.length !== 1 ? 's' : ''})</td>
            <td style="padding:10px 8px;text-align:right;color:var(--text-primary);">$${formatDollars(totalPipeline)}</td>
            <td style="padding:10px 8px;text-align:center;width:80px;color:var(--text-secondary);">${Math.round(avgClose)}%</td>
            <td style="padding:10px 8px;text-align:right;color:#059669;">$${formatDollars(totalWeighted)}</td>
            <td colspan="2" style="padding:10px 8px;"></td>
          </tr>
        </table>
      </div>
    `}
  `;
}

function formatDollars(value) {
  if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
  if (value >= 1000) return Math.round(value).toLocaleString();
  return Math.round(value).toLocaleString();
}

// ============================================
// CUSTOMER ADD/EDIT MODAL
// ============================================
// The customer / vendor add-edit modal. Rendered inside the CRM view by
// getCRMHTML, and mounted on <body> by openVendorEditor when another module
// (AR/AP) needs the form while the CRM view is not on screen (TASK-9793).
function getCustomerModalHTML() {
  return `
    <div id="crm-customer-modal" class="modal hidden" style="z-index:1050;">
      <div class="modal-content" style="max-width:500px;">
        <div class="crm-detail-header" id="crm-customer-modal-header">
          <div class="crm-detail-header-info">
            <div class="crm-detail-header-icon">
              <span class="material-icons" id="crm-customer-modal-icon">business</span>
            </div>
            <h3 id="crm-customer-modal-title">Add Customer</h3>
          </div>
          <button class="modal-close" onclick="window.CRMModule.closeCustomerModal()">&times;</button>
        </div>
        <div class="modal-body" id="crm-customer-form"></div>
        <div class="modal-footer">
          <button class="btn btn-secondary" onclick="window.CRMModule.closeCustomerModal()">Cancel</button>
          <button class="btn btn-primary" id="crm-save-customer-btn" onclick="window.CRMModule.saveCustomer()">Save Customer</button>
        </div>
      </div>
    </div>`;
}

// TASK-9793 — a vendor / subcontractor record, and nothing else. The ONLY
// records the AR/AP path may open in this form: firestore.rules lets a
// billing-module member update a customers/{id} doc only while its type is
// vendor or subcontractor and stays byte-equal, so a customer (typed or the
// legacy untyped kind) is refused HERE too, before any form is drawn.
// Pure, never throws; mirrors shared/billable-customer.js NON_CUSTOMER_TYPES.
function isVendorRecord(rec) {
  if (!rec || typeof rec !== 'object') return false;
  const t = typeof rec.type === 'string' ? rec.type.trim().toLowerCase() : '';
  return t === 'vendor' || t === 'subcontractor';
}

const VENDOR_EDITOR_HOST_ID = 'crm-customer-modal-host';

// TASK-9793 (Chris 2026-10-12: "they will have to be able to edit vendors") —
// open the SAME edit form CRM → Vendors uses, from a module that is not CRM
// (AR/AP → AP, ap.js editVendor). Called as window.CRMModule.openVendorEditor
// — never an import across busters (Pitfall #1). ACCESS = MODULES: the
// caller's module reaches this; firestore.rules (canEditMoney vendor arm)
// decides the write. Vendor / subcontractor records only — customers,
// contacts and sites stay CRM-module surfaces.
//   vendor:   the full customers/{id} doc (id + fields) from the caller's own
//             subscription — CRMState.customers is empty when CRM never ran.
//   tenantId: the caller's tenant; CRMState.tenantId is unset until initCRM.
// Returns true when the form opened, false when refused (toast shown).
function openVendorEditor(vendor, tenantId) {
  if (!isVendorRecord(vendor) || !vendor.id) {
    window.showToast?.('Only vendors and subcontractors can be edited here — customers are edited in CRM.', 'warning');
    return false;
  }
  if (!tenantId && !CRMState.tenantId) {
    window.showToast?.('Unable to open the vendor — reload and try again.', 'error');
    return false;
  }
  if (tenantId) CRMState.tenantId = tenantId;
  // saveCustomer reads the PRIOR record from CRMState.customers, which is
  // empty when the CRM view never ran — hand it the caller's copy instead.
  CRMState._vendorEditorPrior = vendor;
  if (!document.getElementById('crm-customer-modal')) {
    const host = document.createElement('div');
    host.id = VENDOR_EDITOR_HOST_ID;
    host.innerHTML = getCustomerModalHTML();
    document.body.appendChild(host);
  }
  showCustomerForm(vendor, { vendorOnly: true });
  return true;
}

// Remove the <body>-mounted modal host (the caller's view cleanup, and
// initCRM, so the CRM view's own modal is never shadowed by a stale copy).
function removeVendorEditorHost() {
  document.getElementById(VENDOR_EDITOR_HOST_ID)?.remove();
}

function showAddCustomer() {
  showCustomerForm(null);
}

function showEditCustomer(customerId) {
  const customer = CRMState.customers.find(c => c.id === customerId);
  if (!customer) return;
  showCustomerForm(customer);
}

function formatDateInput(d) {
  if (!d) return '';
  const date = d.toDate ? d.toDate() : new Date(d);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
}

function selectCustType(type) {
  document.getElementById('crm-cust-type').value = type;
  document.querySelectorAll('#crm-type-selector label').forEach(el => {
    const sel = el.dataset.type === type;
    el.style.borderColor = sel ? 'var(--brand-blue)' : 'var(--border-light)';
    el.style.background = sel ? '#EFF6FF' : '#fff';
  });
}

function showCustomerForm(customer, opts = {}) {
  const isEdit = !!customer;
  // TASK-9793 — opened from AR/AP for a vendor: the type is read-only (the
  // billing rules arm denies ANY type change, vendor <-> subcontractor
  // included) and there is no Delete (no billing delete arm). Both would
  // otherwise fail at Save with a bare permission error.
  const vendorOnly = opts.vendorOnly === true;
  if (!vendorOnly) CRMState._vendorEditorPrior = null;
  const existingType = customer?.type || 'customer';
  const typeLabel = existingType === 'subcontractor' ? 'Subcontractor' : existingType === 'vendor' ? 'Vendor' : 'Customer';
  // TASK-339 — seed pending logo state so removeVendorLogo + saveCustomer
  // see the existing logo (if any) instead of clobbering it on save with no
  // upload picked. Reset is handled by setting pending: null.
  CRMState.vendorLogo = {
    pending: null,
    existing: customer?.logoUrl ? { url: customer.logoUrl } : null,
  };
  document.getElementById('crm-customer-modal-title').textContent = isEdit ? `Edit ${typeLabel}` : 'Add Customer / Subcontractor';
  document.getElementById('crm-customer-modal-icon').textContent = isEdit ? 'edit' : 'business';

  document.getElementById('crm-customer-form').innerHTML = `
    <input type="hidden" id="crm-cust-id" value="${customer?.id || ''}">

    <div class="crm-form-section">
      <div class="crm-form-section-title"><span class="material-icons">business</span> Company Info</div>
      <div class="form-group">
        <label>Type</label>
        <div style="display:flex;gap:8px;" id="crm-type-selector">
          ${[
            { value: 'customer', icon: 'people', label: 'Customer', color: 'var(--brand-blue)' },
            { value: 'subcontractor', icon: 'engineering', label: 'Sub', color: '#F59E0B' },
            { value: 'vendor', icon: 'storefront', label: 'Vendor', color: '#8B5CF6' }
          ].filter(t => !vendorOnly || t.value === existingType).map(t => {
            const sel = (customer?.type || 'customer') === t.value;
            return `<label data-type="${t.value}" style="display:flex;align-items:center;gap:6px;cursor:${vendorOnly ? 'default' : 'pointer'};padding:8px 12px;border-radius:8px;border:2px solid ${sel ? 'var(--brand-blue)' : 'var(--border-light)'};background:${sel ? '#EFF6FF' : '#fff'};font-size:0.85rem;font-weight:500;flex:1;justify-content:center;" ${vendorOnly ? '' : `onclick="window.CRMModule.selectCustType('${escJsAttr(t.value)}')"`}>
              <span class="material-icons" style="font-size:18px;color:${t.color};">${t.icon}</span> ${t.label}
            </label>`;
          }).join('')}
        </div>
        ${vendorOnly ? '<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">Type is changed in CRM → Vendors.</div>' : ''}
        <input type="hidden" id="crm-cust-type" value="${customer?.type || 'customer'}">
      </div>
      <div class="form-group">
        <label>Company Name *</label>
        <input type="text" id="crm-cust-name" class="form-control" value="${escAttr(customer?.name || '')}" data-maxlen="100">
      </div>
      <!-- TASK-339 — Vendor/Company logo upload. Renders on vendor cards in
           the Vendors tab; field exists for all customer types so customer
           logos can be added later without re-shipping the modal.
           TASK-9402 (Chris 9/28): accept now lists HEIC — _acceptVendorLogo
           converts it (TASK-9397), and the old jpeg/png/webp-only hint hid
           .heic files from Windows/Android pickers. -->
      <div class="form-group">
        <label>Logo <span style="color:var(--text-secondary);font-weight:400;font-size:0.78rem;">— optional, shown on vendor card</span></label>
        <div class="asset-photo-upload" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
          <input type="file" id="crm-vendor-logo-input" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" style="display:none"
            onchange="window.CRMModule.handleVendorLogo(event)">
          <div id="crm-vendor-logo-preview" class="asset-photo-preview ${customer?.logoUrl ? '' : 'hidden'}">${customer?.logoUrl ? `<div class="asset-photo-thumb"><img src="${escAttr(customer.logoUrl)}" alt="Vendor logo"><button type="button" class="btn-icon btn-icon-danger asset-photo-remove" onclick="window.CRMModule.removeVendorLogo()" title="Remove logo"><span class="material-icons">close</span></button></div>` : ''}</div>
          <button type="button" class="btn btn-secondary btn-sm"
            onclick="document.getElementById('crm-vendor-logo-input').click()" style="white-space:nowrap;">
            <span class="material-icons" style="font-size:16px;">add_photo_alternate</span> Logo
          </button>
        </div>
      </div>
      <div class="form-group">
        <label>Status</label>
        <select id="crm-cust-status" class="form-control">
          ${CUSTOMER_STATUSES.map(s => `<option value="${s}" ${(customer?.status || 'Active') === s ? 'selected' : ''}>${s}</option>`).join('')}
        </select>
      </div>
      <div class="form-group">
        <label>Partnership <span style="color:var(--text-secondary);font-weight:400;font-size:0.78rem;">— for channel partners only</span></label>
        <select id="crm-cust-partnership-tier" class="form-control">
          <option value="" ${!customer?.partnershipTier ? 'selected' : ''}>Not a partner</option>
          <option value="prospect" ${customer?.partnershipTier === 'prospect' ? 'selected' : ''}>Prospect — initial outreach</option>
          <option value="engaged" ${customer?.partnershipTier === 'engaged' ? 'selected' : ''}>Engaged — demo / nurture</option>
          <option value="signed" ${customer?.partnershipTier === 'signed' ? 'selected' : ''}>Signed — agreement complete</option>
          <option value="active" ${customer?.partnershipTier === 'active' ? 'selected' : ''}>Active — sending referrals</option>
          <option value="dormant" ${customer?.partnershipTier === 'dormant' ? 'selected' : ''}>Dormant — went quiet</option>
        </select>
      </div>
      ${(CRMState.profiles || []).length > 0 ? `
        <div class="form-group">
          <label>Assigned To <span style="color:var(--text-secondary);font-weight:400;font-size:0.78rem;">— which rep owns this account</span></label>
          <select id="crm-cust-assigned-to" class="form-control">
            <option value="" ${!customer?.assignedTo ? 'selected' : ''}>Unassigned (shared)</option>
            ${(CRMState.profiles || []).map(p => `<option value="${escAttr(p.id)}" ${customer?.assignedTo === p.id ? 'selected' : (!customer && CRMState.activeProfile?.id === p.id && CRMState.activeProfile?.role !== 'supervisor') ? 'selected' : ''}>${escAttr(p.name)}${p.role === 'supervisor' ? ' (Supervisor)' : ''}</option>`).join('')}
          </select>
        </div>
      ` : ''}
    </div>

    <div class="crm-form-section">
      <div class="crm-form-section-title"><span class="material-icons">contact_phone</span> Contact Info</div>
      <div class="form-group">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px;">
          <label style="margin-bottom:0;">Phone Numbers</label>
          <button type="button" class="btn btn-secondary btn-sm" onclick="window.CRMModule.addPhoneRow()" style="padding:3px 10px;font-size:12px;">
            <span class="material-icons" style="font-size:14px;">add</span> Add Phone
          </button>
        </div>
        <div id="crm-cust-phones" style="display:flex;flex-direction:column;gap:6px;"></div>
      </div>
      <div class="form-group">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px;">
          <label style="margin-bottom:0;">Emails</label>
          <button type="button" class="btn btn-secondary btn-sm" onclick="window.CRMModule.addEmailRow()" style="padding:3px 10px;font-size:12px;">
            <span class="material-icons" style="font-size:14px;">add</span> Add Email
          </button>
        </div>
        <div id="crm-cust-emails" style="display:flex;flex-direction:column;gap:6px;"></div>
      </div>
      <div class="form-group">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px;">
          <label style="margin-bottom:0;">Addresses</label>
          <button type="button" class="btn btn-secondary btn-sm" onclick="window.CRMModule.addAddressRow()" style="padding:3px 10px;font-size:12px;">
            <span class="material-icons" style="font-size:14px;">add</span> Add Address
          </button>
        </div>
        <div id="crm-cust-addresses" style="display:flex;flex-direction:column;gap:6px;"></div>
      </div>
    </div>

    <div class="crm-form-section">
      <div class="crm-form-section-title"><span class="material-icons">receipt_long</span> Billing</div>
      <div class="form-group">
        <label style="display:flex;align-items:center;gap:8px;cursor:${_isUnrestrictedCrmCaller() ? 'pointer' : 'not-allowed'};">
          <input type="checkbox" id="crm-cust-internal-time" ${customer?.internalTimeOnly ? 'checked' : ''} ${_isUnrestrictedCrmCaller() ? '' : 'disabled'}>
          <span>Internal time only (no billing)</span>
        </label>
        <p style="font-size:11px;color:var(--text-secondary);margin:2px 0 0 26px;">Time-tracking customers like Shop or Office. Clock-ins log hours but don't create customer ticket lines, and the payroll export breaks these hours out in their own column.${_isUnrestrictedCrmCaller() ? '' : ' <strong>Only the company account can change this</strong> — it\'s the off-books flag, so it\'s locked for non-admins.'}</p>
      </div>
      <div class="form-group">
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer;">
          <input type="checkbox" id="crm-cust-ticket-emails-off" ${customer?.ticketEmailsOff === true ? 'checked' : ''}>
          <span>Don't email tickets to this customer</span>
        </label>
        <p style="font-size:11px;color:var(--text-secondary);margin:2px 0 0 26px;">Hides the 'unsent' count for this customer. You can still email a ticket by hand.</p>
        <p id="crm-cust-ticket-email-note" style="display:none;font-size:11px;color:#92400E;background:#FFFBEB;border:1px solid #FDE68A;border-radius:6px;padding:4px 8px;margin:4px 0 0 26px;"></p>
      </div>
      <div class="form-group">
        <label>Billing Terms</label>
        <select id="crm-cust-billing" class="form-control">
          <option value="">-- Select --</option>
          ${PAYMENT_TERMS.map(t => `<option value="${t}" ${customer?.billingTerms === t ? 'selected' : ''}>${t}</option>`).join('')}
        </select>
      </div>
      <div class="form-group" style="margin-top:8px;">
        <label style="font-size:13px;font-weight:600;">Required Fields</label>
        <p style="font-size:11px;color:var(--text-secondary);margin:2px 0 6px;">A ticket for this customer can't be marked Ready or invoiced until every checked field is filled in (a written exception like "no PO per Eric" counts).</p>
        <div class="required-fields-chips">
          ${[
            { key: 'poNumber', label: 'PO Number' },
            { key: 'vendorNumber', label: 'Vendor #' },
            { key: 'afeNumber', label: 'AFE / Cost Center' },
            { key: 'wellName', label: 'Well Name' },
            { key: 'wellNumber', label: 'Well #' },
            { key: 'location', label: 'Location' },
            { key: 'workPerformed', label: 'Work Performed' },
            { key: 'notes', label: 'Notes' },
          ].map(f => `<label><input type="checkbox" name="crm-req-field" value="${f.key}" ${(customer?.requiredFields || []).includes(f.key) ? 'checked' : ''}>${f.label}</label>`).join('')}
        </div>
      </div>
      <!-- TASK-1619 — ACH bank detail + Tax ID now live in the admin-only
           customersSecure sibling (firestore.rules), NOT on this broadcast
           customer doc. customer here never carries these fields anymore
           (server won't return them to a non-admin, and the migration strips
           them from customers entirely) — so these inputs always start
           EMPTY and, for an admin editing an existing customer, get filled
           in asynchronously by loadCustomerSecureFields() right after this
           form renders (see showCustomerForm). Non-admins see the section
           disabled with no fetch attempted at all (avoids a permission-denied
           console error and never even ASKS for data they can't have). -->
      <div class="crm-form-section-title" style="margin-top:12px;font-size:13px;"><span class="material-icons" style="font-size:16px;">account_balance</span> ACH Information</div>
      <div class="form-group">
        <label>Bank Name</label>
        <input type="text" id="crm-cust-ach-bank" class="form-control" value="" placeholder="${_crmIsTenantAdmin() ? 'e.g. Wells Fargo' : 'Admin only'}" ${_crmIsTenantAdmin() ? '' : 'disabled'}>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
        <div class="form-group">
          <label>Routing Number</label>
          <input type="text" id="crm-cust-ach-routing" class="form-control" value="" placeholder="${_crmIsTenantAdmin() ? '9 digits' : 'Admin only'}" maxlength="9" ${_crmIsTenantAdmin() ? '' : 'disabled'}>
        </div>
        <div class="form-group">
          <label>Account Number</label>
          <input type="text" id="crm-cust-ach-account" class="form-control" value="" placeholder="${_crmIsTenantAdmin() ? 'Account #' : 'Admin only'}" ${_crmIsTenantAdmin() ? '' : 'disabled'}>
        </div>
      </div>
      ${!_crmIsTenantAdmin() ? '<p style="font-size:11px;color:var(--text-secondary);margin:2px 0 0;"><strong>Only an owner/admin can view or edit ACH/tax detail</strong> — it\'s bank routing + account data, locked for non-admins.</p>' : ''}
      <div class="crm-form-section-title" style="margin-top:12px;font-size:13px;"><span class="material-icons" style="font-size:16px;">receipt</span> 1099 Tracking</div>
      <div class="form-group">
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer;">
          <input type="checkbox" id="crm-cust-is1099" ${customer?.is1099 ? 'checked' : ''}>
          <span>This vendor requires a 1099</span>
        </label>
        <input type="hidden" id="crm-cust-1099-suggest-decision" value="">
        ${(customer?.qbo1099Suggested === true && !customer?.is1099 && (customer?.type === 'vendor' || customer?.type === 'subcontractor')) ? `
        <div id="crm-cust-1099-suggest" style="margin-top:6px;padding:8px 10px;border:1px solid #FDE68A;background:#FFFBEB;border-radius:6px;font-size:12px;color:var(--text-primary);display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
          <span class="material-icons" style="font-size:16px;color:#92400E;">info</span>
          <span style="flex:1;min-width:160px;">QuickBooks marks this vendor 1099 &mdash; apply? (Card payments are reported on the card processor's 1099-K, so check how you pay them.)</span>
          <button type="button" class="btn btn-secondary btn-sm" onclick="window.CRMModule.decide1099Suggestion('apply')">Apply</button>
          <button type="button" class="btn btn-secondary btn-sm" onclick="window.CRMModule.decide1099Suggestion('dismiss')">Not 1099</button>
        </div>` : ''}
      </div>
      <div class="form-group">
        <label>Tax ID (EIN/SSN)</label>
        <input type="text" id="crm-cust-taxid" class="form-control" value="" placeholder="${_crmIsTenantAdmin() ? 'XX-XXXXXXX' : 'Admin only'}" maxlength="20" ${_crmIsTenantAdmin() ? '' : 'disabled'}>
      </div>
      <div class="form-group">
        <label>Notes</label>
        <textarea id="crm-cust-notes" class="form-control" rows="3" style="resize:vertical;">${escHTML(customer?.notes || '')}</textarea>
      </div>
    </div>

    ${!isEdit ? `
    <div class="crm-form-section">
      <div class="crm-form-section-title"><span class="material-icons">people</span> Contacts</div>
      <div style="display:flex;align-items:center;justify-content:flex-end;margin-bottom:8px;">
        <button type="button" class="btn btn-secondary btn-sm" onclick="window.CRMModule.addPocRow()" style="padding:4px 10px;font-size:12px;">
          <span class="material-icons" style="font-size:14px;">add</span> Add Contact
        </button>
      </div>
      <div id="crm-cust-pocs"></div>
    </div>
    ` : ''}
  `;

  // Update footer — show delete button only in edit mode
  const footer = document.querySelector('#crm-customer-modal .modal-footer');
  if (footer) {
    if (isEdit && !vendorOnly) {
      footer.style.justifyContent = 'space-between';
      footer.innerHTML = `
        <div style="display:flex;gap:8px;flex-wrap:wrap;">
          <button class="btn btn-sm" style="color:#DC2626;border:1px solid #FECACA;background:#FEF2F2;" onclick="window.CRMModule.deleteCustomer('${escJsAttr(customer.id)}')">
            <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">delete</span> Delete
          </button>
          ${customer.mergedIntoId ? '' : `<button class="btn btn-sm btn-secondary" id="crm-merge-open-btn" onclick="window.CRMModule.showMergeCustomer('${escJsAttr(customer.id)}')">
            <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">call_merge</span> Merge into…
          </button>`}
        </div>
        <div style="display:flex;gap:8px;">
          <button class="btn btn-secondary" onclick="window.CRMModule.closeCustomerModal()">Cancel</button>
          <button class="btn btn-primary" id="crm-save-customer-btn" onclick="window.CRMModule.saveCustomer()">Save Customer</button>
        </div>`;
    } else {
      footer.style.justifyContent = '';
      footer.innerHTML = `
        <button class="btn btn-secondary" onclick="window.CRMModule.closeCustomerModal()">Cancel</button>
        <button class="btn btn-primary" id="crm-save-customer-btn" onclick="window.CRMModule.saveCustomer()">Save Customer</button>`;
    }
  }

  // Populate phone rows
  _phoneCounter = 0;
  if (customer?.phones && customer.phones.length > 0) {
    customer.phones.forEach(p => addPhoneRow(p.label, p.phone));
  } else if (customer?.phone) {
    addPhoneRow('Main', customer.phone);
  } else if (!isEdit) {
    addPhoneRow('Main', '');
  }

  // Populate email rows
  _emailCounter = 0;
  if (customer?.emails && customer.emails.length > 0) {
    customer.emails.forEach(e => addEmailRow(e.label, e.email));
  } else if (customer?.email) {
    addEmailRow('General', customer.email);
  } else if (!isEdit) {
    addEmailRow('General', '');
  }

  // Populate address rows
  _addressCounter = 0;
  if (customer?.addresses && customer.addresses.length > 0) {
    customer.addresses.forEach(a => addAddressRow(a.label, a.address));
  } else if (customer?.address) {
    addAddressRow('Main', customer.address);
  } else if (!isEdit) {
    addAddressRow('Main', '');
  }

  document.getElementById('crm-customer-modal').classList.remove('hidden');
  setTimeout(() => document.getElementById('crm-cust-name')?.focus(), 100);

  // TASK-1619 — ACH/tax fields no longer come with `customer` (they live in
  // customersSecure, gated separately). Fetch + fill them in AFTER the form
  // is up, admin-only, edit-only (a new customer has no secure doc yet).
  // Fire-and-forget: the modal is already usable while this resolves.
  if (isEdit && _crmIsTenantAdmin()) {
    loadCustomerSecureFields(customer.id);
  }
  // TASK-9588 — the "No ticket emails" flag doesn't change the driver close-out
  // box (contract fieldTicketSettings.requireEmailCopy still governs it); say so.
  if (isEdit) loadTicketEmailContractNote(customer.id);
}

// TASK-9588 — one-shot contracts read for the note under "Don't email tickets
// to this customer". Shown only while the box is ticked; stale-modal guarded.
async function loadTicketEmailContractNote(customerId) {
  try {
    const snap = await getDocs(collection(db, 'tenants', CRMState.tenantId, 'customers', customerId, 'contracts'));
    if (document.getElementById('crm-cust-id')?.value !== customerId) return;
    const names = snap.docs.map(d => d.data())
      .filter(c => c && c.fieldTicketSettings && c.fieldTicketSettings.requireEmailCopy === true)
      .map(c => c.title || c.name || 'a contract');
    const note = document.getElementById('crm-cust-ticket-email-note');
    const box = document.getElementById('crm-cust-ticket-emails-off');
    if (!note || !box || !names.length) return;
    note.textContent = `Drivers are still asked to email a copy on ${names.length === 1 ? 'contract ' + names[0] : names.length + ' contracts (' + names.join(', ') + ')'}.`;
    const sync = () => { note.style.display = box.checked ? '' : 'none'; };
    box.addEventListener('change', sync);
    sync();
  } catch (err) {
    console.warn('[CRM] ticket-email contract note skipped:', err.message);
  }
}

// TASK-1619 — one-time read of the admin-gated customersSecure sibling to
// populate the ACH/Tax ID inputs for an existing customer. Guarded by the
// caller (isEdit && _crmIsTenantAdmin()) so this is never even attempted for
// a non-admin or a not-yet-created customer. If the modal was closed (or a
// different customer opened) before this resolves, the id in the hidden
// #crm-cust-id field is checked before writing into the DOM so a slow fetch
// can't stomp a different customer's freshly-opened form.
async function loadCustomerSecureFields(customerId) {
  try {
    const secSnap = await getDoc(doc(db, 'tenants', CRMState.tenantId, 'customersSecure', customerId));
    // Stale-response guard — bail if the modal moved on.
    const currentId = document.getElementById('crm-cust-id')?.value;
    if (currentId !== customerId) return;
    const sec = secSnap.exists() ? secSnap.data() : {};
    const bankEl = document.getElementById('crm-cust-ach-bank');
    const routingEl = document.getElementById('crm-cust-ach-routing');
    const accountEl = document.getElementById('crm-cust-ach-account');
    const taxIdEl = document.getElementById('crm-cust-taxid');
    if (bankEl) bankEl.value = sec.achBankName || '';
    if (routingEl) routingEl.value = sec.achRouting || '';
    if (accountEl) accountEl.value = sec.achAccount || '';
    if (taxIdEl) taxIdEl.value = sec.taxId || '';
  } catch (err) {
    console.warn('[CRM] customersSecure fetch failed:', err.message);
  }
}

let _pocCounter = 0;
function addPocRow() {
  const container = document.getElementById('crm-cust-pocs');
  if (!container) return;
  const idx = _pocCounter++;
  const row = document.createElement('div');
  row.className = 'crm-poc-row';
  row.id = `crm-poc-${idx}`;
  row.innerHTML = `
    <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
      <span style="font-size:12px;font-weight:600;color:var(--text-secondary);">Contact ${container.children.length + 1}</span>
      <button type="button" onclick="window.CRMModule.removePocRow('crm-poc-${idx}')" style="margin-left:auto;background:none;border:none;cursor:pointer;color:var(--text-secondary);padding:2px;" title="Remove">
        <span class="material-icons" style="font-size:16px;">close</span>
      </button>
    </div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
      <input type="text" class="form-control poc-name" placeholder="Name *" style="font-size:13px;padding:6px 8px;">
      <input type="text" class="form-control poc-title" placeholder="Title" style="font-size:13px;padding:6px 8px;">
    </div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-top:6px;">
      <input type="tel" class="form-control poc-phone" placeholder="Phone" style="font-size:13px;padding:6px 8px;">
      <input type="email" class="form-control poc-email" placeholder="Email" style="font-size:13px;padding:6px 8px;">
    </div>
  `;
  container.appendChild(row);
}

function removePocRow(rowId) {
  document.getElementById(rowId)?.remove();
}

function gatherPocs() {
  const rows = document.querySelectorAll('#crm-cust-pocs .crm-poc-row');
  const pocs = [];
  rows.forEach(row => {
    const name = row.querySelector('.poc-name')?.value.trim();
    if (!name) return;
    pocs.push({
      name,
      title: row.querySelector('.poc-title')?.value.trim() || '',
      phone: row.querySelector('.poc-phone')?.value.trim() || '',
      email: row.querySelector('.poc-email')?.value.trim() || ''
    });
  });
  return pocs;
}

// ============================================
// MULTI-PHONE ROWS
// ============================================
const PHONE_LABELS = ['Main', 'Office', 'Cell', 'Dispatch', 'Billing', 'Fax'];
let _phoneCounter = 0;

function addPhoneRow(label, phone) {
  const container = document.getElementById('crm-cust-phones');
  if (!container) return;
  const idx = _phoneCounter++;
  const row = document.createElement('div');
  row.className = 'crm-phone-row';
  row.id = `crm-phone-${idx}`;
  row.innerHTML = `
    <select class="form-control phone-label" style="width:120px;font-size:13px;padding:6px 8px;">
      ${PHONE_LABELS.map(l => `<option value="${l}" ${l === (label || 'Main') ? 'selected' : ''}>${l}</option>`).join('')}
    </select>
    <input type="tel" class="form-control phone-number" placeholder="(701) 555-1234" value="${escAttr(phone || '')}" style="flex:1;font-size:13px;padding:6px 8px;">
    <button type="button" onclick="window.CRMModule.removePhoneRow('crm-phone-${idx}')" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);padding:4px;" title="Remove">
      <span class="material-icons" style="font-size:16px;">close</span>
    </button>
  `;
  container.appendChild(row);
}

function removePhoneRow(rowId) {
  document.getElementById(rowId)?.remove();
}

function gatherPhones() {
  const rows = document.querySelectorAll('#crm-cust-phones .crm-phone-row');
  const phones = [];
  rows.forEach(row => {
    const phone = row.querySelector('.phone-number')?.value.trim();
    if (!phone) return;
    phones.push({
      label: row.querySelector('.phone-label')?.value || 'Main',
      phone
    });
  });
  return phones;
}

// ============================================
// MULTI-EMAIL ROWS
// ============================================
const EMAIL_LABELS = ['General', 'Billing', 'Operations', 'Dispatch', 'Safety', 'Accounting', 'Personal'];
let _emailCounter = 0;

function addEmailRow(label, email) {
  const container = document.getElementById('crm-cust-emails');
  if (!container) return;
  const idx = _emailCounter++;
  const row = document.createElement('div');
  row.className = 'crm-email-row';
  row.id = `crm-email-${idx}`;
  row.innerHTML = `
    <select class="form-control email-label" style="width:120px;font-size:13px;padding:6px 8px;">
      ${EMAIL_LABELS.map(l => `<option value="${l}" ${l === (label || 'General') ? 'selected' : ''}>${l}</option>`).join('')}
    </select>
    <input type="email" class="form-control email-addr" placeholder="email@company.com" value="${escAttr(email || '')}" style="flex:1;font-size:13px;padding:6px 8px;">
    <button type="button" onclick="window.CRMModule.removeEmailRow('crm-email-${idx}')" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);padding:4px;" title="Remove">
      <span class="material-icons" style="font-size:16px;">close</span>
    </button>
  `;
  container.appendChild(row);
}

function removeEmailRow(rowId) {
  document.getElementById(rowId)?.remove();
}

function gatherEmails() {
  const rows = document.querySelectorAll('#crm-cust-emails .crm-email-row');
  const emails = [];
  rows.forEach(row => {
    const email = row.querySelector('.email-addr')?.value.trim();
    if (!email) return;
    emails.push({
      label: row.querySelector('.email-label')?.value || 'General',
      email
    });
  });
  return emails;
}

/** Get primary email from customer (backward compat) */
function getPrimaryEmail(customer) {
  if (customer.emails && customer.emails.length > 0) return customer.emails[0].email;
  return customer.email || '';
}

/** Get all emails as display string */
function getAllEmailsDisplay(customer) {
  if (customer.emails && customer.emails.length > 0) {
    return customer.emails.map(e => e.email).join(', ');
  }
  return customer.email || '';
}

// ============================================
// MULTI-ADDRESS ROWS
// ============================================
const ADDRESS_LABELS = ['Main', 'Billing', 'Mailing', 'Yard', 'Site', 'Home Office'];
let _addressCounter = 0;

function addAddressRow(label, address) {
  const container = document.getElementById('crm-cust-addresses');
  if (!container) return;
  const idx = _addressCounter++;
  const row = document.createElement('div');
  row.className = 'crm-address-row';
  row.id = `crm-addr-${idx}`;
  row.innerHTML = `
    <select class="form-control addr-label" style="width:120px;font-size:13px;padding:6px 8px;">
      ${ADDRESS_LABELS.map(l => `<option value="${l}" ${l === (label || 'Main') ? 'selected' : ''}>${l}</option>`).join('')}
    </select>
    <div style="flex:1;position:relative;">
      <input type="text" class="form-control addr-value" placeholder="123 Main St, City, ST 12345" value="${escAttr(address || '')}" style="font-size:13px;padding:6px 8px;width:100%;">
    </div>
    <button type="button" onclick="window.CRMModule.removeAddressRow('crm-addr-${idx}')" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);padding:4px;" title="Remove">
      <span class="material-icons" style="font-size:16px;">close</span>
    </button>
  `;
  container.appendChild(row);
  // Address autocomplete
  const addrInput = row.querySelector('.addr-value');
  if (addrInput && window.attachAddressAutocomplete) window.attachAddressAutocomplete(addrInput);
}

function removeAddressRow(rowId) {
  document.getElementById(rowId)?.remove();
}

function gatherAddresses() {
  const rows = document.querySelectorAll('#crm-cust-addresses .crm-address-row');
  const addresses = [];
  rows.forEach(row => {
    const address = row.querySelector('.addr-value')?.value.trim();
    if (!address) return;
    addresses.push({
      label: row.querySelector('.addr-label')?.value || 'Main',
      address,
      // TASK-9843 — structured state, read from the text ("City, ST 12345",
      // "City, ST", "City, AB T2P 1J9", a full name). '' when it can't be read
      // with confidence — never a guess. Address rows saved before this carry
      // no `state` and are parsed the same way at read time.
      state: window.RegisteredStates?.parseAddressState(address) || ''
    });
  });
  return addresses;
}

// TASK-9843 — the small red "! Not registered in ST" under a customer whose
// address state is KNOWN and not on the company's registered list (Compliance
// → Company → Registered states). window.RegisteredStates is the shared rule:
// an empty list or an unreadable address = no flag (never guess). A heads-up
// only — nothing here, or anywhere, blocks a job for the customer.
function _crmRegisteredStateFlag(customer, detail) {
  const RS = window.RegisteredStates;
  if (!RS) return '';
  let codes = [];
  try { codes = RS.unregistered(customer, window.AppState?.tenantData?.registeredStates); } catch (_) { codes = []; }
  if (!codes.length) return '';
  const label = RS.flagLabel(codes);
  const tip = `Your company is not listed as registered in ${codes.join(', ')} (Compliance → Company → Registered states). A heads-up only — nothing is blocked.`;
  return `<div class="crm-rs-flag" title="${escAttr(tip)}" style="display:flex;align-items:center;gap:6px;font-size:${detail ? '13px' : '12px'};font-weight:600;color:var(--action-danger);margin-top:${detail ? '6px' : '2px'};">
    <span aria-hidden="true" style="display:inline-flex;align-items:center;justify-content:center;width:14px;height:14px;border-radius:50%;background:var(--action-danger);color:#fff;font-size:10px;line-height:1;flex-shrink:0;">!</span>
    <span>${escHTML(label)}</span>
  </div>`;
}

// ============================================================================
// TASK-339 — Vendor logo upload helpers (Lacey 2026-05-20)
// Mirrors the asset photo pattern from roster.js. Logos render on the vendor
// card in renderVendorList. Field is per-customer (any type may have a logo,
// but only the vendors tab surfaces it for now). Storage path:
// `tenants/{tid}/vendor-logos/{ts}_vendor.jpg`.
// ============================================================================
async function uploadVendorLogo(tenant) {
  const pending = CRMState.vendorLogo?.pending;
  if (!pending) return CRMState.vendorLogo?.existing?.url || null;
  try {
    // TASK-9397 — the raw-HEIC branch is gone (it stored `_vendor.heic`, a
    // logo only Safari could show). The pick already converted; this re-check
    // is a no-op for a drawable file and a conversion for anything that isn't.
    const drawable = await toDrawablePhoto(pending.file);
    const blob = await processPhoto(drawable, { maxDimension: 600, quality: 0.85, maxSizeKB: 200 });
    const fileName = `${Date.now()}_vendor.jpg`;
    const storageRef = ref(storage, `tenants/${tenant}/vendor-logos/${fileName}`);
    await uploadBytes(storageRef, blob, { contentType: 'image/jpeg' });
    return await getDownloadURL(storageRef);
  } catch (err) {
    console.error('[CRM] Vendor logo upload error:', err?.cause || err);
    window.showToast?.('Logo upload failed — vendor saved without logo', 'error');
    return CRMState.vendorLogo?.existing?.url || null;
  }
}

// TASK-9397 — logo picks convert (HEIC → JPEG) before the preview, so the
// thumbnail shown is the file that uploads. saveCustomer waits these out.
const _crmLogoPrep = createPhotoPrepTracker();

function handleVendorLogo(e) {
  const file = e.target.files?.[0];
  if (e?.target) e.target.value = '';
  if (!file) return;
  return _crmLogoPrep.track(_acceptVendorLogo(file));
}

async function _acceptVendorLogo(file) {
  const slot = CRMState.vendorLogo;
  if (!slot) return;
  // COD: claim the pick BEFORE any await, so a Remove / newer pick made
  // while we sniff the bytes always wins.
  const seq = (slot.pickSeq || 0) + 1;
  slot.pickSeq = seq;
  const allowed = ['image/jpeg', 'image/png', 'image/webp'];
  if (!allowed.includes(file.type) && !(await detectHeic(file))) {
    if (slot.pickSeq === seq) window.showToast?.('Only JPG, PNG, WebP or iPhone (HEIC) images allowed', 'error');
    return;
  }
  let drawable;
  try {
    drawable = await toDrawablePhoto(file);
  } catch (err) {
    console.warn('[CRM] logo conversion failed:', err?.cause || err);
    if (slot.pickSeq === seq) window.showToast?.(photoErrorMessage(err), 'error');
    return;
  }
  // A newer pick, a Remove, or a different customer's form superseded this one.
  if (slot.pickSeq !== seq || CRMState.vendorLogo !== slot) return;
  if (slot.pending?.previewUrl) {
    try { URL.revokeObjectURL(slot.pending.previewUrl); } catch (_) {}
  }
  slot.pending = { file: drawable, previewUrl: URL.createObjectURL(drawable) };
  renderVendorLogoPreview();
}

function removeVendorLogo() {
  CRMState.vendorLogo.pickSeq = (CRMState.vendorLogo.pickSeq || 0) + 1;
  if (CRMState.vendorLogo.pending?.previewUrl) {
    URL.revokeObjectURL(CRMState.vendorLogo.pending.previewUrl);
  }
  CRMState.vendorLogo.pending = null;
  CRMState.vendorLogo.existing = null;
  renderVendorLogoPreview();
}

function renderVendorLogoPreview() {
  const preview = document.getElementById('crm-vendor-logo-preview');
  if (!preview) return;
  const photo = CRMState.vendorLogo.pending || CRMState.vendorLogo.existing;
  if (photo) {
    const src = photo.previewUrl || photo.url;
    preview.innerHTML = `
      <div class="asset-photo-thumb">
        <img src="${src}" alt="Vendor logo">
        <button type="button" class="btn-icon btn-icon-danger asset-photo-remove"
          onclick="window.CRMModule.removeVendorLogo()" title="Remove logo">
          <span class="material-icons">close</span>
        </button>
      </div>`;
    preview.classList.remove('hidden');
  } else {
    preview.innerHTML = '';
    preview.classList.add('hidden');
  }
}

// TASK-9792 (Chris 2026-10-12) — the QuickBooks vendor import never sets is1099
// (card payments are on the processor's 1099-K); it stamps qbo1099Suggested and
// the vendor form shows "QuickBooks marks this vendor 1099 — apply?". Apply ticks
// the box, Not 1099 leaves it off; either way nothing is written until Save,
// which clears the suggestion and stamps qbo1099Decided (so a re-preview of the
// import never brings a dismissed one back).
function decide1099Suggestion(decision) {
  const box = document.getElementById('crm-cust-is1099');
  const dec = document.getElementById('crm-cust-1099-suggest-decision');
  const wrap = document.getElementById('crm-cust-1099-suggest');
  if (!box || !dec) return;
  const apply = decision === 'apply';
  box.checked = apply;
  dec.value = apply ? 'applied' : 'dismissed';
  if (wrap) wrap.innerHTML = `<span style="font-size:12px;color:var(--text-secondary);">${apply ? '1099 ticked from the QuickBooks suggestion' : 'Suggestion dismissed'} &mdash; Save to keep it.</span>`;
}

async function saveCustomer() {
  // TASK-9397 — a logo still converting isn't in vendorLogo.pending yet; wait
  // it out BEFORE the in-flight lock + runGuardedSave (whose 20s hand-off
  // must not re-open Save mid-conversion).
  // COD: a Save that waited must not persist a DIFFERENT customer form opened meanwhile.
  const _saveLogoSlot = CRMState.vendorLogo;
  if (!(await settlePhotoPrepBeforeSave(_crmLogoPrep, 'crm-save-customer-btn'))) return;
  if (CRMState.vendorLogo !== _saveLogoSlot) return;
  // In-flight lock — prevents rapid taps / form submit + button click from
  // creating duplicate customer records on flaky mobile networks.
  if (window.__saveCustomerInFlight) {
    window.showToast('Save already in progress — please wait', 'info');
    return;
  }
  window.__saveCustomerInFlight = true;

  const _clamp = window.clampText || ((s, n) => s && s.length > n ? s.slice(0, n) : s || '');
  const id = document.getElementById('crm-cust-id')?.value;
  const name = _clamp(document.getElementById('crm-cust-name')?.value.trim(), 100);
  if (!name) {
    window.showToast('Company name is required', 'error');
    window.__saveCustomerInFlight = false;
    return;
  }

  // Soft-duplicate check for new customers
  if (!id) {
    const lastSaved = window.__crmLastSavedCustomer;
    if (lastSaved && lastSaved.name === name && (Date.now() - lastSaved.ts) < 3000) {
      const proceed = confirm(`You just added "${name}" a moment ago. Are you sure you want to add another?`);
      if (!proceed) {
        window.__saveCustomerInFlight = false;
        return;
      }
    }
  }

  const phones = gatherPhones();
  const primaryPhone = phones.length > 0 ? phones[0].phone : '';
  const emails = gatherEmails();
  const primaryEmail = emails.length > 0 ? emails[0].email : '';
  const addresses = gatherAddresses();
  const primaryAddress = addresses.length > 0 ? addresses[0].address : '';

  const assignedToInput = document.getElementById('crm-cust-assigned-to');
  const assignedTo = assignedToInput ? (assignedToInput.value || null) : undefined;

  const data = {
    name,
    type: document.getElementById('crm-cust-type')?.value || 'customer',
    status: document.getElementById('crm-cust-status')?.value || 'Active',
    partnershipTier: document.getElementById('crm-cust-partnership-tier')?.value || '',
    phone: primaryPhone,
    phones,
    email: primaryEmail,
    emails,
    address: primaryAddress,
    addresses,
    internalTimeOnly: document.getElementById('crm-cust-internal-time')?.checked === true,
    // TASK-9588 — per-customer "No ticket emails" (display only: quiets the
    // Billing → Tickets "unsent" count; never blocks a send).
    ticketEmailsOff: document.getElementById('crm-cust-ticket-emails-off')?.checked === true,
    billingTerms: document.getElementById('crm-cust-billing')?.value || '',
    requiredFields: [...document.querySelectorAll('input[name="crm-req-field"]:checked')].map(cb => cb.value),
    notes: document.getElementById('crm-cust-notes')?.value.trim() || '',
    is1099: document.getElementById('crm-cust-is1099')?.checked || false,
    crmManaged: true,
    updatedAt: Timestamp.now()
  };
  // TASK-9792 — resolve the QuickBooks 1099 suggestion on Save. The banner only
  // renders while qbo1099Suggested is true and is1099 is false; ticking the box
  // (by the Apply button or by hand) = applied, "Not 1099" = dismissed, and an
  // untouched suggestion stays on the record for next time. COD r1: only on a
  // vendor-like SAVE — a form switched to Customer with the banner still in the
  // DOM (selectCustType) never stamps a 1099 decision on a customer.
  let _1099Decision = '';
  if (document.getElementById('crm-cust-1099-suggest') && (data.type === 'vendor' || data.type === 'subcontractor')) {
    const chosen = document.getElementById('crm-cust-1099-suggest-decision')?.value || '';
    _1099Decision = data.is1099 === true ? 'applied' : (chosen === 'dismissed' ? 'dismissed' : '');
    if (_1099Decision) { data.qbo1099Suggested = false; data.qbo1099Decided = _1099Decision; }
  }

  // TASK-1619 — ACH bank routing/account + tax ID (EIN/SSN) are FINANCIAL PII
  // and no longer go on the broadcast `customers` doc (firestore.rules denies
  // it outright — see the STRIP block on customers create/update). They write
  // to the sibling `customersSecure/{id}` instead, admin-only both ways.
  // Only build + write this when the caller IS admin: the ACH/Tax ID inputs
  // are DISABLED (and start empty) for a non-admin, so a non-admin save must
  // never touch customersSecure — reading its always-empty disabled inputs
  // would silently WIPE a real customer's banking detail otherwise. `data`
  // still gets the derived, non-sensitive `taxIdOnFile` boolean (mirrors the
  // existing w9OnFile pattern below) so bi.js's 1099 report can show "on
  // file" status without any digit of the tax ID ever touching the broadcast
  // doc — masked-last-4 counts as a digit-bearing leak too, so this is a
  // pure presence flag, nothing derived from the value itself.
  let secureData = null;
  if (_crmIsTenantAdmin()) {
    const achBankName = document.getElementById('crm-cust-ach-bank')?.value.trim() || '';
    const achRouting = document.getElementById('crm-cust-ach-routing')?.value.trim() || '';
    const achAccount = document.getElementById('crm-cust-ach-account')?.value.trim() || '';
    const taxId = document.getElementById('crm-cust-taxid')?.value.trim() || '';
    secureData = { achBankName, achRouting, achAccount, taxId, updatedAt: Timestamp.now() };
    data.taxIdOnFile = taxId !== '';
  }

  // Only write assignedTo when the form had the selector (i.e. profiles exist)
  if (assignedTo !== undefined) data.assignedTo = assignedTo;

  // TASK-339 — vendor logo upload. Uploads BEFORE the Firestore write so
  // the URL gets stamped atomically. uploadVendorLogo returns existing URL
  // if user didn't pick a new file, null if user explicitly removed.
  try {
    const logoUrl = await uploadVendorLogo(CRMState.tenantId);
    data.logoUrl = logoUrl || null;
  } catch (logoErr) {
    console.error('[CRM] vendor logo step failed (non-fatal):', logoErr);
    data.logoUrl = CRMState.vendorLogo?.existing?.url || null;
  }

  const btn = document.getElementById('crm-save-customer-btn');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Saving...';
  }

  // Background handoff: if the save is still running after 20s, release the UI
  // and let Firestore finish in the background. Firestore queues writes locally
  // and retries on reconnect, so the data is safe. This is SAFER than racing
  // Promise.race against addDoc, which was producing false "failed to save"
  // toasts for writes that actually succeeded (Zeke's 2026-04-11 bug).
  let handedOff = false;
  const safetyTimer = setTimeout(() => {
    handedOff = true;
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = 'Save Customer';
    }
    window.showToast('Still saving in the background — you can keep working', 'info');
    closeCustomerModal();
    window.__saveCustomerInFlight = false;
  }, 20000);

  let newCustomerId = null;
  try {
    if (id) {
      // TASK-823 — capture prior state BEFORE the write so we can detect a
      // legacy→crmManaged flip and migrate orphaned embedded contacts.
      // TASK-9793 — opened from AR/AP (openVendorEditor), CRMState.customers
      // may be empty — or a STALE snapshot left from an earlier CRM visit
      // (COD: its listener stops when the view leaves) — so the record the
      // live AP subscription handed over wins when present.
      const _apPrior = CRMState._vendorEditorPrior && CRMState._vendorEditorPrior.id === id ? CRMState._vendorEditorPrior : null;
      const prior = _apPrior || CRMState.customers.find(c => c.id === id) || undefined;
      // A legacy record (embedded contacts[], not crmManaged) is flipped to
      // crmManaged below and its contacts migrated into the subcollection. A
      // billing-only member cannot write that subcollection (rules: crm /
      // dispatch only), so from the AP path keep the legacy shape instead of
      // flipping it and orphaning the contacts on a failed migration.
      const _keepLegacyContacts = !!_apPrior && prior?.crmManaged !== true
        && Array.isArray(prior?.contacts) && prior.contacts.length > 0;
      if (_keepLegacyContacts) delete data.crmManaged;
      // TASK-1175 — internalTimeOnly is the ONLY off-books mechanism now that the
      // "Shop / No Customer" pseudo-customer is gone. Flipping it on a real paying
      // customer silently kills all their future ticketing, so every toggle is
      // audited (who/when/old→new). The firestore.rules customers-update rule
      // restricts WRITING this field to tenant admins; this is the audit trail.
      const _priorInternalTime = prior?.internalTimeOnly === true;
      const _newInternalTime = data.internalTimeOnly === true;
      await updateDoc(doc(db, 'tenants', CRMState.tenantId, 'customers', id), data);
      // TASK-1619 — secureData is null for a non-admin caller (never built,
      // never written — see the block above). setDoc+merge so this works
      // whether or not a customersSecure doc already exists for this id.
      if (secureData) {
        await setDoc(doc(db, 'tenants', CRMState.tenantId, 'customersSecure', id), secureData, { merge: true });
      }
      if (_priorInternalTime !== _newInternalTime) {
        window.logAudit?.('crm', 'CUSTOMER_INTERNAL_TIME_TOGGLE', {
          customerId: id,
          customerName: data.name,
          from: _priorInternalTime,
          to: _newInternalTime,
        });
      }
      // TASK-9793 (COD Domain) — is1099 decides who gets a 1099-NEC; a hand
      // flip (not the audited QuickBooks suggestion path below) now logs its
      // own old -> new row, whichever module (crm or billing) made it.
      if (prior && !_1099Decision && (prior.is1099 === true) !== (data.is1099 === true)) {
        window.logAudit?.('crm', 'VENDOR_1099_TOGGLE', {
          customerId: id, customerName: data.name, from: { is1099: prior.is1099 === true }, to: { is1099: data.is1099 === true },
        });
      }
      // TASK-9588 — customer id + old/new boolean only (no name / PII).
      if ((prior?.ticketEmailsOff === true) !== (data.ticketEmailsOff === true)) {
        window.logAudit?.('crm', 'CUSTOMER_TICKET_EMAILS_TOGGLE', {
          customerId: id,
          from: prior?.ticketEmailsOff === true,
          to: data.ticketEmailsOff === true,
        });
      }
      if (!_keepLegacyContacts && prior && prior.crmManaged !== true && Array.isArray(prior.contacts) && prior.contacts.length) {
        try { await migrateEmbeddedContacts(id, prior.contacts); }
        catch (mErr) { console.warn('[CRM] embedded-contact migration on flip failed:', mErr.message); }
      }
    } else {
      data.createdAt = Timestamp.now();
      data.color = '';
      const newCustRef = await addDoc(collection(db, 'tenants', CRMState.tenantId, 'customers'), data);
      newCustomerId = newCustRef.id;

      // TASK-1619 — secureData is null for a non-admin caller. A non-admin
      // creating a new customer simply can't set ACH/Tax ID at all (matches
      // the disabled inputs); an admin's values land on the sibling doc,
      // keyed to the id Firestore just minted.
      if (secureData) {
        await setDoc(doc(db, 'tenants', CRMState.tenantId, 'customersSecure', newCustomerId), secureData, { merge: true });
      }

      // TASK-1175 (2nd COD 🔴) — a customer CREATED with internalTimeOnly:true from
      // the start gets no toggle audit (the update-branch audit never runs), so a
      // new off-books customer would be invisible. Log it here so it's discoverable.
      // Enforcement: the checkbox is admin-gated client-side (_isUnrestrictedCrmCaller,
      // above) since the customers firestore.rules is at the prod validator complexity
      // ceiling and can't hold the field-immutability clause; a raw-SDK server-side
      // backstop is a filed follow-up.
      if (data.internalTimeOnly === true) {
        window.logAudit?.('crm', 'CUSTOMER_INTERNAL_TIME_TOGGLE', {
          customerId: newCustomerId,
          customerName: data.name,
          from: false,
          to: true,
          onCreate: true,
        });
      }
      if (data.ticketEmailsOff === true) {
        window.logAudit?.('crm', 'CUSTOMER_TICKET_EMAILS_TOGGLE', {
          customerId: newCustomerId, from: false, to: true, onCreate: true,
        });
      }

      // Save inline POCs as contacts
      const pocs = gatherPocs();
      if (pocs.length > 0) {
        const contactsPath = `tenants/${CRMState.tenantId}/customers/${newCustomerId}/contacts`;
        await Promise.all(pocs.map(poc => addDoc(collection(db, contactsPath), {
          ...poc,
          notes: '',
          showInDispatch: true,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        })));
      }
    }

    if (handedOff) return; // safety timer already took over the UX — don't double-toast

    const savedType = data.type === 'subcontractor' ? 'Subcontractor' : data.type === 'vendor' ? 'Vendor' : 'Customer';
    window.logAudit?.('crm', id ? 'UPDATE_CUSTOMER' : 'CREATE_CUSTOMER', { customerId: id || newCustomerId, customerName: data.name, type: data.type });
    // TASK-9792 — a 1099 decision is a tax-flag flip: its own old -> new row.
    if (_1099Decision) {
      window.logAudit?.('crm', _1099Decision === 'applied' ? 'VENDOR_1099_SUGGESTION_APPLIED' : 'VENDOR_1099_SUGGESTION_DISMISSED', {
        customerId: id || newCustomerId, customerName: data.name, source: 'qbo', from: { is1099: false }, to: { is1099: data.is1099 === true },
      });
    }
    window.showToast(id ? `${savedType} updated` : `${savedType} added`, 'success');
    if (!id) window.__crmLastSavedCustomer = { name: data.name, ts: Date.now() };
    closeCustomerModal();
  } catch (err) {
    if (handedOff) return; // background handoff already messaged the user
    console.error('[CRM] Save customer error:', err);
    console.error('[CRM] Error details:', { code: err?.code, message: err?.message, name: err?.name });
    const reason = err?.code || err?.message || 'unknown error';
    window.showToast(`Failed to save customer: ${reason}`, 'error');
  } finally {
    if (!handedOff) {
      clearTimeout(safetyTimer);
      window.__saveCustomerInFlight = false;
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = 'Save Customer';
      }
    }
  }
}

async function deleteCustomer(customerId) {
  if (!customerId) return;
  const customer = CRMState.customers.find(c => c.id === customerId);
  const name = customer?.name || 'this customer';
  const tenant = CRMState.tenantId;

  // TASK-10030 — delete guard. A merged customer is kept on purpose (its billing
  // history points at it); one with jobs, price books, contacts, sites or
  // projects is set Inactive or merged, never deleted.
  if (customer?.mergedIntoId) {
    await window.showConfirm(`Can't delete "${name}"`, `It was merged into ${customer.mergedIntoName || 'another customer'}. It's kept for its billing history.`, { confirmText: 'OK', hideCancel: true, icon: 'info' });
    return;
  }
  let held;
  try {
    held = await _customerDeleteHolds(customerId, customer?.name || '');
  } catch (e) {
    console.error('[CRM] TASK-10030: delete check failed', e);
    window.showToast?.(`Couldn't check what "${name}" still has, so it was not deleted. Try again.`, 'error');
    return;
  }
  if (held.length) {
    const merge = await window.showConfirm(
      `Can't delete "${name}"`,
      `This customer has ${held.join(', ')}. Deleting it would cut those off. Set it Inactive (Edit → Status) or Merge it into another customer instead.`,
      { confirmText: 'Merge into…', cancelText: 'Close', icon: 'block' });
    if (merge) showMergeCustomer(customerId);
    return;
  }

  // Count related data
  let ticketCount = 0, jobCount = 0, contractCount = 0, opptyCount = 0;
  try {
    const [ticketSnap, jobSnap, contractSnap, opptySnap] = await Promise.all([
      getDocs(query(collection(db, 'tenants', tenant, 'tickets'), where('customerId', '==', customerId))),
      getDocs(query(collection(db, 'tenants', tenant, 'jobs'), where('customerId', '==', customerId))),
      getDocs(collection(db, 'tenants', tenant, 'customers', customerId, 'contracts')),
      getDocs(collection(db, 'tenants', tenant, 'customers', customerId, 'opportunities'))
    ]);
    ticketCount = ticketSnap.size;
    jobCount = jobSnap.size;
    contractCount = contractSnap.size;
    opptyCount = opptySnap.size;
  } catch (e) {
    console.warn('[CRM] Error counting related data:', e);
  }

  const impacts = [];
  if (ticketCount > 0) impacts.push(`${ticketCount} billing ticket${ticketCount !== 1 ? 's' : ''}`);
  if (jobCount > 0) impacts.push(`${jobCount} job${jobCount !== 1 ? 's' : ''}`);
  if (contractCount > 0) impacts.push(`${contractCount} contract${contractCount !== 1 ? 's' : ''} (price book${contractCount !== 1 ? 's' : ''})`);
  if (opptyCount > 0) impacts.push(`${opptyCount} opportunit${opptyCount !== 1 ? 'ies' : 'y'}`);

  let msg = `Delete "${name}"?\n\n`;
  if (impacts.length > 0) {
    msg += `This customer is referenced by:\n• ${impacts.join('\n• ')}\n\nTickets and jobs will keep their data but lose the customer link. Contracts, contacts, sites, and opportunities will be permanently deleted.\n\n`;
  }
  msg += 'This cannot be undone.';

  if (!confirm(msg)) return;

  try {
    // Delete subcollections: contracts→lineItems, contacts, sites
    if (contractCount > 0) {
      const contractSnap = await getDocs(collection(db, 'tenants', tenant, 'customers', customerId, 'contracts'));
      const batch = writeBatch(db);
      let opCount = 0;
      for (const cDoc of contractSnap.docs) {
        const liSnap = await getDocs(collection(db, 'tenants', tenant, 'customers', customerId, 'contracts', cDoc.id, 'lineItems'));
        liSnap.docs.forEach(li => { batch.delete(li.ref); opCount++; });
        batch.delete(cDoc.ref);
        opCount++;
        if (opCount >= 490) { await batch.commit(); opCount = 0; }
      }
      if (opCount > 0) await batch.commit();
    }

    const contactsSnap = await getDocs(collection(db, 'tenants', tenant, 'customers', customerId, 'contacts'));
    if (contactsSnap.size > 0) {
      const b = writeBatch(db);
      contactsSnap.docs.forEach(d => b.delete(d.ref));
      await b.commit();
    }

    const sitesSnap = await getDocs(collection(db, 'tenants', tenant, 'customers', customerId, 'sites'));
    if (sitesSnap.size > 0) {
      const b = writeBatch(db);
      sitesSnap.docs.forEach(d => b.delete(d.ref));
      await b.commit();
    }

    // Delete opportunities linked to this customer (subcollection under customer)
    if (opptyCount > 0) {
      const opSnap = await getDocs(collection(db, 'tenants', tenant, 'customers', customerId, 'opportunities'));
      const b = writeBatch(db);
      opSnap.docs.forEach(d => b.delete(d.ref));
      await b.commit();
    }

    // Audit log BEFORE delete so the snapshot survives
    await window.logAudit?.('crm', 'DELETE_CUSTOMER', {
      customerId,
      customerName: name,
      customerSnapshot: customer || null,
      relatedCounts: { ticketCount, jobCount, contractCount, opptyCount }
    });
    await deleteDoc(doc(db, 'tenants', tenant, 'customers', customerId));

    window.showToast(`"${name}" deleted`, 'success');
    closeCustomerModal();
    closeDetailModal();
  } catch (error) {
    console.error('[CRM] Delete customer error:', error);
    window.showToast('Error deleting customer', 'error');
  }
}

// ============================================
// TASK-10030 — DELETE GUARD + "MERGE INTO…"
// ============================================
// Delete is refused while a customer still has jobs, price books, contacts,
// sites or projects (Chris 2026-10-22, after Wolverine asked how to clean up
// old customers without losing their jobs): Delete would cut the jobs loose and
// throw the rest away. Inactive keeps everything; Merge moves it to another
// customer. The same rule refuses Claude's delete_customer server-side.
async function _customerDeleteHolds(customerId, name, tenantId) {
  const tenant = tenantId || CRMState.tenantId;
  const cref = (sub) => collection(db, 'tenants', tenant, 'customers', customerId, sub);
  // Fail CLOSED on what decides the refusal: an unreadable count never reads as zero.
  const [jobsById, jobsByName, contracts, contacts, sites] = await Promise.all([
    getDocs(query(collection(db, 'tenants', tenant, 'jobs'), where('customerId', '==', customerId))),
    name ? getDocs(query(collection(db, 'tenants', tenant, 'jobs'), where('customer', '==', name))) : Promise.resolve({ docs: [] }),
    getDocs(cref('contracts')), getDocs(cref('contacts')), getDocs(cref('sites')),
  ]);
  const jobIds = new Set(jobsById.docs.map(d => d.id));
  jobsByName.docs.forEach(d => { if (!(d.data() || {}).customerId) jobIds.add(d.id); });
  let projects = 0;
  try {
    const [a, b] = await Promise.all([
      getDocs(query(collection(db, 'tenants', tenant, 'projects'), where('customerIds', 'array-contains', customerId))),
      getDocs(query(collection(db, 'tenants', tenant, 'projects'), where('primaryCustomerId', '==', customerId))),
    ]);
    projects = new Set([...a.docs, ...b.docs].map(d => d.id)).size;
  } catch (e) {
    // A login without Projects cannot read them. Fail CLOSED (independent
    // review): an unread count is not zero.
    console.warn('[CRM] TASK-10030: projects not readable for the delete check', e?.code || e?.message);
    projects = -1;
  }
  const plural = (n, one) => `${n} ${one}${n === 1 ? '' : 's'}`;
  const held = [];
  if (jobIds.size) held.push(plural(jobIds.size, 'job'));
  if (contracts.size) held.push(plural(contracts.size, 'price book'));
  if (contacts.size) held.push(plural(contacts.size, 'contact'));
  if (sites.size) held.push(plural(sites.size, 'site'));
  if (projects > 0) held.push(plural(projects, 'project'));
  if (projects < 0) held.push("projects this login can't see (Projects access is needed to check them)");
  return held;
}

// TASK-10030 — "Merge into…" UI lives in the lazy customerMergeUi.js (perf
// budget). These thin wrappers keep the window.CRMModule.* onclick names.
let _mergeUiLoad = null;
function _mergeUi() {
  if (!_mergeUiLoad) {
    _mergeUiLoad = __ddChunkWrap(import('./customerMergeUi.js?v=10030002')).then((m) => m.createMergeUi({
      getState: () => CRMState, escHTML, escAttr, escJsAttr, isVendorRecord, closeCustomerModal, closeDetailModal, customerWritesSettled,
    })).catch((e) => {
      _mergeUiLoad = null;
      console.warn('[CRM] customerMergeUi load failed:', e?.message || e);
      window.showToast?.('Could not open the merge screen. Check your connection and try again.', 'error');
      throw e;
    });
  }
  return _mergeUiLoad;
}
const _mergeCallUi = (name) => async (...args) => { try { return (await _mergeUi())[name](...args); } catch (_) { return undefined; } };
const showMergeCustomer = _mergeCallUi('showMergeCustomer');
const closeMergeCustomer = _mergeCallUi('closeMergeCustomer');
const onMergeInputChange = _mergeCallUi('onMergeInputChange');
const previewMergeCustomer = _mergeCallUi('previewMergeCustomer');
const onMergeAck = _mergeCallUi('onMergeAck');
const applyMergeCustomer = _mergeCallUi('applyMergeCustomer');
const loadMergeBanner = _mergeCallUi('loadMergeBanner');
const undoMergeCustomer = _mergeCallUi('undoMergeCustomer');
const resumeMergeCustomer = _mergeCallUi('resumeMergeCustomer');

function closeCustomerModal() {
  document.getElementById('crm-customer-modal')?.classList.add('hidden');
}

// ============================================
// CUSTOMER DETAIL MODAL
// ============================================
async function openCustomerDetail(customerId) {
  const customer = CRMState.customers.find(c => c.id === customerId);
  if (!customer) return;

  CRMState.selectedCustomer = customer;
  CRMState.detailTab = 'overview';
  CRMState.sites = [];
  CRMState.contacts = [];
  CRMState.contracts = [];
  CRMState.documents = [];
  // TASK-368 pattern — reset detail subcollection-loaded flags so the contacts
  // and contracts tabs show a skeleton instead of "No contacts/contracts yet"
  // before the first per-customer snapshot lands.
  CRMState._detailContactsLoaded = false;
  CRMState._detailContractsLoaded = false;
  // TASK-10030 — the counts write waits for BOTH lists to load OK; the merge
  // preview waits for every detail list that can write the customer doc.
  CRMState._detailSitesLoaded = false;
  CRMState._detailDocsLoaded = false;
  CRMState._detailCountsBlocked = false; // a list errored: never write counts off a partial read

  // Clean up previous detail subscriptions
  CRMState.detailUnsubscribers.forEach(u => u());
  CRMState.detailUnsubscribers = [];

  document.getElementById('crm-detail-title').textContent = customer.name;
  const subtitle = document.getElementById('crm-detail-subtitle');
  if (subtitle) {
    const escH = (s) => (s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    const status = escH(customer.status || 'Active');
    const terms = customer.billingTerms ? ` · ${escH(customer.billingTerms)}` : '';
    const typeBadge = customer.type === 'subcontractor'
      ? '<span style="background:#FEF3C7;color:#92400E;padding:1px 8px;border-radius:10px;font-size:11px;font-weight:600;margin-left:6px;">SUB</span>'
      : customer.type === 'vendor'
      ? '<span style="background:#EDE9FE;color:#6D28D9;padding:1px 8px;border-radius:10px;font-size:11px;font-weight:600;margin-left:6px;">VENDOR</span>'
      : '<span style="background:#DBEAFE;color:#1E40AF;padding:1px 8px;border-radius:10px;font-size:11px;font-weight:600;margin-left:6px;">CUSTOMER</span>';
    subtitle.innerHTML = `${status}${terms} ${typeBadge}`;
  }
  document.getElementById('crm-detail-modal').classList.remove('hidden');

  renderDetailBody();
  loadSubcollections(customerId);
}

function renderDetailBody(opts) {
  const body = document.getElementById('crm-detail-body');
  if (!body) return;

  // Don't let snapshot re-renders clobber forms with unsaved input
  if (!opts?.force) {
    if (CRMState.detailTab === 'opportunity-detail' && CRMState.editingOpp) return;
    // Check for inline edit forms on other tabs (site, contact, contract, doc forms)
    if (document.getElementById('crm-site-id') ||
        document.getElementById('crm-contract-id') ||
        CRMState._editingContactId || CRMState._showingContactForm) return;
  }

  const c = CRMState.selectedCustomer;
  const tab = CRMState.detailTab;

  const isVendorType = c?.type === 'subcontractor' || c?.type === 'vendor';
  const isSub = isVendorType; // backward compat for label logic
  const detailTabs = [
    { key: 'overview',      icon: 'dashboard',     label: 'Overview',      count: null },
    { key: 'sites',         icon: 'location_on',   label: 'Sites',         count: CRMState.sites.length },
    { key: 'contacts',      icon: 'people',        label: 'Contacts',      count: CRMState.contacts.length },
    { key: 'contracts',     icon: 'description',   label: isVendorType ? 'Rate Cards' : 'Contracts', count: CRMState.contracts.length },
    isVendorType
      ? { key: 'engagements',   icon: 'engineering',   label: 'Engagements',   count: CRMState.opportunities.length }
      : { key: 'opportunities', icon: 'trending_up',   label: 'Opps',          count: CRMState.opportunities.length },
    { key: 'activity',      icon: 'history',       label: 'Activity',      count: CRMState.activities.length },
    { key: 'documents',     icon: 'folder',        label: 'Docs',          count: CRMState.documents.length }
  ];

  body.innerHTML = `
    <!-- Detail Tabs -->
    <div class="crm-detail-tabs">
      ${detailTabs.map(t => `
        <button class="crm-detail-tab ${tab === t.key ? 'active' : ''}" onclick="window.CRMModule.switchDetailTab('${escJsAttr(t.key)}')">
          <span class="material-icons">${t.icon}</span>
          <span class="crm-detail-tab-label">${t.label}</span>
          ${t.count !== null ? `<span class="crm-detail-tab-count">${t.count}</span>` : ''}
        </button>
      `).join('')}
    </div>

    <div style="padding:var(--spacing-md);">
      ${tab === 'overview' ? renderOverviewTab(c) : ''}
      ${tab === 'sites' ? renderSitesTab() : ''}
      ${tab === 'contacts' ? renderContactsTab() : ''}
      ${tab === 'contracts' ? renderContractsTab() : ''}
      ${tab === 'opportunities' ? renderOpportunitiesTab() : ''}
      ${tab === 'engagements' ? renderEngagementsTab() : ''}
      ${tab === 'opportunity-detail' ? renderOpportunityDetailView() : ''}
      ${tab === 'activity' ? renderActivityTab() : ''}
      ${tab === 'documents' ? renderDocumentsTab() : ''}
    </div>
  `;

  // Load recent jobs + revenue intel async for overview tab
  if (tab === 'overview' && c) {
    if (c.mergedIntoId || c.mergeState) loadMergeBanner(c.id); // TASK-10030
    loadActiveProjects(c.id, c.name);
    loadRecentJobs(c.id, c.name);
    loadRevenueIntelligence(c.id);
  }

  // Load orders for opportunity detail
  if (tab === 'opportunity-detail' && CRMState.editingOpp) {
    const editOpp = CRMState.editingOpp;
    const editPortal = editOpp.portal || {};
    if (editPortal.ordersEnabled && editOpp.id) {
      loadOppOrders(editOpp.id);
    }
  }
}

function switchDetailTab(tab) {
  CRMState.detailTab = tab;
  if (tab === 'contacts') { CRMState._editingContactId = null; CRMState._showingContactForm = false; }
  renderDetailBody({ force: true });
}

function loadSubcollections(customerId) {
  const basePath = `tenants/${CRMState.tenantId}/customers/${customerId}`;

  // Sites
  const sitesUnsub = onSnapshot(
    query(collection(db, basePath, 'sites'), orderBy('name')),
    snap => {
      CRMState.sites = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      CRMState._detailSitesLoaded = true;
      updateCustomerCounts(customerId);
      renderDetailBody();
    },
    err => { CRMState._detailCountsBlocked = true; CRMState._detailSitesLoaded = true; console.error('[CRM] Sites error:', err); }
  );
  CRMState.detailUnsubscribers.push(sitesUnsub);

  // Contacts
  const contactsUnsub = onSnapshot(
    query(collection(db, basePath, 'contacts'), orderBy('name')),
    snap => {
      CRMState.contacts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      CRMState._detailContactsLoaded = true; // TASK-368 pattern
      renderDetailBody();
    },
    err => {
      console.error('[CRM] Contacts error:', err);
      CRMState._detailContactsLoaded = true; // TASK-368 pattern — unblock skeleton on error
      window.showToast?.('Error loading contacts', 'error');
      renderDetailBody();
    }
  );
  CRMState.detailUnsubscribers.push(contactsUnsub);

  // Contracts
  const contractsUnsub = onSnapshot(
    query(collection(db, basePath, 'contracts'), orderBy('createdAt', 'desc')),
    snap => {
      CRMState.contracts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      CRMState._detailContractsLoaded = true; // TASK-368 pattern
      // Update customer metadata counts
      updateCustomerCounts(customerId);
      renderDetailBody();
    },
    err => {
      console.error('[CRM] Contracts error:', err);
      CRMState._detailCountsBlocked = true; // TASK-10030 — no counts write off a failed read
      CRMState._detailContractsLoaded = true; // TASK-368 pattern — unblock skeleton on error
      window.showToast?.('Error loading contracts', 'error');
      renderDetailBody();
    }
  );
  CRMState.detailUnsubscribers.push(contractsUnsub);

  // Opportunities
  const oppsUnsub = onSnapshot(
    query(collection(db, basePath, 'opportunities'), orderBy('updatedAt', 'desc')),
    snap => {
      CRMState.opportunities = snap.docs.map(d => ({ id: d.id, customerId, ...d.data() }));
      CRMState.allOpportunitiesLoaded = false; // invalidate cache
      renderDetailBody();
    },
    err => console.error('[CRM] Opportunities error:', err)
  );
  CRMState.detailUnsubscribers.push(oppsUnsub);

  // Activities
  const activitiesUnsub = onSnapshot(
    query(collection(db, basePath, 'activities'), orderBy('createdAt', 'desc')),
    snap => {
      CRMState.activities = snap.docs.map(d => ({ id: d.id, customerId, ...d.data() }));
      renderDetailBody();
    },
    err => console.error('[CRM] Activities error:', err)
  );
  CRMState.detailUnsubscribers.push(activitiesUnsub);

  // Documents
  const docsUnsub = onSnapshot(
    query(collection(db, basePath, 'documents'), orderBy('uploadedAt', 'desc')),
    snap => {
      CRMState.documents = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Update compliance status on customer doc for list-view badges
      updateComplianceStatus(customerId, CRMState.documents);
      CRMState._detailDocsLoaded = true; // TASK-10030 — after the (tracked) compliance write is issued
      renderDetailBody();
    },
    err => { CRMState._detailDocsLoaded = true; console.error('[CRM] Documents error:', err); }
  );
  CRMState.detailUnsubscribers.push(docsUnsub);
}

// TASK-10030 (merge dialog stuck open at 390px) — opening a customer writes its
// _siteCount/_contractCount back to the customer doc. That write used to fire on
// EVERY contracts snapshot, even before sites had loaded (so it could write 0),
// and landed ~0.3s after the detail opened — i.e. while a quick "Merge into… →
// Preview" was in flight. The merge preview fingerprints the old customer doc,
// so Apply saw a different doc and refused ("Something changed since the
// preview"), leaving the dialog open on a stale preview. Now the counts are
// written only once BOTH lists have loaded, only when they differ from what is
// stored, never on a customer being merged, and every such self-write is
// tracked so the merge preview can wait for it (customerWritesSettled).
const _customerSelfWrites = new Map(); // customerId -> Set<Promise>
function _trackCustomerWrite(customerId, p) {
  let set = _customerSelfWrites.get(customerId);
  if (!set) { set = new Set(); _customerSelfWrites.set(customerId, set); }
  const done = Promise.resolve(p).catch(() => {}).finally(() => {
    set.delete(done);
    if (!set.size && _customerSelfWrites.get(customerId) === set) _customerSelfWrites.delete(customerId);
  });
  set.add(done);
  return p;
}
// Resolves once this view's own writes to the customer doc have settled: waits
// (bounded) for the open detail's sites, contracts and documents to load — they
// trigger the counts / compliance writes — then drains tracked writes, including
// any started while it waited, until none are left or the deadline passes.
async function customerWritesSettled(customerId, timeoutMs = 5000) {
  const deadline = Date.now() + timeoutMs;
  const tick = (ms) => new Promise(r => setTimeout(r, Math.max(0, ms)));
  while (CRMState.selectedCustomer?.id === customerId
    && !(CRMState._detailSitesLoaded && CRMState._detailContractsLoaded && CRMState._detailDocsLoaded)
    && Date.now() < deadline) {
    await tick(100);
  }
  for (let set = _customerSelfWrites.get(customerId); set && set.size && Date.now() < deadline; set = _customerSelfWrites.get(customerId)) {
    await Promise.race([Promise.allSettled([...set]), tick(deadline - Date.now())]);
  }
}

function updateCustomerCounts(customerId) {
  if (CRMState.selectedCustomer?.id !== customerId) return;
  if (!CRMState._detailSitesLoaded || !CRMState._detailContractsLoaded || CRMState._detailCountsBlocked) return;
  const siteCount = CRMState.sites.length;
  const contractCount = CRMState.contracts.length;
  const cust = CRMState.customers.find(c => c.id === customerId);
  if (cust && (cust.mergedIntoId || cust.mergeState)) return; // the merge owns these counts
  if (cust && cust._siteCount === siteCount && cust._contractCount === contractCount) return;
  _trackCustomerWrite(customerId, updateDoc(doc(db, 'tenants', CRMState.tenantId, 'customers', customerId), {
    _siteCount: siteCount,
    _contractCount: contractCount
  }).catch(() => { /* non-critical */ }));
}

// ============================================
// OVERVIEW TAB
// ============================================
function renderOverviewTab(c) {
  const pct = c.closingPct ?? 0;
  const clr = closingColor(pct);
  const statusColors = { 'Active': '#10B981', 'Prospect': '#F59E0B', 'Inactive': '#94A3B8' };
  const statusClr = statusColors[c.status] || '#3B82F6';

  // Soonest upcoming (or overdue) next action across this customer's opps + activities.
  const nextActionPeek = (() => {
    const candidates = [];
    (CRMState.opportunities || []).forEach(o => {
      if (o._nextActionDate) candidates.push({ date: o._nextActionDate, action: o._nextAction, label: o.title, type: 'opp', oppId: o.id });
    });
    (CRMState.activities || []).forEach(a => {
      if (a.nextActionDate && !a.opportunityId) candidates.push({ date: a.nextActionDate, action: a.nextAction, label: a.subject || 'Activity', type: 'activity' });
    });
    if (!candidates.length) return '';
    candidates.sort((a, b) => {
      const da = a.date?.toDate ? a.date.toDate() : new Date(a.date);
      const db = b.date?.toDate ? b.date.toDate() : new Date(b.date);
      return da - db;
    });
    const pick = candidates[0];
    const badge = renderNextActionBadge(pick.date, pick.action);
    if (!badge) return '';
    return `
    <div style="display:flex;align-items:center;gap:10px;background:linear-gradient(135deg,#FFFBEB,#FEF3C7);border:1px solid #FDE68A;border-radius:10px;padding:10px 14px;margin-bottom:12px;">
      <span class="material-icons" style="color:#D97706;">event_available</span>
      <div style="flex:1;min-width:0;">
        <div style="font-size:11px;font-weight:700;color:#92400E;text-transform:uppercase;letter-spacing:0.5px;">Next action</div>
        <div style="font-size:13px;color:var(--text-primary);margin-top:2px;">${escHTML(pick.label)}</div>
      </div>
      ${badge}
    </div>`;
  })();

  return `
    <div id="crm-merge-banner"></div>
    ${nextActionPeek}
    ${_chargeAccountCardHtml(c)}
    ${_crmVendorRentalsSlot(c)}
    <!-- Customer Info Card -->
    <div class="crm-overview-card">
      <div class="crm-overview-card-header" style="flex-wrap:wrap;">
        <span class="material-icons" style="font-size:16px;color:var(--brand-blue);">info</span>
        <span>Customer Info</span>
        <div style="margin-left:auto;display:flex;gap:6px;flex-wrap:wrap;justify-content:flex-end;"><!-- TASK-10030: wraps at 390px instead of clipping Merge / Delete -->
          ${(() => {
            const primaryEmail = getPrimaryEmail(c);
            return primaryEmail ? `
          <button class="btn btn-secondary btn-sm" style="padding:4px 12px;" onclick="window.CRMModule.openCrmEmailCompose('${escJsAttr(primaryEmail)}', '${escJsAttr(c.id)}', '${escJsAttr(c.name || '')}')">
            <span class="material-icons" style="font-size:14px;">mail_outline</span> Email
          </button>` : '';
          })()}
          <button class="btn btn-secondary btn-sm" style="padding:4px 12px;" onclick="window.CRMBrochure?.openFromCustomer('${escJsAttr(c.id)}')" title="Create marketing brochure">
            <span class="material-icons" style="font-size:14px;">campaign</span> Brochure
          </button>
          <button class="btn btn-secondary btn-sm" style="padding:4px 12px;" onclick="window.CRMModule.showEditCustomer('${escJsAttr(c.id)}')">
            <span class="material-icons" style="font-size:14px;">edit</span> Edit
          </button>
          ${(c.mergedIntoId || c.type === 'vendor' || c.type === 'subcontractor') ? '' : `<button class="btn btn-secondary btn-sm" style="padding:4px 12px;" onclick="window.CRMModule.showMergeCustomer('${escJsAttr(c.id)}')" title="Move this customer's jobs, contacts, sites and price books to another customer">
            <span class="material-icons" style="font-size:14px;">call_merge</span> Merge into…
          </button>`}
          <button class="btn btn-sm" style="padding:4px 10px;color:#DC2626;border:1px solid #FECACA;background:#FEF2F2;" onclick="window.CRMModule.deleteCustomer('${escJsAttr(c.id)}')">
            <span class="material-icons" style="font-size:14px;">delete</span>
          </button>
        </div>
      </div>
      <div class="crm-overview-grid">
        <div class="crm-overview-field">
          <div class="crm-overview-label">Status</div>
          <div class="crm-overview-value">
            <span style="display:inline-flex;align-items:center;gap:6px;">
              <span style="width:8px;height:8px;border-radius:50%;background:${statusClr};flex-shrink:0;"></span>
              ${c.status || 'Active'}
            </span>
          </div>
        </div>
        <div class="crm-overview-field">
          <div class="crm-overview-label">Billing Terms</div>
          <div class="crm-overview-value">${escHTML(c.billingTerms) || '—'}</div>
        </div>
        <!-- TASK-1619 — ACH bank detail moved to the admin-only customersSecure
             sibling; it no longer arrives on c (this overview render is
             synchronous and fed straight off the broadcast customers listener,
             so there is deliberately no async fetch here — a driver-PIN
             session, and now every non-admin, must not see this even
             momentarily). View/edit ACH + Tax ID from the Edit modal, which
             does the admin-gated async load (loadCustomerSecureFields). -->
        ${_crmIsTenantAdmin() ? `
        <div class="crm-overview-field" style="grid-column:1/-1;">
          <div class="crm-overview-label">ACH Information</div>
          <div class="crm-overview-value" style="font-size:12px;color:var(--text-secondary);">
            <a href="javascript:void(0)" onclick="window.CRMModule.showEditCustomer('${escJsAttr(c.id)}')" style="color:var(--brand-blue);">View / edit in Edit Customer</a>
          </div>
        </div>` : ''}
        <div class="crm-overview-field" style="grid-column:1/-1;">
          <div class="crm-overview-label">Phone Numbers</div>
          <div class="crm-overview-value">${(() => {
            const phones = c.phones && c.phones.length > 0 ? c.phones : (c.phone ? [{ label: 'Main', phone: c.phone }] : []);
            if (phones.length === 0) return '—';
            return phones.map(p => `<div style="display:flex;align-items:center;gap:8px;margin-bottom:2px;"><span style="font-size:11px;font-weight:600;color:var(--text-secondary);background:#F1F5F9;padding:1px 6px;border-radius:4px;min-width:60px;text-align:center;">${escHTML(p.label || 'Main')}</span> <a href="tel:${escAttr(p.phone)}" style="color:var(--brand-blue);text-decoration:none;">${escHTML(p.phone)}</a></div>`).join('');
          })()}</div>
        </div>
        <div class="crm-overview-field" style="grid-column:1/-1;">
          <div class="crm-overview-label">Emails</div>
          <div class="crm-overview-value">${(() => {
            const emails = c.emails && c.emails.length > 0 ? c.emails : (c.email ? [{ label: 'General', email: c.email }] : []);
            if (emails.length === 0) return '—';
            const activeGmailConnected = !!(CRMState.activeProfile && CRMState.activeProfile.gmailConnected);
            // TASK-1412 — a dead connection must not quietly degrade to a plain
            // mailto: link; say why the Gmail send is gone.
            const gmailNeedsReconnect = !activeGmailConnected && !!(CRMState.activeProfile && CRMState.activeProfile.gmailNeedsReconnect);
            return emails.map(e => {
              // A dead connection routes through openCrmEmailCompose too, which
              // explains the reconnect instead of silently opening mailto:.
              const clickHandler = (activeGmailConnected || gmailNeedsReconnect)
                ? `event.preventDefault(); window.CRMModule.openCrmEmailCompose('${escJsAttr(e.email)}', '${escJsAttr(c.id)}', '${escJsAttr(c.name || '')}')`
                : '';
              return `<div style="display:flex;align-items:center;gap:8px;margin-bottom:2px;"><span style="font-size:11px;font-weight:600;color:var(--text-secondary);background:#F1F5F9;padding:1px 6px;border-radius:4px;min-width:60px;text-align:center;">${escHTML(e.label || 'General')}</span> <a href="mailto:${escAttr(e.email)}" ${clickHandler ? `onclick="${clickHandler}"` : ''} style="color:var(--brand-blue);text-decoration:none;">${escHTML(e.email)}</a>${activeGmailConnected ? ' <span class="material-icons" style="font-size:12px;color:#059669;" title="Will send from your connected Gmail">mail</span>' : ''}${gmailNeedsReconnect ? ' <span class="material-icons" style="font-size:12px;color:#B45309;" title="Your Gmail connection stopped working — reconnect it in CRM → Team to send from DispatchDeck">error</span>' : ''}</div>`;
            }).join('');
          })()}</div>
        </div>
        <div class="crm-overview-field" style="grid-column:1/-1;">
          <div class="crm-overview-label">Addresses</div>
          <div class="crm-overview-value">${(() => {
            const addrs = c.addresses && c.addresses.length > 0 ? c.addresses : (c.address ? [{ label: 'Main', address: c.address }] : []);
            if (addrs.length === 0) return '—';
            return addrs.map(a => `<div style="display:flex;align-items:center;gap:8px;margin-bottom:2px;"><span style="font-size:11px;font-weight:600;color:var(--text-secondary);background:#F1F5F9;padding:1px 6px;border-radius:4px;min-width:60px;text-align:center;">${escHTML(a.label || 'Main')}</span> ${escHTML(a.address)}</div>`).join('') + _crmRegisteredStateFlag(c, true);
          })()}</div>
        </div>
        ${c.notes ? `
        <div class="crm-overview-field" style="grid-column:1/-1;">
          <div class="crm-overview-label">Notes</div>
          <div class="crm-overview-value" style="white-space:pre-wrap;font-size:13px;color:var(--text-secondary);">${escHTML(c.notes)}</div>
        </div>` : ''}
      </div>
    </div>

    ${/* TASK-9797 item 3 — '' unless the company turned the checklist on */ _ccCard(c)}

    <!-- Active Projects -->
    <div class="crm-overview-card" id="crm-active-projects" ${(c.type === 'subcontractor' || c.type === 'vendor' || window.canAccessProjects?.() === false) ? 'style="display:none;"' : ''}>
      <div class="crm-overview-card-header">
        <span class="material-icons" style="font-size:16px;color:#8B5CF6;">folder_special</span>
        <span>Active Projects</span>
        ${crmCanCreateProject() ? `<button type="button" class="btn btn-secondary btn-sm" id="crm-add-project-btn" style="margin-left:auto;min-height:44px;display:inline-flex;align-items:center;gap:4px;" onclick="window.CRMModule.startProjectFromCrm('${escJsAttr(c.id)}')"><span class="material-icons" style="font-size:18px;">add</span>New Project</button>` : ''}
      </div>
      <div id="crm-active-projects-list" style="color:var(--text-secondary);font-size:13px;padding:4px 0;">Loading...</div>
    </div>

    <!-- Recent Dispatched Jobs -->
    <div class="crm-overview-card" id="crm-recent-jobs">
      <div class="crm-overview-card-header">
        <span class="material-icons" style="font-size:16px;color:var(--brand-blue);">local_shipping</span>
        <span>Recent Jobs</span>
      </div>
      <div id="crm-recent-jobs-list" style="color:var(--text-secondary);font-size:13px;padding:4px 0;">Loading...</div>
    </div>

    <!-- Revenue Intelligence (async, BI-gated) -->
    <div class="crm-overview-card" id="crm-revenue-intel" ${(c.type === 'subcontractor' || c.type === 'vendor') ? 'style="display:none;"' : ''}>
      <div class="crm-overview-card-header">
        <span class="material-icons" style="font-size:16px;color:#8B5CF6;">analytics</span>
        <span>Revenue Intelligence</span>
      </div>
      <div id="crm-revenue-intel-content" style="font-size:13px;color:var(--text-secondary);padding:4px 0;">Loading...</div>
    </div>

    <!-- Sales Forecast (customers only) -->
    <div class="crm-overview-card" ${(c.type === 'subcontractor' || c.type === 'vendor') ? 'style="display:none;"' : ''}>
      <div class="crm-overview-card-header">
        <span class="material-icons" style="font-size:16px;color:#059669;">trending_up</span>
        <span>Sales Forecast</span>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;margin-bottom:10px;">
        <div class="form-group" style="margin:0;">
          <label class="crm-overview-label">Est. Revenue ($)</label>
          <input type="number" id="crm-forecast-revenue" class="form-control" min="0" step="100" placeholder="0" value="${c.estRevenue || ''}" style="padding:8px 10px;font-size:14px;">
        </div>
        <div class="form-group" style="margin:0;">
          <label class="crm-overview-label">Close %</label>
          <input type="number" id="crm-forecast-pct" class="form-control" min="0" max="100" placeholder="50" value="${c.closingPct ?? ''}" style="padding:8px 10px;font-size:14px;">
        </div>
        <div class="form-group" style="margin:0;">
          <label class="crm-overview-label">Est. Close Date</label>
          <input type="date" id="crm-forecast-date" class="form-control" value="${formatDateInput(c.estCloseDate)}" style="padding:8px 10px;font-size:14px;">
        </div>
      </div>
      ${(c.estRevenue || c.closingPct) ? `
      <div style="display:flex;gap:16px;margin-bottom:10px;padding:8px 12px;background:var(--bg-secondary);border-radius:var(--radius-md);border:1px solid var(--border-light);">
        <div style="flex:1;">
          <div style="font-size:11px;color:var(--text-secondary);">Weighted Value</div>
          <div style="font-weight:700;color:#059669;font-size:1.1rem;">$${Math.round((c.estRevenue || 0) * (pct / 100)).toLocaleString()}</div>
        </div>
        <div>
          <div style="font-size:11px;color:var(--text-secondary);">Confidence</div>
          <span style="display:inline-block;padding:2px 10px;border-radius:var(--radius-sm);background:${clr.bg};color:${clr.text};font-weight:700;font-size:0.9375rem;">${pct}%</span>
        </div>
      </div>` : ''}
      <div style="display:flex;justify-content:flex-end;">
        <button class="btn btn-primary btn-sm" id="crm-forecast-save-btn" onclick="window.CRMModule.saveForecast('${escJsAttr(c.id)}')">
          <span class="material-icons" style="font-size:16px;">save</span> Save Forecast
        </button>
      </div>
    </div>
  `;
}

async function loadActiveProjects(customerId, customerName) {
  const container = document.getElementById('crm-active-projects-list');
  if (!container) return;
  // TASK-9651 — the card is hidden without Projects; never fire a read the
  // rules (projRead) would refuse.
  if (window.canAccessProjects?.() === false) return;

  try {
    // Query projects by primaryCustomerId first, fall back to customerIds array
    const projectsRef = collection(db, 'tenants', CRMState.tenantId, 'projects');
    const q1 = query(projectsRef, where('primaryCustomerId', '==', customerId));
    const snap = await getDocs(q1);

    const projects = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(p => p.status === 'active' || p.status === 'completed')
      .sort((a, b) => {
        const aDate = _crmProjDate(a.startDate)?.getTime() || 0;
        const bDate = _crmProjDate(b.startDate)?.getTime() || 0;
        return bDate - aDate;
      })
      .slice(0, 5);

    // A newer open of a different customer may have repainted the card meanwhile.
    if (CRMState.selectedCustomer && CRMState.selectedCustomer.id !== customerId) return;

    if (projects.length === 0) {
      // TASK-9739 — say what a project is and how to add one.
      const how = crmCanCreateProject()
        ? 'Use <strong>New Project</strong> above to add one.'
        : 'Projects are added in Dispatch → Projects.';
      container.innerHTML = `<div style="color:var(--text-secondary);font-size:13px;padding:4px 0;line-height:1.45;">No projects yet. A project groups this customer's jobs for one site or piece of work, with one standing PO, location and customer portal. ${how}</div>`;
      return;
    }

    container.innerHTML = projects.map(p => {
      const statusColor = p.status === 'active' ? '#10B981' : '#94A3B8';
      const statusIcon = p.status === 'active' ? 'play_circle' : 'check_circle';
      const sd = _crmProjDate(p.startDate), ed = _crmProjDate(p.endDate);
      const startDate = sd ? sd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
      const endDate = ed ? ed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
      const dateStr = startDate ? (endDate ? `${startDate} — ${endDate}` : startDate) : '';
      const rev = p.estimatedRevenue ? `$${Number(p.estimatedRevenue).toLocaleString()}` : '';

      return `
        <div style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border-light);cursor:pointer;" onclick="window.CRMModule.openProjectFromCrm('${escJsAttr(p.id)}')">
          <span class="material-icons" style="font-size:18px;color:${statusColor};">${statusIcon}</span>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:500;font-size:13px;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escHTML(p.name)}</div>
            ${p.tags?.length ? `<div style="font-size:11px;color:var(--text-secondary);">${p.tags.map(t => escHTML(t)).join(', ')}</div>` : ''}
          </div>
          <div style="text-align:right;flex-shrink:0;">
            ${rev ? `<div style="font-size:12px;font-weight:600;color:var(--text-primary);">${rev}</div>` : ''}
            <div style="font-size:11px;color:var(--text-secondary);white-space:nowrap;">${dateStr}</div>
          </div>
        </div>
      `;
    }).join('');
  } catch (err) {
    console.error('[CRM] Load active projects error:', err);
    container.innerHTML = '<div style="color:var(--text-secondary);font-size:13px;">Unable to load projects.</div>';
  }
}

// Project dates are Firestore Timestamps; new Date(Timestamp) is Invalid Date.
function _crmProjDate(v) {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (typeof v.seconds === 'number') return new Date(v.seconds * 1000);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

// TASK-9739 — "New Project" on the customer's Active Projects card.
// Who sees it mirrors who firestore.rules lets CREATE a project (projects block:
// owner, or a member holding the 'dispatch' module; never compliance-only) and
// the same 811-only carve-out as openProjectFromCrm. View/upload-only members
// can't edit a project afterwards, so they don't get the button either.
// TASK-9651 — the rules' tenant-wide create arm is projMgr: owner, or
// 'dispatch' AND 'projects'.
const CRM_PROJECT_CREATE_MODULES = ['dispatch', 'projects'];
function crmCanCreateProject() {
  const S = window.AppState || {};
  if (S.isDemo || S.tenant === 'demo') return false;
  if (typeof window.isComplianceOnlyTenant === 'function' && window.isComplianceOnlyTenant()) return false;
  if (window.isMember811Only?.() === true) return false;
  const uid = S.user?.uid || null;
  const ownerId = S.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const m = S.memberAccess;
  if (!m || m.status === 'suspended' || m.status === 'removed' || m.viewUploadOnly === true) return false;
  const mods = Array.isArray(m.modules) ? m.modules : [];
  return CRM_PROJECT_CREATE_MODULES.every(k => mods.includes(k));
}

// Opens the SAME New Project form as Dispatch → Projects (projects.js
// showCreateProject / submitCreateProject, reached through app.js
// ensureProjectsModule — never an import, Pitfall #1) with this customer
// picked, then repaints the card when the save lands. No second create path.
let _crmProjectCreateFor = null;
async function startProjectFromCrm(customerId) {
  if (!customerId) return;
  if (!crmCanCreateProject()) {
    window.showToast?.('This login can\'t add projects. Ask an admin for Dispatch and Projects access.', 'warning');
    return;
  }
  const cust = (CRMState.selectedCustomer?.id === customerId ? CRMState.selectedCustomer : null)
    || (CRMState.customers || []).find(x => x.id === customerId) || null;
  if (!cust) { window.showToast?.('Customer not found — reopen it and try again.', 'error'); return; }
  const btn = document.getElementById('crm-add-project-btn');
  if (btn) btn.disabled = true;
  let PM = null;
  try {
    PM = typeof window.ensureProjectsModule === 'function' ? await window.ensureProjectsModule() : window.ProjectsModule;
  } catch (err) {
    console.error('[CRM] ensureProjectsModule failed:', err);
  } finally {
    if (btn) btn.disabled = false;
  }
  if (!PM || typeof PM.showCreateProject !== 'function' || typeof PM.submitCreateProject !== 'function') {
    window.showToast?.('Could not open the project form — try Dispatch → Projects.', 'error');
    return;
  }
  // The customer was closed or switched while projects loaded.
  if (CRMState.selectedCustomer?.id !== customerId) return;
  PM.showCreateProject();
  const modal = document.getElementById('modal-create-project');
  const sel = document.getElementById('new-project-customer');
  const form = modal?.querySelector('form');
  if (!modal || !sel || !form) return;
  modal.style.zIndex = '1060'; // above #crm-detail-modal
  const name = cust.name || cust.company || '';
  let opt = Array.from(sel.options).find(o => o.value === customerId);
  if (!opt) {
    // Customer added after the projects picker loaded its list.
    opt = document.createElement('option');
    opt.value = customerId;
    opt.dataset.name = name;
    opt.textContent = name;
    sel.appendChild(opt);
  }
  sel.value = customerId;
  // The customer's price book, so they can see which one this work bills from.
  const books = (CRMState.contracts || []).filter(k => k.status === 'Active');
  if (books.length) {
    const note = document.createElement('small');
    note.id = 'crm-new-project-pricebook';
    note.style.cssText = 'display:block;margin-top:4px;color:var(--text-secondary);font-size:0.75rem;';
    note.textContent = 'Price Book on file: ' + books.map(k => k.title || 'Untitled').join(', ');
    sel.parentElement?.appendChild(note);
    // The picker stays editable; the note only describes this customer.
    sel.addEventListener('change', () => { note.style.display = sel.value === customerId ? 'block' : 'none'; });
  }
  _crmProjectCreateFor = { customerId, name };
  form.setAttribute('onsubmit', 'event.preventDefault();window.CRMModule.submitProjectFromCrm()');
  document.getElementById('new-project-name')?.focus();
}

let _crmProjectSubmitBusy = false;
async function submitProjectFromCrm() {
  const PM = window.ProjectsModule;
  if (_crmProjectSubmitBusy || !PM?.submitCreateProject) return;
  const want = _crmProjectCreateFor;
  _crmProjectSubmitBusy = true;
  try {
    // projects.js validates, saves (createProject / template), toasts, and offers
    // any waiting 811 tickets. Its "open the new project" step paints into
    // #dispatch-content, which isn't on screen here, so it is a no-op from CRM.
    await PM.submitCreateProject();
  } catch (err) {
    console.error('[CRM] submitCreateProject failed:', err);
  } finally {
    _crmProjectSubmitBusy = false;
  }
  // Still open = projects.js refused before saving (e.g. no name): keep the
  // CRM context so the retry still repaints this card.
  const modal = document.getElementById('modal-create-project');
  if (modal && !modal.classList.contains('hidden')) return;
  // Its post-save openProjectDetail parked Projects on a detail page nobody
  // saw (#dispatch-content isn't mounted from CRM). Only reset when that's true.
  const st = PM.getState?.();
  if (st && !document.getElementById('dispatch-content')) { st.viewMode = 'list'; st.currentProject = null; }
  _crmProjectCreateFor = null;
  const open = CRMState.selectedCustomer;
  if (want && open?.id === want.customerId) loadActiveProjects(open.id, open.name);
}

// TASK-9736 — Active Projects row → that project's detail. Projects live inside
// the Dispatch view (#dispatch-content), so a bare openProjectDetail from CRM
// rendered into nothing, and the old `CRMModule.close` it chained was never
// exported (the ?.() hid both). Navigate to Dispatch, wait for its Projects
// handle, then reuse the board's own switch-to-Projects-and-open path
// (DispatchModule.openProjectFromBoard). Window handles only — never an import
// of projects.js / dispatch.js from here (Pitfall #1: a second, empty instance).
const CRM_PROJECT_OPEN_WAIT_MS = 20000;
function openProjectFromCrm(projectId) {
  if (!projectId) return false;
  if (window.canAccessProjects?.() === false) { // TASK-9651
    window.showToast?.('This login does not have Projects. Ask an admin to turn on Projects for you.', 'warning');
    return false;
  }
  const access = typeof window.checkModuleAccess === 'function' ? window.checkModuleAccess('dispatch') : true;
  // TASK-9779 — an 811-only login gets the Dispatch view for the 811 window only.
  if (access === false || window.isMember811Only?.() === true) {
    window.showToast?.('Projects open in Dispatch, and this login does not have Dispatch.', 'warning');
    return false;
  }
  if (typeof window.navigateToView !== 'function') {
    window.showToast?.('Could not open the project — open it from Dispatch → Projects.', 'error');
    return false;
  }
  closeDetailModal();
  // #dispatch-content exists only while the Dispatch view is painted (the main
  // content is replaced on every navigate), so a ProjectsModule left over from
  // an earlier Dispatch visit can't fire while CRM is still on screen.
  // (AppState.currentView is not a signal: navigateToView doesn't update it.)
  const ready = () => !!document.getElementById('dispatch-content')
    && typeof window.DispatchModule?.openProjectFromBoard === 'function'
    && typeof window.ProjectsModule?.openProjectDetail === 'function';
  if (!ready()) window.navigateToView('dispatch');
  const started = Date.now();
  const tick = () => {
    if (ready()) {
      Promise.resolve(window.DispatchModule.openProjectFromBoard(projectId))
        .catch(err => console.error('[CRM] openProjectFromBoard failed:', err));
      return;
    }
    // The user went somewhere else while we waited — don't yank them back.
    const h = String(window.location.hash || '');
    if (h && !/^#\/(dispatch|crm)(\/|\?|$)/.test(h)) return;
    // The nav gate can refuse (trial wall, module access, PIN) and shows its
    // own message; stop polling rather than spin forever.
    if (Date.now() - started > CRM_PROJECT_OPEN_WAIT_MS) return;
    setTimeout(tick, 150);
  };
  setTimeout(tick, 0);
  return true;
}

async function loadRecentJobs(customerId, customerName) {
  const container = document.getElementById('crm-recent-jobs-list');
  if (!container) return;

  try {
    // Query jobs by customer name (most reliable match)
    const jobsRef = collection(db, 'tenants', CRMState.tenantId, 'jobs');
    const q = query(jobsRef, where('customer', '==', customerName));
    const snap = await getDocs(q);

    if (snap.empty) {
      container.innerHTML = '<div style="color:var(--text-secondary);font-size:13px;padding:4px 0;">No dispatched jobs found for this customer.</div>';
      return;
    }

    // Sort by startDate desc, take 5
    const jobs = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => {
        const aDate = a.startDate?.toDate ? a.startDate.toDate().getTime() : (a.startDate ? new Date(a.startDate).getTime() : 0);
        const bDate = b.startDate?.toDate ? b.startDate.toDate().getTime() : (b.startDate ? new Date(b.startDate).getTime() : 0);
        return bDate - aDate;
      })
      .slice(0, 5);

    container.innerHTML = jobs.map(j => {
      const title = j.title || 'Untitled Job';
      const location = j.location || j.address || '';
      const status = j.status === 'finished' ? 'Finished' : 'Active';
      const statusColor = status === 'Finished' ? '#94A3B8' : '#3B82F6';
      const startDate = j.startDate?.toDate ? j.startDate.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) :
                         (j.startDate ? new Date(j.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
      const endDate = j.endDate?.toDate ? j.endDate.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) :
                       (j.endDate ? new Date(j.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
      const dateStr = startDate ? (endDate ? `${startDate} — ${endDate}` : startDate) : '';

      return `
        <div style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border-light);">
          <span class="material-icons" style="font-size:18px;color:${statusColor};">${status === 'Finished' ? 'check_circle' : 'schedule'}</span>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:500;font-size:13px;color:var(--text-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escHTML(title)}</div>
            ${location ? `<div style="font-size:12px;color:var(--text-secondary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escHTML(location)}</div>` : ''}
          </div>
          <div style="font-size:12px;color:var(--text-secondary);white-space:nowrap;">${dateStr}</div>
        </div>
      `;
    }).join('');
  } catch (err) {
    console.error('[CRM] Load recent jobs error:', err);
    container.innerHTML = '<div style="color:var(--text-secondary);font-size:13px;">Unable to load jobs.</div>';
  }
}

async function loadRevenueIntelligence(customerId) {
  const container = document.getElementById('crm-revenue-intel-content');
  if (!container) return;

  const hasBi = window.hasAddOnPackage?.('bi');

  if (!hasBi) {
    container.innerHTML = `
      <div style="text-align:center;padding:12px 0;">
        <span class="material-icons" style="font-size:28px;color:#8B5CF6;opacity:0.5;">lock</span>
        <div style="font-size:12px;color:var(--text-secondary);margin-top:4px;">Actual revenue, hours & job metrics</div>
        <div style="font-size:11px;color:#8B5CF6;margin-top:4px;font-weight:600;">Available with Pro+ upgrade</div>
      </div>`;
    return;
  }

  try {
    // Query tickets for this customer
    const ticketsRef = collection(db, 'tenants', CRMState.tenantId, 'tickets');
    const tq = query(ticketsRef, where('customerId', '==', customerId));
    // TASK-9646 S3b-5 — the same customer's ticketMoney mirrors, in parallel (one
    // query; the mirror carries customerId). A login the rules deny the mirror
    // (a crm member without billing / bi, not the owner) or a failed read ->
    // null: every ticket keeps its own fields, silently, exactly as before. A
    // ticket whose mirror is missing / stale / closed without a frozen snapshot
    // keeps its own fields too (the shared helper's rule).
    // A signed-in member the rules will plainly deny (not the owner, no billing /
    // bi module) is not even asked — no doomed request.
    const ma = window.AppState?.memberAccess;
    const mayReadMoney = !ma || ma.role === 'owner'
      || (Array.isArray(ma.modules) && (ma.modules.includes('billing') || ma.modules.includes('bi')));
    const [ticketSnap, moneySnap] = await Promise.all([
      getDocs(tq),
      mayReadMoney
        ? getDocs(query(collection(db, 'tenants', CRMState.tenantId, 'ticketMoney'), where('customerId', '==', customerId))).catch(() => null)
        : null,
    ]);
    const moneyById = new Map();
    if (moneySnap) moneySnap.docs.forEach(d => moneyById.set(d.id, d.data()));
    // the helper chunk, only when there is a mirror to apply; a failed load = no mirror (own fields)
    if (moneyById.size && !(await _crmLoadTmv())) moneyById.clear();

    let totalRevenue = 0;
    let ticketCount = 0;
    let paidCount = 0;
    let unpaidRevenue = 0;

    ticketSnap.docs.forEach(d => {
      const t = _tmvWithTicketMoney(d.data(), moneyById.get(d.id) || null);
      const amount = t.grandTotal || 0;
      totalRevenue += amount;
      ticketCount++;
      if (t.status === 'paid' || t.status === 'invoiced') {
        paidCount++;
      } else {
        unpaidRevenue += amount;
      }
    });

    // Query clock entries for this customer (via job → customer linkage)
    const jobsRef = collection(db, 'tenants', CRMState.tenantId, 'jobs');
    const jq = query(jobsRef, where('customerId', '==', customerId));
    const jobSnap = await getDocs(jq);
    const jobIds = new Set(jobSnap.docs.map(d => d.id));
    const jobCount = jobIds.size;

    let totalHours = 0;
    if (jobIds.size > 0) {
      // Query clock entries for these jobs (batch in chunks of 10 for Firestore 'in' limit)
      const jobIdArr = [...jobIds];
      const clockRef = collection(db, 'tenants', CRMState.tenantId, 'clockEntries');
      for (let i = 0; i < jobIdArr.length; i += 10) {
        const chunk = jobIdArr.slice(i, i + 10);
        const cq = query(clockRef, where('jobId', 'in', chunk));
        const clockSnap = await getDocs(cq);
        clockSnap.docs.forEach(d => {
          const ce = d.data();
          if (ce.clockIn && ce.clockOut) {
            const inTime = ce.clockIn?.toDate ? ce.clockIn.toDate() : new Date(ce.clockIn);
            const outTime = ce.clockOut?.toDate ? ce.clockOut.toDate() : new Date(ce.clockOut);
            totalHours += (outTime - inTime) / 3600000;
          }
        });
      }
    }

    const fmt = (n) => n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n.toFixed(0)}`;

    container.innerHTML = `
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
        <div style="background:var(--bg-secondary);border-radius:var(--radius-sm);padding:8px;text-align:center;">
          <div style="font-size:16px;font-weight:700;color:var(--text-primary);">${fmt(totalRevenue)}</div>
          <div style="font-size:11px;color:var(--text-secondary);">Total Billed</div>
        </div>
        <div style="background:var(--bg-secondary);border-radius:var(--radius-sm);padding:8px;text-align:center;">
          <div style="font-size:16px;font-weight:700;color:var(--text-primary);">${ticketCount}</div>
          <div style="font-size:11px;color:var(--text-secondary);">Tickets</div>
        </div>
        <div style="background:var(--bg-secondary);border-radius:var(--radius-sm);padding:8px;text-align:center;">
          <div style="font-size:16px;font-weight:700;color:var(--text-primary);">${totalHours.toFixed(1)}h</div>
          <div style="font-size:11px;color:var(--text-secondary);">Total Hours</div>
        </div>
        <div style="background:var(--bg-secondary);border-radius:var(--radius-sm);padding:8px;text-align:center;">
          <div style="font-size:16px;font-weight:700;color:var(--text-primary);">${jobCount}</div>
          <div style="font-size:11px;color:var(--text-secondary);">Jobs</div>
        </div>
      </div>
      ${unpaidRevenue > 0 ? `<div style="margin-top:6px;font-size:11px;color:#F59E0B;"><span class="material-icons" style="font-size:12px;vertical-align:middle;">warning</span> ${fmt(unpaidRevenue)} outstanding</div>` : ''}
    `;
  } catch (err) {
    console.error('[CRM] Load revenue intelligence error:', err);
    container.innerHTML = '<div style="color:var(--text-secondary);font-size:13px;">Unable to load revenue data.</div>';
  }
}

async function saveForecast(customerId) {
  const btn = document.getElementById('crm-forecast-save-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  const closeDateVal = document.getElementById('crm-forecast-date')?.value;
  const data = {
    estRevenue: parseFloat(document.getElementById('crm-forecast-revenue')?.value) || 0,
    closingPct: parseInt(document.getElementById('crm-forecast-pct')?.value) || 0,
    estCloseDate: closeDateVal ? Timestamp.fromDate(new Date(closeDateVal + 'T00:00:00')) : null,
    updatedAt: Timestamp.now()
  };

  try {
    await updateDoc(doc(db, 'tenants', CRMState.tenantId, 'customers', customerId), data);
    window.showToast('Forecast saved', 'success');
    // Update local state so overview re-renders with new weighted value
    const cust = CRMState.customers.find(c => c.id === customerId);
    if (cust) {
      cust.estRevenue = data.estRevenue;
      cust.closingPct = data.closingPct;
      cust.estCloseDate = data.estCloseDate;
    }
    CRMState.selectedCustomer = cust || CRMState.selectedCustomer;
    renderDetailBody();
  } catch (err) {
    console.error('[CRM] Save forecast error:', err);
    window.showToast('Failed to save forecast', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">save</span> Save Forecast'; }
  }
}

// ============================================
// SITES TAB
// ============================================
function renderSitesTab() {
  const cid = CRMState.selectedCustomer?.id;
  return `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
      <div style="font-weight:600;color:var(--text-primary);">Sites</div>
      <button class="btn btn-primary btn-sm" onclick="window.CRMModule.showSiteForm('${escJsAttr(cid)}')">
        <span class="material-icons" style="font-size:16px;">add</span> Add Site
      </button>
    </div>
    ${CRMState.sites.length === 0 ? '<div style="text-align:center;color:var(--text-secondary);padding:24px;">No sites yet.</div>' :
    CRMState.sites.map(s => `
      <div class="card" style="padding:12px;margin-bottom:8px;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;">
          <div>
            <div style="font-weight:600;font-size:14px;color:var(--text-primary);">${escHTML(s.name)}</div>
            ${s.address ? `<div style="font-size:13px;color:var(--text-secondary);margin-top:2px;">${escHTML(s.address)}</div>` : ''}
            ${s.contactName ? `<div style="font-size:12px;color:var(--text-secondary);margin-top:2px;">Contact: ${escHTML(s.contactName)} ${s.contactPhone ? '- ' + escHTML(s.contactPhone) : ''}</div>` : ''}
            ${(s.hours || s.gateAccess) ? `<div style="display:flex;gap:10px;flex-wrap:wrap;margin-top:4px;font-size:12px;color:var(--text-secondary);">
              ${s.hours ? `<span style="display:inline-flex;align-items:center;gap:3px;"><span class="material-icons" style="font-size:13px;color:#0891B2;">schedule</span>${escHTML(s.hours)}</span>` : ''}
              ${s.gateAccess ? `<span style="display:inline-flex;align-items:center;gap:3px;"><span class="material-icons" style="font-size:13px;color:#D97706;">vpn_key</span>${escHTML(s.gateAccess)}</span>` : ''}
            </div>` : ''}
            ${s.notes ? `<div style="font-size:12px;color:var(--text-secondary);margin-top:2px;">${escHTML(s.notes)}</div>` : ''}
          </div>
          <div style="display:flex;gap:4px;">
            <button class="btn btn-sm" style="padding:4px;" onclick="event.stopPropagation();window.CRMModule.showSiteForm('${escJsAttr(cid)}','${escJsAttr(s.id)}')">
              <span class="material-icons" style="font-size:16px;">edit</span>
            </button>
            <button class="btn btn-sm" style="padding:4px;color:var(--danger);" onclick="event.stopPropagation();window.CRMModule.deleteSite('${escJsAttr(cid)}','${escJsAttr(s.id)}')">
              <span class="material-icons" style="font-size:16px;">delete</span>
            </button>
          </div>
        </div>
      </div>
    `).join('')}
  `;
}

function showSiteForm(customerId, siteId) {
  const site = siteId ? CRMState.sites.find(s => s.id === siteId) : null;
  const isEdit = !!site;

  const html = `
    <div style="padding:var(--spacing-md);border:1px solid var(--border-color);border-radius:var(--radius-md);margin-bottom:12px;background:var(--bg-secondary);">
      <div style="font-weight:600;margin-bottom:8px;color:var(--text-primary);">${isEdit ? 'Edit Site' : 'Add Site'}</div>
      <input type="hidden" id="crm-site-id" value="${siteId || ''}">
      <div class="form-group" style="margin-bottom:8px;">
        <label style="font-size:13px;">Site Name *</label>
        <input type="text" id="crm-site-name" class="form-control" value="${escAttr(site?.name || '')}" data-maxlen="100">
      </div>
      <div class="form-group" style="margin-bottom:8px;">
        <label style="font-size:13px;">Address</label>
        <input type="text" id="crm-site-address" class="form-control" value="${escAttr(site?.address || '')}">
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;">Site Contact</label>
          <input type="text" id="crm-site-contact" class="form-control" value="${escAttr(site?.contactName || '')}">
        </div>
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;">Contact Phone</label>
          <input type="tel" id="crm-site-phone" class="form-control" value="${escAttr(site?.contactPhone || '')}">
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;">Dock / Access Hours</label>
          <input type="text" id="crm-site-hours" class="form-control" placeholder="e.g. Mon-Fri 7a-4p" value="${escAttr(site?.hours || '')}" data-maxlen="100">
        </div>
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;">Gate / Access</label>
          <input type="text" id="crm-site-gate" class="form-control" placeholder="e.g. Call ahead, code #4821" value="${escAttr(site?.gateAccess || '')}" data-maxlen="150">
        </div>
      </div>
      <div class="form-group" style="margin-bottom:8px;">
        <label style="font-size:13px;">Notes</label>
        <input type="text" id="crm-site-notes" class="form-control" value="${escAttr(site?.notes || '')}">
      </div>
      <div style="display:flex;gap:8px;justify-content:flex-end;">
        <button class="btn btn-secondary btn-sm" onclick="window.CRMModule.switchDetailTab('sites')">Cancel</button>
        <button class="btn btn-primary btn-sm" onclick="window.CRMModule.saveSite('${escJsAttr(customerId)}')">Save Site</button>
      </div>
    </div>
  `;

  // Render inline form at top of sites tab
  const body = document.getElementById('crm-detail-body');
  const panel = body?.querySelector('[style*="padding"]');
  if (panel) {
    panel.innerHTML = html + renderSitesTab().replace(/^[\s\S]*?<\/div>\s*/, ''); // Remove the header
  }
  setTimeout(() => {
    document.getElementById('crm-site-name')?.focus();
    const siteAddr = document.getElementById('crm-site-address');
    if (siteAddr && window.attachAddressAutocomplete) window.attachAddressAutocomplete(siteAddr);
  }, 100);
}

async function saveSite(customerId) {
  const name = document.getElementById('crm-site-name')?.value.trim();
  if (!name) { window.showToast('Site name is required', 'error'); return; }

  const siteId = document.getElementById('crm-site-id')?.value;
  const data = {
    name,
    address: document.getElementById('crm-site-address')?.value.trim() || '',
    contactName: document.getElementById('crm-site-contact')?.value.trim() || '',
    contactPhone: document.getElementById('crm-site-phone')?.value.trim() || '',
    hours: document.getElementById('crm-site-hours')?.value.trim() || '',
    gateAccess: document.getElementById('crm-site-gate')?.value.trim() || '',
    notes: document.getElementById('crm-site-notes')?.value.trim() || '',
    updatedAt: Timestamp.now()
  };

  try {
    const basePath = `tenants/${CRMState.tenantId}/customers/${customerId}/sites`;
    if (siteId) {
      await updateDoc(doc(db, basePath, siteId), data);
    } else {
      data.createdAt = Timestamp.now();
      await addDoc(collection(db, basePath), data);
    }
    window.showToast(siteId ? 'Site updated' : 'Site added', 'success');
    CRMState.detailTab = 'sites';
    // renderDetailBody will be called by onSnapshot
  } catch (err) {
    console.error('[CRM] Save site error:', err);
    window.showToast('Failed to save site', 'error');
  }
}

async function deleteSite(customerId, siteId) {
  if (!confirm('Delete this site?')) return;
  try {
    await deleteDoc(doc(db, `tenants/${CRMState.tenantId}/customers/${customerId}/sites`, siteId));
    window.showToast('Site deleted', 'success');
  } catch (err) {
    console.error('[CRM] Delete site error:', err);
    window.showToast('Failed to delete site', 'error');
  }
}

// ============================================
// CONTACTS TAB
// ============================================
function renderContactsTab() {
  const cid = CRMState.selectedCustomer?.id;

  // Form section (hidden by default, shown via showContactForm)
  const formSection = '<div id="crm-contact-form-slot"></div>';

  // TASK-368 pattern — show skeleton until first contacts snapshot lands
  // instead of "No contacts yet" during cold subscribe.
  const cardsSection = !CRMState._detailContactsLoaded
    ? `<div style="text-align:center;padding:var(--spacing-xl);"><div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div><div style="color:var(--text-secondary);font-size:0.875rem;">Loading contacts…</div></div>`
    : CRMState.contacts.length === 0
    ? `<div style="text-align:center;color:var(--text-secondary);padding:32px 16px;">
        <span class="material-icons" style="font-size:40px;color:var(--border-color);display:block;margin-bottom:8px;">contacts</span>
        No contacts yet. Add one to get started.
      </div>`
    : `<div class="crm-contacts-strip">
        ${CRMState.contacts.map(ct => `
          <div class="crm-contact-card ${CRMState._editingContactId === ct.id ? 'crm-contact-card-active' : ''}" data-contact-id="${ct.id}">
            <div class="crm-contact-card-header">
              <div style="flex:1;min-width:0;">
                <div class="crm-contact-card-name">${escHTML(ct.name)}</div>
                ${ct.title ? `<div class="crm-contact-card-title">${escHTML(ct.title)}</div>` : ''}
              </div>
              <div style="display:flex;gap:2px;flex-shrink:0;">
                ${ct.phone && _canPlaceCalls() ? `<button class="btn btn-sm crm-contact-card-btn" title="Call (recorded)" aria-label="Call ${escAttr(ct.name || 'contact')}" onclick="event.stopPropagation();window.CRMModule.startRecordedCall('${escJsAttr(cid)}','${escJsAttr(ct.id)}')">
                  <span class="material-icons" style="font-size:15px;">phone_in_talk</span>
                </button>` : ''}
                <button class="btn btn-sm crm-contact-card-btn" onclick="event.stopPropagation();window.CRMModule.showContactForm('${escJsAttr(cid)}','${escJsAttr(ct.id)}')">
                  <span class="material-icons" style="font-size:15px;">edit</span>
                </button>
                <button class="btn btn-sm crm-contact-card-btn crm-contact-card-btn-del" onclick="event.stopPropagation();window.CRMModule.deleteContact('${escJsAttr(cid)}','${escJsAttr(ct.id)}')">
                  <span class="material-icons" style="font-size:15px;">delete</span>
                </button>
              </div>
            </div>
            ${ct.showInDispatch !== false ? '<span class="crm-contact-dispatch-badge"><span class="material-icons" style="font-size:12px;">local_shipping</span>Dispatch</span>' : ''}
            <div class="crm-contact-card-details">
              ${ct.phone ? `<div><span class="material-icons" style="font-size:14px;vertical-align:-2px;">phone</span> ${escHTML(ct.phone)}</div>` : ''}
              ${ct.email ? `<div><span class="material-icons" style="font-size:14px;vertical-align:-2px;">email</span> ${escHTML(ct.email)}</div>` : ''}
            </div>
            ${ct.notes ? `<div class="crm-contact-card-notes">${escHTML(ct.notes)}</div>` : ''}
          </div>
        `).join('')}
      </div>`;

  const showingForm = !!CRMState._editingContactId || CRMState._showingContactForm;

  return `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
      <div style="font-weight:600;color:var(--text-primary);">Contacts <span style="font-size:12px;color:var(--text-secondary);font-weight:400;">${CRMState.contacts.length}</span></div>
      ${showingForm ? '' : `<button class="btn btn-primary btn-sm" onclick="window.CRMModule.showContactForm('${escJsAttr(cid)}')">
        <span class="material-icons" style="font-size:16px;">add</span> Add Contact
      </button>`}
    </div>
    ${formSection}
    ${cardsSection}
  `;
}

// TASK-9823 slice 1 — recorded call to a contact. UI hint only: the
// placeRecordedCall callable re-checks (tenant from the auth claim, owner via
// ownerId or the `calls` module, credits, numbers read server-side).
// listen=true: open saved audio (a read-only login may listen, not dial).
function _canPlaceCalls(listen) {
  if (_crmIsDemoTenant()) return false; // no real calls from the demo
  const S = window.AppState || {};
  if (S.tenantData?.ownerId && S.user?.uid === S.tenantData.ownerId) return true; // owner by ownerId, never a role
  const m = S.memberAccess;
  if (!m) return true; // legacy single login — the server decides
  if (m.status && m.status !== 'active') return false;
  if (!listen && (m.viewUploadOnly === true || m.readOnly === true)) return false;
  return Array.isArray(m.modules) && m.modules.includes('calls');
}

// Saved call audio: storage.rules lets NO client read the prefix. The
// callRecording callable (owner or `calls`) writes the audit row, then streams
// the bytes back in chunks. No Storage path or URL ever reaches the page.
const _callFn = (d) => httpsCallable(functions, 'callRecording')(d).then((r) => r?.data || {});
const _crmModal = (id, inner) => {
  document.getElementById(id)?.remove();
  const ov = document.createElement('div');
  ov.id = id;
  ov.setAttribute('style', 'position:fixed;inset:0;z-index:10050;background:rgba(15,23,42,0.55);display:flex;align-items:center;justify-content:center;padding:16px;');
  ov.innerHTML = `<div role="dialog" aria-modal="true" style="background:#fff;border-radius:12px;max-width:400px;width:100%;padding:20px;box-shadow:0 20px 40px rgba(0,0,0,0.25);text-align:center;">${inner}</div>`;
  ov.addEventListener('click', (e) => { if (e.target === ov) ov.remove(); });
  document.body.appendChild(ov);
  return ov;
};
const _keepLabel = (d) => (d === 90 ? '90 days' : d === 1095 ? '3 years' : '1 year');
async function openCallRecording(callRecordId) {
  let d;
  try { d = await _callFn({ action: 'open', callRecordId }); } catch (e) { window.showToast?.(e?.message || 'Could not open the recording.', 'error'); return; }
  if (!d.openId) {
    const why = { not_saved: 'Audio was not saved for this call.', expired: 'This audio was deleted when its keep time ran out.', deleted: 'This audio was deleted.', not_kept_no_notice: 'Audio was not kept: the other person did not get the recording notice.' }[d.audioStatus] || 'There is no saved audio for this call.';
    window.showToast?.(why, 'info');
    return;
  }
  const ov = _crmModal('crm-callrec-modal', `
    <div style="font-weight:700;color:var(--text-primary);margin-bottom:10px;">Call with ${escHTML(d.contactName || 'contact')}</div>
    <div id="crm-callrec-load" style="font-size:0.85rem;color:var(--text-primary);margin-bottom:6px;">Loading audio…</div>
    <audio id="crm-callrec-audio" controls style="width:100%;min-height:44px;"></audio>
    <div style="font-size:0.8rem;color:var(--text-secondary);margin-top:6px;">${d.deletesAtMs ? `Deleted automatically ${new Date(d.deletesAtMs).toLocaleDateString()}.` : ''} Opening a recording is logged.</div>
    <div style="display:flex;gap:8px;justify-content:center;margin-top:14px;">
      ${d.canDelete ? '<button class="btn btn-secondary" id="crm-callrec-del">Delete audio</button>' : ''}
      <button class="btn btn-primary" id="crm-callrec-close">Close</button>
    </div>`);
  ov.querySelector('#crm-callrec-close').onclick = () => ov.remove();
  const del = ov.querySelector('#crm-callrec-del');
  if (del) del.onclick = async () => {
    if (!confirm('Delete this call audio for good?')) return;
    try { await _callFn({ action: 'delete', callRecordId }); ov.remove(); window.showToast?.('Call audio deleted.', 'success'); }
    catch (e) { window.showToast?.(e?.message || 'Could not delete the audio.', 'error'); }
  };
  // The audio prefix is server-only in storage.rules: the bytes come back from
  // the callable in chunks, tied to the audited open (openId).
  try {
    const parts = []; let offset = 0;
    for (let i = 0; i < 64; i++) {
      const c = await _callFn({ action: 'chunk', callRecordId, openId: d.openId, offset });
      if (c.data) parts.push(Uint8Array.from(atob(c.data), (ch) => ch.charCodeAt(0)));
      offset = c.nextOffset;
      const ld = ov.querySelector('#crm-callrec-load');
      if (ld && d.sizeBytes) ld.textContent = `Loading audio… ${Math.min(100, Math.round((offset / d.sizeBytes) * 100))}%`;
      if (c.done || !document.body.contains(ov)) break;
    }
    ov.querySelector('#crm-callrec-load')?.remove();
    const a = ov.querySelector('#crm-callrec-audio');
    if (a) { a.src = URL.createObjectURL(new Blob(parts, { type: 'audio/mpeg' })); a.addEventListener('emptied', () => URL.revokeObjectURL(a.src), { once: true }); }
  } catch (e) { window.showToast?.(e?.message || "Couldn't load the audio.", 'error'); }
}

function _recordedCallDialog(contactName) {
  return new Promise((resolve) => {
    document.getElementById('crm-call-modal')?.remove();
    const ov = document.createElement('div');
    ov.id = 'crm-call-modal';
    ov.setAttribute('style', 'position:fixed;inset:0;z-index:10050;background:rgba(15,23,42,0.55);display:flex;align-items:center;justify-content:center;padding:16px;');
    ov.innerHTML = `
      <div role="dialog" aria-modal="true" aria-labelledby="crm-call-title" style="background:#fff;border-radius:12px;max-width:400px;width:100%;padding:20px;box-shadow:0 20px 40px rgba(0,0,0,0.25);text-align:center;">
        <span class="material-icons" style="font-size:36px;color:var(--brand-blue);">phone_in_talk</span>
        <div id="crm-call-title" style="font-weight:700;font-size:1.05rem;color:var(--text-primary);margin:6px 0 10px;">Call ${escHTML(contactName || 'this contact')}?</div>
        <div style="color:var(--text-primary);font-size:0.9rem;line-height:1.45;text-align:left;">
          DispatchDeck rings <b>your cell</b> first. Press 1 and it connects you from the company number.<br>
          The call is <b>recorded</b>, and both of you hear that before anyone talks.<br>
          Costs <b>2 SMS credits a minute</b> of talk time. Ringing and unanswered calls are free.
        </div>
        <label style="display:flex;align-items:center;gap:8px;margin:14px 0 4px;font-size:0.9rem;color:var(--text-primary);text-align:left;cursor:pointer;">
          <input type="checkbox" id="crm-call-save-audio"> Save audio (kept <span id="crm-call-keep">1 year</span>)
        </label>
        <button type="button" id="crm-call-keep-chg" class="btn btn-sm btn-secondary" style="display:none;margin-top:2px;font-size:0.75rem;">Change how long audio is kept</button>
        <select id="crm-call-keep-sel" class="form-input" style="display:none;margin-top:6px;" aria-label="How long saved call audio is kept">
          <option value="90">Keep saved audio 90 days</option><option value="365">Keep saved audio 1 year</option><option value="1095">Keep saved audio 3 years</option>
        </select>
        <div style="display:flex;gap:8px;justify-content:center;margin-top:16px;">
          <button class="btn btn-secondary" id="crm-call-cancel">Cancel</button>
          <button class="btn btn-primary" id="crm-call-go"><span class="material-icons" style="font-size:16px;vertical-align:-3px;">call</span> Call me now</button>
        </div>
      </div>`;
    const done = (v) => { ov.remove(); document.removeEventListener('keydown', onKey); resolve(v); };
    const onKey = (e) => { if (e.key === 'Escape') done(null); };
    ov.addEventListener('click', (e) => { if (e.target === ov) done(null); });
    document.addEventListener('keydown', onKey);
    document.body.appendChild(ov);
    ov.querySelector('#crm-call-cancel').onclick = () => done(null);
    ov.querySelector('#crm-call-go').onclick = () => done({ saveAudio: ov.querySelector('#crm-call-save-audio').checked === true });
    ov.querySelector('#crm-call-go').focus();
    // Retention: shown to everyone, changeable by the owner only (server re-checks + audits old/new).
    _callFn({ action: 'settings' }).then((s) => {
      const keep = ov.querySelector('#crm-call-keep'); const sel = ov.querySelector('#crm-call-keep-sel');
      if (!keep || !sel) return;
      keep.textContent = _keepLabel(s.retentionDays);
      if (!s.canEdit) return;
      sel.value = String(s.retentionDays);
      const chg = ov.querySelector('#crm-call-keep-chg'); // owner only; kept out of the way of a quick call (COD UX)
      if (chg) { chg.style.display = ''; chg.onclick = () => { chg.style.display = 'none'; sel.style.display = ''; sel.focus(); }; }
      sel.onchange = async () => {
        const days = Number(sel.value);
        if (days < s.retentionDays && !confirm(`Saved call audio older than ${_keepLabel(days)} will be deleted tonight. Continue?`)) { sel.value = String(s.retentionDays); return; }
        try { const r = await _callFn({ action: 'setRetention', days }); s.retentionDays = r.retentionDays; keep.textContent = _keepLabel(r.retentionDays); window.showToast?.('Saved.', 'success'); }
        catch (e) { sel.value = String(s.retentionDays); window.showToast?.(e?.message || 'Could not save.', 'error'); }
      };
    }).catch(() => {});
  });
}

let _recordedCallBusy = false;
async function startRecordedCall(customerId, contactId) {
  if (_recordedCallBusy) return;
  const ct = (CRMState.contacts || []).find((c) => c.id === contactId);
  const choice = await _recordedCallDialog(ct?.name);
  if (!choice) return;
  _recordedCallBusy = true;
  try {
    const fn = httpsCallable(functions, 'placeRecordedCall');
    const res = await fn({ customerId, contactId, saveAudio: choice.saveAudio });
    const d = res?.data || {};
    window.showToast?.(`Ringing your cell (…${d.ringingLast4 || ''}). Answer and press 1 to connect.`, 'success');
  } catch (e) {
    window.showToast?.(e?.message || 'The call could not be started.', 'error');
  } finally {
    _recordedCallBusy = false;
  }
}

function showContactForm(customerId, contactId) {
  const contact = contactId ? CRMState.contacts.find(c => c.id === contactId) : null;
  const isEdit = !!contact;

  // Track which contact is being edited (for active highlight on card)
  CRMState._editingContactId = contactId || null;
  CRMState._showingContactForm = true;

  // Re-render the tab to get the form slot + cards with active state
  switchDetailTab('contacts');

  // Now fill the form slot
  const slot = document.getElementById('crm-contact-form-slot');
  if (!slot) return;

  slot.innerHTML = `
    <div class="crm-contact-form">
      <div class="crm-contact-form-title">
        <span class="material-icons">${isEdit ? 'edit' : 'person_add'}</span>
        ${isEdit ? 'Edit Contact' : 'Add Contact'}
      </div>
      <input type="hidden" id="crm-contact-id" value="${contactId || ''}">

      <div class="crm-form-section">
        <div class="crm-form-section-title"><span class="material-icons">badge</span> Contact Info</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
          <div class="form-group" style="margin-bottom:8px;">
            <label style="font-size:13px;">Name *</label>
            <input type="text" id="crm-contact-name" class="form-control" value="${escAttr(contact?.name || '')}" data-maxlen="100">
          </div>
          <div class="form-group" style="margin-bottom:8px;">
            <label style="font-size:13px;">Title</label>
            <input type="text" id="crm-contact-title" class="form-control" value="${escAttr(contact?.title || '')}">
          </div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
          <div class="form-group" style="margin-bottom:8px;">
            <label style="font-size:13px;">Phone</label>
            <input type="tel" id="crm-contact-phone" class="form-control" value="${escAttr(contact?.phone || '')}">
          </div>
          <div class="form-group" style="margin-bottom:8px;">
            <label style="font-size:13px;">Email</label>
            <input type="email" id="crm-contact-email" class="form-control" value="${escAttr(contact?.email || '')}">
          </div>
        </div>
      </div>

      <div class="crm-form-section">
        <div class="crm-form-section-title"><span class="material-icons">notes</span> Details</div>
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;">Notes</label>
          <input type="text" id="crm-contact-notes" class="form-control" value="${escAttr(contact?.notes || '')}">
        </div>
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <input type="checkbox" id="crm-contact-dispatch" ${(contact?.showInDispatch !== false) ? 'checked' : ''} style="width:16px;height:16px;">
          <label for="crm-contact-dispatch" style="font-size:13px;cursor:pointer;margin:0;color:var(--text-primary);">Show in Dispatch POC list</label>
        </div>
      </div>

      <div style="display:flex;gap:8px;justify-content:flex-end;padding-top:8px;border-top:1px solid var(--border-light);">
        <button class="btn btn-secondary btn-sm" onclick="window.CRMModule.switchDetailTab('contacts')">Cancel</button>
        <button class="btn btn-primary btn-sm" onclick="window.CRMModule.saveContact('${escJsAttr(customerId)}')">Save Contact</button>
      </div>
    </div>
  `;

  setTimeout(() => document.getElementById('crm-contact-name')?.focus(), 100);
}

async function saveContact(customerId) {
  const _clamp = window.clampText || ((s, n) => s && s.length > n ? s.slice(0, n) : s || '');
  const name = _clamp(document.getElementById('crm-contact-name')?.value.trim(), 100);
  if (!name) { window.showToast('Contact name is required', 'error'); return; }

  const contactId = document.getElementById('crm-contact-id')?.value;
  const data = {
    name,
    title: document.getElementById('crm-contact-title')?.value.trim() || '',
    phone: document.getElementById('crm-contact-phone')?.value.trim() || '',
    email: document.getElementById('crm-contact-email')?.value.trim() || '',
    notes: document.getElementById('crm-contact-notes')?.value.trim() || '',
    showInDispatch: document.getElementById('crm-contact-dispatch')?.checked !== false,
    updatedAt: Timestamp.now()
  };

  try {
    const basePath = `tenants/${CRMState.tenantId}/customers/${customerId}/contacts`;
    if (contactId) {
      await updateDoc(doc(db, basePath, contactId), data);
    } else {
      data.createdAt = Timestamp.now();
      await addDoc(collection(db, basePath), data);
    }
    window.showToast(contactId ? 'Contact updated' : 'Contact added', 'success');
    CRMState._editingContactId = null;
    CRMState._showingContactForm = false;
    CRMState.detailTab = 'contacts';
  } catch (err) {
    console.error('[CRM] Save contact error:', err);
    window.showToast('Failed to save contact', 'error');
  }
}

async function deleteContact(customerId, contactId) {
  if (!confirm('Delete this contact?')) return;
  try {
    await deleteDoc(doc(db, `tenants/${CRMState.tenantId}/customers/${customerId}/contacts`, contactId));
    window.showToast('Contact deleted', 'success');
  } catch (err) {
    console.error('[CRM] Delete contact error:', err);
    window.showToast('Failed to delete contact', 'error');
  }
}

// TASK-823 — CRM owns the customer-contacts SUBCOLLECTION schema (title /
// showInDispatch). The dispatch "Add Job → edit customer" modal must NOT
// reimplement that schema (Architect ❌ — module boundary); it calls these two
// helpers when the customer is crmManaged so a POC added there lands in the
// same subcollection the dispatch POC dropdown reads (the desync bug: dispatch
// wrote the embedded contacts[] array, the dropdown read the subcollection →
// saved-but-invisible). Cross-module access is via window.CRMModule.* ONLY
// (Pitfall #1 — never import crm.js with a ?v= cache-buster: duplicate instance).
//
// Row shape exchanged with dispatch (its embedded-contact shape, _crmId added):
//   { _crmId?, name, role, phone, phone2, email, isPrimary }
// Subcollection doc shape (canonical): { name, title, phone, phone2, email,
//   isPrimary, showInDispatch, customerId, notes, createdAt, updatedAt }.
// role <-> title is the only field rename; phone2 is preserved as an extra field
// (the CRM contact editor ignores it, but a dispatch round-trip keeps it).
// TASK-1395 — CRMState.tenantId is set ONLY by initCRM (:182), i.e. after the
// user has visited the CRM view. Dispatch calls these three helpers via
// window.CRMModule.* from a session that may never have opened CRM, so the
// tenant was unset and the contact store was unreachable: loadContacts returned
// [] SILENTLY (dispatch then stamped contactsLoaded='1' and showed "No contacts
// added yet") and saveContacts threw, killing the whole Edit Customer save with
// a generic "Error saving customer" — name/phone/email edits lost too.
// Fall back to the canonical app-level tenant (app.js:10355 passes exactly this
// value into initCRM). Both halves must use it: fixing only the save side would
// reconcile against the never-loaded empty list and DELETE every existing
// contact (COD Code-Reviewer ❌).
// AppState.tenant is preferred over CRMState.tenantId, not the other way round
// (COD Code-Reviewer ⚠️): CRMState is a snapshot taken at initCRM and is never
// re-synced, so a session that opened CRM under one tenant and later moved to
// another would keep writing to the first. AppState.tenant is the live value.
// No in-app tenant switcher exists today, so this is defensive, not a live bug.
function crmTenantId() {
  return window.AppState?.tenant || CRMState.tenantId || null;
}

async function loadContacts(customerId) {
  const tid = crmTenantId();
  // THROW, don't return [], when the tenant can't be resolved at all. An empty
  // array is indistinguishable from "this customer has no contacts", and the
  // caller (dispatch openEditCustomer) treats a resolved value as a successful
  // load — which would arm saveContacts' reconcile-with-deletions against a
  // list nobody ever loaded and wipe the subcollection. Failing loud keeps
  // dispatch's contactsLoaded flag at '0' so the save preserves contacts.
  if (!tid) throw new Error('loadContacts: tenant unresolved');
  if (!customerId) return [];
  const snap = await getDocs(
    query(collection(db, `tenants/${tid}/customers/${customerId}/contacts`), orderBy('name'))
  );
  return snap.docs.map(d => {
    const c = d.data();
    return {
      _crmId: d.id,
      name: c.name || '',
      role: c.title || '',
      phone: c.phone || '',
      phone2: c.phone2 || '',
      email: c.email || '',
      isPrimary: !!c.isPrimary,
      notes: c.notes || ''
    };
  });
}

// Reconcile the subcollection against the full set of rows the caller wants to
// persist: update rows that carry a _crmId, add rows that don't, and delete any
// existing subcollection contact whose id is absent from the incoming set (the
// user removed that row). One batch = atomic. Throws on failure so the caller
// can surface an error and NOT close the modal.
async function saveContacts(customerId, rows) {
  const tid = crmTenantId(); // TASK-1395 — see loadContacts
  if (!tid || !customerId) throw new Error('saveContacts: missing tenant/customer');
  const base = `tenants/${tid}/customers/${customerId}/contacts`;
  const clean = (rows || []).filter(r => (r.name || '').trim());

  // Existing ids, to compute deletions.
  const existing = await getDocs(collection(db, base));
  const keepIds = new Set(clean.map(r => r._crmId).filter(Boolean));

  const batch = writeBatch(db);
  const now = Timestamp.now();
  for (const r of clean) {
    const payload = {
      name: (r.name || '').trim(),
      title: (r.role || '').trim(),       // role(dispatch) -> title(CRM)
      phone: (r.phone || '').trim(),
      phone2: (r.phone2 || '').trim(),
      email: (r.email || '').trim(),
      isPrimary: !!r.isPrimary,
      showInDispatch: true,               // added here = meant to be dispatchable
      customerId,                         // self-describe parent (integrity + future rule)
      updatedAt: now
    };
    // Existing row → merge-update by id; new row → Firestore auto-id ref.
    const ref = r._crmId ? doc(db, base, r._crmId) : doc(collection(db, base));
    batch.set(ref, r._crmId ? payload : { ...payload, notes: r.notes || '', createdAt: now }, { merge: true });
  }
  // Deletions — existing docs the user removed.
  existing.docs.forEach(d => { if (!keepIds.has(d.id)) batch.delete(d.ref); });
  await batch.commit();
}

// TASK-823 (Blast ⚠️ call site) — flipping a legacy customer (embedded
// contacts[]) to crmManaged makes the POC dropdown read the EMPTY subcollection,
// orphaning those contacts. Migrate embedded contacts into the subcollection
// once, deduping by name against whatever is already there. Idempotent.
async function migrateEmbeddedContacts(customerId, embedded) {
  const tid = crmTenantId(); // TASK-1395 — same cross-module reachability fix
  if (!tid || !customerId || !Array.isArray(embedded) || !embedded.length) return 0;
  const base = `tenants/${tid}/customers/${customerId}/contacts`;
  const existing = await getDocs(collection(db, base));
  const have = new Set(existing.docs.map(d => (d.data().name || '').trim().toLowerCase()).filter(Boolean));
  const toAdd = embedded.filter(c => (c.name || '').trim() && !have.has((c.name || '').trim().toLowerCase()));
  if (!toAdd.length) return 0;
  const batch = writeBatch(db);
  const now = Timestamp.now();
  toAdd.forEach(c => batch.set(doc(collection(db, base)), {
    name: (c.name || '').trim(),
    title: (c.role || c.title || '').trim(),
    phone: (c.phone || '').trim(),
    phone2: (c.phone2 || '').trim(),
    email: (c.email || '').trim(),
    isPrimary: !!c.isPrimary,
    notes: c.notes || '',
    showInDispatch: true,
    customerId,
    migratedFromDispatch: true,
    createdAt: now,
    updatedAt: now
  }));
  await batch.commit();
  return toAdd.length;
}

// ============================================
// CONTRACTS TAB
// ============================================
// TASK-10138 — the back button names the list the same way the tab does.
function _contractsListLabel() {
  const t = CRMState.selectedCustomer?.type;
  return (t === 'subcontractor' || t === 'vendor') ? 'Rate Cards' : 'Contracts';
}

function renderContractsTab() {
  const cid = CRMState.selectedCustomer?.id;
  return `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
      <div style="font-weight:600;color:var(--text-primary);">Contracts</div>
      <button class="btn btn-primary btn-sm" onclick="window.CRMModule.showContractForm('${escJsAttr(cid)}')">
        <span class="material-icons" style="font-size:16px;">add</span> Add Contract
      </button>
    </div>
    ${!CRMState._detailContractsLoaded
      ? `<div style="text-align:center;padding:var(--spacing-xl);"><div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div><div style="color:var(--text-secondary);font-size:0.875rem;">Loading contracts…</div></div>`
      : CRMState.contracts.length === 0 ? '<div class="empty-state-small" style="text-align:center;color:var(--text-secondary);padding:24px;"><span class="material-icons" style="font-size:24px;vertical-align:middle;margin-right:6px;opacity:0.5;">description</span>No contracts yet. Use "Add Contract" above to create the first one.</div>' :
    CRMState.contracts.map(ct => {
      const startDate = ct.startDate?.toDate ? ct.startDate.toDate().toLocaleDateString() : ct.startDate || '';
      const endDate = ct.endDate?.toDate ? ct.endDate.toDate().toLocaleDateString() : ct.endDate || '';
      const statusColor = ct.status === 'Active' ? 'var(--brand-blue)' : ct.status === 'Draft' ? '#f59e0b' : 'var(--text-muted)';
      return `
        <div class="card" style="padding:12px;margin-bottom:8px;cursor:pointer;border-left:3px solid ${statusColor};"
          onclick="window.CRMModule.viewContract('${escJsAttr(cid)}','${escJsAttr(ct.id)}')">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;">
            <div>
              <div style="font-weight:600;font-size:14px;color:var(--text-primary);">${escHTML(ct.title || 'Untitled Contract')}</div>
              <div style="font-size:13px;color:var(--text-secondary);margin-top:2px;">
                ${startDate}${endDate ? ' — ' + endDate : ''} | ${ct.paymentTerms || 'No terms'}
              </div>
              <div style="font-size:12px;color:var(--text-secondary);margin-top:2px;">${ct._lineItemCount || 0} line items</div>
            </div>
            <span style="font-size:11px;padding:2px 6px;border-radius:4px;background:${statusColor}15;color:${statusColor};font-weight:500;">${ct.status || 'Draft'}</span>
          </div>
        </div>
      `;
    }).join('')}
  `;
}

function showContractForm(customerId, contractId) {
  const contract = contractId ? CRMState.contracts.find(c => c.id === contractId) : null;
  const isEdit = !!contract;

  const formatDate = (d) => {
    if (!d) return '';
    const date = d.toDate ? d.toDate() : new Date(d);
    return date.toISOString().split('T')[0];
  };

  // Build "Copy contract from" section — only for new contracts
  let copyFromHtml = '';
  if (!isEdit) {
    // Same-customer contracts
    const sameOpts = CRMState.contracts.map(c =>
      `<option value="${customerId}|${c.id}">${escHTML(c.title)} (${c.status || 'Draft'})</option>`
    ).join('');
    // Other customers for cross-customer copy
    // P0 2026-10-06 — copy within the same KIND only: a customer's price book
    // from another customer, a vendor's rate card from another vendor. Never a
    // vendor's cost rates into a customer's billing price book (or back).
    const _BC = window.BillableCustomer;
    const _selfVendor = _BC.isVendorLikeCustomer(CRMState.customers.find(c => c.id === customerId));
    const otherCustomers = CRMState.customers.filter(c => c.id !== customerId && (c._contractCount || 0) > 0
      && _BC.isVendorLikeCustomer(c) === _selfVendor);
    const custOpts = otherCustomers.map(c =>
      `<option value="${c.id}">${escHTML(c.name)}</option>`
    ).join('');

    copyFromHtml = `
      <div class="form-group" style="margin-bottom:8px;">
        <label style="font-size:13px;">Copy contract from (optional)</label>
        <select id="crm-contract-copy-source" class="form-control" onchange="window.CRMModule.onCopySourceChange('${escJsAttr(customerId)}')">
          <option value="">-- None --</option>
          ${sameOpts ? `<optgroup label="This customer">${sameOpts}</optgroup>` : ''}
          ${custOpts ? `<optgroup label="Another customer">${custOpts}</optgroup>` : ''}
        </select>
        <div id="crm-contract-copy-target" style="margin-top:6px;display:none;"></div>
        <input type="hidden" id="crm-contract-copy-from" value="">
      </div>`;
  }

  const html = `
    <button type="button" class="btn btn-secondary btn-sm" onclick="window.CRMModule.switchDetailTab('contracts')" style="margin-bottom:12px;">
      <span class="material-icons" style="font-size:16px;">arrow_back</span> Back to ${_contractsListLabel()}
    </button>
    <div style="padding:var(--spacing-md);border:1px solid var(--border-color);border-radius:var(--radius-md);margin-bottom:12px;background:var(--bg-secondary);">
      <div style="font-weight:600;margin-bottom:8px;color:var(--text-primary);">${isEdit ? 'Edit Contract' : 'Add Contract'}</div>
      <input type="hidden" id="crm-contract-id" value="${contractId || ''}">
      <div class="form-group" style="margin-bottom:8px;">
        <label style="font-size:13px;">Title *</label>
        <input type="text" id="crm-contract-title" class="form-control" value="${escAttr(contract?.title || '')}" data-maxlen="100">
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;">Status</label>
          <select id="crm-contract-status" class="form-control">
            <option value="Draft" ${(contract?.status || 'Draft') === 'Draft' ? 'selected' : ''}>Draft</option>
            <option value="Active" ${contract?.status === 'Active' ? 'selected' : ''}>Active</option>
            <option value="Expired" ${contract?.status === 'Expired' ? 'selected' : ''}>Expired</option>
            <option value="Cancelled" ${contract?.status === 'Cancelled' ? 'selected' : ''}>Cancelled</option>
          </select>
        </div>
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;">Payment Terms</label>
          <select id="crm-contract-terms" class="form-control">
            <option value="">-- Select --</option>
            ${PAYMENT_TERMS.map(t => `<option value="${t}" ${contract?.paymentTerms === t ? 'selected' : ''}>${t}</option>`).join('')}
          </select>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;">Start Date</label>
          <input type="date" id="crm-contract-start" class="form-control" value="${formatDate(contract?.startDate)}">
        </div>
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;">End Date</label>
          <input type="date" id="crm-contract-end" class="form-control" value="${formatDate(contract?.endDate)}">
        </div>
      </div>
      <div class="form-group" style="margin-bottom:8px;">
        <label style="font-size:13px;">Notes</label>
        <textarea id="crm-contract-notes" class="form-control" rows="2" style="resize:vertical;">${escHTML(contract?.notes || '')}</textarea>
      </div>
      <div class="form-group" style="margin-bottom:8px;padding:10px;background:var(--bg-tertiary, #f8fafc);border-radius:var(--radius-sm);border:1px solid var(--border-color);">
        <label style="font-size:13px;display:flex;align-items:center;gap:8px;cursor:pointer;margin:0;">
          <input type="checkbox" id="crm-contract-permpo-enabled" ${contract?.permanentPo ? 'checked' : ''} onchange="window.CRMModule.togglePermPoField()" style="margin:0;">
          <span style="font-weight:600;">Permanent PO</span>
          <span style="font-weight:400;color:var(--text-secondary);font-size:12px;">(auto-fills on new tickets for this customer)</span>
        </label>
        <input type="text" id="crm-contract-permpo-value" class="form-control" value="${escAttr(contract?.permanentPoNumber || '')}" placeholder="e.g., PO-2026-0042" style="margin-top:8px;display:${contract?.permanentPo ? 'block' : 'none'};" data-maxlen="50">
      </div>
      ${copyFromHtml}
      <div style="display:flex;gap:8px;justify-content:flex-end;">
        <button class="btn btn-secondary btn-sm" onclick="window.CRMModule.switchDetailTab('contracts')">Cancel</button>
        <button class="btn btn-primary btn-sm" onclick="window.CRMModule.saveContract('${escJsAttr(customerId)}')">Save Contract</button>
      </div>
    </div>
  `;

  const body = document.getElementById('crm-detail-body');
  const panel = body?.querySelector('[style*="padding"]');
  if (panel) {
    panel.innerHTML = html;
  }
  setTimeout(() => document.getElementById('crm-contract-title')?.focus(), 100);
}

function togglePermPoField() {
  const cb = document.getElementById('crm-contract-permpo-enabled');
  const input = document.getElementById('crm-contract-permpo-value');
  if (!cb || !input) return;
  input.style.display = cb.checked ? 'block' : 'none';
  if (cb.checked) setTimeout(() => input.focus(), 50);
}

async function onCopySourceChange(targetCustomerId) {
  const sourceSelect = document.getElementById('crm-contract-copy-source');
  const targetDiv = document.getElementById('crm-contract-copy-target');
  const hiddenInput = document.getElementById('crm-contract-copy-from');
  if (!sourceSelect || !targetDiv || !hiddenInput) return;

  const val = sourceSelect.value;
  if (!val) {
    targetDiv.style.display = 'none';
    targetDiv.innerHTML = '';
    hiddenInput.value = '';
    return;
  }

  // Check if it's a direct contract pick (customerId|contractId) or a customer pick (just customerId)
  if (val.includes('|')) {
    // Same-customer contract — direct selection
    const [srcCust, srcContract] = val.split('|');
    hiddenInput.value = `${srcCust}|${srcContract}`;
    targetDiv.style.display = 'none';
    targetDiv.innerHTML = '';
    return;
  }

  // Cross-customer: load that customer's contracts
  const srcCustomerId = val;
  targetDiv.style.display = 'block';
  targetDiv.innerHTML = '<span style="font-size:12px;color:var(--text-secondary);">Loading contracts...</span>';
  hiddenInput.value = '';

  try {
    const snap = await getDocs(collection(db, `tenants/${CRMState.tenantId}/customers/${srcCustomerId}/contracts`));
    if (snap.empty) {
      targetDiv.innerHTML = '<span style="font-size:12px;color:var(--text-secondary);">No contracts found.</span>';
      return;
    }
    const contracts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const opts = contracts.map(c =>
      `<option value="${srcCustomerId}|${c.id}">${escHTML(c.title)} (${c.status || 'Draft'}) — ${c._lineItemCount || 0} items</option>`
    ).join('');
    targetDiv.innerHTML = `
      <select id="crm-contract-copy-pick" class="form-control" onchange="document.getElementById('crm-contract-copy-from').value=this.value">
        <option value="">-- Pick a contract --</option>
        ${opts}
      </select>
    `;
  } catch (err) {
    console.error('[CRM] Load cross-customer contracts error:', err);
    targetDiv.innerHTML = '<span style="font-size:12px;color:var(--error);">Failed to load contracts.</span>';
  }
}

async function saveContract(customerId) {
  const title = document.getElementById('crm-contract-title')?.value.trim();
  if (!title) { window.showToast('Contract title is required', 'error'); return; }

  const contractId = document.getElementById('crm-contract-id')?.value;
  const startVal = document.getElementById('crm-contract-start')?.value;
  const endVal = document.getElementById('crm-contract-end')?.value;
  const copyFromRaw = document.getElementById('crm-contract-copy-from')?.value || '';
  // Format: "sourceCustomerId|sourceContractId" or empty
  const copyFromCustomerId = copyFromRaw ? copyFromRaw.split('|')[0] : '';
  const copyFromId = copyFromRaw ? copyFromRaw.split('|')[1] : '';

  const permPoEnabled = document.getElementById('crm-contract-permpo-enabled')?.checked || false;
  const permPoValue = document.getElementById('crm-contract-permpo-value')?.value.trim() || '';
  // Only treat as "permanent PO set" if the checkbox is on AND a value was entered.
  // Empty checkbox-on keeps data consistent (no orphan flag without a value).
  const hasPermPo = permPoEnabled && permPoValue.length > 0;

  const data = {
    title,
    status: document.getElementById('crm-contract-status')?.value || 'Draft',
    paymentTerms: document.getElementById('crm-contract-terms')?.value || '',
    startDate: startVal ? Timestamp.fromDate(new Date(startVal + 'T00:00:00')) : null,
    endDate: endVal ? Timestamp.fromDate(new Date(endVal + 'T00:00:00')) : null,
    notes: document.getElementById('crm-contract-notes')?.value.trim() || '',
    permanentPo: hasPermPo,
    permanentPoNumber: hasPermPo ? permPoValue : '',
    updatedAt: Timestamp.now()
  };

  try {
    const basePath = `tenants/${CRMState.tenantId}/customers/${customerId}/contracts`;
    if (contractId) {
      await updateDoc(doc(db, basePath, contractId), data);
      // TASK-9446 — a status flip (Draft -> Active) must reach the New Job form
      // without a reload. crm.js cannot import dispatch.js (Pitfall #1).
      window.DispatchModule?.invalidateCustomerPriceBooks?.(customerId);
      window.showToast('Contract updated', 'success');
      // TASK-10138 (Wolverine / Steffani 10/19) — go back to the list after an
      // edit. Setting detailTab alone left the form on screen: renderDetailBody
      // refuses to repaint while #crm-contract-id exists. Only when this same
      // form is still on screen: another customer, or another contract form
      // opened during the save, is left alone (COD: never discard its input).
      if (CRMState.selectedCustomer?.id === customerId &&
          document.getElementById('crm-contract-id')?.value === contractId) {
        switchDetailTab('contracts');
      }
    } else {
      data.createdAt = Timestamp.now();
      data._lineItemCount = 0;
      const newRef = await addDoc(collection(db, basePath), data);
      window.DispatchModule?.invalidateCustomerPriceBooks?.(customerId); // TASK-9446

      // Copy line items from source contract if selected (supports cross-customer)
      if (copyFromId && copyFromCustomerId) {
        const srcPath = `tenants/${CRMState.tenantId}/customers/${copyFromCustomerId}/contracts/${copyFromId}/lineItems`;
        const srcSnap = await getDocs(collection(db, srcPath));
        if (srcSnap.size > 0) {
          const batch = writeBatch(db);
          const destCol = collection(db, basePath, newRef.id, 'lineItems');
          const now = Timestamp.now();
          srcSnap.docs.forEach(d => {
            const li = d.data();
            const newLiRef = doc(destCol);
            batch.set(newLiRef, { ...li, createdAt: now, updatedAt: now });
          });
          await batch.commit();
          // Update _lineItemCount on the new contract
          await updateDoc(doc(db, basePath, newRef.id), { _lineItemCount: srcSnap.size });
        }
        window.showToast(`Contract added with ${srcSnap.size} copied line items`, 'success');
      } else {
        window.showToast('Contract added', 'success');
      }

      // Navigate to the new contract detail so user sees it
      viewContract(customerId, newRef.id);
      return;
    }
  } catch (err) {
    console.error('[CRM] Save contract error:', err);
    window.showToast('Failed to save contract', 'error');
  }
}

async function deleteContract(customerId, contractId) {
  if (!confirm('Delete this contract and all its line items?')) return;
  try {
    const basePath = `tenants/${CRMState.tenantId}/customers/${customerId}/contracts/${contractId}`;
    // Batch-delete all lineItems in subcollection
    const liSnap = await getDocs(collection(db, basePath, 'lineItems'));
    if (liSnap.size > 0) {
      const batch = writeBatch(db);
      liSnap.docs.forEach(d => batch.delete(d.ref));
      await batch.commit();
    }
    // Delete the contract doc
    await deleteDoc(doc(db, `tenants/${CRMState.tenantId}/customers/${customerId}/contracts`, contractId));
    window.DispatchModule?.invalidateCustomerPriceBooks?.(customerId); // TASK-9446
    window.showToast('Contract deleted', 'success');
    // Switch back to contracts list — onSnapshot will update _contractCount automatically
    CRMState.detailTab = 'contracts';
    renderDetailBody();
  } catch (err) {
    console.error('[CRM] Delete contract error:', err);
    window.showToast('Failed to delete contract', 'error');
  }
}

// ============================================
// CONTRACT DETAIL VIEW (with Line Items)
// ============================================
// TASK-9671 S3 — `opts.still()` (a Claude link's open only): false after the
// line-items read = paint nothing. Every other caller passes no opts.
async function viewContract(customerId, contractId, opts) {
  const contract = CRMState.contracts.find(c => c.id === contractId);
  if (!contract) return;

  // Load line items
  const basePath = `tenants/${CRMState.tenantId}/customers/${customerId}/contracts/${contractId}/lineItems`;
  let lineItems = [];
  try {
    const snap = await getDocs(query(collection(db, basePath), orderBy('category')));
    lineItems = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('[CRM] Load line items error:', err);
  }
  // TASK-9671 S3 (COD Code Reviewer) — another customer opened during the read:
  // never paint this customer's Price Book into that customer's details.
  if (CRMState.selectedCustomer && CRMState.selectedCustomer.id !== customerId) return;
  if (opts && typeof opts.still === 'function' && opts.still() !== true) return; // COD r2: mount / access / customer, re-checked after the read

  const body = document.getElementById('crm-detail-body');
  if (!body) return;

  const formatDate = (d) => {
    if (!d) return '—';
    const date = d.toDate ? d.toDate() : new Date(d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const statusColor = contract.status === 'Active' ? 'var(--brand-blue)' : contract.status === 'Draft' ? '#f59e0b' : 'var(--text-muted)';

  body.innerHTML = `
    <div style="padding:var(--spacing-md);">
      <button class="btn btn-secondary btn-sm" onclick="window.CRMModule.switchDetailTab('contracts')" style="margin-bottom:12px;">
        <span class="material-icons" style="font-size:16px;">arrow_back</span> Back to ${_contractsListLabel()}
      </button>

      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;">
        <div>
          <div style="font-weight:700;font-size:18px;color:var(--text-primary);">${escHTML(contract.title)}</div>
          <div style="font-size:13px;color:var(--text-secondary);margin-top:4px;">
            ${formatDate(contract.startDate)} — ${formatDate(contract.endDate)} | ${contract.paymentTerms || 'No terms'}
          </div>
        </div>
        <div style="display:flex;gap:8px;align-items:center;">
          <span style="font-size:12px;padding:3px 8px;border-radius:4px;background:${statusColor}15;color:${statusColor};font-weight:600;">${contract.status || 'Draft'}</span>
          <button class="btn btn-secondary btn-sm" onclick="window.CRMModule.printRateSheet('${escJsAttr(customerId)}','${escJsAttr(contractId)}')" title="Print Rate Sheet">
            <span class="material-icons" style="font-size:16px;">print</span>
          </button>
          <button class="btn btn-secondary btn-sm" onclick="window.CRMBrochure?.openFromRateSheet('${escJsAttr(customerId)}','${escJsAttr(contractId)}')" title="Create marketing brochure from this contract">
            <span class="material-icons" style="font-size:16px;">campaign</span>
          </button>
          <button class="btn btn-secondary btn-sm" onclick="window.CRMModule.showContractForm('${escJsAttr(customerId)}','${escJsAttr(contractId)}')">
            <span class="material-icons" style="font-size:16px;">edit</span>
          </button>
          <button class="btn btn-secondary btn-sm" onclick="window.CRMModule.deleteContract('${escJsAttr(customerId)}','${escJsAttr(contractId)}')" title="Delete" style="color:#ef4444;">
            <span class="material-icons" style="font-size:16px;">delete</span>
          </button>
        </div>
      </div>

      ${contract.notes ? `<div style="font-size:13px;color:var(--text-secondary);margin-bottom:16px;padding:8px;background:var(--bg-secondary);border-radius:var(--radius-sm);">${escHTML(contract.notes)}</div>` : ''}

      <!-- Line Items -->
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
        <div style="font-weight:600;color:var(--text-primary);">Line Items (${lineItems.length})</div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;justify-content:flex-end;">
          <button class="btn btn-secondary btn-sm" title="Update this price book from a rate sheet" onclick="window.RateSheetIntake && window.RateSheetIntake.open({ surface: 'price_book', customerId: '${escJsAttr(customerId)}', contractId: '${escJsAttr(contractId)}' })">
            <span class="material-icons" style="font-size:16px;">request_quote</span> From rate sheet
          </button>
          <button class="btn btn-primary btn-sm" onclick="window.CRMModule.showLineItemForm('${escJsAttr(customerId)}','${escJsAttr(contractId)}')">
            <span class="material-icons" style="font-size:16px;">add</span> Add Line Item
          </button>
        </div>
      </div>

      <div id="crm-line-items-list">
        ${renderLineItems(lineItems, customerId, contractId)}
      </div>
    </div>
  `;
}

function renderLineItems(lineItems, customerId, contractId) {
  if (lineItems.length === 0) {
    return '<div style="text-align:center;color:var(--text-secondary);padding:24px;">No line items yet.</div>';
  }

  // Group by appliesTo.kind (fall back to CATEGORY_KIND_MAP for legacy data)
  const grouped = {};
  lineItems.forEach(li => {
    const kind = li.appliesTo?.kind || CATEGORY_KIND_MAP[li.category] || null;
    const groupKey = kind || 'other';
    if (!grouped[groupKey]) grouped[groupKey] = [];
    grouped[groupKey].push(li);
  });

  const kindOrder = ['person', 'truck', 'trailer', 'asset', 'other'];
  const sortedEntries = Object.entries(grouped).sort((a, b) => {
    const ai = kindOrder.indexOf(a[0]); const bi = kindOrder.indexOf(b[0]);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sortedEntries.map(([groupKey, items]) => `
    <div style="margin-bottom:12px;">
      <div style="font-weight:600;font-size:13px;color:var(--text-secondary);margin-bottom:6px;text-transform:uppercase;letter-spacing:0.5px;">${escHTML(KIND_LABELS[groupKey] || 'Other')}</div>
      ${items.map(li => {
        const inactive = li.active === false;
        return `
          <div class="card" style="padding:10px 12px;margin-bottom:4px;${inactive ? 'opacity:0.5;' : ''}display:flex;justify-content:space-between;align-items:center;">
            <div style="flex:1;">
              <div style="font-weight:500;font-size:14px;color:var(--text-primary);${inactive ? 'text-decoration:line-through;' : ''}">
                ${escHTML(li.description || 'Unnamed item')}
              </div>
              <div style="font-size:13px;color:var(--text-secondary);">
                ${(li.noCharge === true && !(Number(li.rate) || 0)) ? 'N/C' : `$${(li.rate || 0).toFixed(2)}`} / ${escapeHtml(li.rateUnit || 'Hourly')}${(!inactive && li.needsPrice === true && !(Number(li.rate) || 0)) ? ' <span style="font-size:0.7rem;padding:1px 6px;border-radius:4px;background:#FEF3C7;color:#92400E;font-weight:600;">Needs price</span>' : ''}
                ${li.quantity ? ` x ${li.quantity}` : ''}
                ${li.revenueCategory ? ` <span style="font-size:0.7rem;padding:1px 6px;border-radius:4px;background:#EFF6FF;color:#1D4ED8;font-weight:500;">${escHTML(li.revenueCategory)}</span>` : ''}
              </div>
            </div>
            <div style="display:flex;gap:4px;align-items:center;">
              <button class="btn btn-sm" style="padding:4px;" title="${inactive ? 'Activate' : 'Deactivate'}"
                onclick="window.CRMModule.toggleLineItem('${escJsAttr(customerId)}','${escJsAttr(contractId)}','${escJsAttr(li.id)}',${!inactive})">
                <span class="material-icons" style="font-size:16px;">${inactive ? 'toggle_off' : 'toggle_on'}</span>
              </button>
              <button class="btn btn-sm" style="padding:4px;" onclick="window.CRMModule.showLineItemForm('${escJsAttr(customerId)}','${escJsAttr(contractId)}','${escJsAttr(li.id)}')">
                <span class="material-icons" style="font-size:16px;">edit</span>
              </button>
            </div>
          </div>
        `;
      }).join('')}
    </div>
  `).join('');
}

// ============================================
// RATE SHEET PDF PRINT
// ============================================
async function printRateSheet(customerId, contractId) {
  const contract = CRMState.contracts.find(c => c.id === contractId);
  const customer = CRMState.selectedCustomer;
  if (!contract || !customer) return;

  // Load line items
  const basePath = `tenants/${CRMState.tenantId}/customers/${customerId}/contracts/${contractId}/lineItems`;
  let lineItems = [];
  try {
    const snap = await getDocs(query(collection(db, basePath), orderBy('category')));
    lineItems = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(li => li.active !== false);
  } catch (err) {
    console.error('[CRM] Load line items for rate sheet:', err);
    window.showToast('Failed to load line items', 'error');
    return;
  }

  if (lineItems.length === 0) {
    window.showToast('No active line items to print', 'error');
    return;
  }

  // Company info
  const tenantData = window.AppState?.tenantData || {};
  const companyName = tenantData.companyName || 'DispatchDeck';
  const companyLogo = sanitizeImageUrl(tenantData.companyLogoUrl || '');
  const companyPhone = tenantData.companyPhone || tenantData.phone || '';
  const companyEmail = tenantData.companyEmail || tenantData.email || '';
  const companyAddress = tenantData.companyAddress || tenantData.address || '';

  // Format dates
  const formatDate = (d) => {
    if (!d) return '';
    const date = d.toDate ? d.toDate() : new Date(d);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Group line items by appliesTo.kind (fall back to CATEGORY_KIND_MAP for legacy data)
  const grouped = {};
  lineItems.forEach(li => {
    const kind = li.appliesTo?.kind || CATEGORY_KIND_MAP[li.category] || null;
    const groupKey = kind || 'other';
    if (!grouped[groupKey]) grouped[groupKey] = [];
    grouped[groupKey].push(li);
  });

  const kindOrder = ['person', 'truck', 'trailer', 'asset', 'other'];
  const sortedEntries = Object.entries(grouped).sort((a, b) => {
    const ai = kindOrder.indexOf(a[0]); const bi = kindOrder.indexOf(b[0]);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  // Build category rows
  const categoryHTML = sortedEntries.map(([groupKey, items]) => `
    <tr class="category-header">
      <td colspan="4">${escHTML(KIND_LABELS[groupKey] || 'Other')}</td>
    </tr>
    ${items.map(li => `
      <tr>
        <!-- item code hidden from UI -->
        <td>${escHTML(li.description || 'Unnamed item')}</td>
        <td class="rate-col">$${(li.rate || 0).toFixed(2)}</td>
        <td class="unit-col">${escHTML(li.rateUnit || 'Hourly')}</td>
      </tr>
    `).join('')}
  `).join('');

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    window.showToast('Pop-up blocked — please allow pop-ups', 'error');
    return;
  }

  printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
<title>Rate Sheet — ${escHTML(customer.name)} — ${escHTML(contract.title)}</title>
<style>
  @page { margin: 0.6in 0.75in; size: letter; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', -apple-system, Arial, sans-serif; color: #1e293b; font-size: 13px; line-height: 1.5; }

  .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 28px; padding-bottom: 16px; border-bottom: 2px solid #3B82F6; gap: 16px; }
  .header-left { display: flex; align-items: center; gap: 14px; }
  .header-right { display: flex; align-items: center; gap: 16px; }
  .header-logo { max-height: 56px; max-width: 180px; object-fit: contain; }
  .header-company { font-size: 22px; font-weight: 700; color: #0f172a; }
  .header-contact { font-size: 11px; color: #64748b; line-height: 1.6; text-align: right; }

  .title-bar { background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%); color: #fff; padding: 14px 20px; border-radius: 6px; margin-bottom: 20px; }
  .title-bar h2 { font-size: 18px; font-weight: 700; margin-bottom: 2px; }
  .title-bar .subtitle { font-size: 12px; opacity: 0.85; }

  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px; }
  .info-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px 14px; }
  .info-label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: #94a3b8; margin-bottom: 4px; }
  .info-value { font-size: 13px; font-weight: 500; color: #1e293b; }

  table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
  th { background: #f1f5f9; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.4px; color: #475569; padding: 8px 12px; text-align: left; border-bottom: 2px solid #e2e8f0; }
  td { padding: 8px 12px; border-bottom: 1px solid #f1f5f9; font-size: 13px; }
  tr:hover td { background: #fafbfc; }
  .category-header td { background: #eef2ff; font-weight: 600; font-size: 12px; color: #3B82F6; text-transform: uppercase; letter-spacing: 0.3px; padding: 6px 12px; border-bottom: 1px solid #dbeafe; }
  .code-col { width: 80px; color: #64748b; font-size: 12px; }
  .rate-col { width: 90px; font-weight: 600; text-align: right; color: #0f172a; }
  .unit-col { width: 80px; color: #64748b; text-align: center; }

  .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-size: 11px; color: #94a3b8; }

  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .no-print { display: none; }
    tr { page-break-inside: avoid; }
  }

  .print-btn { background: #3B82F6; color: #fff; border: none; padding: 10px 20px; border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; box-shadow: 0 2px 8px rgba(59,130,246,0.3); white-space: nowrap; }
  .print-btn:hover { background: #2563eb; }
</style>
</head>
<body>
  <div class="header">
    <div class="header-left">
      ${companyLogo ? `<img src="${companyLogo}" class="header-logo" crossorigin="anonymous">` : ''}
      <div class="header-company">${escHTML(companyName)}</div>
    </div>
    <div class="header-right">
      <button class="print-btn no-print" onclick="window.print()">&#128438; Print / Save PDF</button>
      <div class="header-contact">
        ${companyPhone ? escHTML(companyPhone) + '<br>' : ''}
        ${companyEmail ? escHTML(companyEmail) + '<br>' : ''}
        ${companyAddress ? escHTML(companyAddress) : ''}
      </div>
    </div>
  </div>

  <div class="title-bar">
    <h2>Rate Sheet / Price Book</h2>
    <div class="subtitle">${escHTML(contract.title)} — ${escHTML(customer.name)}</div>
  </div>

  <div class="info-grid">
    <div class="info-box">
      <div class="info-label">Customer</div>
      <div class="info-value">${escHTML(customer.name)}</div>
      ${customer.address ? `<div style="font-size:12px;color:#64748b;margin-top:2px;">${escHTML(customer.address)}</div>` : ''}
      ${customer.phone ? `<div style="font-size:12px;color:#64748b;">${escHTML(customer.phone)}</div>` : ''}
    </div>
    <div class="info-box">
      <div class="info-label">Contract Details</div>
      <div class="info-value">${escHTML(contract.status || 'Draft')}</div>
      <div style="font-size:12px;color:#64748b;margin-top:2px;">
        ${formatDate(contract.startDate)}${contract.endDate ? ' — ' + formatDate(contract.endDate) : ''}
      </div>
      ${contract.paymentTerms ? `<div style="font-size:12px;color:#64748b;">Terms: ${escHTML(contract.paymentTerms)}</div>` : ''}
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Description</th>
        <th style="text-align:right;">Rate</th>
        <th style="text-align:center;">Per</th>
      </tr>
    </thead>
    <tbody>
      ${categoryHTML}
    </tbody>
  </table>

  ${contract.notes ? `<div style="background:#fffbeb;border:1px solid #fde68a;border-radius:6px;padding:10px 14px;margin-bottom:20px;font-size:12px;color:#92400e;"><strong>Notes:</strong> ${escHTML(contract.notes)}</div>` : ''}

  <div class="footer">
    <div>${escHTML(companyName)}</div>
    <div>Generated ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
  </div>
</body>
</html>`);
  printWindow.document.close();
}

function showLineItemForm(customerId, contractId, lineItemId) {
  // We need to find the line item if editing
  let li = null;
  if (lineItemId) {
    // Try to find in DOM data or fetch
    const listEl = document.getElementById('crm-line-items-list');
    // We'll re-fetch from Firestore
    getDoc(doc(db, `tenants/${CRMState.tenantId}/customers/${customerId}/contracts/${contractId}/lineItems`, lineItemId))
      .then(snap => {
        if (snap.exists()) {
          renderLineItemFormUI(customerId, contractId, { id: snap.id, ...snap.data() });
        }
      });
    return;
  }
  renderLineItemFormUI(customerId, contractId, null);
}

function renderLineItemFormUI(customerId, contractId, li) {
  const isEdit = !!li;
  const listEl = document.getElementById('crm-line-items-list');
  if (!listEl) return;
  // TASK-9621 S0 #3 — a line stored with an off-list unit (an old "Per bbl")
  // keeps that unit as the selected option; without this the browser falls
  // to the first option and a plain Save would silently rewrite it to Hourly.
  const curUnit = String(li?.rateUnit || 'Hourly');
  const unitOptions = RATE_UNITS.includes(curUnit) ? RATE_UNITS : [curUnit, ...RATE_UNITS];

  const formHTML = `
    <div id="crm-lineitem-form" style="padding:var(--spacing-md);border:1px solid var(--border-color);border-radius:var(--radius-md);margin-bottom:12px;background:var(--bg-secondary);">
      <div style="font-weight:600;margin-bottom:8px;color:var(--text-primary);">${isEdit ? 'Edit Line Item' : 'Add Line Item'}</div>
      <input type="hidden" id="crm-li-id" value="${li?.id || ''}">
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;">Applies To</label>
          <select id="crm-li-applies-kind" class="form-control" onchange="window.CRMModule.onCrmAppliesToKindChange()">
            <option value="" ${!li?.appliesTo?.kind ? 'selected' : ''}>Any</option>
            <option value="person" ${li?.appliesTo?.kind === 'person' ? 'selected' : ''}>Person</option>
            <option value="truck" ${li?.appliesTo?.kind === 'truck' ? 'selected' : ''}>Vehicle</option>
            <option value="trailer" ${li?.appliesTo?.kind === 'trailer' ? 'selected' : ''}>Trailer</option>
            <option value="asset" ${li?.appliesTo?.kind === 'asset' ? 'selected' : ''}>Asset</option>
            <!-- TASK-9436 — without these two, saving a Load Permit / Misc item from this
                 form silently reset its Applies To to "Any" (the select had no matching
                 option), so a permit item stopped being a permit and its markup stopped applying. -->
            <option value="misc" ${li?.appliesTo?.kind === 'misc' ? 'selected' : ''}>Miscellaneous</option>
            <option value="permit" ${li?.appliesTo?.kind === 'permit' ? 'selected' : ''}>Load Permit</option>
          </select>
        </div>
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;">Type</label>
          <select id="crm-li-applies-type" class="form-control" ${!li?.appliesTo?.kind ? 'disabled' : ''}>
            <option value="">Any Type</option>
          </select>
        </div>
      </div>
      <div class="form-group" style="margin-bottom:8px;">
        <label style="font-size:13px;">Description *</label>
        <input type="text" id="crm-li-desc" class="form-control" value="${escAttr(li?.description || '')}">
      </div>
      <!-- TASK-9672 follow-up — the line's Item code (was a hidden input); rendered by the lazy chunk shared with billing.js. -->
      <div id="crm-li-itemcode-slot" class="form-group" style="margin-bottom:8px;"></div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;">
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;">Rate ($)</label>
          <input type="number" id="crm-li-rate" class="form-control" step="0.01" min="0" value="${li?.noCharge === true ? '' : (li?.rate || '')}"
            placeholder="${li?.noCharge === true ? 'No charge' : 'Needs a price'}" ${li?.noCharge === true ? 'disabled' : ''}>
        </div>
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;">Unit</label>
          <select id="crm-li-unit" class="form-control">
            ${unitOptions.map(u => `<option value="${escapeHtml(u)}" ${curUnit === u ? 'selected' : ''}>${escapeHtml(u)}</option>`).join('')}
          </select>
        </div>
        <div class="form-group" style="margin-bottom:8px;">
          <label style="font-size:13px;">Qty</label>
          <input type="number" id="crm-li-qty" class="form-control" min="0" value="${li?.quantity || ''}">
        </div>
      </div>
      <!-- TASK-9666 — deliberate $0 vs. "no price yet" (same as Billing → Price Book) -->
      <div style="margin-bottom:8px;">
        <label style="display:flex;align-items:center;gap:8px;font-size:13px;color:var(--text-primary);cursor:pointer;margin:0;">
          <input type="checkbox" id="crm-li-show-zero" ${li?.noCharge === true ? 'checked' : ''}
            onchange="(function(c){var r=document.getElementById('crm-li-rate');if(r){r.disabled=c;if(c)r.value='';r.placeholder=c?'No charge':'Needs a price';}})(this.checked)">
          Show at $0 (no charge)
        </label>
        <div style="font-size:12px;color:var(--text-secondary);margin-top:4px;">Shows on the invoice at $0.00 — the customer sees what was used, but isn't charged.</div>
      </div>
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
        <input type="checkbox" id="crm-li-field-ticket" ${li?.includeOnFieldTicket ? 'checked' : ''}>
        <label for="crm-li-field-ticket" style="font-size:13px;color:var(--text-primary);cursor:pointer;margin:0;">Include on field ticket</label>
      </div>
      <div class="form-group" style="margin-bottom:8px;">
        <label style="font-size:13px;">Revenue Category</label>
        <input type="text" id="crm-li-rev-cat" class="form-control" placeholder="e.g. Trucking, Equipment Rental, Labor" value="${escAttr(li?.revenueCategory || '')}">
      </div>
      <div style="display:flex;gap:8px;justify-content:flex-end;">
        <button class="btn btn-secondary btn-sm" onclick="window.CRMModule.viewContract('${escJsAttr(customerId)}','${escJsAttr(contractId)}')">Cancel</button>
        <button class="btn btn-primary btn-sm" onclick="window.CRMModule.saveLineItem('${escJsAttr(customerId)}','${escJsAttr(contractId)}')">Save</button>
      </div>
    </div>
  `;

  // Insert form at top of line items list
  const existing = document.getElementById('crm-lineitem-form');
  if (existing) existing.remove();
  listEl.insertAdjacentHTML('afterbegin', formHTML);
  setTimeout(() => {
    document.getElementById('crm-li-desc')?.focus();
    if (li?.appliesTo?.kind) onCrmAppliesToKindChange(li.appliesTo.type || '');
  }, 100);
  _crmIcMod().then(m => m.renderItemCodeField('crm-li-itemcode', li?.itemCode, CRMState.itemCodes, escapeHtml))
    .catch(e => console.warn('[CRM] item code picker failed to load (code kept as is):', e?.message));
}

function onCrmAppliesToKindChange(preselect) {
  const kind = document.getElementById('crm-li-applies-kind')?.value;
  const typeSelect = document.getElementById('crm-li-applies-type');
  if (!typeSelect) return;

  if (!kind || kind === 'misc' || kind === 'permit') {
    typeSelect.innerHTML = '<option value="">Any Type</option>';
    typeSelect.disabled = true;
    return;
  }

  const kindMap = { person: 'crewTypes', truck: 'truckTypes', trailer: 'trailerTypes', asset: 'assetCategories' };
  const typeList = window.getTypeList?.(kindMap[kind]) || [];
  typeSelect.innerHTML = '<option value="">Any Type</option>' +
    typeList.map(t => `<option value="${escAttr(t.slug)}">${escHTML(t.label)}</option>`).join('');
  typeSelect.disabled = false;
  if (preselect) typeSelect.value = preselect;
}

// TASK-9666 — TWIN of billing.js _priceBookRateFields (keep identical;
// tests/task9666-price-book-zero-states.unit.cjs runs both). Blank/0 without the
// "Show at $0" box still stores 0 — what this form always wrote — but flagged.
function _crmPriceBookRateFields(rawRate, showAtZero, kind) {
  if (showAtZero === true) return { rate: 0, noCharge: true, needsPrice: false };
  const s = rawRate == null ? '' : String(rawRate).trim();
  const n = s === '' ? NaN : parseFloat(s);
  if (Number.isFinite(n) && n !== 0) return { rate: n, noCharge: false, needsPrice: false };
  return { rate: 0, noCharge: false, needsPrice: kind !== 'person' };
}

// TASK-9672 follow-up — the item code picker + audit helpers: the LAZY
// views/priceBookItemCode.js, same ?v= as billing.js's import (pitfall #1).
function _crmIcMod() { return __ddChunkWrap(import('./priceBookItemCode.js?v=9672002')); }
// Same warning as billing.js: another ACTIVE line on this Price Book with the
// code — only one prices a ticket count (2128 COD Edge Case). Best-effort read.
async function _crmWarnItemCodeTwin(ic, basePath, code, selfId) {
  try {
    const snap = await getDocs(collection(db, basePath));
    ic.warnItemCodeTwin(ic.itemCodeTwin(snap.docs.map(d => ({ id: d.id, ...d.data() })), code, selfId), code, (m, t) => window.showToast(m, t));
  } catch (_) { /* warning only */ }
}

async function saveLineItem(customerId, contractId) {
  const desc = document.getElementById('crm-li-desc')?.value.trim();
  if (!desc) { window.showToast('Description is required', 'error'); return; }

  const liId = document.getElementById('crm-li-id')?.value;
  const appliesToKind = document.getElementById('crm-li-applies-kind')?.value || '';
  const appliesToType = document.getElementById('crm-li-applies-type')?.value || '';
  const includeOnFieldTicket = document.getElementById('crm-li-field-ticket')?.checked || false;
  // TASK-9666 — rate + its state (priced / Show at $0 / needs a price).
  const _rf = _crmPriceBookRateFields(
    document.getElementById('crm-li-rate')?.value,
    document.getElementById('crm-li-show-zero')?.checked === true,
    appliesToKind);

  const data = {
    category: KIND_TO_CATEGORY[appliesToKind] || 'Other',
    // TASK-9672 follow-up — visible picker; null means "keep the stored code" (resolved below).
    itemCode: (() => { const s = document.getElementById('crm-li-itemcode'); return s ? String(s.value || '').trim() : null; })(),
    description: desc,
    rate: _rf.rate,
    noCharge: _rf.noCharge,
    needsPrice: _rf.needsPrice,
    rateUnit: document.getElementById('crm-li-unit')?.value || 'Hourly',
    quantity: parseInt(document.getElementById('crm-li-qty')?.value) || 0,
    active: true,
    appliesTo: appliesToKind ? { kind: appliesToKind, type: appliesToType || null } : null,
    includeOnFieldTicket,
    revenueCategory: document.getElementById('crm-li-rev-cat')?.value.trim() || '',
    updatedAt: Timestamp.now()
  };

  let icAudit = null;
  try {
    const _ic = await _crmIcMod(); // before any write: no audit helper, no save
    const basePath = `tenants/${CRMState.tenantId}/customers/${customerId}/contracts/${contractId}/lineItems`;
    if (liId) {
      // Snapshot before-state for audit (real pricing mutation)
      let beforeSnap = null;
      try {
        const liSnap = await getDoc(doc(db, basePath, liId));
        if (liSnap.exists()) beforeSnap = liSnap.data();
      } catch {}
      // No picker on screen: keep the stored code (never blank it on a failed read).
      if (data.itemCode === null) delete data.itemCode;
      // 2128 COD folds (unreadable stored line: a CHANGE vs the code the form
      // opened with is refused, an unchanged code is not written; a change asks
      // first): the chunk's itemCodeSaveGate, shared with billing.js.
      const _g = await _ic.itemCodeSaveGate({ data, existingId: liId, storedRead: !!beforeSnap, before: beforeSnap?.itemCode,
        cachedCode: document.getElementById('crm-li-itemcode')?.dataset?.orig,
        ctx: { customerId, contractId, lineItemId: liId, description: desc, isNew: false },
        toast: (m, t) => window.showToast(m, t), confirm: (a, b, c) => window.showConfirm?.(a, b, c) });
      if (!_g) return;
      icAudit = _g.audit;
      if (icAudit && icAudit.to) await _crmWarnItemCodeTwin(_ic, basePath, data.itemCode, liId);
      try {
        const rateBefore = beforeSnap?.rate ?? null;
        const qtyBefore = beforeSnap?.quantity ?? null;
        const catBefore = beforeSnap?.category ?? null;
        window.logAudit?.('crm', 'CRM_LINE_ITEM_EDIT', {
          entityType: 'crmLineItem',
          entityId: liId,
          source: 'crm-line-item-modal',
          customerId,
          contractId,
          before: { rate: rateBefore, quantity: qtyBefore, category: catBefore, itemCode: beforeSnap?.itemCode ?? null },
          after: { rate: data.rate, quantity: data.quantity, category: data.category, itemCode: 'itemCode' in data ? data.itemCode : (beforeSnap?.itemCode ?? null) },
          redFlags: {
            itemCodeChanged: !!icAudit,
            rateChanged: rateBefore !== null && rateBefore !== data.rate,
            qtyChanged: qtyBefore !== null && qtyBefore !== data.quantity,
            categoryChanged: catBefore !== null && catBefore !== data.category
          }
        });
      } catch {}
      await updateDoc(doc(db, basePath, liId), data);
    } else {
      if (data.itemCode === null) data.itemCode = '';
      if (data.itemCode) await _crmWarnItemCodeTwin(_ic, basePath, data.itemCode, null);
      data.createdAt = Timestamp.now();
      const newRef = await addDoc(collection(db, basePath), data);
      icAudit = _ic.itemCodeAudit('', data.itemCode, { customerId, contractId, lineItemId: newRef.id, description: desc, isNew: true });
      // Update line item count on contract
      const contractRef = doc(db, `tenants/${CRMState.tenantId}/customers/${customerId}/contracts`, contractId);
      const contractSnap = await getDoc(contractRef);
      if (contractSnap.exists()) {
        const currentCount = contractSnap.data()._lineItemCount || 0;
        await updateDoc(contractRef, { _lineItemCount: currentCount + 1 });
      }
    }
    // TASK-9672 follow-up — money-governing: the item code logs old + new.
    if (icAudit) {
      try { await window.logAudit?.('billing', 'PRICE_BOOK_ITEM_CODE_CHANGE', { ...icAudit, source: 'crm-line-item-modal' }); }
      catch (auditErr) { console.warn('[CRM] Item code audit failed (non-blocking):', auditErr); }
    }
    window.showToast(liId ? 'Line item updated' : 'Line item added', 'success');
    viewContract(customerId, contractId);
  } catch (err) {
    console.error('[CRM] Save line item error:', err);
    window.showToast('Failed to save line item', 'error');
  }
}

async function toggleLineItem(customerId, contractId, lineItemId, newActive) {
  try {
    await updateDoc(
      doc(db, `tenants/${CRMState.tenantId}/customers/${customerId}/contracts/${contractId}/lineItems`, lineItemId),
      { active: newActive, updatedAt: Timestamp.now() }
    );
    window.showToast(newActive ? 'Line item activated' : 'Line item deactivated', 'success');
    viewContract(customerId, contractId);
  } catch (err) {
    console.error('[CRM] Toggle line item error:', err);
    window.showToast('Failed to update line item', 'error');
  }
}


// ============================================
// CLOSE DETAIL MODAL
// ============================================
function closeDetailModal() {
  document.getElementById('crm-detail-modal')?.classList.add('hidden');
  CRMState.detailUnsubscribers.forEach(u => u());
  CRMState.detailUnsubscribers = [];
  CRMState.selectedCustomer = null;
}

// ============================================
// ROSTER DATA SUBSCRIPTIONS (for asset selection)
// ============================================
function subscribeToRosterData() {
  // Trucks
  const trucksUnsub = onSnapshot(
    collection(db, 'tenants', CRMState.tenantId, 'trucks'),
    snap => { CRMState.trucks = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => t.active !== false); },
    err => console.error('[CRM] Trucks sub error:', err)
  );
  CRMState.unsubscribers.push(trucksUnsub);

  // Trailers
  const trailersUnsub = onSnapshot(
    collection(db, 'tenants', CRMState.tenantId, 'trailers'),
    snap => { CRMState.trailers = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => t.active !== false); },
    err => console.error('[CRM] Trailers sub error:', err)
  );
  CRMState.unsubscribers.push(trailersUnsub);

  // Assets (equipment)
  const assetsUnsub = onSnapshot(
    query(collection(db, 'tenants', CRMState.tenantId, 'assets'), where('active', '==', true)),
    snap => { CRMState.rosterAssets = snap.docs.map(d => ({ id: d.id, ...d.data() })); },
    err => console.error('[CRM] Assets sub error:', err)
  );
  CRMState.unsubscribers.push(assetsUnsub);

  // People (for bid assignments + @mentions)
  const peopleUnsub = onSnapshot(
    collection(db, 'tenants', CRMState.tenantId, 'people'),
    snap => { CRMState.people = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.active !== false); },
    err => console.error('[CRM] People sub error:', err)
  );
  CRMState.unsubscribers.push(peopleUnsub);
}

// ============================================
// OPPORTUNITIES TAB (per-customer detail modal)
// ============================================
function renderOpportunitiesTab() {
  const opps = CRMState.opportunities;
  const customerId = CRMState.selectedCustomer?.id;

  let html = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-md);">
      <div style="font-weight:600;color:var(--text-primary);">${opps.length} Opportunit${opps.length === 1 ? 'y' : 'ies'}</div>
      <button class="btn btn-primary btn-sm" onclick="window.CRMModule.showOpportunityForm('${escJsAttr(customerId)}')">
        <span class="material-icons" style="font-size:16px;">add</span> Add Opportunity
      </button>
    </div>
  `;

  if (opps.length === 0) {
    html += `
      <div style="text-align:center;padding:var(--spacing-xl);color:var(--text-secondary);">
        <span class="material-icons" style="font-size:48px;color:var(--text-secondary);opacity:0.5;">developer_board</span>
        <div style="margin-top:var(--spacing-md);font-weight:600;">No opportunities yet</div>
        <div style="font-size:0.8125rem;margin-top:4px;">Track deals and pipeline for this customer.</div>
      </div>
    `;
    return html;
  }

  opps.forEach(opp => {
    const stage = STAGE_MAP[opp.stage] || STAGE_MAP['initiating'];
    const revenue = opp.revenue ? `$${formatDollars(opp.revenue)}` : '—';
    const estClose = opp.estCloseDate?.toDate ? opp.estCloseDate.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
    const assetChips = (opp.assets || []).map(a => `<span class="crm-opp-asset-chip">${escHTML(a.assetUnit || a.assetName)}</span>`).join('');
    const updatedAgo = opp.updatedAt?.toDate ? formatRelativeTime(opp.updatedAt.toDate()) : '';

    html += `
      <div class="crm-opp-card" onclick="window.CRMModule.showOpportunityForm('${escJsAttr(customerId)}', '${escJsAttr(opp.id)}')">
        <div class="crm-opp-card-top">
          <div style="flex:1;">
            <div class="crm-opp-card-title">${escHTML(opp.title)}</div>
            <div class="crm-opp-card-meta">${revenue} &middot; Close: ${estClose}</div>
          </div>
          <div class="crm-opp-stage-badge" style="background:${stage.color}15;color:${stage.color};border:1px solid ${stage.color}30;">
            <span class="material-icons" style="font-size:14px;">${stage.icon}</span>
            ${stage.label} (${stage.probability}%)
          </div>
        </div>
        ${assetChips ? `<div class="crm-opp-asset-chips">${assetChips}</div>` : ''}
        ${opp.notes ? `<div style="font-size:0.8125rem;color:var(--text-secondary);margin-top:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escHTML(opp.notes)}</div>` : ''}
        <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:6px;">${updatedAgo}</div>
      </div>
    `;
  });

  return html;
}

// ============================================
// ENGAGEMENTS TAB (subcontractors — replaces Opps)
// ============================================
function renderEngagementsTab() {
  const opps = CRMState.opportunities;
  const customerId = CRMState.selectedCustomer?.id;

  let html = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-md);">
      <div style="font-weight:600;color:var(--text-primary);">${opps.length} Engagement${opps.length === 1 ? '' : 's'}</div>
      <button class="btn btn-primary btn-sm" onclick="window.CRMModule.showOpportunityForm('${escJsAttr(customerId)}')">
        <span class="material-icons" style="font-size:16px;">add</span> Add Engagement
      </button>
    </div>
  `;

  if (opps.length === 0) {
    html += `
      <div style="text-align:center;padding:var(--spacing-xl);color:var(--text-secondary);">
        <span class="material-icons" style="font-size:48px;color:var(--text-secondary);opacity:0.5;">engineering</span>
        <div style="margin-top:var(--spacing-md);font-weight:600;">No engagements yet</div>
        <div style="font-size:0.8125rem;margin-top:4px;">Track jobs, rates, and availability for this subcontractor.</div>
      </div>
    `;
    return html;
  }

  opps.forEach(opp => {
    const stage = STAGE_MAP[opp.stage] || STAGE_MAP['initiating'];
    const revenue = opp.revenue ? `$${formatDollars(opp.revenue)}` : '—';
    const assetChips = (opp.assets || []).map(a => `<span class="crm-opp-asset-chip">${escHTML(a.assetUnit || a.assetName)}</span>`).join('');
    const updatedAgo = opp.updatedAt?.toDate ? formatRelativeTime(opp.updatedAt.toDate()) : '';
    const dispatchDates = opp.dispatchStartDate ? `${opp.dispatchStartDate}${opp.dispatchEndDate ? ' → ' + opp.dispatchEndDate : ''}` : '';

    // Engagement-specific labels
    const statusLabels = { initiating: 'Inquiring', qualifying: 'Vetting', prospecting: 'Quoting', negotiating: 'Negotiating', closing: 'Confirming', on_hold: 'On Hold', won: 'Active', lost: 'Ended' };
    const statusLabel = statusLabels[opp.stage] || stage.label;

    html += `
      <div class="crm-opp-card" onclick="window.CRMModule.showOpportunityForm('${escJsAttr(customerId)}', '${escJsAttr(opp.id)}')" style="border-left:3px solid ${stage.color};">
        <div class="crm-opp-card-top">
          <div style="flex:1;">
            <div class="crm-opp-card-title">${escHTML(opp.title)}</div>
            <div class="crm-opp-card-meta">${revenue}${dispatchDates ? ' &middot; ' + dispatchDates : ''}</div>
          </div>
          <div class="crm-opp-stage-badge" style="background:${stage.color}15;color:${stage.color};border:1px solid ${stage.color}30;">
            <span class="material-icons" style="font-size:14px;">${stage.icon}</span>
            ${statusLabel}
          </div>
        </div>
        ${assetChips ? `<div class="crm-opp-asset-chips">${assetChips}</div>` : ''}
        ${(() => { const _pp = window.ProjectPeople.normalizeOppPeople(opp); return _pp.length ? `<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;"><span class="material-icons" style="font-size:14px;vertical-align:middle;">group</span> ${_pp.map(p => escHTML(p.personName)).join(', ')}</div>` : ''; })()}
        <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:6px;">${updatedAgo}</div>
      </div>
    `;
  });

  return html;
}

// ============================================
// ACTIVITY TAB (per-customer detail modal)
// ============================================
function renderActivityTab() {
  const activities = CRMState.activities;
  const customerId = CRMState.selectedCustomer?.id;

  // Quick-log buttons
  let html = `
    <div class="crm-activity-quicklog">
      ${ACTIVITY_TYPES.map(t => `
        <button class="crm-activity-quicklog-btn" onclick="window.CRMModule.showActivityForm('${escJsAttr(customerId)}', '${escJsAttr(t.key)}')" style="--act-color:${t.color};">
          <span class="material-icons">${t.icon}</span> ${t.label.replace('Phone ', '')}
        </button>
      `).join('')}
    </div>
  `;

  if (activities.length === 0) {
    html += `
      <div style="text-align:center;padding:var(--spacing-xl);color:var(--text-secondary);">
        <span class="material-icons" style="font-size:48px;color:var(--text-secondary);opacity:0.5;">history</span>
        <div style="margin-top:var(--spacing-md);font-weight:600;">No activities logged</div>
        <div style="font-size:0.8125rem;margin-top:4px;">Log calls, emails, meetings, and notes.</div>
      </div>
    `;
    return html;
  }

  // Timeline
  html += `<div class="crm-activity-timeline">`;
  activities.forEach((act, i) => {
    const typeInfo = ACTIVITY_TYPES.find(t => t.key === act.type) || ACTIVITY_TYPES[3];
    const timeAgo = act.createdAt?.toDate ? formatRelativeTime(act.createdAt.toDate()) : '';
    const outcomeIcons = { positive: '👍', neutral: '😐', negative: '👎' };
    const outcomeBadge = act.outcome ? `<span class="crm-activity-outcome">${outcomeIcons[act.outcome] || ''}</span>` : '';
    const oppLink = act.opportunityTitle ? `<span style="color:var(--brand-blue);font-size:0.75rem;">→ ${escHTML(act.opportunityTitle)}</span>` : '';
    const contactBadge = act.contactName ? `<span style="font-size:0.75rem;color:var(--text-secondary);">with ${escHTML(act.contactName)}</span>` : '';
    const durationStr = act.type === 'call' && act.duration ? `<span style="font-size:0.75rem;color:var(--text-secondary);">${act.duration} min</span>` : '';
    const isFollowup = act.type === 'followup' && act.nextActionDate;
    const nextDateStr = act.nextActionDate?.toDate ? act.nextActionDate.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

    html += `
      <div class="crm-activity-entry ${isFollowup ? 'followup' : ''}" onclick="this.querySelector('.crm-activity-notes')?.classList.toggle('expanded')">
        <div class="crm-activity-icon" style="background:${typeInfo.color};">
          <span class="material-icons" style="font-size:16px;color:#fff;">${typeInfo.icon}</span>
        </div>
        ${i < activities.length - 1 ? '<div class="crm-activity-line"></div>' : ''}
        <div class="crm-activity-content">
          <div class="crm-activity-subject">
            ${escHTML(act.subject)} ${outcomeBadge}
          </div>
          <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;">
            ${contactBadge} ${durationStr} ${oppLink}
            ${act.recorded && /^[A-Za-z0-9_-]{1,128}$/.test(act.callRecordId || '') && _canPlaceCalls(true) ? `<button class="btn btn-sm btn-secondary" style="padding:2px 8px;font-size:0.75rem;" onclick="event.stopPropagation();window.CRMModule.openCallRecording('${escJsAttr(act.callRecordId)}')"><span class="material-icons" style="font-size:14px;vertical-align:-2px;">play_arrow</span> Recording</button>` : ''}
          </div>
          ${isFollowup ? `<div class="crm-activity-followup-badge"><span class="material-icons" style="font-size:14px;">event</span> Follow-up: ${nextDateStr} ${act.nextAction ? '— ' + escHTML(act.nextAction) : ''}</div>` : ''}
          ${act.notes ? `<div class="crm-activity-notes">${escHTML(act.notes)}</div>` : ''}
          <div class="crm-activity-time">${timeAgo}</div>
        </div>
      </div>
    `;
  });
  html += `</div>`;

  return html;
}

// ============================================
// DOCUMENTS TAB
// ============================================
function escapeHtml(str) {
  if (!str) return '';
  return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

// HOTFIX (stored XSS) — onclick="fn('${…}')" is a JS string inside an attribute;
// escapeHtml above does not even touch ', and no HTML escaper can fix that
// position. Delegates to sharedFormat.js escJsAttr (TASK-4838), looked up at
// call time (Pitfall #1).
function escJsAttr(str) {
  return window.escJsAttr(str);
}

// HOTFIX (stored XSS) — a URL from a stored doc goes into href/src only if it is
// https (blocks javascript:, data:, protocol-relative); callers still escape it.
function _safeHttpsUrl(u) {
  const s = String(u == null ? '' : u).trim();
  return /^https:\/\//i.test(s) ? s : '';
}

// TASK-9623 — the key terms Smart Tools read off a filed contract / COI, as one
// line the server wrote at filing (documents.keyTermsSummary). Escaped.
function _docKeyTermsLine(d) {
  return d && d.keyTermsSummary ? '<div style="font-size:0.78rem;color:var(--text-primary);">Terms: ' + escapeHtml(String(d.keyTermsSummary)) + '</div>' : '';
}

function renderDocumentsTab() {
  const docs = CRMState.documents;
  const customerId = CRMState.selectedCustomer?.id;

  // Split into compliance items and regular docs
  const complianceItems = docs.filter(d => d.isCompliance);
  const regularDocs = docs.filter(d => !d.isCompliance);
  const now = new Date();

  // --- Compliance Tracking Section ---
  let html = `
    <div style="margin-bottom:20px;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
        <h4 style="margin:0;font-size:1rem;font-weight:700;display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:#F59E0B;">verified_user</span> Compliance Tracking
        </h4>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">
          <button class="btn btn-secondary btn-small" onclick="window.DocIntake.open({ surface: 'customer', customerId: '${escJsAttr(customerId || '')}' })">
            <span class="material-icons" style="font-size:14px;">note_add</span> Read a contract / COI
          </button>
          <button class="btn btn-primary btn-small" onclick="window.CRMModule.showComplianceForm()">
            <span class="material-icons" style="font-size:14px;">add</span> Add Item
          </button>
        </div>
      </div>
      <p style="color:var(--text-secondary);font-size:0.82rem;margin-bottom:10px;">
        Track COI, W-9, licenses, and other compliance documents. Items turn red when expired.
      </p>
  `;

  if (complianceItems.length === 0) {
    html += `<div style="text-align:center;padding:20px 16px;color:var(--text-secondary);background:var(--bg-secondary);border-radius:8px;border:1px dashed var(--border-light);">
      <span class="material-icons" style="font-size:28px;opacity:0.3;">shield</span>
      <p style="margin:6px 0 0;font-size:0.85rem;">No compliance items. Add a COI, insurance cert, or license to track.</p>
    </div>`;
  } else {
    html += `<div style="display:flex;flex-direction:column;gap:6px;">`;
    for (const item of complianceItems) {
      const expDate = item.expiresAt?.toDate ? item.expiresAt.toDate() : item.expiresAt ? new Date(item.expiresAt) : null;
      const isExpired = expDate && expDate < now;
      const isExpiringSoon = expDate && !isExpired && (expDate - now) < 30 * 24 * 60 * 60 * 1000; // 30 days
      const borderColor = isExpired ? '#dc2626' : isExpiringSoon ? '#F59E0B' : 'var(--border-light)';
      const bgColor = isExpired ? '#FEF2F2' : isExpiringSoon ? '#FFFBEB' : 'var(--bg-secondary)';
      const iconColor = isExpired ? '#dc2626' : isExpiringSoon ? '#F59E0B' : '#059669';
      const statusIcon = isExpired ? 'error' : isExpiringSoon ? 'warning' : 'check_circle';
      const expStr = expDate ? expDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'No expiration';
      const statusLabel = isExpired ? 'EXPIRED' : isExpiringSoon ? 'EXPIRING SOON' : 'CURRENT';
      const statusColor = isExpired ? '#dc2626' : isExpiringSoon ? '#92400E' : '#059669';

      html += `
        <div style="display:flex;align-items:center;gap:10px;padding:10px 12px;background:${bgColor};border-radius:8px;border:1.5px solid ${borderColor};">
          <span class="material-icons" style="font-size:22px;color:${iconColor};flex-shrink:0;">${statusIcon}</span>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:600;font-size:0.9rem;display:flex;align-items:center;gap:6px;">
              ${escapeHtml(item.label || 'Untitled')}
              <span style="font-size:10px;padding:1px 6px;border-radius:4px;background:${isExpired ? '#FEE2E2' : isExpiringSoon ? '#FEF3C7' : '#D1FAE5'};color:${statusColor};font-weight:700;">${statusLabel}</span>
            </div>
            ${_docKeyTermsLine(item)}
            <div style="font-size:0.78rem;color:var(--text-secondary);">Expires: ${expStr}${item.url ? ' \u00B7 <a href="' + escapeHtml(_safeHttpsUrl(item.url)) + '" target="_blank" rel="noopener" style="color:var(--brand-blue);">View File</a>' : ''}</div>
          </div>
          <button class="btn-icon" onclick="window.CRMModule.deleteCustomerDoc('${escJsAttr(item.id)}','${escJsAttr(item.storagePath || '')}')" title="Delete" style="color:#dc2626;">
            <span class="material-icons" style="font-size:18px;">close</span>
          </button>
        </div>`;
    }
    html += `</div>`;
  }
  html += `</div>`;

  // --- Regular Documents Section ---
  html += `
    <div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
        <h4 style="margin:0;font-size:1rem;font-weight:700;">Documents</h4>
        <label class="btn btn-primary btn-small" style="cursor:pointer;margin:0;">
          <span class="material-icons" style="font-size:14px;">upload_file</span> Upload
          <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.csv,.txt" style="display:none;"
            onchange="window.CRMModule.uploadCustomerDocs(this.files)">
        </label>
      </div>
  `;

  if (regularDocs.length === 0) {
    html += `<div style="text-align:center;padding:20px 16px;color:var(--text-secondary);background:var(--bg-secondary);border-radius:8px;border:1px dashed var(--border-light);">
      <span class="material-icons" style="font-size:28px;opacity:0.3;">folder_open</span>
      <p style="margin:6px 0 0;font-size:0.85rem;">No documents yet.</p>
    </div>`;
  } else {
    html += `<div style="display:flex;flex-direction:column;gap:8px;">`;
    for (const d of regularDocs) {
      const ext = (d.fileName || '').split('.').pop().toUpperCase();
      const icon = ['PDF'].includes(ext) ? 'picture_as_pdf' : ['PNG','JPG','JPEG','GIF'].includes(ext) ? 'image' : ['DOC','DOCX'].includes(ext) ? 'article' : ['XLS','XLSX','CSV'].includes(ext) ? 'table_chart' : 'description';
      const dateStr = d.uploadedAt?.toDate ? d.uploadedAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
      const label = d.label || d.fileName || 'Document';
      html += `
        <div style="display:flex;align-items:center;gap:12px;padding:10px 12px;background:var(--bg-secondary);border-radius:8px;border:1px solid var(--border-light);">
          <span class="material-icons" style="font-size:24px;color:var(--brand-blue);flex-shrink:0;">${icon}</span>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:600;font-size:0.9rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(label)}</div>
            <div style="font-size:0.78rem;color:var(--text-secondary);">${escapeHtml(ext)} \u00B7 ${dateStr}</div>
            ${_docKeyTermsLine(d)}
          </div>
          <a href="${escapeHtml(_safeHttpsUrl(d.url))}" target="_blank" rel="noopener" class="btn-icon" title="Download" style="color:var(--brand-blue);">
            <span class="material-icons">download</span>
          </a>
          <button class="btn-icon" onclick="window.CRMModule.deleteCustomerDoc('${escJsAttr(d.id)}','${escJsAttr(d.storagePath || '')}')" title="Delete" style="color:#dc2626;">
            <span class="material-icons">delete_outline</span>
          </button>
        </div>`;
    }
    html += `</div>`;
  }
  html += `</div>`;

  return html;
}

function updateComplianceBadges() {
  const vendors = CRMState.customers.filter(c => window.BillableCustomer.isVendorLikeCustomer(c)); // ALL vendor-like, folded payees included (TASK-9684)
  const hasExpired = vendors.some(c => c.complianceStatus === 'expired' || c._complianceExpired);
  const hasExpiring = vendors.some(c => c.complianceStatus === 'expiring' || c._complianceExpiringSoon);
  // Subcontractor missing a W-9 = compliance gap (red — backup-withholding risk).
  const hasMissingW9 = vendors.some(c => c.type === 'subcontractor' && !c.w9OnFile);
  const showBadge = hasExpired || hasExpiring || hasMissingW9;
  const badgeColor = (hasExpired || hasMissingW9) ? '#dc2626' : '#F59E0B';

  // Vendors tab badge
  const vendorBadge = document.getElementById('crm-vendors-compliance-badge');
  if (vendorBadge) {
    vendorBadge.style.display = showBadge ? 'inline-block' : 'none';
    vendorBadge.style.background = badgeColor;
  }

  // Sidebar CRM nav badge
  const crmNav = document.querySelector('[data-view="crm"]');
  if (crmNav) {
    let dot = crmNav.querySelector('.crm-compliance-dot');
    if (showBadge) {
      if (!dot) {
        dot = document.createElement('span');
        dot.className = 'crm-compliance-dot';
        dot.style.cssText = 'width:8px;height:8px;border-radius:50%;position:absolute;top:4px;right:4px;';
        crmNav.style.position = 'relative';
        crmNav.appendChild(dot);
      }
      dot.style.background = badgeColor;
      dot.style.display = '';
    } else if (dot) {
      dot.style.display = 'none';
    }
  }
}

// True if a compliance doc is a W-9 (explicit type flag set on add, OR legacy
// label match for docs created before the structured flag existed).
function isW9Doc(d) {
  if (!d || !d.isCompliance) return false;
  if (d.complianceType === 'w9' || d.isW9 === true) return true;
  return /w-?9/i.test(d.label || '');
}

function updateComplianceStatus(customerId, docs) {
  const cust = CRMState.customers.find(c => c.id === customerId);
  const isSub = (cust?.type) === 'subcontractor';

  // --- W-9 on-file flag (structured, derived from W-9 compliance docs) ---
  // Recompute every time docs change so a deleted W-9 clears the flag and a
  // newly-added one sets it. NOTE: don't gate this on complianceItems.length —
  // a sub with zero docs must still flip w9OnFile -> false so it flags "needed".
  const hasW9Doc = docs.some(isW9Doc);
  // TASK-550: capture the PREVIOUS persisted value BEFORE mutating local state.
  // This line used to sit after the assignment below, so prevOnFile always read
  // back the value we had just written, the !== guard never fired, and w9OnFile
  // was NEVER persisted -- every sub showed a red "W-9 needed" and the 24%
  // backup-withholding confirm cried wolf even with a W-9 on file.
  const prevOnFile = cust?.w9OnFile === true;
  if (cust) cust.w9OnFile = hasW9Doc; // local state for immediate card/badge render

  // Only persist the W-9 flag for subcontractors (the only type that flags).
  // w9CollectedAt is APPEND-ONLY: set it on first collection, NEVER overwrite,
  // NEVER null it — the IRS backup-withholding defense hinges on "was a W-9 on
  // file at time of payment", so deleting the doc must not destroy the record
  // of when it was first collected. Only w9OnFile flips back to false on delete
  // (so the card correctly re-flags "needed"). Guard the write so we don't emit
  // a setDoc on every docs snapshot — only when something actually changed.
  if (isSub) {
    const w9Update = {};
    if (hasW9Doc !== prevOnFile) w9Update.w9OnFile = hasW9Doc;
    if (hasW9Doc && !cust?.w9CollectedAt) {
      const ts = Timestamp.now();
      w9Update.w9CollectedAt = ts;
      if (cust) cust.w9CollectedAt = ts;
    }
    if (Object.keys(w9Update).length > 0) {
      _trackCustomerWrite(customerId, setDoc(doc(db, 'tenants', CRMState.tenantId, 'customers', customerId), w9Update, { merge: true }).catch(e => {
        console.warn('[CRM] W-9 flag save failed:', e.message);
        window.logError?.(e, { module: 'crm', operation: 'w9-onfile', customerId });
      }));
    }
  }

  // --- Expiry status (existing behavior) ---
  const complianceItems = docs.filter(d => d.isCompliance);
  if (complianceItems.length === 0) {
    // No compliance docs: clear any stale expiry flags, then refresh badges so
    // a no-W-9 sub still lights up via the W-9 path above.
    if (cust) { cust._complianceExpired = false; cust._complianceExpiringSoon = false; }
    updateComplianceBadges();
    return;
  }
  const now = new Date();
  const hasExpired = complianceItems.some(d => {
    const exp = d.expiresAt?.toDate ? d.expiresAt.toDate() : d.expiresAt ? new Date(d.expiresAt) : null;
    return exp && exp < now;
  });
  const hasExpiringSoon = complianceItems.some(d => {
    const exp = d.expiresAt?.toDate ? d.expiresAt.toDate() : d.expiresAt ? new Date(d.expiresAt) : null;
    return exp && exp >= now && (exp - now) < 30 * 24 * 60 * 60 * 1000;
  });
  // Update local state for immediate badge rendering
  if (cust) {
    cust._complianceExpired = hasExpired;
    cust._complianceExpiringSoon = hasExpiringSoon;
  }
  updateComplianceBadges();
  // Persist to Firestore (non-blocking) so it's available on next page load
  const status = hasExpired ? 'expired' : hasExpiringSoon ? 'expiring' : 'current';
  if (cust && cust.complianceStatus === status) return; // TASK-10030 — no no-op rewrite of the customer doc
  _trackCustomerWrite(customerId, setDoc(doc(db, 'tenants', CRMState.tenantId, 'customers', customerId), { complianceStatus: status }, { merge: true }).catch(e => {
    console.warn('[CRM] Compliance status save failed:', e.message);
    window.logError?.(e, { module: 'crm', operation: 'compliance-status', customerId });
  }));
}

function showComplianceForm() {
  const modal = document.getElementById('crm-detail-modal');
  if (!modal) return;

  // Insert inline form at top of documents tab
  const container = document.getElementById('crm-compliance-form-area');
  if (container) { container.remove(); return; } // Toggle off if already showing

  const docsTab = modal.querySelector('.crm-tab-content.active, [style*="display"]') || modal.querySelector('#crm-vendor-list')?.parentElement;
  const target = modal.querySelector('.crm-detail-tabs')?.nextElementSibling || modal.querySelector('.crm-overview-card')?.parentElement;

  // Build inline form
  const formDiv = document.createElement('div');
  formDiv.id = 'crm-compliance-form-area';
  formDiv.style.cssText = 'padding:16px;background:#FFFBEB;border:1.5px solid #F59E0B;border-radius:10px;margin-bottom:16px;';
  formDiv.innerHTML = `
    <div style="font-weight:700;font-size:0.95rem;margin-bottom:10px;display:flex;align-items:center;gap:6px;">
      <span class="material-icons" style="font-size:18px;color:#F59E0B;">add_task</span> Add Compliance Item
    </div>
    <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
      <div class="form-group" style="margin:0;">
        <label style="font-size:12px;font-weight:600;color:var(--text-secondary);">Item Name *</label>
        <input type="text" id="crm-compliance-name" class="form-control" placeholder="e.g. Certificate of Insurance" style="padding:8px 10px;font-size:14px;">
      </div>
      <div class="form-group" style="margin:0;">
        <label style="font-size:12px;font-weight:600;color:var(--text-secondary);">Expiration Date</label>
        <input type="date" id="crm-compliance-date" class="form-control" style="padding:8px 10px;font-size:14px;">
      </div>
    </div>
    <div style="margin-bottom:10px;">
      <label style="font-size:12px;font-weight:600;color:var(--text-secondary);">Attach File (optional)</label>
      <input type="file" id="crm-compliance-file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif" style="font-size:13px;margin-top:4px;">
    </div>
    <div style="margin-bottom:10px;">
      <label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-size:13px;font-weight:600;color:var(--text-primary);">
        <input type="checkbox" id="crm-compliance-is-w9">
        This is a W-9 (clears the "W-9 needed" flag for subcontractors)
      </label>
    </div>
    <div style="display:flex;gap:8px;justify-content:flex-end;">
      <button class="btn btn-secondary btn-small" onclick="document.getElementById('crm-compliance-form-area')?.remove()">Cancel</button>
      <button class="btn btn-primary btn-small" id="crm-compliance-save-btn" onclick="window.CRMModule.saveComplianceItem()">
        <span class="material-icons" style="font-size:14px;">save</span> Save
      </button>
    </div>
  `;

  // Insert before the compliance tracking section content
  const docsContent = modal.querySelector('[id$="crm-detail-body"]') || modal.querySelector('.crm-detail-tabs')?.parentElement;
  const complianceHeader = modal.querySelector('h4');
  if (complianceHeader?.parentElement) {
    complianceHeader.parentElement.insertBefore(formDiv, complianceHeader.nextElementSibling?.nextElementSibling);
  } else if (docsContent) {
    docsContent.prepend(formDiv);
  }
  document.getElementById('crm-compliance-name')?.focus();
}

async function saveComplianceItem() {
  const name = document.getElementById('crm-compliance-name')?.value?.trim();
  const dateVal = document.getElementById('crm-compliance-date')?.value;
  const fileInput = document.getElementById('crm-compliance-file');
  const file = fileInput?.files?.[0];
  // Explicit "this is a W-9" checkbox OR a label that reads like one.
  const isW9 = (document.getElementById('crm-compliance-is-w9')?.checked === true) || /w-?9/i.test(name || '');

  if (!name) {
    showToast('Item name is required', 'error');
    return;
  }

  const btn = document.getElementById('crm-compliance-save-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    const customerId = CRMState.selectedCustomer?.id;
    if (!customerId) throw new Error('No customer selected');

    const docData = {
      label: name,
      isCompliance: true,
      // Structured W-9 type — drives the w9OnFile flag (not label string-matching).
      complianceType: isW9 ? 'w9' : null,
      isW9: isW9,
      expiresAt: dateVal ? new Date(dateVal + 'T00:00:00') : null,
      uploadedAt: Timestamp.now(),
      fileName: file?.name || '',
      url: '',
      storagePath: '',
      contentType: file?.type || '',
      size: file?.size || 0
    };

    // Upload file if provided
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        showToast('File too large (max 10MB)', 'error');
        return;
      }
      const path = `tenants/${CRMState.tenantId}/customers/${customerId}/documents/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
      docData.url = await getDownloadURL(storageRef);
      docData.storagePath = path;
    }

    await addDoc(collection(db, 'tenants', CRMState.tenantId, 'customers', customerId, 'documents'), docData);
    if (isW9) {
      window.logAudit?.('crm', 'W9_COLLECTED', {
        customerId,
        customerName: CRMState.selectedCustomer?.name || '',
        label: name,
        hasFile: !!file
      });
    }
    showToast('Compliance item added', 'success');
    document.getElementById('crm-compliance-form-area')?.remove();
  } catch (err) {
    console.error('[CRM] Save compliance error:', err);
    showToast('Failed to save: ' + err.message, 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">save</span> Save'; }
  }
}

async function uploadCustomerDocs(files) {
  if (!files || files.length === 0) return;
  const customerId = CRMState.selectedCustomer?.id;
  const tenantId = CRMState.tenantId;
  if (!customerId || !tenantId) return;

  const maxSize = 10 * 1024 * 1024; // 10MB
  let uploaded = 0;

  for (const file of files) {
    if (file.size > maxSize) {
      window.showToast?.(`${file.name} exceeds 10MB limit`, 'error');
      continue;
    }

    try {
      const storagePath = `tenants/${tenantId}/customers/${customerId}/documents/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
      const url = await getDownloadURL(storageRef);

      await addDoc(collection(db, 'tenants', tenantId, 'customers', customerId, 'documents'), {
        fileName: file.name,
        label: file.name,
        url,
        storagePath,
        contentType: file.type,
        size: file.size,
        uploadedAt: Timestamp.now()
      });
      uploaded++;
    } catch (err) {
      console.error('[CRM] Upload error:', err);
      window.showToast?.(`Error uploading ${file.name}`, 'error');
    }
  }

  if (uploaded > 0) {
    window.showToast?.(`${uploaded} document${uploaded > 1 ? 's' : ''} uploaded`, 'success');
  }
}

async function deleteCustomerDoc(docId, storagePath) {
  const customerId = CRMState.selectedCustomer?.id;
  const tenantId = CRMState.tenantId;
  if (!customerId || !tenantId || !docId) return;

  const confirmed = await window.showConfirm(
    'Delete Document',
    'Are you sure you want to delete this document?',
    { confirmText: 'Delete', cancelText: 'Cancel', danger: true }
  );
  if (!confirmed) return;

  try {
    await deleteDoc(doc(db, 'tenants', tenantId, 'customers', customerId, 'documents', docId));
    if (storagePath) {
      try { await deleteObject(ref(storage, storagePath)); } catch (e) { /* file may already be gone */ }
    }
    window.showToast?.('Document deleted', 'success');
  } catch (err) {
    console.error('[CRM] Delete doc error:', err);
    window.showToast?.('Error deleting document', 'error');
  }
}

// ============================================
// QUICK ACTIONS (Dashboard)
// ============================================

function quickLogActivity(preselectedType) {
  showActivityForm(null, preselectedType);
}

function quickNewOpportunity() {
  showOpportunityForm(null);
}

// ============================================
// OPPORTUNITY FORM
// ============================================
async function showOpportunityForm(customerId, oppId) {
  const isEdit = !!oppId;
  const isQuickAction = !customerId && !isEdit;

  // Quick action (from pipeline, no customer context) — use simple modal
  if (isQuickAction) {
    showQuickOpportunityModal();
    return;
  }

  // If detail modal isn't open, open customer detail first
  const detailModal = document.getElementById('crm-detail-modal');
  if (detailModal?.classList.contains('hidden')) {
    await openCustomerDetail(customerId);
  }

  // Full-page form in detail body
  let opp = {};
  if (isEdit) {
    opp = CRMState.opportunities.find(o => o.id === oppId)
       || CRMState.allOpportunities.find(o => o.id === oppId)
       || {};
  }

  // Store editing state
  CRMState.editingOpp = { customerId, oppId, opp, isEdit };
  // TASK-4705 — one list, migrated on read from assignedPeople ∪ portal.assignees.
  CRMState.editingOppPeople = window.ProjectPeople.normalizeOppPeople(opp);
  CRMState.editingOppBidLines = (opp.bidLines || []).map(l => ({ ...l }));
  CRMState.editingOppPortal = opp.portal ? { ...opp.portal, trackerSteps: (opp.portal.trackerSteps || []).map(s => ({ ...s })), notes: (opp.portal.notes || []).map(n => ({ ...n })), ordersEnabled: opp.portal.ordersEnabled || false, orderCategories: opp.portal.orderCategories || [], requireApproval: opp.portal.requireApproval !== false, showPricing: opp.portal.showPricing || false, orderNotifications: opp.portal.orderNotifications !== false } : { enabled: false, trackerEnabled: false, trackerSteps: [], assignees: [], pin: '', notes: [], ordersEnabled: false, orderCategories: [], requireApproval: true, showPricing: false, orderNotifications: true };

  CRMState.detailTab = 'opportunity-detail';
  renderDetailBody({ force: true });

  // Load comments if editing
  if (isEdit && oppId) {
    loadOppComments(customerId, oppId);
  }

  // Populate project dropdown
  loadOppProjectDropdown(customerId, opp.projectId || '');
}

async function loadOppProjectDropdown(customerId, selectedProjectId) {
  const select = document.getElementById('crm-opp-full-project');
  if (!select) return;

  // TASK-9651 — no Projects access: no project read (the rules refuse it).
  // Keep an existing link as the selected value so saving the opportunity
  // never silently unlinks it, and lock the picker.
  if (window.canAccessProjects?.() === false) {
    select.innerHTML = selectedProjectId
      ? `<option value="${window.escAttr(selectedProjectId)}" selected>Linked project (needs Projects access to view)</option>`
      : '<option value="">No project linked</option>';
    select.disabled = true;
    select.title = 'Linking a project needs Projects access.';
    return;
  }

  try {
    const projectsRef = collection(db, 'tenants', CRMState.tenantId, 'projects');
    const q1 = query(projectsRef, where('primaryCustomerId', '==', customerId));
    const snap = await getDocs(q1);

    const projects = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(p => p.status !== 'archived');

    select.innerHTML = `<option value="">No project linked</option>` +
      projects.map(p => `<option value="${p.id}" ${p.id === selectedProjectId ? 'selected' : ''}>${escHTML(p.name)} (${p.status})</option>`).join('');

    // Show project preview if selected
    select.onchange = () => {
      const preview = document.getElementById('crm-opp-project-preview');
      if (!preview) return;
      const proj = projects.find(p => p.id === select.value);
      if (!proj) { preview.innerHTML = ''; return; }
      const startDate = proj.startDate ? new Date(proj.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
      const endDate = proj.endDate ? new Date(proj.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
      preview.innerHTML = `
        <div style="background:var(--bg-secondary);border-radius:var(--radius-sm);padding:8px 12px;font-size:12px;display:flex;gap:16px;flex-wrap:wrap;">
          <span><strong>Status:</strong> ${proj.status}</span>
          ${startDate ? `<span><strong>Timeline:</strong> ${startDate}${endDate ? ` — ${endDate}` : ''}</span>` : ''}
          ${proj.estimatedRevenue ? `<span><strong>Est. Revenue:</strong> $${Number(proj.estimatedRevenue).toLocaleString()}</span>` : ''}
        </div>`;
    };

    // Trigger preview for initially selected project
    if (selectedProjectId) select.onchange();
  } catch (err) {
    console.error('[CRM] Load project dropdown error:', err);
  }
}

function showQuickOpportunityModal() {
  // Show customer picker modal, then open full opportunity form for selected customer
  document.getElementById('crm-opp-modal-title').textContent = 'Add Opportunity';
  const form = document.getElementById('crm-opp-form');
  form.innerHTML = `
    <div style="display:flex;flex-direction:column;gap:var(--spacing-md);padding:var(--spacing-md);">
      <div>
        <label style="font-weight:600;font-size:0.875rem;display:block;margin-bottom:4px;">Select Customer *</label>
        <select id="crm-opp-customer-select" style="width:100%;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);">
          <option value="">Select a customer...</option>
          ${/* TASK-9548 — the builder drops vendors; decision 2026-10-10: no internal (Shop/Office) customers on a sales opportunity */ window.PickerGroups.customerOptionsHtml((CRMState.customers || []).filter(c => c && (c.status || 'Active') !== 'Inactive'), { nameOf: c => c.name || '', excludeInternal: true })}
        </select>
      </div>
      <button class="btn btn-primary" onclick="window.CRMModule.openFullOppFormFromPicker()" style="align-self:flex-end;">
        <span class="material-icons" style="font-size:16px;">arrow_forward</span> Continue
      </button>
    </div>
  `;
  document.getElementById('crm-opportunity-modal').classList.remove('hidden');
}

async function openFullOppFormFromPicker() {
  const customerId = document.getElementById('crm-opp-customer-select')?.value;
  if (!customerId) { window.showToast?.('Select a customer first', 'error'); return; }

  // Close modal
  document.getElementById('crm-opportunity-modal').classList.add('hidden');

  // Navigate to customer detail, then open the full opp form
  await openCustomerDetail(customerId);
  setTimeout(() => showOpportunityForm(customerId), 150);
}

// ============================================
// OPPORTUNITY DETAIL VIEW (full-page in detail body)
// ============================================
function renderOpportunityDetailView() {
  const { customerId, oppId, opp, isEdit } = CRMState.editingOpp || {};
  if (!CRMState.editingOpp) return '<div>No opportunity selected</div>';

  const currentStage = opp.stage || 'initiating';
  const stageInfo = STAGE_MAP[currentStage] || STAGE_MAP['initiating'];
  const contracts = CRMState.contracts || [];
  const selectedContactIds = (opp.contacts || []).map(c => c.contactId);
  const portal = CRMState.editingOppPortal;
  const bidLines = CRMState.editingOppBidLines;
  const bidTotal = bidLines.reduce((s, l) => s + (l.total || 0), 0);
  const gud = window.getUnitDisplay || (item => item.unit || item.name);

  let html = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-md);">
      <div style="display:flex;align-items:center;gap:8px;">
        <button class="btn btn-secondary btn-sm" onclick="window.CRMModule.closeOpportunityFullForm()" title="Back" style="padding:6px 8px;">
          <span class="material-icons" style="font-size:18px;">arrow_back</span>
        </button>
        <h3 style="margin:0;font-size:1.05rem;color:var(--text-primary);">${isEdit ? 'Edit Opportunity' : 'New Opportunity'}</h3>
      </div>
      ${isEdit ? `<button class="btn btn-secondary btn-sm" onclick="window.CRMModule.copyOpportunity()" title="Duplicate">
        <span class="material-icons" style="font-size:16px;">content_copy</span> Copy
      </button>` : ''}
    </div>

    <!-- SECTION 1: Basic Info -->
    <div class="opp-form-section">
      <div class="opp-form-section-header">
        <span class="material-icons">info</span> Basic Information
      </div>
      <div class="opp-form-section-body">
        <input type="hidden" id="crm-opp-full-id" value="${oppId || ''}">
        <input type="hidden" id="crm-opp-full-customer-id" value="${customerId || ''}">
        <div class="opp-form-grid">
          <div class="opp-form-field">
            <label>Title *</label>
            <input type="text" id="crm-opp-full-title" value="${escAttr(opp.title || '')}" placeholder="e.g. Q2 Hydrovac Contract">
          </div>
          <div class="opp-form-field">
            <label>Stage</label>
            <div style="display:flex;align-items:center;gap:8px;">
              <select id="crm-opp-full-stage" onchange="window.CRMModule.onFullStageChange()">
                ${OPPORTUNITY_STAGES.map(s => `<option value="${s.key}" ${s.key === currentStage ? 'selected' : ''}>${s.label}</option>`).join('')}
              </select>
              <span id="crm-opp-full-prob" class="crm-opp-stage-badge" style="background:${stageInfo.color}15;color:${stageInfo.color};border:1px solid ${stageInfo.color}30;white-space:nowrap;">
                <span class="material-icons" style="font-size:14px;">${stageInfo.icon}</span> ${stageInfo.probability}%
              </span>
            </div>
          </div>
        </div>
        <div class="opp-form-grid">
          <div class="opp-form-field">
            <label>Revenue ($)</label>
            <input type="number" id="crm-opp-full-revenue" value="${opp.revenue || ''}" placeholder="0" min="0">
          </div>
          <div class="opp-form-field">
            <label>Est. Close Date</label>
            <input type="date" id="crm-opp-full-close-date" value="${opp.estCloseDate?.toDate ? opp.estCloseDate.toDate().toISOString().split('T')[0] : (opp.estCloseDate || '')}">
          </div>
        </div>
        <div class="opp-form-grid">
          <div class="opp-form-field">
            <label style="display:flex;align-items:center;gap:8px;cursor:pointer;">
              <input type="checkbox" id="crm-opp-full-dispatch" ${opp.showOnDispatch ? 'checked' : ''} onchange="document.getElementById('crm-opp-full-dispatch-dates').style.display=this.checked?'flex':'none'">
              Show on Dispatch Calendar
            </label>
            <div id="crm-opp-full-dispatch-dates" style="display:${opp.showOnDispatch ? 'flex' : 'none'};gap:var(--spacing-md);margin-top:8px;">
              <div style="flex:1;">
                <label style="font-size:0.75rem;color:var(--text-secondary);">Start</label>
                <input type="date" id="crm-opp-full-dispatch-start" value="${opp.dispatchStartDate || ''}">
              </div>
              <div style="flex:1;">
                <label style="font-size:0.75rem;color:var(--text-secondary);">End</label>
                <input type="date" id="crm-opp-full-dispatch-end" value="${opp.dispatchEndDate || ''}">
              </div>
            </div>
          </div>
          <div class="opp-form-field">
            <label>On Hold Reason</label>
            <input type="text" id="crm-opp-full-hold-reason" value="${escAttr(opp.onHoldReason || '')}" placeholder="e.g. Waiting on permits">
          </div>
        </div>
        <div class="opp-form-field">
          <label>Notes</label>
          <textarea id="crm-opp-full-notes" rows="2" placeholder="Additional notes...">${escHTML(opp.notes || '')}</textarea>
        </div>
      </div>
    </div>

    <!-- SECTION 1B: Linked Project -->
    <div class="opp-form-section">
      <div class="opp-form-section-header">
        <span class="material-icons">folder_special</span> Linked Project
      </div>
      <div class="opp-form-section-body">
        <div class="opp-form-field">
          <label>Project</label>
          <select id="crm-opp-full-project" style="width:100%;">
            <option value="">No project linked</option>
          </select>
          <div id="crm-opp-project-preview" style="margin-top:8px;"></div>
        </div>
      </div>
    </div>

    <!-- SECTION 2: Bid Builder -->
    <div class="opp-form-section">
      <div class="opp-form-section-header">
        <span class="material-icons">request_quote</span> Bid Builder
        ${bidTotal > 0 ? `<span style="margin-left:auto;font-weight:700;color:var(--text-primary);font-size:1rem;">$${formatDollars(bidTotal)}</span>` : ''}
      </div>
      <div class="opp-form-section-body">
        <div style="display:flex;align-items:center;gap:var(--spacing-md);margin-bottom:var(--spacing-md);">
          <select id="crm-opp-bid-contract" onchange="window.CRMModule.onBidContractSelect()" style="flex:1;">
            <option value="">Select a contract / price book...</option>
            ${contracts.map(c => `<option value="${c.id}" ${c.id === opp.contractId ? 'selected' : ''}>${escHTML(c.title || 'Untitled')} (${c.status || 'Draft'})</option>`).join('')}
          </select>
        </div>
        <div id="crm-bid-lines-container">
          ${renderBidLinesHTML(bidLines)}
        </div>
      </div>
    </div>

    <!-- SECTION 3: Assets -->
    <div class="opp-form-section">
      <div class="opp-form-section-header">
        <span class="material-icons">local_shipping</span> Assets
      </div>
      <div class="opp-form-section-body" id="crm-opp-full-assets">
        ${renderOppAssetsHTML(opp)}
      </div>
    </div>

    <!-- SECTION 4: People & Roles -->
    <div class="opp-form-section">
      <div class="opp-form-section-header">
        <span class="material-icons">group</span> People & Roles
      </div>
      <div class="opp-form-section-body">
        ${renderOppPeopleHTML(opp)}
      </div>
    </div>

    <!-- SECTION 5: Contacts -->
    <div class="opp-form-section">
      <div class="opp-form-section-header">
        <span class="material-icons">contacts</span> Contacts Involved
      </div>
      <div class="opp-form-section-body">
        <div class="crm-opp-chips-container" id="crm-opp-full-contacts">
          ${CRMState.contacts.map(c => `
            <label class="crm-opp-chip ${selectedContactIds.includes(c.id) ? 'selected' : ''}" onclick="this.classList.toggle('selected')">
              <input type="checkbox" value="${c.id}" data-name="${escAttr(c.name)}" data-role="${escAttr(c.title || '')}" ${selectedContactIds.includes(c.id) ? 'checked' : ''} style="display:none;" onchange="this.parentElement.classList.toggle('selected', this.checked)">
              ${escHTML(c.name)}${c.title ? ` <span style="color:var(--text-secondary);font-size:0.75rem;">(${escHTML(c.title)})</span>` : ''}
            </label>
          `).join('') || '<span style="color:var(--text-secondary);font-size:0.8125rem;">No contacts — add them in the Contacts tab first</span>'}
        </div>
      </div>
    </div>

    <!-- SECTION 6: Customer Portal -->
    <div class="opp-form-section">
      <div class="opp-form-section-header">
        <span class="material-icons">language</span> Customer Portal
        <label style="margin-left:auto;display:flex;align-items:center;gap:6px;font-weight:400;font-size:0.8125rem;cursor:pointer;">
          <input type="checkbox" id="crm-opp-portal-enabled" ${portal.enabled ? 'checked' : ''} onchange="window.CRMModule.togglePortal()"> Enable Portal
        </label>
      </div>
      <div class="opp-form-section-body" id="crm-portal-body" style="display:${portal.enabled ? 'block' : 'none'};">
        ${renderPortalBuilderHTML(portal, opp)}
      </div>
    </div>

    <!-- SECTION 7: Order Management (edit only, portal orders enabled) -->
    ${isEdit && portal.ordersEnabled ? `
    <div class="opp-form-section">
      <div class="opp-form-section-header">
        <span class="material-icons">shopping_cart</span> Customer Orders
        <span id="crm-opp-orders-count" style="margin-left:auto;font-size:0.75rem;font-weight:400;color:var(--text-secondary);"></span>
      </div>
      <div class="opp-form-section-body" id="crm-opp-orders-body">
        <div style="text-align:center;padding:var(--spacing-md);color:var(--text-secondary);font-size:0.8125rem;">
          <span class="btn-spinner"></span> Loading orders...
        </div>
      </div>
    </div>
    ` : ''}

    <!-- SECTION 8: Comments (edit only) -->
    ${isEdit ? `
    <div class="opp-form-section">
      <div class="opp-form-section-header">
        <span class="material-icons">comment</span> Comments
      </div>
      <div class="opp-form-section-body" id="crm-opp-comments-body">
        ${renderOppCommentsHTML()}
      </div>
    </div>
    ` : ''}

    <!-- Sticky Save Bar -->
    <div class="opp-form-save-bar">
      <button class="btn btn-secondary" onclick="window.CRMModule.closeOpportunityFullForm()">Cancel</button>
      <button class="btn btn-primary" id="crm-save-opp-full-btn2" onclick="window.CRMModule.saveOpportunityFull()">
        <span class="material-icons" style="font-size:16px;">save</span> Save Opportunity
      </button>
    </div>
  `;

  // Auto-load bid lines if contract is pre-selected
  if (opp.contractId && bidLines.length > 0) {
    // Lines already loaded from opp data
  } else if (opp.contractId) {
    setTimeout(() => onBidContractSelect(), 100);
  }

  return html;
}

// ============================================
// BID BUILDER FUNCTIONS
// ============================================
function renderBidLinesHTML(bidLines) {
  if (!bidLines || bidLines.length === 0) {
    return `<div id="crm-bid-empty" style="text-align:center;color:var(--text-secondary);font-size:0.8125rem;padding:var(--spacing-md);">
      <span class="material-icons" style="font-size:32px;opacity:0.4;display:block;margin-bottom:4px;">request_quote</span>
      Select a contract above to build your bid
    </div>`;
  }

  const total = bidLines.reduce((s, l) => s + (l.total || 0), 0);

  let html = `<div class="opp-bid-table">
    <div class="opp-bid-header">
      <span style="flex:2;">Line Item</span>
      <span style="flex:1;text-align:center;">Rate</span>
      <span style="flex:1;text-align:center;">Qty</span>
      <span style="flex:1;text-align:right;">Total</span>
      <span style="width:32px;"></span>
    </div>`;

  bidLines.forEach((line, i) => {
    html += `
      <div class="opp-bid-line">
        <div style="flex:2;">
          <div style="font-weight:500;color:var(--text-primary);font-size:0.875rem;">${escHTML(line.lineItemName || line.description || 'Unnamed')}</div>
          <div style="font-size:0.75rem;color:var(--text-secondary);">${escHTML(line.category || '')}</div>
        </div>
        <div style="flex:1;text-align:center;font-size:0.875rem;color:var(--text-secondary);">
          $${(line.rate || 0).toFixed(2)}/${line.rateType || 'hr'}
        </div>
        <div style="flex:1;text-align:center;">
          <input type="number" min="0" step="0.5" value="${line.qty || 0}"
            onchange="window.CRMModule.updateBidLineQty(${i}, parseFloat(this.value)||0)"
            style="width:70px;padding:4px 8px;border:1px solid var(--border-light);border-radius:var(--radius-sm);text-align:center;">
        </div>
        <div style="flex:1;text-align:right;font-weight:600;color:var(--text-primary);font-size:0.875rem;">
          $${formatDollars(line.total || 0)}
        </div>
        <div style="width:32px;text-align:center;">
          <button onclick="window.CRMModule.removeBidLine(${i})" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);padding:2px;" title="Remove">
            <span class="material-icons" style="font-size:18px;">close</span>
          </button>
        </div>
      </div>
    `;
  });

  html += `
    <div class="opp-bid-total">
      <span style="font-weight:700;font-size:1rem;">Bid Total</span>
      <span style="font-weight:700;font-size:1.125rem;color:var(--text-primary);">$${formatDollars(total)}</span>
    </div>
  </div>`;

  return html;
}

async function onBidContractSelect() {
  const contractId = document.getElementById('crm-opp-bid-contract')?.value;
  const container = document.getElementById('crm-bid-lines-container');
  if (!contractId || !container) {
    if (container) container.innerHTML = renderBidLinesHTML([]);
    return;
  }

  const customerId = CRMState.editingOpp?.customerId;
  if (!customerId) return;

  container.innerHTML = '<div style="text-align:center;padding:var(--spacing-md);"><span class="btn-spinner"></span> Loading line items...</div>';

  try {
    const basePath = `tenants/${CRMState.tenantId}/customers/${customerId}/contracts/${contractId}/lineItems`;
    const snap = await getDocs(query(collection(db, basePath), orderBy('category')));
    const lineItems = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(li => li.active !== false);

    if (lineItems.length === 0) {
      container.innerHTML = '<div style="text-align:center;color:var(--text-secondary);padding:var(--spacing-md);">No active line items in this contract.</div>';
      return;
    }

    // Show line items as selectable list
    let pickHtml = `<div class="opp-bid-pick-list">
      <div style="font-size:0.8125rem;color:var(--text-secondary);margin-bottom:8px;">Select line items to add to bid:</div>`;

    lineItems.forEach(li => {
      const alreadyAdded = CRMState.editingOppBidLines.some(bl => bl.lineItemId === li.id);
      pickHtml += `
        <div class="opp-bid-pick-item ${alreadyAdded ? 'added' : ''}" onclick="${alreadyAdded ? '' : `window.CRMModule.addBidLine('${escJsAttr(li.id)}','${escJsAttr(li.description || '')}','${escJsAttr(li.appliesTo?.kind || '')}',${Number(li.rate) || 0},'${escJsAttr(li.rateUnit || 'Hourly')}')`}">
          <div style="flex:1;">
            <div style="font-weight:500;color:var(--text-primary);">${escHTML(li.description || 'Unnamed')}</div>
            <div style="font-size:0.75rem;color:var(--text-secondary);">${escapeHtml(li.appliesTo?.kind ? KIND_LABELS[li.appliesTo.kind] || li.appliesTo.kind : 'General')}</div>
          </div>
          <div style="font-size:0.875rem;color:var(--text-secondary);">$${(li.rate || 0).toFixed(2)}/${escapeHtml(li.rateUnit || 'Hourly')}</div>
          <span class="material-icons" style="font-size:18px;color:${alreadyAdded ? 'var(--text-secondary)' : 'var(--brand-blue)'};">${alreadyAdded ? 'check_circle' : 'add_circle'}</span>
        </div>
      `;
    });

    pickHtml += '</div>';

    // Show pick list + current bid lines
    container.innerHTML = pickHtml + '<div style="margin-top:var(--spacing-md);" id="crm-bid-lines-list">' + renderBidLinesHTML(CRMState.editingOppBidLines) + '</div>';
  } catch (err) {
    console.error('[CRM] Load line items for bid:', err);
    container.innerHTML = '<div style="color:var(--text-secondary);padding:var(--spacing-md);">Failed to load line items.</div>';
  }
}

function addBidLine(lineItemId, description, category, rate, rateType) {
  if (CRMState.editingOppBidLines.some(bl => bl.lineItemId === lineItemId)) return;

  const rateMap = { 'Hourly': 'hr', 'Daily': 'day', 'Monthly': 'mo' };
  CRMState.editingOppBidLines.push({
    lineItemId,
    lineItemName: description,
    category: category || '',
    rate: rate || 0,
    rateType: rateMap[rateType] || rateType || 'hr',
    qty: 1,
    total: rate || 0
  });

  // Re-render
  onBidContractSelect();
}

function removeBidLine(index) {
  CRMState.editingOppBidLines.splice(index, 1);
  onBidContractSelect();
}

function updateBidLineQty(index, qty) {
  const line = CRMState.editingOppBidLines[index];
  if (!line) return;
  line.qty = qty;
  line.total = (line.rate || 0) * qty;

  // Update total display
  const listEl = document.getElementById('crm-bid-lines-list');
  if (listEl) listEl.innerHTML = renderBidLinesHTML(CRMState.editingOppBidLines);

  // Update header total
  const headerTotal = CRMState.editingOppBidLines.reduce((s, l) => s + (l.total || 0), 0);
  const headerSpan = document.querySelector('.opp-form-section-header span[style*="margin-left:auto"]');
  // Simple approach — just update the section
}

// ============================================
// ASSETS SECTION (reuse existing pattern)
// ============================================
function renderOppAssetsHTML(opp) {
  const gud = window.getUnitDisplay || (item => item.unit || item.name);
  const trucksArr = CRMState.trucks.map(t => ({ id: t.id, type: 'truck', unit: t.unit || t.name, name: gud(t) }));
  const trailersArr = CRMState.trailers.map(t => ({ id: t.id, type: 'trailer', unit: t.unit || t.name, name: gud(t) }));
  const assetsArr = CRMState.rosterAssets.map(a => ({ id: a.id, type: 'asset', unit: a.partNumber || a.name, name: `${a.name}${a.partNumber ? ' #' + a.partNumber : ''}` }));
  const selectedAssetIds = (opp.assets || []).map(a => a.assetId);

  const renderGroup = (items, label, sectionKey) => {
    if (items.length === 0) return '';
    const checkedCount = items.filter(t => selectedAssetIds.includes(t.id)).length;
    return `
      <div class="collapsible-section ${checkedCount > 0 ? 'open' : ''}" id="crm-opp-full-section-${sectionKey}">
        <div class="collapsible-header" onclick="this.parentElement.classList.toggle('open')">
          <span class="material-icons collapsible-arrow">expand_more</span>
          <span class="collapsible-title">${label}</span>
          <span class="collapsible-count" id="crm-opp-full-count-${sectionKey}" style="display:${checkedCount ? '' : 'none'}">${checkedCount}</span>
        </div>
        <div class="collapsible-body">
          ${items.map(t => `
            <div class="checkbox-item ${selectedAssetIds.includes(t.id) ? 'checked' : ''}" onclick="window.CRMModule.toggleOppFullAsset(this, '${escJsAttr(sectionKey)}')">
              <input type="checkbox" value="${t.id}" data-type="${t.type}" data-unit="${escAttr(t.unit)}" data-name="${escAttr(t.name)}" ${selectedAssetIds.includes(t.id) ? 'checked' : ''}>
              <span class="checkbox-name">${escHTML(t.name)}</span>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  };

  return renderGroup(trucksArr, 'Vehicles', 'trucks') + renderGroup(trailersArr, 'Trailers', 'trailers') + renderGroup(assetsArr, 'Equipment', 'assets');
}

function toggleOppFullAsset(element, sectionKey) {
  const checkbox = element.querySelector('input[type="checkbox"]');
  if (checkbox) {
    checkbox.checked = !checkbox.checked;
    element.classList.toggle('checked', checkbox.checked);
    const countEl = document.getElementById(`crm-opp-full-count-${sectionKey}`);
    if (countEl) {
      const section = element.closest('.collapsible-section');
      const checked = section ? section.querySelectorAll('.checkbox-item input:checked').length : 0;
      countEl.textContent = checked;
      countEl.style.display = checked > 0 ? '' : 'none';
    }
  }
}

// ============================================
// PEOPLE & ROLES SECTION
// ============================================
function renderOppPeopleHTML(opp) {
  // TASK-4705 — ONE list (window.ProjectPeople). The renderer renders; this host
  // decides the access shape: anyone who can open this form could already edit
  // both old lists, so canEdit stays true; Firestore rules decide the save.
  const people = CRMState.editingOppPeople || window.ProjectPeople.normalizeOppPeople(opp);
  const assignedRoles = opp.assignedRoles || [];

  let html = `
    <div style="margin-bottom:var(--spacing-md);">
      <div style="font-size:0.8125rem;color:var(--text-secondary);margin-bottom:8px;">Who is on this job. Tick a person to let them check off tracker steps from their Driver PIN.</div>
      ${window.ProjectPeople.renderHTML({
        people,
        roster: CRMState.people || [],
        idPrefix: 'crm-opp-people',
        canEdit: true,
        showCapability: true,
        capabilityLabel: 'Can tick tracker steps',
        // COD r1 (Domain/Adversary): say plainly when the tick grants nothing right now.
        capabilityHint: CRMState.editingOppPortal?.enabled
          ? 'Works while the customer portal is on and has a progress tracker.'
          : 'The customer portal is off, so nobody can tick steps right now. Ticks are kept and work again when it is turned on.',
        onAdd: 'window.CRMModule.addAssignedPerson',
        onRemove: 'window.CRMModule.removeAssignedPerson',
        onToggle: 'window.CRMModule.toggleOppPersonSteps',
        emptyText: 'Nobody on this job yet.',
      })}
      <div id="crm-opp-assigned-roles" style="margin-top:8px;">
  `;

  assignedRoles.forEach((ar, i) => {
    html += `
      <div class="opp-person-row" style="border-left-color:#F59E0B;">
        <span class="material-icons" style="font-size:18px;color:#F59E0B;">badge</span>
        <span style="font-weight:500;flex:1;">${escHTML(ar.roleName)}</span>
        <span style="font-size:0.75rem;color:var(--text-secondary);">Role (unfilled)</span>
        <button onclick="window.CRMModule.removeAssignedRole(${i})" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);"><span class="material-icons" style="font-size:16px;">close</span></button>
      </div>
    `;
  });

  html += `</div>
    <div style="display:flex;gap:8px;margin-top:6px;">
      <input type="text" id="crm-opp-add-role" placeholder="Add a role (e.g. Operator, Swamper)" style="flex:1;min-width:0;padding:6px 10px;border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.8125rem;">
      <button class="btn btn-secondary btn-sm" onclick="window.CRMModule.addAssignedRole()">
        <span class="material-icons" style="font-size:16px;">badge</span>
      </button>
    </div>
  </div>`;

  return html;
}

function _editingOppPeople() {
  if (!CRMState.editingOppPeople) CRMState.editingOppPeople = window.ProjectPeople.normalizeOppPeople(CRMState.editingOpp?.opp);
  return CRMState.editingOppPeople;
}

function addAssignedPerson() {
  if (!CRMState.editingOpp) return;
  const row = window.ProjectPeople.readPicker('crm-opp-people');
  if (!row) return;
  if (!window.ProjectPeople.addPerson(_editingOppPeople(), row)) { window.showToast?.('Already on this job', 'error'); return; }
  refreshPeopleSection();
}

function removeAssignedPerson(index) {
  const people = _editingOppPeople();
  if (!Number.isInteger(index) || index < 0 || index >= people.length) return;
  people.splice(index, 1);
  refreshPeopleSection();
}

// TASK-4705 — the capability (was the separate "Portal Assignees" list).
function toggleOppPersonSteps(index, checked) {
  const people = _editingOppPeople();
  const row = people[index];
  if (!row || !row.personId) return;
  row.canCompleteSteps = checked === true;
  refreshPeopleSection();
}

function addAssignedRole() {
  const input = document.getElementById('crm-opp-add-role');
  if (!input || !input.value.trim()) return;
  const opp = CRMState.editingOpp?.opp;
  if (!opp.assignedRoles) opp.assignedRoles = [];
  opp.assignedRoles.push({ roleName: input.value.trim() });
  input.value = '';
  refreshPeopleSection();
}

function removeAssignedRole(index) {
  const opp = CRMState.editingOpp?.opp;
  if (!opp?.assignedRoles) return;
  opp.assignedRoles.splice(index, 1);
  refreshPeopleSection();
}

function refreshPeopleSection() {
  const opp = CRMState.editingOpp?.opp;
  const container = document.getElementById('crm-opp-people-list')?.closest('.opp-form-section-body');
  if (container && opp) container.innerHTML = renderOppPeopleHTML(opp);
  refreshPortalAssignees();
}

// ============================================
// PORTAL BUILDER FUNCTIONS
// ============================================
function renderPortalBuilderHTML(portal, opp) {
  const people = CRMState.people || [];
  const steps = portal.trackerSteps || [];
  const notes = portal.notes || [];
  const pin = portal.pin || '';

  let html = `
    <div style="margin-bottom:var(--spacing-md);">
      <label style="display:flex;align-items:center;gap:8px;font-weight:600;font-size:0.8125rem;cursor:pointer;margin-bottom:8px;">
        <input type="checkbox" id="crm-portal-tracker" ${portal.trackerEnabled ? 'checked' : ''} onchange="window.CRMModule.togglePortalTracker()">
        Enable Progress Tracker
      </label>
      <div id="crm-portal-steps" style="display:${portal.trackerEnabled ? 'block' : 'none'};">
        <div style="font-size:0.8125rem;color:var(--text-secondary);margin-bottom:8px;">Define the steps customers will see:</div>
        <div id="crm-portal-steps-list">
          ${steps.map((step, i) => `
            <div class="opp-portal-step" draggable="true" data-step-idx="${i}">
              <span class="material-icons" style="cursor:grab;color:var(--text-secondary);font-size:16px;">drag_indicator</span>
              <input type="checkbox" ${step.completed ? 'checked' : ''} onchange="window.CRMModule.togglePortalStepComplete(${i})" title="Mark complete" style="width:16px;height:16px;cursor:pointer;">
              <span class="opp-portal-step-num" style="${step.completed ? 'background:var(--brand-blue);color:#fff;' : ''}">${i + 1}</span>
              <input type="text" value="${escAttr(step.name)}" onchange="window.CRMModule.updatePortalStep(${i}, this.value)" placeholder="Step name..." style="flex:1;padding:4px 8px;border:1px solid var(--border-light);border-radius:var(--radius-sm);${step.completed ? 'text-decoration:line-through;color:var(--text-secondary);' : ''}">
              ${step.completed ? `<span style="font-size:0.7rem;color:var(--text-secondary);white-space:nowrap;" title="${step.completedBy || ''}">${step.completedAt?.toDate ? step.completedAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>` : ''}
              <button onclick="window.CRMModule.removePortalStep(${i})" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);"><span class="material-icons" style="font-size:16px;">close</span></button>
            </div>
          `).join('')}
        </div>
        <button class="btn btn-secondary btn-sm" onclick="window.CRMModule.addPortalStep()" style="margin-top:8px;">
          <span class="material-icons" style="font-size:16px;">add</span> Add Step
        </button>
      </div>
    </div>

    <div style="margin-bottom:var(--spacing-md);">
      <label style="display:flex;align-items:center;gap:8px;font-weight:600;font-size:0.8125rem;cursor:pointer;margin-bottom:8px;">
        <input type="checkbox" id="crm-portal-orders" ${portal.ordersEnabled ? 'checked' : ''} onchange="window.CRMModule.togglePortalOrders()">
        Enable Orders
      </label>
      <div id="crm-portal-orders-config" style="display:${portal.ordersEnabled ? 'block' : 'none'};padding-left:26px;">
        <div style="font-size:0.8125rem;color:var(--text-secondary);margin-bottom:8px;">Customers can place orders for materials/equipment through the portal.</div>
        ${renderOrderCategoriesConfig(portal)}
        <div style="display:flex;flex-direction:column;gap:8px;margin-top:12px;">
          <label style="display:flex;align-items:center;gap:8px;font-size:0.8125rem;cursor:pointer;">
            <input type="checkbox" ${portal.requireApproval ? 'checked' : ''} onchange="window.CRMModule.toggleOrderSetting('requireApproval', this.checked)">
            Require approval before processing
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:0.8125rem;cursor:pointer;">
            <input type="checkbox" ${portal.showPricing ? 'checked' : ''} onchange="window.CRMModule.toggleOrderSetting('showPricing', this.checked)">
            Show pricing to customer
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:0.8125rem;cursor:pointer;">
            <input type="checkbox" ${portal.orderNotifications ? 'checked' : ''} onchange="window.CRMModule.toggleOrderSetting('orderNotifications', this.checked)">
            SMS notification on new orders
          </label>
        </div>
        ${renderOrderNotificationWarning()}
      </div>
    </div>

    <div style="margin-bottom:var(--spacing-md);">
      <div style="font-weight:600;font-size:0.8125rem;margin-bottom:4px;">Who can update status</div>
      <div id="crm-portal-assignees-list" style="font-size:0.8125rem;color:var(--text-secondary);">${_portalStepsSummaryHTML()}</div>
    </div>

    ${opp.portal?.url ? `
    <div style="margin-bottom:var(--spacing-md);">
      <div style="padding:8px 12px;background:var(--bg-secondary);border-radius:var(--radius-sm);display:flex;align-items:center;gap:8px;margin-bottom:8px;">
        <span class="material-icons" style="color:var(--brand-blue);">link</span>
        <code style="flex:1;font-size:0.8125rem;word-break:break-all;">${escHTML(opp.portal.url)}</code>
        <button class="btn btn-secondary btn-sm" onclick="navigator.clipboard.writeText('${escJsAttr(opp.portal.url)}');window.showToast?.('Copied!','success');">Copy</button>
      </div>
      <div style="padding:8px 12px;background:var(--bg-secondary);border-radius:var(--radius-sm);display:flex;align-items:center;gap:8px;">
        <span class="material-icons" style="color:var(--brand-blue);">pin</span>
        <span style="font-size:0.8125rem;color:var(--text-secondary);">Customer PIN:</span>
        <code style="font-size:1rem;font-weight:700;letter-spacing:4px;">${escHTML(pin)}</code>
        <button class="btn btn-secondary btn-sm" onclick="navigator.clipboard.writeText('${escJsAttr(pin)}');window.showToast?.('PIN copied!','success');" style="margin-left:auto;">Copy</button>
        <button class="btn btn-secondary btn-sm" onclick="window.CRMModule.regeneratePortalPin()" title="Regenerate PIN"><span class="material-icons" style="font-size:16px;">refresh</span></button>
      </div>
    </div>
    ` : `<div style="font-size:0.8125rem;color:var(--text-secondary);margin-bottom:var(--spacing-md);">Portal URL and customer PIN will be generated when you save.</div>`}

    <!-- Portal Notes / Feedback -->
    <div>
      <div style="font-weight:600;font-size:0.8125rem;margin-bottom:8px;">Project Feedback <span style="font-weight:400;color:var(--text-secondary);">(${notes.length})</span></div>
      <div id="crm-portal-notes-list">
        ${notes.length === 0 ? `<div style="text-align:center;color:var(--text-secondary);font-size:0.8125rem;padding:var(--spacing-sm);">No feedback yet</div>` : notes.map((n, i) => {
          const dt = n.createdAt?.toDate ? n.createdAt.toDate() : (n.createdAt ? new Date(n.createdAt) : null);
          const dateStr = dt ? dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
          const typeLabel = n.authorType === 'customer' ? 'Customer' : n.authorType === 'crew' ? 'Crew' : 'Admin';
          const typeColor = n.authorType === 'customer' ? '#10B981' : n.authorType === 'crew' ? '#3B82F6' : '#8B5CF6';
          return `
          <div class="portal-note-row" style="padding:8px;border:1px solid var(--border-light);border-radius:var(--radius-sm);margin-bottom:6px;">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
              <span style="font-size:0.7rem;font-weight:600;color:${typeColor};background:${typeColor}15;padding:1px 6px;border-radius:4px;">${typeLabel}</span>
              <span style="font-size:0.75rem;color:var(--text-secondary);flex:1;">${escHTML(n.author || 'Unknown')} · ${dateStr}</span>
              <button onclick="window.CRMModule.deletePortalNote(${i})" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);padding:0;"><span class="material-icons" style="font-size:14px;">close</span></button>
            </div>
            <div style="font-size:0.8125rem;color:var(--text-primary);line-height:1.4;">${escHTML(n.text)}</div>
          </div>`;
        }).join('')}
      </div>
      <div style="display:flex;gap:8px;margin-top:8px;">
        <input type="text" id="crm-portal-note-input" placeholder="Add a note..." maxlength="500" style="flex:1;padding:6px 10px;border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.8125rem;">
        <button class="btn btn-secondary btn-sm" onclick="window.CRMModule.addPortalNote()">
          <span class="material-icons" style="font-size:16px;">send</span>
        </button>
      </div>
    </div>
  `;

  return html;
}

function togglePortal() {
  const enabled = document.getElementById('crm-opp-portal-enabled')?.checked;
  CRMState.editingOppPortal.enabled = enabled;
  const body = document.getElementById('crm-portal-body');
  if (body) body.style.display = enabled ? 'block' : 'none';
  // Auto-generate PIN on first enable
  if (enabled && !CRMState.editingOppPortal.pin) {
    CRMState.editingOppPortal.pin = generatePortalPin();
  }
  refreshPeopleSection(); // TASK-4705 — the capability hint follows the portal switch
}

function generatePortalPin() {
  return Math.floor(1000 + Math.random() * 9000).toString();
}

function regeneratePortalPin() {
  CRMState.editingOppPortal.pin = generatePortalPin();
  window.showToast?.('New PIN generated', 'success');
  // Re-render portal builder
  const body = document.getElementById('crm-portal-body');
  if (body) {
    const opp = CRMState.editingOpp?.opp || {};
    body.innerHTML = renderPortalBuilderHTML(CRMState.editingOppPortal, opp);
  }
}

function togglePortalStepComplete(index) {
  const step = CRMState.editingOppPortal.trackerSteps[index];
  if (!step) return;
  step.completed = !step.completed;
  if (step.completed) {
    step.completedBy = window.AppState?.userEmail || 'admin';
    step.completedAt = Timestamp.now();
  } else {
    step.completedBy = null;
    step.completedAt = null;
  }
  refreshPortalSteps();
}

function addPortalNote() {
  const input = document.getElementById('crm-portal-note-input');
  if (!input || !input.value.trim()) return;
  if (!CRMState.editingOppPortal.notes) CRMState.editingOppPortal.notes = [];
  CRMState.editingOppPortal.notes.push({
    id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
    text: input.value.trim().slice(0, 500),
    author: window.AppState?.userEmail || 'Admin',
    authorType: 'admin',
    createdAt: Timestamp.now()
  });
  input.value = '';
  refreshPortalNotes();
}

function deletePortalNote(index) {
  if (!CRMState.editingOppPortal.notes) return;
  CRMState.editingOppPortal.notes.splice(index, 1);
  refreshPortalNotes();
}

function refreshPortalNotes() {
  const body = document.getElementById('crm-portal-body');
  if (body) {
    const opp = CRMState.editingOpp?.opp || {};
    body.innerHTML = renderPortalBuilderHTML(CRMState.editingOppPortal, opp);
  }
}

function togglePortalTracker() {
  const enabled = document.getElementById('crm-portal-tracker')?.checked;
  CRMState.editingOppPortal.trackerEnabled = enabled;
  const steps = document.getElementById('crm-portal-steps');
  if (steps) steps.style.display = enabled ? 'block' : 'none';
}

function renderOrderCategoriesConfig(portal) {
  const categories = window.getTypeList?.('assetCategories') || [];
  const selected = portal.orderCategories || [];
  const allSelected = selected.length === 0; // empty = all allowed
  return `
    <div style="font-size:0.8125rem;font-weight:600;margin-bottom:4px;">Allowed categories:</div>
    <div style="display:flex;flex-wrap:wrap;gap:6px;">
      <label style="display:flex;align-items:center;gap:4px;font-size:0.8rem;cursor:pointer;padding:3px 8px;border:1px solid var(--border-light);border-radius:12px;background:${allSelected ? 'var(--brand-blue)' : 'var(--bg-primary)'};color:${allSelected ? '#fff' : 'var(--text-primary)'};">
        <input type="checkbox" ${allSelected ? 'checked' : ''} onchange="window.CRMModule.toggleOrderCategory('all', this.checked)" style="display:none;">
        All
      </label>
      ${categories.map(c => {
        const isOn = allSelected || selected.includes(c.slug);
        return `<label style="display:flex;align-items:center;gap:4px;font-size:0.8rem;cursor:pointer;padding:3px 8px;border:1px solid var(--border-light);border-radius:12px;background:${isOn && !allSelected ? 'var(--brand-blue)' : 'var(--bg-primary)'};color:${isOn && !allSelected ? '#fff' : 'var(--text-primary)'};">
          <input type="checkbox" ${isOn ? 'checked' : ''} onchange="window.CRMModule.toggleOrderCategory('${escJsAttr(c.slug)}', this.checked)" style="display:none;">
          ${escHTML(c.label)}
        </label>`;
      }).join('')}
    </div>
  `;
}

function togglePortalOrders() {
  const enabled = document.getElementById('crm-portal-orders')?.checked;
  CRMState.editingOppPortal.ordersEnabled = enabled;
  const config = document.getElementById('crm-portal-orders-config');
  if (config) config.style.display = enabled ? 'block' : 'none';
}

function toggleOrderCategory(slug, checked) {
  const portal = CRMState.editingOppPortal;
  if (slug === 'all') {
    portal.orderCategories = []; // empty = all
  } else {
    // If was "all" (empty), initialize with all categories minus the unchecked one
    if (portal.orderCategories.length === 0) {
      const allCats = (window.getTypeList?.('assetCategories') || []).map(c => c.slug);
      portal.orderCategories = checked ? [] : allCats.filter(c => c !== slug);
    } else {
      if (checked && !portal.orderCategories.includes(slug)) {
        portal.orderCategories.push(slug);
      } else if (!checked) {
        portal.orderCategories = portal.orderCategories.filter(c => c !== slug);
      }
    }
  }
  refreshPortalNotes(); // re-render the whole portal body
}

function toggleOrderSetting(key, value) {
  CRMState.editingOppPortal[key] = value;
}

function renderOrderNotificationWarning() {
  const people = CRMState.people || [];
  const hasAssetRecipients = people.some(p => {
    if (!p.active) return false;
    const tags = p.notifications?.tags || p.notifyTags || [];
    return tags.includes('ALL') || tags.includes('ASSETS');
  });
  if (hasAssetRecipients) return '';
  return `
    <div style="margin-top:12px;padding:10px 12px;background:#FEF3C7;border:1px solid #F59E0B40;border-radius:var(--radius-sm);display:flex;align-items:flex-start;gap:8px;">
      <span class="material-icons" style="font-size:18px;color:#D97706;flex-shrink:0;margin-top:1px;">warning</span>
      <div style="font-size:0.8125rem;line-height:1.4;">
        <strong style="color:#92400E;">No one will receive order notifications.</strong>
        <div style="color:#78350F;margin-top:2px;">Go to <strong>Roster → Edit Person → Notifications</strong> and enable the <strong>"Orders / Assets"</strong> tag for at least one person so they get alerted when customers submit orders.</div>
      </div>
    </div>
  `;
}

// ============================================
// ORDER MANAGEMENT (CRM ADMIN)
// ============================================
async function loadOppOrders(oppId) {
  const container = document.getElementById('crm-opp-orders-body');
  const countEl = document.getElementById('crm-opp-orders-count');
  if (!container) return;

  try {
    // Load orders for this opportunity OR this customer
    const customerId = CRMState.selectedCustomer?.id;
    let orders = [];

    // Try by opportunityId first
    try {
      const snap = await getDocs(
        query(
          collection(db, 'tenants', CRMState.tenantId, 'orders'),
          where('opportunityId', '==', oppId),
          orderBy('createdAt', 'desc')
        )
      );
      orders = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (e) {
      // Index might not exist yet, fall back to customerId
      if (customerId) {
        const snap = await getDocs(
          query(
            collection(db, 'tenants', CRMState.tenantId, 'orders'),
            where('customerId', '==', customerId),
            orderBy('createdAt', 'desc')
          )
        );
        orders = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      }
    }

    if (countEl) countEl.textContent = `${orders.length} order${orders.length !== 1 ? 's' : ''}`;
    container.innerHTML = renderOppOrdersHTML(orders);
  } catch (err) {
    console.error('[CRM] Error loading orders:', err);
    container.innerHTML = '<div style="color:var(--text-secondary);font-size:0.8125rem;padding:8px;">Failed to load orders.</div>';
  }
}

function renderOppOrdersHTML(orders) {
  if (!orders || orders.length === 0) {
    return `
      <div style="text-align:center;padding:var(--spacing-lg);color:var(--text-secondary);">
        <span class="material-icons" style="font-size:36px;opacity:0.4;display:block;margin-bottom:4px;">shopping_cart</span>
        <div style="font-size:0.8125rem;">No orders yet</div>
        <div style="font-size:0.75rem;margin-top:4px;">Orders will appear here when customers submit them through the portal.</div>
      </div>
    `;
  }

  const statusColors = { pending: '#F59E0B', approved: '#3B82F6', prep: '#06B6D4', shipping: '#8B5CF6', delivered: '#10B981', cancelled: '#EF4444' };
  const statusLabels = { pending: 'Pending', approved: 'Approved', prep: 'Preparing', shipping: 'In Transit', delivered: 'Delivered', cancelled: 'Cancelled' };
  const statusOptions = ['pending', 'approved', 'prep', 'shipping', 'delivered', 'cancelled'];

  return orders.map(o => {
    const sc = statusColors[o.status] || '#94A3B8';
    const sl = statusLabels[o.status] || o.status;
    const date = o.createdAt?.toDate ? o.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
    const reqDate = o.requestedDate?.toDate ? o.requestedDate.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    const items = o.items || [];

    return `
      <div class="crm-order-card" style="border:1px solid var(--border-light);border-radius:var(--radius-sm);padding:12px;margin-bottom:8px;border-left:3px solid ${sc};">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;margin-bottom:8px;">
          <div>
            <div style="font-weight:600;font-size:0.875rem;">${items.length} item${items.length !== 1 ? 's' : ''}${o.poNumber ? ` · <span style="color:var(--text-secondary);">PO: ${escHTML(o.poNumber)}</span>` : ''}</div>
            <div style="font-size:0.75rem;color:var(--text-secondary);">Submitted ${date}${reqDate ? ` · Requested: ${reqDate}` : ''}</div>
            ${o.location ? `<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;"><span class="material-icons" style="font-size:12px;vertical-align:middle;">location_on</span> ${escHTML(o.location)}</div>` : ''}
          </div>
          <select class="control-auto" onchange="window.CRMModule.updateOrderStatus('${escJsAttr(o.id)}', this.value)" style="padding:4px 8px;border:1px solid ${sc}40;border-radius:var(--radius-sm);font-size:0.75rem;font-weight:600;color:${sc};background:${sc}10;cursor:pointer;">
            ${statusOptions.map(s => `<option value="${s}" ${o.status === s ? 'selected' : ''} style="color:#333;background:#fff;">${statusLabels[s]}</option>`).join('')}
          </select>
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:4px;">
          ${items.map(item => `
            <span style="font-size:0.75rem;padding:3px 8px;background:var(--bg-secondary);border-radius:var(--radius-sm);border:1px solid var(--border-light);">
              ${escHTML(item.partNumber || '')}${item.name ? ` - ${escHTML(item.name)}` : ''} × ${item.quantity || 1}
            </span>
          `).join('')}
        </div>
        ${o.notes ? `<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:6px;font-style:italic;">${escHTML(o.notes)}</div>` : ''}
      </div>
    `;
  }).join('');
}

async function updateOrderStatus(orderId, newStatus) {
  try {
    const orderRef = doc(db, 'tenants', CRMState.tenantId, 'orders', orderId);
    // Read current doc to append to statusHistory
    const orderSnap = await getDoc(orderRef);
    const history = orderSnap.exists() ? (orderSnap.data().statusHistory || []) : [];
    history.push({
      status: newStatus,
      timestamp: Timestamp.now(),
      updatedBy: auth.currentUser?.displayName || auth.currentUser?.email || 'admin'
    });
    await updateDoc(orderRef, {
      status: newStatus,
      updatedAt: Timestamp.now(),
      statusHistory: history
    });
    window.showToast?.(`Order updated to ${newStatus}`, 'success');
    // Reload orders
    if (CRMState.editingOpp?.id) {
      loadOppOrders(CRMState.editingOpp.id);
    }
  } catch (err) {
    console.error('[CRM] Error updating order:', err);
    window.showToast?.('Failed to update order status', 'error');
  }
}

function addPortalStep() {
  CRMState.editingOppPortal.trackerSteps.push({ name: '', completed: false });
  refreshPortalSteps();
}

function removePortalStep(index) {
  CRMState.editingOppPortal.trackerSteps.splice(index, 1);
  refreshPortalSteps();
}

function updatePortalStep(index, name) {
  if (CRMState.editingOppPortal.trackerSteps[index]) {
    CRMState.editingOppPortal.trackerSteps[index].name = name;
  }
}

function movePortalStep(index, direction) {
  const steps = CRMState.editingOppPortal.trackerSteps;
  const newIdx = index + direction;
  if (newIdx < 0 || newIdx >= steps.length) return;
  [steps[index], steps[newIdx]] = [steps[newIdx], steps[index]];
  refreshPortalSteps();
}

function refreshPortalSteps() {
  const list = document.getElementById('crm-portal-steps-list');
  if (!list) return;
  const steps = CRMState.editingOppPortal.trackerSteps;
  list.innerHTML = steps.map((step, i) => `
    <div class="opp-portal-step" data-step-idx="${i}">
      <span class="material-icons" style="cursor:grab;color:var(--text-secondary);font-size:16px;">drag_indicator</span>
      <input type="checkbox" ${step.completed ? 'checked' : ''} onchange="window.CRMModule.togglePortalStepComplete(${i})" title="Mark complete" style="width:16px;height:16px;cursor:pointer;">
      <span class="opp-portal-step-num" style="${step.completed ? 'background:var(--brand-blue);color:#fff;' : ''}">${i + 1}</span>
      <input type="text" value="${escAttr(step.name)}" onchange="window.CRMModule.updatePortalStep(${i}, this.value)" placeholder="Step name..." style="flex:1;padding:4px 8px;border:1px solid var(--border-light);border-radius:var(--radius-sm);${step.completed ? 'text-decoration:line-through;color:var(--text-secondary);' : ''}">
      ${step.completed ? `<span style="font-size:0.7rem;color:var(--text-secondary);white-space:nowrap;">${step.completedAt?.toDate ? step.completedAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>` : ''}
      <button onclick="window.CRMModule.removePortalStep(${i})" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);"><span class="material-icons" style="font-size:16px;">close</span></button>
    </div>
  `).join('');
}

// TASK-4705 — there is no second "Portal Assignees" list any more. Who may tick
// steps is the canCompleteSteps capability on the one People list; the portal
// section only summarises it. portal.assignees is DERIVED from that list on save.
function _portalStepsSummaryHTML() {
  const people = (CRMState.editingOppPeople || []).filter(p => p.canCompleteSteps);
  const names = people.map(p => escHTML(p.personName || 'Unnamed')).join(', ');
  return (names
    ? `<span style="color:var(--text-primary);">${names}</span>`
    : 'Nobody yet.')
    + ' Set this per person under <strong>People &amp; Roles</strong> (“Can tick tracker steps”).';
}

function addPortalAssignee() { refreshPortalAssignees(); }

function removePortalAssignee() { refreshPortalAssignees(); }

function refreshPortalAssignees() {
  const list = document.getElementById('crm-portal-assignees-list');
  if (list) list.innerHTML = _portalStepsSummaryHTML();
}

// ============================================
// COMMENTS SECTION
// ============================================
function renderOppCommentsHTML() {
  const comments = CRMState.oppComments || [];
  const people = CRMState.people || [];

  let html = `<div id="crm-opp-comments-list">`;

  if (comments.length === 0) {
    html += `<div style="text-align:center;color:var(--text-secondary);font-size:0.8125rem;padding:var(--spacing-md);">No comments yet</div>`;
  } else {
    comments.forEach(c => {
      const dt = c.createdAt?.toDate ? c.createdAt.toDate() : null;
      const dateStr = dt ? dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
      const timeStr = dt ? dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
      const timeAgo = dt ? formatRelativeTime(dt) : '';
      const mentionHtml = (c.text || '').replace(/@(\w[\w\s]*?\w)/g, '<span class="opp-mention">@$1</span>');
      html += `
        <div class="opp-comment">
          <div class="opp-comment-header">
            <span style="font-weight:600;color:var(--text-primary);font-size:0.8125rem;">${escHTML(c.authorName || 'Unknown')}</span>
            <span class="opp-comment-date" title="${dateStr} at ${timeStr}">${dateStr} at ${timeStr}${timeAgo ? ` (${timeAgo})` : ''}</span>
          </div>
          <div class="opp-comment-body">${mentionHtml}</div>
        </div>
      `;
    });
  }

  html += `</div>
    <div class="opp-comment-input-wrap">
      <div style="position:relative;">
        <textarea id="crm-opp-comment-text" rows="4" placeholder="Add a comment... Use @name to mention someone" style="width:100%;padding:10px 14px;border:1px solid var(--border-light);border-radius:var(--radius-sm);resize:vertical;font-size:0.875rem;min-height:80px;" oninput="window.CRMModule.onCommentInput(event)"></textarea>
        <div id="crm-mention-dropdown" class="opp-mention-dropdown hidden"></div>
      </div>
      <button class="btn btn-primary btn-sm" onclick="window.CRMModule.addOppComment()" style="margin-top:6px;">
        <span class="material-icons" style="font-size:16px;">send</span> Comment
      </button>
    </div>
  `;

  return html;
}

async function loadOppComments(customerId, oppId) {
  // Clean up previous listener
  if (CRMState.oppCommentsUnsub) { CRMState.oppCommentsUnsub(); CRMState.oppCommentsUnsub = null; }

  const commentsPath = `tenants/${CRMState.tenantId}/customers/${customerId}/opportunities/${oppId}/comments`;
  CRMState.oppCommentsUnsub = onSnapshot(
    query(collection(db, commentsPath), orderBy('createdAt', 'asc')),
    snap => {
      CRMState.oppComments = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const body = document.getElementById('crm-opp-comments-body');
      if (body) {
        const commentInput = document.getElementById('crm-opp-comment-text');
        const savedText = commentInput ? commentInput.value : '';
        const hadFocus = document.activeElement === commentInput;
        body.innerHTML = renderOppCommentsHTML();
        const newInput = document.getElementById('crm-opp-comment-text');
        if (newInput && savedText) { newInput.value = savedText; }
        if (newInput && hadFocus) { newInput.focus(); }
      }
    },
    err => console.error('[CRM] Comments error:', err)
  );
}

async function addOppComment() {
  const textarea = document.getElementById('crm-opp-comment-text');
  const text = textarea?.value?.trim();
  if (!text) return;

  const { customerId, oppId } = CRMState.editingOpp || {};
  if (!customerId || !oppId) { window.showToast?.('Save the opportunity first', 'error'); return; }

  // Parse @mentions
  const mentionRegex = /@(\w[\w\s]*?\w)/g;
  const mentions = [];
  let match;
  while ((match = mentionRegex.exec(text)) !== null) {
    const person = CRMState.people.find(p => p.name.toLowerCase() === match[1].toLowerCase());
    if (person) mentions.push(person.id);
  }

  try {
    const commentsPath = `tenants/${CRMState.tenantId}/customers/${customerId}/opportunities/${oppId}/comments`;
    await addDoc(collection(db, commentsPath), {
      text,
      authorId: window.AppState?.userId || '',
      authorName: window.AppState?.userName || window.AppState?.userEmail || 'Unknown',
      mentions,
      createdAt: Timestamp.now()
    });
    textarea.value = '';
  } catch (err) {
    console.error('[CRM] Add comment error:', err);
    window.showToast?.('Failed to add comment', 'error');
  }
}

function onCommentInput(event) {
  const textarea = event.target;
  const text = textarea.value;
  const cursorPos = textarea.selectionStart;
  const dropdown = document.getElementById('crm-mention-dropdown');
  if (!dropdown) return;

  // Find @ trigger
  const beforeCursor = text.substring(0, cursorPos);
  const atMatch = beforeCursor.match(/@(\w*)$/);

  if (atMatch) {
    const search = atMatch[1].toLowerCase();
    const matches = CRMState.people.filter(p => p.name.toLowerCase().includes(search)).slice(0, 5);

    if (matches.length > 0) {
      dropdown.innerHTML = matches.map(p => `
        <div class="opp-mention-item" onmousedown="window.CRMModule.insertMention('${escJsAttr(p.name)}')">
          <span class="material-icons" style="font-size:16px;">person</span> ${escHTML(p.name)}
        </div>
      `).join('');
      dropdown.classList.remove('hidden');
      return;
    }
  }

  dropdown.classList.add('hidden');
}

function insertMention(name) {
  const textarea = document.getElementById('crm-opp-comment-text');
  if (!textarea) return;
  const text = textarea.value;
  const cursorPos = textarea.selectionStart;
  const beforeCursor = text.substring(0, cursorPos);
  const afterCursor = text.substring(cursorPos);
  const atIdx = beforeCursor.lastIndexOf('@');

  textarea.value = beforeCursor.substring(0, atIdx) + '@' + name + ' ' + afterCursor;
  textarea.focus();
  const newPos = atIdx + name.length + 2;
  textarea.setSelectionRange(newPos, newPos);

  document.getElementById('crm-mention-dropdown')?.classList.add('hidden');
}

// ============================================
// FULL FORM STAGE CHANGE
// ============================================
function onFullStageChange() {
  const stageKey = document.getElementById('crm-opp-full-stage').value;
  const stage = STAGE_MAP[stageKey];
  if (!stage) return;
  const badge = document.getElementById('crm-opp-full-prob');
  if (badge) {
    badge.style.background = `${stage.color}15`;
    badge.style.color = stage.color;
    badge.style.borderColor = `${stage.color}30`;
    badge.innerHTML = `<span class="material-icons" style="font-size:14px;">${stage.icon}</span> ${stage.probability}%`;
  }
}

// ============================================
// SAVE FULL OPPORTUNITY
// ============================================
async function saveOpportunityFull() {
  const { customerId, oppId, isEdit } = CRMState.editingOpp || {};
  if (!customerId) { window.showToast?.('No customer', 'error'); return; }

  const title = document.getElementById('crm-opp-full-title')?.value?.trim();
  if (!title) { window.showToast?.('Title is required', 'error'); return; }

  const stageKey = document.getElementById('crm-opp-full-stage')?.value || 'initiating';
  const stage = STAGE_MAP[stageKey];
  const revenue = parseFloat(document.getElementById('crm-opp-full-revenue')?.value) || 0;
  const estCloseDateStr = document.getElementById('crm-opp-full-close-date')?.value;
  const showOnDispatch = document.getElementById('crm-opp-full-dispatch')?.checked;
  const dispatchStartDate = document.getElementById('crm-opp-full-dispatch-start')?.value || null;
  const dispatchEndDate = document.getElementById('crm-opp-full-dispatch-end')?.value || null;
  const notes = document.getElementById('crm-opp-full-notes')?.value?.trim() || '';
  const onHoldReason = document.getElementById('crm-opp-full-hold-reason')?.value?.trim() || '';
  const projectId = document.getElementById('crm-opp-full-project')?.value || null;

  // Gather selected assets
  const assets = [];
  document.querySelectorAll('#crm-opp-full-assets .checkbox-item input:checked').forEach(cb => {
    assets.push({ assetId: cb.value, assetType: cb.dataset.type, assetUnit: cb.dataset.unit, assetName: cb.dataset.name });
  });

  // Gather selected contacts
  const contacts = [];
  document.querySelectorAll('#crm-opp-full-contacts input:checked').forEach(cb => {
    contacts.push({ contactId: cb.value, contactName: cb.dataset.name, role: cb.dataset.role || '' });
  });

  // Bid data
  const contractId = document.getElementById('crm-opp-bid-contract')?.value || null;
  const bidLines = CRMState.editingOppBidLines.map(l => ({
    lineItemId: l.lineItemId,
    lineItemName: l.lineItemName,
    category: l.category,
    rate: l.rate,
    rateType: l.rateType,
    qty: l.qty,
    total: l.total
  }));
  const bidTotal = bidLines.reduce((s, l) => s + (l.total || 0), 0);

  // People & roles — TASK-4705: `people` is the one list; both legacy fields
  // are DERIVED from it (assignedPeople for old readers, portal.assignees = the
  // server's step-completion grant = people with canCompleteSteps).
  const opp = CRMState.editingOpp.opp;
  const people = window.ProjectPeople.peopleForSave(CRMState.editingOppPeople || window.ProjectPeople.normalizeOppPeople(opp));
  const _legacyPeople = window.ProjectPeople.legacyFieldsFromPeople(people);
  const assignedPeople = _legacyPeople.assignedPeople;
  const assignedRoles = opp.assignedRoles || [];

  // Portal
  const portal = CRMState.editingOppPortal;

  const btn1 = document.getElementById('crm-save-opp-full-btn');
  const btn2 = document.getElementById('crm-save-opp-full-btn2');
  [btn1, btn2].forEach(b => { if (b) { b.disabled = true; b.innerHTML = '<span class="btn-spinner"></span> Saving...'; }});

  try {
    const customerName = CRMState.selectedCustomer?.name || '';
    const data = {
      title,
      stage: stageKey,
      probability: stage.probability,
      revenue,
      assets,
      contacts,
      estCloseDate: estCloseDateStr ? Timestamp.fromDate(new Date(estCloseDateStr + 'T00:00:00')) : null,
      showOnDispatch,
      dispatchStartDate: showOnDispatch ? dispatchStartDate : null,
      dispatchEndDate: showOnDispatch ? dispatchEndDate : null,
      notes,
      onHoldReason: stageKey === 'on_hold' ? onHoldReason : null,
      customerName,
      // Bid
      contractId,
      bidLines,
      bidTotal,
      // Project
      projectId,
      // People (TASK-4705)
      people,
      assignedPeople,
      assignedRoles,
      // Portal
      portal: portal.enabled ? {
        enabled: true,
        trackerEnabled: portal.trackerEnabled,
        trackerSteps: (portal.trackerSteps || []).map(s => ({ name: s.name, completed: s.completed || false, completedBy: s.completedBy || null, completedAt: s.completedAt || null })),
        assignees: _legacyPeople.portalAssignees,
        pin: portal.pin || generatePortalPin(),
        notes: (portal.notes || []).map(n => ({ id: n.id, text: n.text, author: n.author, authorType: n.authorType, createdAt: n.createdAt })),
        ordersEnabled: portal.ordersEnabled || false,
        orderCategories: portal.orderCategories || [],
        requireApproval: portal.requireApproval !== false,
        showPricing: portal.showPricing || false,
        orderNotifications: portal.orderNotifications !== false
      } : { enabled: false },
      updatedAt: Timestamp.now()
    };

    if (stageKey === 'on_hold' && !opp.onHoldDate) {
      data.onHoldDate = Timestamp.now();
    }

    const basePath = `tenants/${CRMState.tenantId}/customers/${customerId}/opportunities`;
    let savedOppId = oppId;

    if (oppId) {
      await updateDoc(doc(db, basePath, oppId), data);
    } else {
      data.createdBy = window.AppState?.userEmail || 'unknown';
      data.createdAt = Timestamp.now();
      data._activityCount = 0;
      data._lastActivityDate = null;
      data._nextActionDate = null;
      data._nextAction = null;
      const newRef = await addDoc(collection(db, basePath), data);
      savedOppId = newRef.id;
    }

    // Generate portal URL if needed
    if (portal.enabled && !opp.portal?.url) {
      // TASK-1421 — CSPRNG-random instead of opportunityId + timestamp.
      // opportunityId is read from the token DOC (portal.token field), never
      // parsed out of the id.
      const token = window.mintAccessTokenId('portal');
      const portalUrl = `${window.location.origin}/portal.html?access=${token}`;
      await updateDoc(doc(db, basePath, savedOppId), {
        'portal.url': portalUrl,
        'portal.token': token,
        'portal.pin': portal.pin || generatePortalPin()
      });
      // Store access token
      await setDoc(doc(db, 'accessTokens', token), {
        type: 'portal',
        customerId,
        opportunityId: savedOppId,
        tenantId: CRMState.tenantId,
        createdAt: Timestamp.now()
      });
    }

    // Sync dispatch hold
    await syncOpportunityHold(customerId, savedOppId, data);

    CRMState.allOpportunitiesLoaded = false;
    window.showToast?.('Opportunity saved', 'success');

    // Clean up and go back
    closeOpportunityFullForm();
  } catch (err) {
    console.error('[CRM] Save opportunity full error:', err);
    window.showToast?.('Failed to save opportunity', 'error');
  } finally {
    [btn1, btn2].forEach(b => { if (b) { b.disabled = false; b.innerHTML = '<span class="material-icons" style="font-size:16px;">save</span> Save Opportunity'; }});
  }
}

function copyOpportunity() {
  const { customerId, opp } = CRMState.editingOpp || {};
  if (!customerId || !opp) { window.showToast?.('No opportunity to copy', 'error'); return; }

  // Build a copy — new opp (no oppId), reset stage to initiating, clear portal
  const copiedOpp = {
    ...opp,
    title: (opp.title || '') + ' (Copy)',
    stage: 'initiating',
    probability: STAGE_MAP['initiating'].probability,
    portal: { enabled: false },
    onHoldReason: null,
    onHoldDate: null,
    showOnDispatch: false,
    dispatchStartDate: null,
    dispatchEndDate: null,
    // Keep: revenue, assets, contacts, bidLines, people, assignedRoles, notes, contractId
    // TASK-4705 — read the one list off the ORIGINAL (its portal still holds the grant).
    people: window.ProjectPeople.normalizeOppPeople(opp),
  };

  // Set up as new opportunity (no oppId = create mode)
  CRMState.editingOpp = { customerId, oppId: null, opp: copiedOpp, isEdit: false };
  CRMState.editingOppPeople = window.ProjectPeople.normalizeOppPeople(copiedOpp);
  CRMState.editingOppBidLines = (opp.bidLines || []).map(l => ({ ...l }));
  CRMState.editingOppPortal = { enabled: false, trackerEnabled: false, trackerSteps: [], assignees: [], pin: '', notes: [] };

  // Re-render form in create mode with copied data
  CRMState.detailTab = 'opportunity-detail';
  renderDetailBody({ force: true });
  window.showToast?.('Opportunity copied — edit and save as new', 'info');
}

function closeOpportunityFullForm() {
  // Clean up comments listener
  if (CRMState.oppCommentsUnsub) { CRMState.oppCommentsUnsub(); CRMState.oppCommentsUnsub = null; }
  CRMState.oppComments = [];
  CRMState.editingOpp = null;
  CRMState.editingOppPeople = null; // TASK-4705
  CRMState.editingOppBidLines = [];
  CRMState.editingOppPortal = null;

  CRMState.detailTab = 'opportunities';
  renderDetailBody();
}

function toggleOppAsset(element, sectionKey) {
  const checkbox = element.querySelector('input[type="checkbox"]');
  if (checkbox) {
    checkbox.checked = !checkbox.checked;
    element.classList.toggle('checked', checkbox.checked);
    // Update count badge
    const countEl = document.getElementById(`crm-opp-count-${sectionKey}`);
    if (countEl) {
      const section = element.closest('.collapsible-section');
      const checked = section ? section.querySelectorAll('.checkbox-item input:checked').length : 0;
      countEl.textContent = checked;
      countEl.style.display = checked > 0 ? '' : 'none';
    }
  }
}

function onStageChange() {
  const stageKey = document.getElementById('crm-opp-stage').value;
  const stage = STAGE_MAP[stageKey];
  if (!stage) return;
  const badge = document.getElementById('crm-opp-prob-badge');
  badge.style.background = `${stage.color}15`;
  badge.style.color = stage.color;
  badge.style.borderColor = `${stage.color}30`;
  badge.innerHTML = `<span class="material-icons" style="font-size:14px;">${stage.icon}</span> ${stage.probability}%`;
}

async function onOppCustomerChange() {
  const custId = document.getElementById('crm-opp-customer-select')?.value;
  document.getElementById('crm-opp-customer-id').value = custId || '';

  const contactsContainer = document.getElementById('crm-opp-contacts-chips');
  if (!custId) {
    if (contactsContainer) contactsContainer.innerHTML = '<span style="color:var(--text-secondary);font-size:0.8125rem;">Select a customer first</span>';
    return;
  }

  try {
    const contactsSnap = await getDocs(query(collection(db, `tenants/${CRMState.tenantId}/customers/${custId}/contacts`), orderBy('name')));
    const contacts = contactsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    if (contactsContainer) {
      contactsContainer.innerHTML = contacts.length ? contacts.map(c => `
        <label class="crm-opp-chip" onclick="this.classList.toggle('selected')">
          <input type="checkbox" value="${c.id}" data-name="${escAttr(c.name)}" data-role="${escAttr(c.title || '')}" style="display:none;" onchange="this.parentElement.classList.toggle('selected', this.checked)">
          ${escHTML(c.name)}${c.title ? ` <span style="color:var(--text-secondary);font-size:0.75rem;">(${escHTML(c.title)})</span>` : ''}
        </label>
      `).join('') : '<span style="color:var(--text-secondary);font-size:0.8125rem;">No contacts — add them first</span>';
    }
  } catch (err) {
    console.error('[CRM] Load contacts for opp quick action:', err);
  }
}

async function saveOpportunity() {
  const customerSelect = document.getElementById('crm-opp-customer-select');
  const customerId = customerSelect ? customerSelect.value : document.getElementById('crm-opp-customer-id').value;
  if (!customerId) { window.showToast?.('Select a customer', 'error'); return; }

  const oppId = document.getElementById('crm-opp-edit-id').value;
  const title = document.getElementById('crm-opp-title').value.trim();
  if (!title) { window.showToast?.('Title is required', 'error'); return; }

  const stageKey = document.getElementById('crm-opp-stage').value;
  const stage = STAGE_MAP[stageKey];
  const revenue = parseFloat(document.getElementById('crm-opp-revenue').value) || 0;
  const estCloseDateStr = document.getElementById('crm-opp-close-date')?.value || '';
  const showOnDispatch = document.getElementById('crm-opp-dispatch-toggle')?.checked || false;
  const dispatchStartDate = document.getElementById('crm-opp-dispatch-start')?.value || null;
  const dispatchEndDate = document.getElementById('crm-opp-dispatch-end')?.value || null;
  const notes = document.getElementById('crm-opp-notes')?.value?.trim() || '';

  // Gather selected assets from collapsible sections
  const assets = [];
  document.querySelectorAll('#crm-opp-assets-section .checkbox-item input:checked').forEach(cb => {
    assets.push({ assetId: cb.value, assetType: cb.dataset.type, assetUnit: cb.dataset.unit, assetName: cb.dataset.name });
  });

  // Gather selected contacts
  const contacts = [];
  document.querySelectorAll('#crm-opp-contacts-chips input:checked').forEach(cb => {
    contacts.push({ contactId: cb.value, contactName: cb.dataset.name, role: cb.dataset.role || '' });
  });

  // Check for Won/Lost stage transition
  const isEdit = !!oppId;
  if (isEdit) {
    // renders-audit #14 — same dual fallback the open-detail path uses (:5096):
    // if the per-customer subscription hasn't landed, the per-customer array
    // misses the opp and the Won/Lost prompt (wonDate / lostReason) was
    // silently skipped on exactly the transition it exists to record.
    const existingOpp = CRMState.opportunities.find(o => o.id === oppId)
      || CRMState.allOpportunities.find(o => o.id === oppId);
    if (existingOpp && existingOpp.stage !== stageKey && (stageKey === 'won' || stageKey === 'lost')) {
      // Store pending data and show Won/Lost prompt
      CRMState._pendingOppSave = { customerId, oppId, title, stageKey, stage, revenue, estCloseDateStr, showOnDispatch, dispatchStartDate, dispatchEndDate, notes, assets, contacts };
      showWonLostPrompt(stageKey, revenue);
      return;
    }
  }

  await doSaveOpportunity(customerId, oppId, title, stageKey, stage, revenue, estCloseDateStr, showOnDispatch, dispatchStartDate, dispatchEndDate, notes, assets, contacts, null, null, null);
}

async function doSaveOpportunity(customerId, oppId, title, stageKey, stage, revenue, estCloseDateStr, showOnDispatch, dispatchStartDate, dispatchEndDate, notes, assets, contacts, wonDate, lostDate, lostReason) {
  const btn = document.getElementById('crm-save-opp-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = `<span class="btn-spinner"></span> Saving...`; }

  try {
    const customerName = CRMState.selectedCustomer?.name || CRMState.customers.find(c => c.id === customerId)?.name || '';
    const data = {
      title,
      stage: stageKey,
      probability: stage.probability,
      revenue,
      assets,
      contacts,
      estCloseDate: estCloseDateStr ? Timestamp.fromDate(new Date(estCloseDateStr + 'T00:00:00')) : null,
      showOnDispatch,
      dispatchStartDate: showOnDispatch ? dispatchStartDate : null,
      dispatchEndDate: showOnDispatch ? dispatchEndDate : null,
      notes,
      customerName,
      updatedAt: Timestamp.now()
    };

    if (wonDate) data.wonDate = Timestamp.fromDate(new Date(wonDate));
    if (lostDate) data.lostDate = Timestamp.fromDate(new Date(lostDate));
    if (lostReason) data.lostReason = lostReason;

    const basePath = `tenants/${CRMState.tenantId}/customers/${customerId}/opportunities`;

    if (oppId) {
      await updateDoc(doc(db, basePath, oppId), data);
    } else {
      data.createdBy = window.AppState?.userEmail || 'unknown';
      data.createdAt = Timestamp.now();
      data._activityCount = 0;
      data._lastActivityDate = null;
      data._nextActionDate = null;
      data._nextAction = null;
      const newRef = await addDoc(collection(db, basePath), data);
      oppId = newRef.id;
    }

    // Manage dispatch hold
    await syncOpportunityHold(customerId, oppId, data);

    CRMState.allOpportunitiesLoaded = false;
    window.showToast?.('Opportunity saved', 'success');
    closeOpportunityModal();
  } catch (err) {
    console.error('[CRM] Save opportunity error:', err);
    window.showToast?.('Failed to save opportunity', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = 'Save Opportunity'; }
  }
}

function showWonLostPrompt(stageKey, currentRevenue) {
  const isWon = stageKey === 'won';
  const form = document.getElementById('crm-wonlost-form');
  document.getElementById('crm-wonlost-title').textContent = isWon ? 'Deal Won!' : 'Deal Lost';

  form.innerHTML = `
    <div style="padding:var(--spacing-md);display:flex;flex-direction:column;gap:var(--spacing-md);">
      <input type="hidden" id="crm-wonlost-stage" value="${stageKey}">
      ${isWon ? `
        <div>
          <label style="font-weight:600;font-size:0.875rem;display:block;margin-bottom:4px;">Actual Revenue ($)</label>
          <input type="number" id="crm-wonlost-revenue" value="${currentRevenue || ''}" min="0" style="width:100%;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);">
        </div>
        <div>
          <label style="font-weight:600;font-size:0.875rem;display:block;margin-bottom:4px;">Won Date</label>
          <input type="date" id="crm-wonlost-date" value="${new Date().toISOString().split('T')[0]}" style="width:100%;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);">
        </div>
      ` : `
        <div>
          <label style="font-weight:600;font-size:0.875rem;display:block;margin-bottom:4px;">Reason</label>
          <select id="crm-wonlost-reason" style="width:100%;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);">
            ${LOST_REASONS.map(r => `<option value="${r}">${r}</option>`).join('')}
          </select>
        </div>
        <div>
          <label style="font-weight:600;font-size:0.875rem;display:block;margin-bottom:4px;">Lost Date</label>
          <input type="date" id="crm-wonlost-date" value="${new Date().toISOString().split('T')[0]}" style="width:100%;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);">
        </div>
        <div>
          <label style="font-weight:600;font-size:0.875rem;display:block;margin-bottom:4px;">Notes</label>
          <textarea id="crm-wonlost-notes" rows="2" style="width:100%;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);resize:vertical;"></textarea>
        </div>
      `}
    </div>
  `;

  document.getElementById('crm-wonlost-modal').classList.remove('hidden');
}

async function confirmWonLost() {
  const pending = CRMState._pendingOppSave;
  if (!pending) return;

  const stageKey = document.getElementById('crm-wonlost-stage').value;
  const isWon = stageKey === 'won';
  const dateVal = document.getElementById('crm-wonlost-date')?.value || new Date().toISOString().split('T')[0];

  let wonDate = null, lostDate = null, lostReason = null;
  if (isWon) {
    wonDate = dateVal;
    pending.revenue = parseFloat(document.getElementById('crm-wonlost-revenue')?.value) || pending.revenue;
  } else {
    lostDate = dateVal;
    lostReason = document.getElementById('crm-wonlost-reason')?.value || 'Other';
    pending.notes = (pending.notes ? pending.notes + '\n' : '') + (document.getElementById('crm-wonlost-notes')?.value?.trim() || '');
  }

  document.getElementById('crm-wonlost-modal').classList.add('hidden');
  await doSaveOpportunity(pending.customerId, pending.oppId, pending.title, pending.stageKey, pending.stage, pending.revenue, pending.estCloseDateStr, pending.showOnDispatch, pending.dispatchStartDate, pending.dispatchEndDate, pending.notes, pending.assets, pending.contacts, wonDate, lostDate, lostReason);
  CRMState._pendingOppSave = null;
}

function closeOpportunityModal() {
  document.getElementById('crm-opportunity-modal')?.classList.add('hidden');
}

// ============================================
// OPPORTUNITY HOLD SYNC (Dispatch Integration)
// ============================================
async function syncOpportunityHold(customerId, oppId, oppData) {
  const holdsPath = `tenants/${CRMState.tenantId}/opportunityHolds`;

  // Find existing hold for this opportunity
  const existingSnap = await getDocs(query(collection(db, holdsPath), where('opportunityId', '==', oppId)));
  const existingHold = existingSnap.docs[0];

  const shouldHaveHold = oppData.showOnDispatch && oppData.dispatchStartDate && oppData.stage !== 'won' && oppData.stage !== 'lost';

  if (shouldHaveHold) {
    const holdData = {
      opportunityId: oppId,
      customerId,
      customerName: oppData.customerName,
      opportunityTitle: oppData.title,
      stage: oppData.stage,
      probability: oppData.probability,
      revenue: oppData.revenue,
      assets: (oppData.assets || []).map(a => ({ assetId: a.assetId, assetType: a.assetType, assetUnit: a.assetUnit })),
      startDate: oppData.dispatchStartDate,
      endDate: oppData.dispatchEndDate || oppData.dispatchStartDate,
      status: 'active',
      updatedAt: Timestamp.now()
    };

    if (existingHold) {
      await updateDoc(doc(db, holdsPath, existingHold.id), holdData);
    } else {
      holdData.createdAt = Timestamp.now();
      await addDoc(collection(db, holdsPath), holdData);
    }
  } else if (existingHold) {
    // Release hold
    await updateDoc(doc(db, holdsPath, existingHold.id), { status: 'released', updatedAt: Timestamp.now() });
  }
}

// ============================================
// ACTIVITY FORM
// ============================================
function showActivityForm(customerId, preselectedType) {
  const isQuickAction = !customerId;
  document.getElementById('crm-activity-modal-title').textContent = 'Log Activity';

  // Build customer dropdown if quick-action (no pre-bound customer)
  const customerDropdownHTML = isQuickAction ? `
      <div>
        <label style="font-weight:600;font-size:0.875rem;display:block;margin-bottom:4px;">Customer *</label>
        <select id="crm-act-customer-select" onchange="window.CRMModule.onActivityCustomerChange()" style="width:100%;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);">
          <option value="">Select a customer...</option>
          ${CRMState.customers.filter(c => (c.status || 'Active') !== 'Inactive').map(c => `<option value="${c.id}">${escHTML(c.name)}</option>`).join('')}
        </select>
      </div>
  ` : '';

  const form = document.getElementById('crm-activity-form');
  form.innerHTML = `
    <div style="display:flex;flex-direction:column;gap:var(--spacing-md);padding:var(--spacing-md);">
      <input type="hidden" id="crm-act-customer-id" value="${customerId || ''}">
      ${customerDropdownHTML}

      <div>
        <label style="font-weight:600;font-size:0.875rem;display:block;margin-bottom:8px;">Type</label>
        <div class="crm-activity-type-selector">
          ${ACTIVITY_TYPES.map(t => `
            <button type="button" class="crm-activity-type-btn ${t.key === preselectedType ? 'selected' : ''}" data-type="${t.key}" onclick="window.CRMModule.selectActivityType('${escJsAttr(t.key)}')" style="--act-color:${t.color};">
              <span class="material-icons">${t.icon}</span>
              <span>${t.label.replace('Phone ', '')}</span>
            </button>
          `).join('')}
        </div>
      </div>

      <div>
        <label style="font-weight:600;font-size:0.875rem;display:block;margin-bottom:4px;">Subject *</label>
        <input type="text" id="crm-act-subject" placeholder="e.g. Discussed pricing options" style="width:100%;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);">
      </div>

      <div>
        <label style="font-weight:600;font-size:0.875rem;display:block;margin-bottom:4px;">Link to Opportunity</label>
        <select id="crm-act-opp" style="width:100%;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);">
          <option value="">None</option>
          ${isQuickAction ? '' : CRMState.opportunities.map(o => `<option value="${o.id}" data-title="${escAttr(o.title)}">${escHTML(o.title)}</option>`).join('')}
        </select>
      </div>

      <div>
        <label style="font-weight:600;font-size:0.875rem;display:block;margin-bottom:4px;">Contact</label>
        <select id="crm-act-contact" style="width:100%;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);">
          <option value="">None</option>
          ${isQuickAction ? '' : CRMState.contacts.map(c => `<option value="${c.id}" data-name="${escAttr(c.name)}">${escHTML(c.name)}</option>`).join('')}
        </select>
      </div>

      <div id="crm-act-duration-row" style="display:${preselectedType === 'call' ? 'block' : 'none'};">
        <label style="font-weight:600;font-size:0.875rem;display:block;margin-bottom:4px;">Duration (minutes)</label>
        <input type="number" id="crm-act-duration" min="0" placeholder="0" style="width:100%;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);">
      </div>

      <div>
        <label style="font-weight:600;font-size:0.875rem;display:block;margin-bottom:8px;">Outcome</label>
        <div style="display:flex;gap:8px;">
          <button type="button" class="crm-outcome-btn" data-outcome="positive" onclick="window.CRMModule.selectOutcome('positive')" style="flex:1;padding:8px;border:2px solid var(--border-light);border-radius:var(--radius-md);background:var(--bg-card);cursor:pointer;text-align:center;font-size:1.25rem;" title="Positive">👍</button>
          <button type="button" class="crm-outcome-btn" data-outcome="neutral" onclick="window.CRMModule.selectOutcome('neutral')" style="flex:1;padding:8px;border:2px solid var(--border-light);border-radius:var(--radius-md);background:var(--bg-card);cursor:pointer;text-align:center;font-size:1.25rem;" title="Neutral">😐</button>
          <button type="button" class="crm-outcome-btn" data-outcome="negative" onclick="window.CRMModule.selectOutcome('negative')" style="flex:1;padding:8px;border:2px solid var(--border-light);border-radius:var(--radius-md);background:var(--bg-card);cursor:pointer;text-align:center;font-size:1.25rem;" title="Negative">👎</button>
        </div>
      </div>

      <div>
        <label style="font-weight:600;font-size:0.875rem;display:block;margin-bottom:4px;">Next Action</label>
        <input type="text" id="crm-act-next-action" placeholder="e.g. Send proposal by Friday" style="width:100%;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);">
      </div>

      <div>
        <label style="font-weight:600;font-size:0.875rem;display:block;margin-bottom:4px;">Next Action Date</label>
        <input type="date" id="crm-act-next-date" style="width:100%;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);">
      </div>

      <div>
        <label style="font-weight:600;font-size:0.875rem;display:block;margin-bottom:4px;">Notes</label>
        <textarea id="crm-act-notes" rows="3" placeholder="Additional notes..." style="width:100%;padding:8px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);resize:vertical;"></textarea>
      </div>
    </div>
  `;

  document.getElementById('crm-activity-modal').classList.remove('hidden');
}

function selectActivityType(typeKey) {
  document.querySelectorAll('.crm-activity-type-btn').forEach(btn => {
    btn.classList.toggle('selected', btn.dataset.type === typeKey);
  });
  const durationRow = document.getElementById('crm-act-duration-row');
  if (durationRow) durationRow.style.display = typeKey === 'call' ? 'block' : 'none';
}

function selectOutcome(outcome) {
  document.querySelectorAll('.crm-outcome-btn').forEach(btn => {
    const isSelected = btn.dataset.outcome === outcome && !btn.classList.contains('selected');
    btn.classList.toggle('selected', isSelected);
    btn.style.borderColor = isSelected ? 'var(--brand-blue)' : 'var(--border-light)';
    btn.style.background = isSelected ? 'rgba(59,130,246,0.08)' : 'var(--bg-card)';
  });
}

async function onActivityCustomerChange() {
  const custId = document.getElementById('crm-act-customer-select')?.value;
  document.getElementById('crm-act-customer-id').value = custId || '';

  // Reload opportunities and contacts for selected customer
  const oppSelect = document.getElementById('crm-act-opp');
  const contactSelect = document.getElementById('crm-act-contact');
  if (!custId) {
    if (oppSelect) oppSelect.innerHTML = '<option value="">None</option>';
    if (contactSelect) contactSelect.innerHTML = '<option value="">None</option>';
    return;
  }

  try {
    const [oppsSnap, contactsSnap] = await Promise.all([
      getDocs(query(collection(db, `tenants/${CRMState.tenantId}/customers/${custId}/opportunities`), orderBy('updatedAt', 'desc'))),
      getDocs(query(collection(db, `tenants/${CRMState.tenantId}/customers/${custId}/contacts`), orderBy('name')))
    ]);
    const opps = oppsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    const contacts = contactsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

    if (oppSelect) {
      oppSelect.innerHTML = '<option value="">None</option>' + opps.map(o => `<option value="${o.id}" data-title="${escAttr(o.title)}">${escHTML(o.title)}</option>`).join('');
    }
    if (contactSelect) {
      contactSelect.innerHTML = '<option value="">None</option>' + contacts.map(c => `<option value="${c.id}" data-name="${escAttr(c.name)}">${escHTML(c.name)}</option>`).join('');
    }
  } catch (err) {
    console.error('[CRM] Load subcollections for quick action:', err);
  }
}

async function saveActivity() {
  const customerSelect = document.getElementById('crm-act-customer-select');
  const customerId = customerSelect ? customerSelect.value : document.getElementById('crm-act-customer-id').value;
  if (!customerId) { window.showToast?.('Select a customer', 'error'); return; }

  const typeBtn = document.querySelector('.crm-activity-type-btn.selected');
  const type = typeBtn?.dataset.type;
  if (!type) { window.showToast?.('Select an activity type', 'error'); return; }

  const subject = document.getElementById('crm-act-subject').value.trim();
  if (!subject) { window.showToast?.('Subject is required', 'error'); return; }

  const oppSelect = document.getElementById('crm-act-opp');
  const contactSelect = document.getElementById('crm-act-contact');
  const outcomeBtn = document.querySelector('.crm-outcome-btn.selected');
  const nextActionDate = document.getElementById('crm-act-next-date').value;

  const btn = document.getElementById('crm-save-activity-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = `<span class="btn-spinner"></span> Saving...`; }

  try {
    const data = {
      type,
      subject,
      notes: document.getElementById('crm-act-notes').value.trim(),
      opportunityId: oppSelect.value || null,
      opportunityTitle: oppSelect.value ? oppSelect.options[oppSelect.selectedIndex]?.dataset.title || null : null,
      contactId: contactSelect.value || null,
      contactName: contactSelect.value ? contactSelect.options[contactSelect.selectedIndex]?.dataset.name || null : null,
      duration: type === 'call' ? (parseInt(document.getElementById('crm-act-duration').value) || null) : null,
      outcome: outcomeBtn?.dataset.outcome || null,
      nextAction: document.getElementById('crm-act-next-action').value.trim() || null,
      nextActionDate: nextActionDate ? Timestamp.fromDate(new Date(nextActionDate + 'T00:00:00')) : null,
      createdBy: window.AppState?.userEmail || 'unknown',
      // TASK-352 Slice 3: per-rep call/activity attribution. Code Reviewer's
      // prior COD surfaced that saveActivity logged only `createdBy: email`
      // — Chris's Q3 reporting assumed per-profile attribution worked.
      // Legacy activities (pre-Slice-3) have no profileId; per-profile
      // reports should treat null as "legacy unattributed".
      profileId: CRMState.activeProfile?.id || null,
      createdAt: Timestamp.now()
    };

    const activitiesPath = `tenants/${CRMState.tenantId}/customers/${customerId}/activities`;
    await addDoc(collection(db, activitiesPath), data);

    // Update opportunity activity count + next-action rollup if linked
    if (data.opportunityId) {
      const oppPath = `tenants/${CRMState.tenantId}/customers/${customerId}/opportunities/${data.opportunityId}`;
      const oppSnap = await getDoc(doc(db, oppPath));
      if (oppSnap.exists()) {
        const count = (oppSnap.data()._activityCount || 0) + 1;
        const patch = { _activityCount: count, _lastActivityDate: Timestamp.now() };
        if (data.nextActionDate) {
          patch._nextActionDate = data.nextActionDate;
          patch._nextAction = data.nextAction || null;
        }
        await updateDoc(doc(db, oppPath), patch);
      }
    }

    window.showToast?.('Activity logged', 'success');
    closeActivityModal();
  } catch (err) {
    console.error('[CRM] Save activity error:', err);
    window.showToast?.('Failed to log activity', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = 'Log Activity'; }
  }
}

function closeActivityModal() {
  document.getElementById('crm-activity-modal')?.classList.add('hidden');
}

// ============================================
// OPPORTUNITIES KANBAN (cross-customer)
// ============================================
async function loadAllOpportunities() {
  if (CRMState.allOpportunitiesLoaded) return;

  const all = [];
  const promises = CRMState.customers.map(async (customer) => {
    const oppsPath = `tenants/${CRMState.tenantId}/customers/${customer.id}/opportunities`;
    const snap = await getDocs(query(collection(db, oppsPath), orderBy('updatedAt', 'desc')));
    snap.docs.forEach(d => {
      all.push({ id: d.id, customerId: customer.id, customerName: customer.name, ...d.data() });
    });
  });

  await Promise.all(promises);
  CRMState.allOpportunities = all;
  CRMState.allOpportunitiesLoaded = true;
}

async function renderOpportunitiesKanban() {
  const container = document.getElementById('crm-opportunities-content');
  if (!container) return;

  container.innerHTML = `<div class="loading-screen"><div class="loading-spinner"></div><p>Loading opportunities...</p></div>`;

  await loadAllOpportunities();
  // Pipeline only shows customer opportunities (not subcontractors)
  const subCustomerIds = new Set(CRMState.customers.filter(c => window.BillableCustomer.isVendorLikeCustomer(c)).map(c => c.id));
  const opps = CRMState.allOpportunities.filter(o => !subCustomerIds.has(o.customerId));

  // Kanban view toggle
  let html = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-md);">
      <div style="font-size:0.875rem;color:var(--text-secondary);">${opps.length} total opportunities</div>
      <button class="btn btn-primary btn-sm" onclick="window.CRMModule.quickNewOpportunity()">
        <span class="material-icons" style="font-size:16px;">add</span> Add Opportunity
      </button>
    </div>
  `;

  const isMobile = window.innerWidth <= 768;

  if (isMobile) {
    // MOBILE: Flat grouped list — only show stages with opportunities
    const ALL_STAGES = OPPORTUNITY_STAGES;
    const totalRevenue = opps.reduce((s, o) => s + (o.revenue || 0), 0);
    if (totalRevenue > 0) {
      html += `<div style="text-align:center;margin-bottom:12px;">
        <span style="font-size:1.25rem;font-weight:800;color:var(--text-primary);">$${formatDollars(totalRevenue)}</span>
        <span style="font-size:0.75rem;color:var(--text-secondary);margin-left:4px;">pipeline value</span>
      </div>`;
    }

    ALL_STAGES.forEach(stage => {
      const stageOpps = opps.filter(o => o.stage === stage.key);
      if (stageOpps.length === 0) return; // skip empty stages on mobile
      const stageRevenue = stageOpps.reduce((s, o) => s + (o.revenue || 0), 0);
      const stageBodyId = `crm-mob-stage-${stage.key}`;

      html += `
        <div style="margin-bottom:8px;">
          <div onclick="document.getElementById('${escJsAttr(stageBodyId)}').classList.toggle('hidden');this.querySelector('.crm-stage-chevron').textContent=document.getElementById('${escJsAttr(stageBodyId)}').classList.contains('hidden')?'expand_more':'expand_less'"
               style="display:flex;align-items:center;justify-content:space-between;padding:10px 12px;background:${stage.color}0D;border-left:3px solid ${stage.color};border-radius:0 8px 8px 0;cursor:pointer;-webkit-tap-highlight-color:transparent;">
            <div style="display:flex;align-items:center;gap:8px;">
              <span class="material-icons" style="font-size:18px;color:${stage.color};">${stage.icon}</span>
              <span style="font-weight:700;font-size:0.875rem;color:var(--text-primary);">${stage.label}</span>
              <span style="font-size:0.75rem;color:var(--text-secondary);">${stage.probability}%</span>
              <span class="count-badge" style="background:${stage.color}20;color:${stage.color};font-size:0.6875rem;">${stageOpps.length}</span>
            </div>
            <div style="display:flex;align-items:center;gap:6px;">
              ${stageRevenue > 0 ? `<span style="font-size:0.8125rem;font-weight:600;color:var(--text-secondary);">$${formatDollars(stageRevenue)}</span>` : ''}
              <span class="material-icons crm-stage-chevron" style="font-size:20px;color:var(--text-secondary);transition:transform 0.2s;">expand_more</span>
            </div>
          </div>
          <div id="${stageBodyId}" class="hidden" style="padding-top:6px;">
      `;

      stageOpps.forEach(opp => {
        const assetStr = (opp.assets || []).map(a => a.assetUnit).filter(Boolean).join(', ');
        const updatedAgo = opp.updatedAt?.toDate ? formatRelativeTime(opp.updatedAt.toDate()) : '';
        const lastActivity = opp._lastActivityDate?.toDate ? formatRelativeTime(opp._lastActivityDate.toDate()) : 'No activity';
        const onHoldReason = stage.key === 'on_hold' && opp.onHoldReason ? ` — ${escHTML(opp.onHoldReason)}` : '';
        const nextBadge = renderNextActionBadge(opp._nextActionDate, opp._nextAction);

        html += `
          <div onclick="window.CRMModule.showOpportunityForm('${escJsAttr(opp.customerId)}','${escJsAttr(opp.id)}')"
               style="background:var(--bg-card);border:1px solid var(--border-light);border-radius:10px;padding:10px 14px;margin-bottom:6px;cursor:pointer;transition:box-shadow 0.15s;">
            <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">
              <div style="flex:1;min-width:0;">
                <div style="font-size:0.75rem;color:var(--text-secondary);">${escHTML(opp.customerName)}</div>
                <div style="font-weight:600;font-size:0.875rem;color:var(--text-primary);margin-top:1px;">${escHTML(opp.title)}${onHoldReason}</div>
                ${assetStr ? `<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;">${escHTML(assetStr)}</div>` : ''}
              </div>
              ${opp.revenue ? `<div style="font-weight:700;color:var(--text-primary);font-size:0.9375rem;white-space:nowrap;">$${formatDollars(opp.revenue)}</div>` : ''}
            </div>
            ${nextBadge ? `<div style="margin-top:6px;">${nextBadge}</div>` : ''}
            <div style="display:flex;justify-content:space-between;margin-top:6px;font-size:0.6875rem;color:var(--text-secondary);">
              <span>${lastActivity}</span>
              <span>${updatedAgo}</span>
            </div>
          </div>
        `;
      });

      html += `</div></div>`;
    });

    // If no opps at all, show empty state
    if (opps.length === 0) {
      html += `<div style="text-align:center;padding:var(--spacing-xl);color:var(--text-secondary);">
        <span class="material-icons" style="font-size:48px;opacity:0.4;display:block;margin-bottom:8px;">developer_board</span>
        <div style="font-weight:600;">No opportunities yet</div>
        <div style="font-size:0.8125rem;margin-top:4px;">Tap "Add Opportunity" to start tracking deals.</div>
      </div>`;
    }
  } else {
    // DESKTOP: Accordion pipeline — collapsible stages with drag-and-drop
    const totalRevenue = opps.reduce((s, o) => s + (o.revenue || 0), 0);
    if (totalRevenue > 0) {
      html += `<div style="text-align:center;margin-bottom:12px;">
        <span style="font-size:1.5rem;font-weight:800;color:var(--text-primary);">$${formatDollars(totalRevenue)}</span>
        <span style="font-size:0.8125rem;color:var(--text-secondary);margin-left:6px;">pipeline value</span>
      </div>`;
    }

    OPPORTUNITY_STAGES.forEach(stage => {
      const stageOpps = opps.filter(o => o.stage === stage.key);
      const stageRevenue = stageOpps.reduce((s, o) => s + (o.revenue || 0), 0);
      const stageBodyId = `crm-desk-stage-${stage.key}`;
      // All stages collapsed by default
      const expanded = false;

      html += `
        <div class="crm-pipeline-stage" data-stage="${stage.key}"
             ondragover="event.preventDefault();this.classList.add('drag-over')"
             ondragleave="this.classList.remove('drag-over')"
             ondrop="window.CRMModule.onKanbanDrop(event,'${escJsAttr(stage.key)}');this.classList.remove('drag-over')"
             style="margin-bottom:6px;">
          <div onclick="document.getElementById('${escJsAttr(stageBodyId)}').classList.toggle('hidden');this.querySelector('.crm-stage-chevron').textContent=document.getElementById('${escJsAttr(stageBodyId)}').classList.contains('hidden')?'expand_more':'expand_less'"
               style="display:flex;align-items:center;justify-content:space-between;padding:12px 16px;background:${stage.color}08;border-left:4px solid ${stage.color};border-radius:0 10px 10px 0;cursor:pointer;user-select:none;transition:background 0.15s;"
               onmouseenter="this.style.background='${stage.color}12'" onmouseleave="this.style.background='${stage.color}08'">
            <div style="display:flex;align-items:center;gap:10px;">
              <span class="material-icons" style="font-size:20px;color:${stage.color};">${stage.icon}</span>
              <span style="font-weight:700;font-size:0.9375rem;color:var(--text-primary);">${stage.label}</span>
              <span style="font-size:0.8125rem;color:var(--text-secondary);">${stage.probability}%</span>
              <span class="count-badge" style="background:${stage.color}20;color:${stage.color};font-size:0.75rem;">${stageOpps.length}</span>
            </div>
            <div style="display:flex;align-items:center;gap:8px;">
              ${stageRevenue > 0 ? `<span style="font-size:0.9375rem;font-weight:700;color:var(--text-primary);">$${formatDollars(stageRevenue)}</span>` : ''}
              <span class="material-icons crm-stage-chevron" style="font-size:22px;color:var(--text-secondary);transition:transform 0.2s;">${expanded ? 'expand_less' : 'expand_more'}</span>
            </div>
          </div>
          <div id="${stageBodyId}" class="${expanded ? '' : 'hidden'}" style="padding:8px 0 4px 0;">
      `;

      if (stageOpps.length === 0) {
        html += `<div style="text-align:center;color:var(--text-secondary);font-size:0.8125rem;padding:16px;">No opportunities in this stage</div>`;
      } else {
        stageOpps.forEach(opp => {
          const assetStr = (opp.assets || []).map(a => a.assetUnit).filter(Boolean).join(', ');
          const updatedAgo = opp.updatedAt?.toDate ? formatRelativeTime(opp.updatedAt.toDate()) : '';
          const lastActivity = opp._lastActivityDate?.toDate ? formatRelativeTime(opp._lastActivityDate.toDate()) : 'No activity';
          const onHoldReason = stage.key === 'on_hold' && opp.onHoldReason ? ` — ${escHTML(opp.onHoldReason)}` : '';
          const dispatchDates = opp.dispatchStartDate ? `${opp.dispatchStartDate}${opp.dispatchEndDate ? ' → ' + opp.dispatchEndDate : ''}` : '';
          const nextBadge = renderNextActionBadge(opp._nextActionDate, opp._nextAction);

          html += `
            <div class="crm-pipeline-card" draggable="true"
                 data-opp-id="${opp.id}" data-customer-id="${opp.customerId}"
                 ondragstart="event.dataTransfer.setData('text/plain','${escJsAttr(opp.id)}|${escJsAttr(opp.customerId)}')"
                 onclick="window.CRMModule.showOpportunityForm('${escJsAttr(opp.customerId)}','${escJsAttr(opp.id)}')"
                 style="display:flex;align-items:center;justify-content:space-between;gap:12px;background:var(--bg-card);border:1px solid var(--border-light);border-radius:10px;padding:12px 16px;margin:0 8px 6px 8px;cursor:pointer;transition:box-shadow 0.15s,transform 0.1s;"
                 onmouseenter="this.style.boxShadow='var(--shadow-md)';this.style.transform='translateY(-1px)'" onmouseleave="this.style.boxShadow='none';this.style.transform='none'">
              <div style="flex:1;min-width:0;">
                <div style="font-size:0.75rem;color:var(--text-secondary);">${escHTML(opp.customerName)}</div>
                <div style="font-weight:600;font-size:0.9375rem;color:var(--text-primary);margin-top:1px;">${escHTML(opp.title)}${onHoldReason}</div>
                <div style="display:flex;gap:12px;margin-top:4px;font-size:0.75rem;color:var(--text-secondary);">
                  ${assetStr ? `<span>${escHTML(assetStr)}</span>` : ''}
                  ${dispatchDates ? `<span>${dispatchDates}</span>` : ''}
                </div>
                ${nextBadge ? `<div style="margin-top:6px;">${nextBadge}</div>` : ''}
                <div style="display:flex;gap:12px;margin-top:4px;font-size:0.6875rem;color:var(--text-secondary);">
                  <span>${lastActivity}</span>
                  <span>${updatedAgo}</span>
                </div>
              </div>
              ${opp.revenue ? `<div style="font-weight:800;color:var(--text-primary);font-size:1.0625rem;white-space:nowrap;">$${formatDollars(opp.revenue)}</div>` : ''}
            </div>
          `;
        });
      }

      html += `</div></div>`;
    });

    // Empty state
    if (opps.length === 0) {
      html += `<div style="text-align:center;padding:var(--spacing-xl);color:var(--text-secondary);">
        <span class="material-icons" style="font-size:48px;opacity:0.4;display:block;margin-bottom:8px;">developer_board</span>
        <div style="font-weight:600;">No opportunities yet</div>
        <div style="font-size:0.8125rem;margin-top:4px;">Click "Add Opportunity" to start tracking deals.</div>
      </div>`;
    }
  }

  // ── CUSTOMER BAR CHART + DEAL TIMELINE ─────────────────────────────────
  if (opps.length > 0) {
    html += buildCustomerBarChart(opps);
    html += buildDealTimeline(opps);
  }

  container.innerHTML = html;

  // Wire up interactivity
  if (opps.length > 0) {
    initTimelineTooltips();
    initOppFilterSystem(opps);
  }
}

function buildCustomerBarChart(opps) {
  // Group by customer
  const byCustomer = {};
  opps.forEach(opp => {
    if (!byCustomer[opp.customerId]) {
      byCustomer[opp.customerId] = { name: opp.customerName, opps: [] };
    }
    byCustomer[opp.customerId].opps.push(opp);
  });

  // Build rows sorted by total revenue desc
  const customers = Object.entries(byCustomer).map(([id, c]) => {
    const total = c.opps.reduce((s, o) => s + (o.revenue || 0), 0);
    // Stage breakdown
    const stages = {};
    c.opps.forEach(o => {
      const key = o.stage || 'initiating';
      stages[key] = (stages[key] || 0) + (o.revenue || 0);
    });
    return { id, name: c.name, total, count: c.opps.length, stages };
  }).sort((a, b) => b.total - a.total);

  const maxValue = Math.max(...customers.map(c => c.total), 1);

  // Summary stats
  const totalPipeline = customers.reduce((s, c) => s + c.total, 0);
  const activeOpps = opps.filter(o => o.stage !== 'won' && o.stage !== 'lost');
  const weightedValue = activeOpps.reduce((s, o) => {
    const stage = STAGE_MAP[o.stage];
    return s + ((o.revenue || 0) * (stage ? stage.probability / 100 : 0));
  }, 0);
  const avgDeal = opps.length > 0 ? Math.round(totalPipeline / opps.length) : 0;

  let rowsHtml = '';
  customers.forEach((cust, i) => {
    // Build stacked segments
    let segsHtml = '';
    const barPct = maxValue > 0 ? (cust.total / maxValue) * 100 : 0;

    // Order segments by pipeline stage order
    OPPORTUNITY_STAGES.forEach(stage => {
      const val = cust.stages[stage.key] || 0;
      if (val <= 0) return;
      const segPct = cust.total > 0 ? (val / cust.total) * 100 : 0;
      const label = segPct > 15 ? `$${formatDollars(val)}` : '';
      segsHtml += `<div class="opp-bc-seg" style="width:${segPct}%;background:${stage.color};animation-delay:${(i * 0.08).toFixed(2)}s;" title="${stage.label}: $${formatDollars(val)}">${label}</div>`;
    });

    rowsHtml += `
      <div class="opp-bc-row" data-bc-customer="${cust.id}" data-bc-name="${escHTML(cust.name)}">
        <div class="opp-bc-label" title="${escHTML(cust.name)}">${escHTML(cust.name)}</div>
        <div class="opp-bc-track" style="width:${Math.max(barPct, 8)}%;">${segsHtml}</div>
        <div class="opp-bc-count">${cust.count}</div>
        <div class="opp-bc-value">${cust.total > 0 ? '$' + formatDollars(cust.total) : '—'}</div>
      </div>`;
  });

  return `
    <div class="opp-barchart-wrap">
      <div class="opp-barchart-header">
        <h3>
          <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">bar_chart</span>
          Pipeline by Customer
        </h3>
        <div style="display:flex;gap:20px;font-size:0.75rem;color:var(--text-secondary);">
          <div><span style="font-weight:800;color:var(--text-primary);font-size:0.9375rem;">$${formatDollars(totalPipeline)}</span> total</div>
          <div><span style="font-weight:800;color:var(--brand-blue);font-size:0.9375rem;">$${formatDollars(Math.round(weightedValue))}</span> weighted</div>
          <div><span style="font-weight:800;color:var(--text-primary);font-size:0.9375rem;">$${formatDollars(avgDeal)}</span> avg</div>
        </div>
      </div>
      <div class="opp-barchart-body">${rowsHtml}</div>
      <div class="opp-tl-legend" style="border-top:1px solid var(--border-light);">
        ${OPPORTUNITY_STAGES.filter(s => opps.some(o => o.stage === s.key)).map(s =>
          `<div class="opp-tl-legend-item"><div class="opp-tl-legend-dot" style="background:${s.color};"></div>${s.label}</div>`
        ).join('')}
      </div>
    </div>`;
}

function buildDealTimeline(opps) {
  const now = new Date();

  // Parse dates for each opp
  const parsed = opps.map(opp => {
    const created = opp.createdAt?.toDate ? opp.createdAt.toDate() : now;
    let close = opp.estCloseDate?.toDate ? opp.estCloseDate.toDate() :
                (opp.estCloseDate && typeof opp.estCloseDate === 'string' ? new Date(opp.estCloseDate) : null);
    const won = opp.wonDate?.toDate ? opp.wonDate.toDate() : null;
    const lost = opp.lostDate?.toDate ? opp.lostDate.toDate() : null;
    // For won/lost, use that date as end
    const end = won || lost || close;
    return { ...opp, _created: created, _end: end };
  });

  // Time range: earliest created to latest end (or now + 60 days)
  let minDate = new Date(Math.min(...parsed.map(p => p._created.getTime())));
  let maxDate = new Date(Math.max(...parsed.map(p => (p._end || now).getTime()), now.getTime() + 60 * 86400000));

  // Pad 15 days on each side
  minDate = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  maxDate = new Date(maxDate.getFullYear(), maxDate.getMonth() + 2, 0);

  const totalMs = maxDate.getTime() - minDate.getTime();
  const pxPerDay = 4; // pixels per day
  const totalDays = Math.ceil(totalMs / 86400000);
  const canvasWidth = Math.max(totalDays * pxPerDay, 600);

  // Build month columns
  let monthsHtml = '';
  let gridLinesHtml = '';
  const cur = new Date(minDate);
  while (cur < maxDate) {
    const monthStart = new Date(cur);
    const monthEnd = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
    const startPx = ((monthStart.getTime() - minDate.getTime()) / totalMs) * canvasWidth;
    const endPx = ((Math.min(monthEnd.getTime(), maxDate.getTime()) - minDate.getTime()) / totalMs) * canvasWidth;
    const widthPx = endPx - startPx;
    const label = monthStart.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    monthsHtml += `<div class="opp-tl-month" style="width:${widthPx}px;">${label}</div>`;
    gridLinesHtml += `<div class="opp-tl-grid-line" style="left:${startPx}px;"></div>`;
    cur.setMonth(cur.getMonth() + 1);
  }

  // Today marker position
  const todayPx = ((now.getTime() - minDate.getTime()) / totalMs) * canvasWidth;

  // Sort: active deals first (by revenue desc), then won, then lost
  const stageOrder = { won: 90, lost: 99 };
  const sorted = [...parsed].sort((a, b) => {
    const aO = stageOrder[a.stage] || 0;
    const bO = stageOrder[b.stage] || 0;
    if (aO !== bO) return aO - bO;
    return (b.revenue || 0) - (a.revenue || 0);
  });

  // Build rows
  const rowHeight = window.innerWidth <= 768 ? 36 : 44;
  let rowsHtml = '';
  sorted.forEach((opp, i) => {
    const stage = STAGE_MAP[opp.stage] || OPPORTUNITY_STAGES[0];
    const startPx = ((opp._created.getTime() - minDate.getTime()) / totalMs) * canvasWidth;
    const delay = (i * 0.06).toFixed(2);

    if (opp._end) {
      // Bar from created → end
      let endPx = ((opp._end.getTime() - minDate.getTime()) / totalMs) * canvasWidth;
      let barW = Math.max(endPx - startPx, 24); // min 24px
      const rev = opp.revenue ? `$${formatDollars(opp.revenue)}` : '';
      const barLabel = barW > 120 ? `${escHTML(opp.title)} ${rev}` :
                       barW > 60 ? escHTML(opp.title.substring(0, 12)) : '';

      // Gradient for bar
      const isWon = opp.stage === 'won';
      const isLost = opp.stage === 'lost';
      const bgStyle = isWon ? `background:linear-gradient(135deg, ${stage.color}, #A78BFA)` :
                      isLost ? `background:linear-gradient(135deg, ${stage.color}, #F87171);opacity:0.55` :
                      `background:linear-gradient(135deg, ${stage.color}, ${stage.color}CC)`;

      rowsHtml += `
        <div class="opp-tl-row" data-tl-customer="${opp.customerId}">
          <div class="opp-tl-bar" style="left:${startPx}px;width:${barW}px;top:${(rowHeight - 28) / 2}px;${bgStyle};animation-delay:${delay}s;"
               onclick="window.CRMModule.showOpportunityForm('${escJsAttr(opp.customerId)}','${escJsAttr(opp.id)}')"
               data-tl-opp='${JSON.stringify({ title: opp.title, customer: opp.customerName, stage: stage.label, revenue: opp.revenue || 0, created: opp._created.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), end: opp._end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), color: stage.color }).replace(/'/g, '&#39;')}'>
            ${barLabel}
          </div>
        </div>`;
    } else {
      // Dot at created date
      rowsHtml += `
        <div class="opp-tl-row" data-tl-customer="${opp.customerId}">
          <div class="opp-tl-dot" style="left:${startPx - 7}px;top:${(rowHeight - 14) / 2}px;background:${stage.color};animation-delay:${delay}s;"
               onclick="window.CRMModule.showOpportunityForm('${escJsAttr(opp.customerId)}','${escJsAttr(opp.id)}')"
               data-tl-opp='${JSON.stringify({ title: opp.title, customer: opp.customerName, stage: stage.label, revenue: opp.revenue || 0, created: opp._created.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), end: null, color: stage.color }).replace(/'/g, '&#39;')}'>
          </div>
          <div class="opp-tl-label" style="left:${startPx + 14}px;">${escHTML(opp.title)}</div>
        </div>`;
    }
  });

  const canvasHeight = sorted.length * rowHeight;

  // Legend
  const activeStages = [...new Set(opps.map(o => o.stage))];
  let legendHtml = '';
  OPPORTUNITY_STAGES.forEach(s => {
    if (!activeStages.includes(s.key)) return;
    legendHtml += `<div class="opp-tl-legend-item"><div class="opp-tl-legend-dot" style="background:${s.color};"></div>${s.label}</div>`;
  });

  // Build customer dropdown options
  const customerNames = [...new Set(opps.map(o => o.customerName))].sort();
  const customerIds = {};
  opps.forEach(o => { customerIds[o.customerName] = o.customerId; });
  let filterOpts = `<option value="all">All Customers</option>`;
  customerNames.forEach(name => {
    const count = opps.filter(o => o.customerName === name).length;
    filterOpts += `<option value="${customerIds[name]}">${escHTML(name)} (${count})</option>`;
  });

  return `
    <div class="opp-timeline-wrap">
      <div class="opp-timeline-header">
        <h3>
          <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">timeline</span>
          Deal Timeline
        </h3>
        <div class="opp-tl-filter">
          <select id="opp-tl-customer-filter">${filterOpts}</select>
          <span style="font-size:0.75rem;color:var(--text-secondary);">${sorted.length} deals</span>
        </div>
      </div>
      <div class="opp-timeline-body">
        <div class="opp-timeline-canvas" style="width:${canvasWidth}px;min-height:${canvasHeight + 40}px;">
          <div class="opp-tl-months">${monthsHtml}</div>
          <div class="opp-tl-rows" style="position:relative;height:${canvasHeight}px;">
            ${gridLinesHtml}
            ${rowsHtml}
            <div class="opp-tl-today" style="left:${todayPx}px;"></div>
          </div>
        </div>
      </div>
      <div class="opp-tl-legend">${legendHtml}</div>
    </div>`;
}

function initTimelineTooltips() {
  // Create or reuse tooltip
  let tip = document.getElementById('opp-tl-tip');
  if (!tip) {
    tip = document.createElement('div');
    tip.id = 'opp-tl-tip';
    tip.className = 'opp-tl-tooltip';
    document.body.appendChild(tip);
  }

  const bars = document.querySelectorAll('[data-tl-opp]');
  bars.forEach(bar => {
    bar.addEventListener('mouseenter', (e) => {
      try {
        const d = JSON.parse(bar.getAttribute('data-tl-opp'));
        const rev = d.revenue ? `$${Number(d.revenue).toLocaleString()}` : '—';
        const dateRange = d.end ? `${d.created} → ${d.end}` : `Created ${d.created}`;
        tip.innerHTML = `
          <div class="opp-tl-tooltip-title">${escapeHtml(d.title)}</div>
          <div class="opp-tl-tooltip-customer">${escapeHtml(d.customer)}</div>
          <div class="opp-tl-tooltip-row"><span>Stage</span><strong><span style="color:${d.color};">&#9679;</span> ${d.stage}</strong></div>
          <div class="opp-tl-tooltip-row"><span>Value</span><strong>${rev}</strong></div>
          <div class="opp-tl-tooltip-row"><span>Dates</span><strong>${dateRange}</strong></div>
        `;
        tip.classList.add('visible');
      } catch (_) {}
    });
    bar.addEventListener('mousemove', (e) => {
      tip.style.left = (e.clientX + 14) + 'px';
      tip.style.top = (e.clientY - 10) + 'px';
    });
    bar.addEventListener('mouseleave', () => {
      tip.classList.remove('visible');
    });
  });
}

function initOppFilterSystem() {
  const dropdown = document.getElementById('opp-tl-customer-filter');
  if (!dropdown) return;

  function applyFilter(customerId) {
    const rows = document.querySelectorAll('.opp-tl-row[data-tl-customer]');
    const barRows = document.querySelectorAll('.opp-bc-row[data-bc-customer]');

    if (customerId === 'all') {
      rows.forEach(r => r.classList.remove('opp-tl-dimmed'));
      barRows.forEach(r => { r.classList.remove('opp-bc-dimmed'); r.classList.remove('opp-bc-row-active'); });
    } else {
      rows.forEach(r => {
        r.classList.toggle('opp-tl-dimmed', r.getAttribute('data-tl-customer') !== customerId);
      });
      barRows.forEach(r => {
        const isMatch = r.getAttribute('data-bc-customer') === customerId;
        r.classList.toggle('opp-bc-dimmed', !isMatch);
        r.classList.toggle('opp-bc-row-active', isMatch);
      });
    }
  }

  // Dropdown change → filter timeline + highlight bar chart
  dropdown.addEventListener('change', () => applyFilter(dropdown.value));

  // Bar chart row click → set dropdown + filter
  document.querySelectorAll('.opp-bc-row[data-bc-customer]').forEach(row => {
    row.addEventListener('click', () => {
      const cid = row.getAttribute('data-bc-customer');
      // Toggle: if already selected, deselect
      if (dropdown.value === cid) {
        dropdown.value = 'all';
        applyFilter('all');
      } else {
        dropdown.value = cid;
        applyFilter(cid);
      }
    });
  });
}

async function onKanbanDrop(event, newStageKey) {
  event.preventDefault();
  const data = event.dataTransfer.getData('text/plain');
  const [oppId, customerId] = data.split('|');
  if (!oppId || !customerId) return;

  const opp = CRMState.allOpportunities.find(o => o.id === oppId);
  if (!opp || opp.stage === newStageKey) return;

  const newStage = STAGE_MAP[newStageKey];

  // Won/Lost needs prompt
  if (newStageKey === 'won' || newStageKey === 'lost') {
    CRMState._pendingKanbanMove = { oppId, customerId, opp, newStageKey };
    showWonLostPrompt(newStageKey, opp.revenue);

    // Override confirmWonLost to handle kanban-specific flow
    const origConfirm = confirmWonLost;
    CRMState._kanbanConfirmOverride = async function() {
      const pending = CRMState._pendingKanbanMove;
      if (!pending) return;
      const stageKey = document.getElementById('crm-wonlost-stage').value;
      const isWon = stageKey === 'won';
      const dateVal = document.getElementById('crm-wonlost-date')?.value || new Date().toISOString().split('T')[0];

      const updateData = {
        stage: stageKey,
        probability: STAGE_MAP[stageKey].probability,
        updatedAt: Timestamp.now()
      };

      if (isWon) {
        updateData.wonDate = Timestamp.fromDate(new Date(dateVal));
        updateData.revenue = parseFloat(document.getElementById('crm-wonlost-revenue')?.value) || pending.opp.revenue || 0;
      } else {
        updateData.lostDate = Timestamp.fromDate(new Date(dateVal));
        updateData.lostReason = document.getElementById('crm-wonlost-reason')?.value || 'Other';
      }

      document.getElementById('crm-wonlost-modal').classList.add('hidden');

      try {
        const oppPath = `tenants/${CRMState.tenantId}/customers/${pending.customerId}/opportunities/${pending.oppId}`;
        await updateDoc(doc(db, oppPath), updateData);
        await syncOpportunityHold(pending.customerId, pending.oppId, { ...pending.opp, ...updateData });
        CRMState.allOpportunitiesLoaded = false;
        await renderOpportunitiesKanban();
        window.showToast?.(`Moved to ${STAGE_MAP[stageKey].label}`, 'success');
      } catch (err) {
        console.error('[CRM] Kanban move error:', err);
        window.showToast?.('Failed to update stage', 'error');
      }

      CRMState._pendingKanbanMove = null;
      CRMState._kanbanConfirmOverride = null;
    };
    return;
  }

  // Simple stage move (including on_hold)
  try {
    const oppPath = `tenants/${CRMState.tenantId}/customers/${customerId}/opportunities/${oppId}`;
    const updateData = { stage: newStageKey, probability: newStage.probability, updatedAt: Timestamp.now() };

    // Set onHoldDate when moving to on_hold
    if (newStageKey === 'on_hold') {
      updateData.onHoldDate = Timestamp.now();
    }

    await updateDoc(doc(db, oppPath), updateData);
    await syncOpportunityHold(customerId, oppId, { ...opp, ...updateData });
    window.logAudit?.('crm', 'PIPELINE_MOVE', { oppId, customerId, oppName: opp.name, oldStage: opp.stage, newStage: newStageKey });
    CRMState.allOpportunitiesLoaded = false;
    await renderOpportunitiesKanban();
    window.showToast?.(`Moved to ${newStage.label}`, 'success');
  } catch (err) {
    console.error('[CRM] Kanban move error:', err);
    window.showToast?.('Failed to update stage', 'error');
  }
}

// Override confirmWonLost to check for kanban override
const _origConfirmWonLost = confirmWonLost;
async function confirmWonLostDispatch() {
  if (CRMState._kanbanConfirmOverride) {
    await CRMState._kanbanConfirmOverride();
  } else {
    await _origConfirmWonLost();
  }
}

// ============================================
// ENHANCED DASHBOARD
// ============================================

async function loadAccountabilityMetrics() {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const cutoff = Timestamp.fromDate(thirtyDaysAgo);

  let meetings = 0, calls = 0, emails = 0;
  let latestMeeting = null, latestCall = null, latestEmail = null;

  // Query activities from all customers in parallel
  const activityPromises = CRMState.customers.map(async (customer) => {
    const actPath = `tenants/${CRMState.tenantId}/customers/${customer.id}/activities`;
    const snap = await getDocs(query(collection(db, actPath), where('createdAt', '>=', cutoff)));
    snap.docs.forEach(d => {
      const data = d.data();
      const type = data.type;
      const ts = data.createdAt?.toDate ? data.createdAt.toDate().getTime() : 0;
      if (type === 'meeting') { meetings++; if (!latestMeeting || ts > latestMeeting.ts) latestMeeting = { ts, customerId: customer.id }; }
      else if (type === 'call') { calls++; if (!latestCall || ts > latestCall.ts) latestCall = { ts, customerId: customer.id }; }
      else if (type === 'email') { emails++; if (!latestEmail || ts > latestEmail.ts) latestEmail = { ts, customerId: customer.id }; }
    });
  });
  await Promise.all(activityPromises);

  // Opportunity metrics from already-loaded allOpportunities
  await loadAllOpportunities();
  const opps = CRMState.allOpportunities;
  const newOpps = opps.filter(o => {
    const created = o.createdAt?.toDate ? o.createdAt.toDate() : null;
    return created && created >= thirtyDaysAgo;
  }).length;

  const wonRecent = opps.filter(o => {
    if (o.stage !== 'won') return false;
    const updated = o.updatedAt?.toDate ? o.updatedAt.toDate() : null;
    return updated && updated >= thirtyDaysAgo;
  });
  const dealsWon = wonRecent.length;
  const revenueWon = wonRecent.reduce((s, o) => s + (o.revenue || 0), 0);

  // Find most recent opp customer
  const recentNewOpp = [...opps].filter(o => {
    const created = o.createdAt?.toDate ? o.createdAt.toDate() : null;
    return created && created >= thirtyDaysAgo;
  }).sort((a, b) => (b.createdAt?.toDate?.().getTime() || 0) - (a.createdAt?.toDate?.().getTime() || 0))[0];
  const recentWon = wonRecent.sort((a, b) => (b.updatedAt?.toDate?.().getTime() || 0) - (a.updatedAt?.toDate?.().getTime() || 0))[0];

  return {
    meetings, calls, emails, newOpps, dealsWon, revenueWon,
    latestMeetingCust: latestMeeting?.customerId || null,
    latestCallCust: latestCall?.customerId || null,
    latestEmailCust: latestEmail?.customerId || null,
    latestNewOppCust: recentNewOpp?.customerId || null,
    latestWonCust: recentWon?.customerId || null
  };
}

function renderDashboardOpportunityStats() {
  // Returns HTML for opportunity stats to inject into dashboard (customers only, not subs)
  const subCustomerIds = new Set(CRMState.customers.filter(c => window.BillableCustomer.isVendorLikeCustomer(c)).map(c => c.id));
  const opps = CRMState.allOpportunities.filter(o => !subCustomerIds.has(o.customerId));
  const openOpps = opps.filter(o => o.stage !== 'won' && o.stage !== 'lost');
  const totalPipeline = openOpps.reduce((s, o) => s + (o.revenue || 0), 0);
  const weightedRevenue = openOpps.reduce((s, o) => s + ((o.revenue || 0) * ((STAGE_MAP[o.stage]?.probability || 0) / 100)), 0);

  const now = new Date();
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const closingThisMonth = openOpps.filter(o => {
    if (!o.estCloseDate) return false;
    const d = o.estCloseDate?.toDate ? o.estCloseDate.toDate() : new Date(o.estCloseDate);
    return d <= endOfMonth;
  }).length;

  // Stale deals (no activity in 14+ days)
  const staleThreshold = new Date();
  staleThreshold.setDate(staleThreshold.getDate() - 14);
  const staleDeals = openOpps.filter(o => {
    const lastAct = o._lastActivityDate?.toDate ? o._lastActivityDate.toDate() : null;
    if (!lastAct) return true; // never had activity
    return lastAct < staleThreshold;
  });

  // Performance metrics (all-time from closed opportunities)
  const wonOpps = opps.filter(o => o.stage === 'won');
  const lostOpps = opps.filter(o => o.stage === 'lost');
  const totalClosed = wonOpps.length + lostOpps.length;
  const winRate = totalClosed > 0 ? Math.round((wonOpps.length / totalClosed) * 100) : null;
  const avgDealSize = wonOpps.length > 0
    ? wonOpps.reduce((s, o) => s + (o.revenue || 0), 0) / wonOpps.length
    : null;

  // Avg sales cycle (days from created → won)
  const cycleDays = wonOpps.map(o => {
    const created = o.createdAt?.toDate ? o.createdAt.toDate() : null;
    const won = o.wonDate?.toDate ? o.wonDate.toDate() : (o.updatedAt?.toDate ? o.updatedAt.toDate() : null);
    if (created && won) return (won - created) / (1000 * 60 * 60 * 24);
    return null;
  }).filter(d => d !== null && d >= 0);
  const avgSalesCycle = cycleDays.length > 0
    ? Math.round(cycleDays.reduce((s, d) => s + d, 0) / cycleDays.length)
    : null;

  // Pipeline velocity ($ won per month, based on last 90 days)
  const ninetyDaysAgo = new Date();
  ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
  const recentWonRevenue = wonOpps.filter(o => {
    const won = o.wonDate?.toDate ? o.wonDate.toDate() : (o.updatedAt?.toDate ? o.updatedAt.toDate() : null);
    return won && won >= ninetyDaysAgo;
  }).reduce((s, o) => s + (o.revenue || 0), 0);
  const pipelineVelocity = recentWonRevenue / 3; // per month (90 days / 3)

  return {
    totalPipeline, weightedRevenue, openCount: openOpps.length, closingThisMonth, staleDeals,
    winRate, avgDealSize, avgSalesCycle, pipelineVelocity,
    wonCount: wonOpps.length, lostCount: lostOpps.length
  };
}

// ============================================
// CRM TASKS
// ============================================
const TASK_TYPES = [
  { key: 'call',     label: 'Call',      icon: 'phone',     color: '#3B82F6' },
  { key: 'email',    label: 'Email',     icon: 'email',     color: '#8B5CF6' },
  { key: 'meeting',  label: 'Meeting',   icon: 'groups',    color: '#10B981' },
  { key: 'followup', label: 'Follow-up', icon: 'event',     color: '#F59E0B' },
  { key: 'note',     label: 'Note',      icon: 'edit_note', color: '#64748B' }
];
const TASK_TYPE_MAP = {};
TASK_TYPES.forEach(t => { TASK_TYPE_MAP[t.key] = t; });

function subscribeToCrmTasks() {
  if (CRMState.tasksUnsubscriber) CRMState.tasksUnsubscriber();
  const t = CRMState.tenantId;
  const q = query(collection(db, `tenants/${t}/crmTasks`), orderBy('dueDate', 'asc'));
  CRMState.tasksUnsubscriber = onSnapshot(q, snap => {
    CRMState._allCrmTasks = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    CRMState.crmTasks = scopedCrmTasks(CRMState._allCrmTasks);
    CRMState._tasksLoaded = true; // TASK-368 pattern — flip flag in success callback
    updateTasksBadge();
    if (CRMState.currentTab === 'tasks') renderTasksTab();
  }, err => {
    console.error('[CRM] Tasks subscription error:', err);
    CRMState._tasksLoaded = true; // TASK-368 pattern — flip on error so skeleton doesn't hang forever
    window.showToast?.('Error loading tasks', 'error');
    if (CRMState.currentTab === 'tasks') renderTasksTab();
  });
  CRMState.unsubscribers.push(() => {
    if (CRMState.tasksUnsubscriber) { CRMState.tasksUnsubscriber(); CRMState.tasksUnsubscriber = null; }
  });
}

function updateTasksBadge() {
  const badge = document.getElementById('crm-tasks-badge');
  if (!badge) return;
  const now = new Date();
  now.setHours(23, 59, 59, 999);
  const overdue = CRMState.crmTasks.filter(t => {
    if (t.status !== 'pending') return false;
    if (t.snoozedUntil) {
      const snoozed = t.snoozedUntil.toDate ? t.snoozedUntil.toDate() : new Date(t.snoozedUntil);
      if (snoozed > new Date()) return false;
    }
    const due = t.dueDate?.toDate ? t.dueDate.toDate() : new Date(t.dueDate);
    return due <= now;
  });
  if (overdue.length > 0) {
    badge.textContent = overdue.length;
    badge.style.display = 'inline-flex';
  } else {
    badge.style.display = 'none';
  }
}

function renderTasksTab() {
  const container = document.getElementById('crm-tasks-content');
  if (!container) return;

  // TASK-368 pattern — show skeleton loader until first crmTasks snapshot lands,
  // so the user doesn't see "No tasks due" during the ~200ms-2s cold fetch.
  if (!CRMState._tasksLoaded) {
    container.innerHTML = `<div style="text-align:center;padding:var(--spacing-xl);"><div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div><div style="color:var(--text-secondary);font-size:0.875rem;">Loading tasks…</div></div>`;
    return;
  }

  const filter = CRMState.taskFilter;
  const now = new Date();
  const todayEnd = new Date(now); todayEnd.setHours(23, 59, 59, 999);
  const weekEnd = new Date(now); weekEnd.setDate(weekEnd.getDate() + 7); weekEnd.setHours(23, 59, 59, 999);

  let filtered = CRMState.crmTasks;
  if (filter === 'due') {
    filtered = filtered.filter(t => {
      if (t.status !== 'pending') return false;
      if (t.snoozedUntil) {
        const s = t.snoozedUntil.toDate ? t.snoozedUntil.toDate() : new Date(t.snoozedUntil);
        if (s > now) return false;
      }
      const due = t.dueDate?.toDate ? t.dueDate.toDate() : new Date(t.dueDate);
      return due <= todayEnd;
    });
  } else if (filter === 'upcoming') {
    filtered = filtered.filter(t => {
      if (t.status !== 'pending') return false;
      const due = t.dueDate?.toDate ? t.dueDate.toDate() : new Date(t.dueDate);
      return due > todayEnd && due <= weekEnd;
    });
  } else if (filter === 'completed') {
    filtered = filtered.filter(t => t.status === 'completed');
    filtered.sort((a, b) => {
      const aT = a.completedAt?.toDate ? a.completedAt.toDate() : new Date(0);
      const bT = b.completedAt?.toDate ? b.completedAt.toDate() : new Date(0);
      return bT - aT;
    });
  } else if (filter === 'snoozed') {
    filtered = filtered.filter(t => {
      if (t.status !== 'pending' || !t.snoozedUntil) return false;
      const s = t.snoozedUntil.toDate ? t.snoozedUntil.toDate() : new Date(t.snoozedUntil);
      return s > now;
    });
  }

  // Count for pills
  const dueCount = CRMState.crmTasks.filter(t => {
    if (t.status !== 'pending') return false;
    if (t.snoozedUntil) { const s = t.snoozedUntil.toDate ? t.snoozedUntil.toDate() : new Date(t.snoozedUntil); if (s > now) return false; }
    const due = t.dueDate?.toDate ? t.dueDate.toDate() : new Date(t.dueDate);
    return due <= todayEnd;
  }).length;
  const upcomingCount = CRMState.crmTasks.filter(t => {
    if (t.status !== 'pending') return false;
    const due = t.dueDate?.toDate ? t.dueDate.toDate() : new Date(t.dueDate);
    return due > todayEnd && due <= weekEnd;
  }).length;
  const snoozedCount = CRMState.crmTasks.filter(t => {
    if (t.status !== 'pending' || !t.snoozedUntil) return false;
    const s = t.snoozedUntil.toDate ? t.snoozedUntil.toDate() : new Date(t.snoozedUntil);
    return s > now;
  }).length;

  container.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px;flex-wrap:wrap;gap:8px;">
      <div class="crm-tasks-filter-pills">
        <button class="crm-tasks-filter-pill ${filter === 'due' ? 'active' : ''}" onclick="window.CRMModule.setTaskFilter('due')">
          Due / Overdue ${dueCount > 0 ? `<span class="crm-task-pill-count">${dueCount}</span>` : ''}
        </button>
        <button class="crm-tasks-filter-pill ${filter === 'upcoming' ? 'active' : ''}" onclick="window.CRMModule.setTaskFilter('upcoming')">
          Upcoming 7d ${upcomingCount > 0 ? `<span class="crm-task-pill-count">${upcomingCount}</span>` : ''}
        </button>
        <button class="crm-tasks-filter-pill ${filter === 'snoozed' ? 'active' : ''}" onclick="window.CRMModule.setTaskFilter('snoozed')">
          Snoozed ${snoozedCount > 0 ? `<span class="crm-task-pill-count">${snoozedCount}</span>` : ''}
        </button>
        <button class="crm-tasks-filter-pill ${filter === 'completed' ? 'active' : ''}" onclick="window.CRMModule.setTaskFilter('completed')">
          Completed
        </button>
      </div>
      <button class="btn btn-primary btn-sm" onclick="window.CRMModule.showTaskForm()">
        <span class="material-icons" style="font-size:16px;">add</span> Add Task
      </button>
    </div>

    ${filtered.length === 0 ? `
      <div class="empty-state" style="padding:40px;text-align:center;">
        <span class="material-icons" style="font-size:48px;color:var(--text-secondary);">task_alt</span>
        <div style="margin-top:12px;font-size:14px;color:var(--text-secondary);">${filter === 'completed' ? 'No completed tasks yet.' : filter === 'snoozed' ? 'No snoozed tasks.' : 'No tasks due. Use Deal Desk or add one manually.'}</div>
      </div>
    ` : `
      <div class="crm-tasks-list">
        ${filtered.map(task => renderTaskCard(task)).join('')}
      </div>
    `}
  `;
  // TASK-9616 morning follow-up email opt-in (lazy; not in the demo)
  if (!_crmIsDemoTenant()) __ddChunkWrap(import('./crmReminderEmailPref.js?v=9616001')).then((m) => m.mount(container, CRMState.tenantId)).catch(() => {});
}

function renderTaskCard(task) {
  const typeInfo = TASK_TYPE_MAP[task.taskType] || TASK_TYPE_MAP['note'];
  const now = new Date();
  const due = task.dueDate?.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
  const todayStart = new Date(now); todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(now); todayEnd.setHours(23, 59, 59, 999);
  const isOverdue = due < todayStart && task.status === 'pending';
  const isDueToday = due >= todayStart && due <= todayEnd && task.status === 'pending';
  const isSnoozed = task.snoozedUntil && task.status === 'pending' && (task.snoozedUntil.toDate ? task.snoozedUntil.toDate() : new Date(task.snoozedUntil)) > now;
  const isCompleted = task.status === 'completed';

  let accentClass = '';
  if (isCompleted) accentClass = 'completed';
  else if (isSnoozed) accentClass = 'snoozed';
  else if (isOverdue) accentClass = 'overdue';
  else if (isDueToday) accentClass = 'due-today';

  const dueDateStr = due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  let badge = '';
  if (isOverdue) badge = '<span class="crm-task-due-badge overdue">Overdue</span>';
  else if (isDueToday) badge = '<span class="crm-task-due-badge today">Due today</span>';
  else if (isSnoozed) {
    const snoozedUntil = task.snoozedUntil.toDate ? task.snoozedUntil.toDate() : new Date(task.snoozedUntil);
    badge = `<span class="crm-task-due-badge snoozed">Snoozed until ${snoozedUntil.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>`;
  }

  const priorityIcon = task.priority === 'high' ? '<span class="material-icons" style="font-size:14px;color:#EF4444;" title="High priority">priority_high</span>' : '';

  return `
    <div class="crm-task-card ${accentClass}">
      <div class="crm-task-icon" style="background:${typeInfo.color}15;color:${typeInfo.color};">
        <span class="material-icons">${typeInfo.icon}</span>
      </div>
      <div class="crm-task-body">
        <div class="crm-task-title">${priorityIcon} ${escHTML(task.title)}${isCompleted ? ' <span class="material-icons" style="font-size:16px;color:#10B981;vertical-align:middle;">check_circle</span>' : ''}</div>
        ${task.customerName ? `<div class="crm-task-meta">${escHTML(task.customerName)}${task.contactName ? ' — ' + escHTML(task.contactName) : ''}</div>` : ''}
        <div class="crm-task-meta">${dueDateStr} ${badge}</div>
        ${task.description ? `<div class="crm-task-meta" style="font-style:italic;">${escHTML(task.description)}</div>` : ''}
      </div>
      <div class="crm-task-actions">
        ${!isCompleted ? `
          <button class="btn-icon" title="Complete" onclick="window.CRMModule.completeTask('${escJsAttr(task.id)}')">
            <span class="material-icons" style="color:#10B981;">check_circle_outline</span>
          </button>
          <button class="btn-icon" title="Snooze" onclick="window.CRMModule.showSnoozeMenu('${escJsAttr(task.id)}', event)">
            <span class="material-icons" style="color:#F59E0B;">snooze</span>
          </button>
        ` : ''}
        <button class="btn-icon" title="Edit" onclick="window.CRMModule.showTaskForm('${escJsAttr(task.id)}')">
          <span class="material-icons" style="color:var(--text-secondary);">edit</span>
        </button>
        <button class="btn-icon" title="Delete" onclick="window.CRMModule.deleteTask('${escJsAttr(task.id)}')">
          <span class="material-icons" style="color:var(--text-secondary);">delete_outline</span>
        </button>
      </div>
    </div>
  `;
}

function setTaskFilter(filter) {
  CRMState.taskFilter = filter;
  renderTasksTab();
}

async function completeTask(taskId) {
  try {
    await updateDoc(doc(db, `tenants/${CRMState.tenantId}/crmTasks/${taskId}`), {
      status: 'completed',
      completedAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    window.showToast('Task completed', 'success');
  } catch (err) {
    console.error('[CRM] Complete task error:', err);
    window.showToast('Error completing task', 'error');
  }
}

function showSnoozeMenu(taskId, event) {
  // Remove any existing snooze menu
  document.querySelectorAll('.crm-snooze-menu').forEach(m => m.remove());

  const menu = document.createElement('div');
  menu.className = 'crm-snooze-menu';
  menu.innerHTML = `
    <button onclick="window.CRMModule.snoozeTask('${escJsAttr(taskId)}', 1)">Tomorrow</button>
    <button onclick="window.CRMModule.snoozeTask('${escJsAttr(taskId)}', 3)">3 Days</button>
    <button onclick="window.CRMModule.snoozeTask('${escJsAttr(taskId)}', 7)">1 Week</button>
  `;

  // Position near the button
  const rect = event.currentTarget.getBoundingClientRect();
  menu.style.position = 'fixed';
  menu.style.top = (rect.bottom + 4) + 'px';
  menu.style.left = (rect.left - 60) + 'px';
  menu.style.zIndex = '9999';
  document.body.appendChild(menu);

  // Close on click outside
  const closer = (e) => {
    if (!menu.contains(e.target)) {
      menu.remove();
      document.removeEventListener('click', closer, true);
    }
  };
  setTimeout(() => document.addEventListener('click', closer, true), 10);
}

async function snoozeTask(taskId, days) {
  // Remove snooze menu
  document.querySelectorAll('.crm-snooze-menu').forEach(m => m.remove());

  const snoozeDate = new Date();
  snoozeDate.setDate(snoozeDate.getDate() + days);
  snoozeDate.setHours(9, 0, 0, 0);

  try {
    const taskRef = doc(db, `tenants/${CRMState.tenantId}/crmTasks/${taskId}`);
    const snap = await getDoc(taskRef);
    const count = (snap.data()?.snoozeCount || 0) + 1;
    await updateDoc(taskRef, {
      snoozedUntil: Timestamp.fromDate(snoozeDate),
      snoozeCount: count,
      updatedAt: Timestamp.now()
    });
    window.showToast(`Snoozed for ${days} day${days > 1 ? 's' : ''}`, 'success');
  } catch (err) {
    console.error('[CRM] Snooze error:', err);
    window.showToast('Error snoozing task', 'error');
  }
}

async function deleteTask(taskId) {
  if (!confirm('Delete this task?')) return;
  try {
    await deleteDoc(doc(db, `tenants/${CRMState.tenantId}/crmTasks/${taskId}`));
    window.showToast('Task deleted', 'success');
  } catch (err) {
    console.error('[CRM] Delete task error:', err);
    window.showToast('Error deleting task', 'error');
  }
}

function showTaskForm(taskId) {
  const existing = taskId ? CRMState.crmTasks.find(t => t.id === taskId) : null;

  // Build customer options
  const customerOpts = CRMState.customers.map(c => `<option value="${c.id}" ${existing?.customerId === c.id ? 'selected' : ''}>${escHTML(c.name)}</option>`).join('');

  // Build the form in the customer modal (reuse the z-index 1050 modal)
  const modal = document.getElementById('crm-customer-modal');
  document.getElementById('crm-customer-modal-title').textContent = existing ? 'Edit Task' : 'Add Task';

  const dueVal = existing?.dueDate
    ? (existing.dueDate.toDate ? existing.dueDate.toDate() : new Date(existing.dueDate)).toISOString().split('T')[0]
    : new Date(Date.now() + 86400000).toISOString().split('T')[0];

  document.getElementById('crm-customer-form').innerHTML = `
    <div class="form-group">
      <label class="form-label">Title *</label>
      <input type="text" id="crm-task-title" class="form-control" value="${escAttr(existing?.title || '')}" placeholder="e.g. Call Joe about drilling contract" data-maxlen="100">
    </div>
    <div class="form-group">
      <label class="form-label">Type</label>
      <select id="crm-task-type" class="form-control">
        ${TASK_TYPES.map(t => `<option value="${t.key}" ${(existing?.taskType || 'call') === t.key ? 'selected' : ''}>${t.label}</option>`).join('')}
      </select>
    </div>
    <div class="form-group">
      <label class="form-label">Due Date *</label>
      <input type="date" id="crm-task-due" class="form-control" value="${dueVal}">
    </div>
    <div class="form-group">
      <label class="form-label">Priority</label>
      <select id="crm-task-priority" class="form-control">
        <option value="low" ${existing?.priority === 'low' ? 'selected' : ''}>Low</option>
        <option value="normal" ${(!existing || existing?.priority === 'normal') ? 'selected' : ''}>Normal</option>
        <option value="high" ${existing?.priority === 'high' ? 'selected' : ''}>High</option>
      </select>
    </div>
    <div class="form-group">
      <label class="form-label">Customer</label>
      <select id="crm-task-customer" class="form-control">
        <option value="">— None —</option>
        ${customerOpts}
      </select>
    </div>
    ${(CRMState.profiles || []).length > 0 ? `
    <div class="form-group">
      <label class="form-label">Assigned To</label>
      <select id="crm-task-assigned-to" class="form-control">
        <option value="" ${!existing?.assignedTo ? 'selected' : ''}>Unassigned (shared)</option>
        ${(CRMState.profiles || []).map(p => `<option value="${escAttr(p.id)}" ${existing?.assignedTo === p.id ? 'selected' : (!existing && CRMState.activeProfile?.id === p.id && CRMState.activeProfile?.role !== 'supervisor') ? 'selected' : ''}>${escAttr(p.name)}${p.role === 'supervisor' ? ' (Supervisor)' : ''}</option>`).join('')}
      </select>
    </div>
    ` : ''}
    <div class="form-group">
      <label class="form-label">Description</label>
      <textarea id="crm-task-desc" class="form-control" rows="3" placeholder="Optional notes...">${escHTML(existing?.description || '')}</textarea>
    </div>
  `;

  // Override save button
  const saveBtn = document.getElementById('crm-save-customer-btn');
  if (saveBtn) {
    saveBtn.textContent = existing ? 'Update Task' : 'Create Task';
    saveBtn.onclick = () => saveTask(taskId || ''); // TASK-10086-D: no id in a JS string
  }

  modal.classList.remove('hidden');
}

async function saveTask(taskId) {
  const _clamp = window.clampText || ((s, n) => s && s.length > n ? s.slice(0, n) : s || '');
  const title = _clamp(document.getElementById('crm-task-title')?.value?.trim(), 100);
  const taskType = document.getElementById('crm-task-type')?.value || 'call';
  const dueStr = document.getElementById('crm-task-due')?.value;
  const priority = document.getElementById('crm-task-priority')?.value || 'normal';
  const customerId = document.getElementById('crm-task-customer')?.value || '';
  const assignedTo = document.getElementById('crm-task-assigned-to')?.value || '';
  const description = document.getElementById('crm-task-desc')?.value?.trim() || '';

  if (!title) { window.showToast('Title is required', 'error'); return; }
  if (!dueStr) { window.showToast('Due date is required', 'error'); return; }

  const dueDate = new Date(dueStr + 'T09:00:00');
  const customerName = customerId ? (CRMState.customers.find(c => c.id === customerId)?.name || '') : '';

  const data = {
    title,
    taskType,
    dueDate: Timestamp.fromDate(dueDate),
    priority,
    customerId: customerId || null,
    customerName: customerName || null,
    contactId: null,
    contactName: null,
    opportunityId: null,
    opportunityTitle: null,
    description,
    updatedAt: Timestamp.now()
  };

  if ((CRMState.profiles || []).length > 0) {
    data.assignedTo = assignedTo || null;
  }

  try {
    if (taskId) {
      await updateDoc(doc(db, `tenants/${CRMState.tenantId}/crmTasks/${taskId}`), data);
      window.showToast('Task updated', 'success');
    } else {
      data.status = 'pending';
      data.snoozedUntil = null;
      data.snoozeCount = 0;
      data.completedAt = null;
      data.createdBy = 'manual';
      data.createdAt = Timestamp.now();
      await addDoc(collection(db, `tenants/${CRMState.tenantId}/crmTasks`), data);
      window.showToast('Task created', 'success');
    }
    document.getElementById('crm-customer-modal').classList.add('hidden');
    // Restore save button for customer form
    const saveBtn = document.getElementById('crm-save-customer-btn');
    saveBtn.textContent = 'Save';
    saveBtn.setAttribute('onclick', 'window.CRMModule.saveCustomer()');
  } catch (err) {
    console.error('[CRM] Save task error:', err);
    window.showToast('Error saving task', 'error');
  }
}

// ============================================
// DEAL DESK BOT
// ============================================
function toggleDealDesk() {
  CRMState.botVisible = !CRMState.botVisible;
  const panel = document.getElementById('crm-dealdesk-panel');
  // TASK-914 — toggle a body flag so CSS hides the AI Agent FAB while the Deal
  // Desk panel is open (the fixed FAB otherwise covers the panel's send button).
  document.body.classList.toggle('crm-dealdesk-open', CRMState.botVisible);
  if (panel) {
    panel.classList.toggle('hidden', !CRMState.botVisible);
    if (CRMState.botVisible) {
      renderDealDeskTranscriptNotice();
      document.getElementById('crm-dealdesk-input')?.focus();
    }
  }
}

// TASK-9268 slice 5 — crmAssistant records an owner's / active member's
// questions. Same copy as the other recorded assistants: it lives in app.js
// (window.aiTranscriptNoticeText), read via window, never imported (Pitfall #1).
// Filled each time the panel opens, so it reflects the signed-in account; empty
// (and hidden) on demo, which records nothing.
function renderDealDeskTranscriptNotice() {
  const el = document.getElementById('crm-dealdesk-transcript-notice');
  if (!el) return;
  const t = typeof window.aiTranscriptNoticeText === 'function'
    ? window.aiTranscriptNoticeText()
    : 'Questions you ask here are saved for 90 days. Your account owner can review them.';
  el.textContent = t || '';
  el.style.display = t ? 'block' : 'none';
}

// TASK-9301 — mirrors ASSISTANT_INPUT_MAX_CHARS in functions/src/index.js (tripwire-checked).
const CRM_DEALDESK_MAX_MESSAGE_CHARS = 5000;
async function sendBotMessage() {
  const input = document.getElementById('crm-dealdesk-input');
  const message = input?.value?.trim();
  if (!message) return;
  if (message.length > CRM_DEALDESK_MAX_MESSAGE_CHARS) {   // TASK-9301 — told plainly, nothing sent, text kept
    window.showToast?.(`Your message is ${Number(message.length).toLocaleString('en-US')} characters — this assistant takes up to ${Number(CRM_DEALDESK_MAX_MESSAGE_CHARS).toLocaleString('en-US')}. Nothing was sent. Shorten it or split it into two messages.`, 'error', null, 12000);
    return;
  }
  input.value = '';

  // Add user message to chat
  CRMState.botHistory.push({ role: 'user', content: message });
  appendBotMessage('user', message);

  // Show typing indicator
  const typingId = appendBotMessage('bot', '<span class="crm-dealdesk-typing"><span>.</span><span>.</span><span>.</span></span>');

  try {
    // Build CRM context — includes company profile for document generation
    const td = window.AppState?.tenantData || {};
    // Detect analytical intent for enriched context
    const msgLower = message.toLowerCase();
    const analyticalKeywords = ['who should i call', 'who to call', 'priorities', 'pipeline health', 'health check',
      'time sink', 'wasting', 'cut list', 'no roi', 'stale', 'neglect', 'dropped', 'forgot', 'missed',
      'forecast', 'revenue forecast', 'close forecast', 'weekly', 'recap', 'summary', 'digest',
      'probability', 'odds', 'chances', 'analyze', 'analysis', 'insight', 'how healthy', 'how is my',
      'what am i', 'how much', 'report', 'performance', 'score', 'grade'];
    const isAnalytical = analyticalKeywords.some(k => msgLower.includes(k));

    // Build base CRM context
    const crmContext = {
      companyProfile: {
        name: td.companyName || td.name || '',
        phone: td.companyPhone || td.phone || '',
        email: td.companyEmail || td.email || '',
        address: td.companyAddress || td.address || ''
      },
      customers: CRMState.customers.slice(0, 50).map(c => ({ id: c.id, name: c.name, status: c.status, type: c.type || 'customer' })),
      opportunities: CRMState.allOpportunities.slice(0, 50).map(o => {
        const base = { id: o.id, customerId: o.customerId, customerName: o.customerName, title: o.title, stage: o.stage, revenue: o.revenue };
        if (isAnalytical) {
          const now = Date.now();
          const createdAt = o.createdAt?.toDate ? o.createdAt.toDate() : (o.createdAt ? new Date(o.createdAt) : null);
          const updatedAt = o.updatedAt?.toDate ? o.updatedAt.toDate() : (o.updatedAt ? new Date(o.updatedAt) : null);
          const lastActivity = o._lastActivityDate?.toDate ? o._lastActivityDate.toDate() : (o._lastActivityDate ? new Date(o._lastActivityDate) : null);
          base.daysSinceCreated = createdAt ? Math.floor((now - createdAt.getTime()) / 86400000) : null;
          base.daysSinceUpdated = updatedAt ? Math.floor((now - updatedAt.getTime()) / 86400000) : null;
          base.daysSinceLastActivity = lastActivity ? Math.floor((now - lastActivity.getTime()) / 86400000) : null;
          base.activityCount = o._activityCount || 0;
          base.estCloseDate = o.estCloseDate?.toDate ? o.estCloseDate.toDate().toISOString().split('T')[0] : null;
        }
        return base;
      }),
      stages: OPPORTUNITY_STAGES.map(s => ({ key: s.key, label: s.label, probability: s.probability })),
      recentActivities: CRMState.activities.slice(0, 50).map(a => {
        const customerName = CRMState.customers.find(c => c.id === a.customerId)?.name || '';
        const createdAt = a.createdAt?.toDate ? a.createdAt.toDate().toISOString() : null;
        return {
          type: a.type,
          subject: a.subject,
          customerName,
          createdAt,
          outcome: a.outcome || null,
          duration: a.duration || null,
          createdBy: a.createdBy || null
        };
      }),
      pendingTasks: CRMState.crmTasks.filter(t => t.status === 'pending').slice(0, 20).map(t => ({
        id: t.id, title: t.title, taskType: t.taskType || 'followup',
        dueDate: t.dueDate?.toDate ? t.dueDate.toDate().toISOString().split('T')[0] : '',
        customerName: t.customerName || '', customerId: t.customerId || ''
      }))
    };

    // Add pre-computed analytics summary for analytical queries
    if (isAnalytical) {
      const now = Date.now();
      const activeOpps = CRMState.allOpportunities.filter(o => o.stage !== 'won' && o.stage !== 'lost');
      const wonOpps = CRMState.allOpportunities.filter(o => o.stage === 'won');
      const lostOpps = CRMState.allOpportunities.filter(o => o.stage === 'lost');
      const completedTasks = CRMState.crmTasks.filter(t => t.status === 'completed');
      const pendingTasks = CRMState.crmTasks.filter(t => t.status === 'pending');
      const overdueTasks = pendingTasks.filter(t => {
        const due = t.dueDate?.toDate ? t.dueDate.toDate() : new Date(t.dueDate);
        return due < new Date();
      });

      crmContext.analyticsSummary = {
        totalCustomers: CRMState.customers.length,
        activeOpportunities: activeOpps.length,
        wonDeals: wonOpps.length,
        lostDeals: lostOpps.length,
        totalPipelineValue: activeOpps.reduce((sum, o) => sum + (o.revenue || 0), 0),
        weightedPipelineValue: activeOpps.reduce((sum, o) => {
          const stageInfo = OPPORTUNITY_STAGES.find(s => s.key === o.stage);
          return sum + (o.revenue || 0) * ((stageInfo?.probability || 0) / 100);
        }, 0),
        wonRevenue: wonOpps.reduce((sum, o) => sum + (o.revenue || 0), 0),
        taskStats: { completed: completedTasks.length, pending: pendingTasks.length, overdue: overdueTasks.length },
        staleDeals: activeOpps.filter(o => {
          const lastAct = o._lastActivityDate?.toDate ? o._lastActivityDate.toDate() : null;
          return !lastAct || (now - lastAct.getTime()) > 7 * 86400000;
        }).length
      };
    }

    const token = await auth.currentUser?.getIdToken();
    const functionUrl = `https://us-central1-dispatch-deck-be88c.cloudfunctions.net/crmAssistant`;
    const resp = await fetch(functionUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ data: { tenantId: CRMState.tenantId, message, history: CRMState.botHistory.slice(-20), crmContext } })
    });

    const result = await resp.json();
    removeBotMessage(typingId);

    if (result.error) {
      appendBotMessage('bot', `Error: ${result.error}`);
      return;
    }

    const response = result.result || result.data || result;
    CRMState.botHistory.push({ role: 'assistant', content: response.message });

    if (response.type === 'document' && response.action?.fields?.content) {
      appendBotMessage('bot', response.message);
      showBotGeneratedDocument(response.action.fields);
    } else if (response.type === 'multi_action' && response.actions && response.actions.length > 0) {
      appendBotMessage('bot', response.message);
      showBotMultiConfirmation(response.actions);
    } else if (response.type === 'action' && response.action) {
      // Check if it's a GENERATE_DOCUMENT action returned as 'action' type
      if (response.action.type === 'GENERATE_DOCUMENT' && response.action.fields?.content) {
        appendBotMessage('bot', response.message);
        showBotGeneratedDocument(response.action.fields);
      } else {
        appendBotMessage('bot', response.message);
        showBotConfirmation(response.action);
      }
    } else if (response.type === 'query_result' && response.data) {
      appendBotMessage('bot', response.message);
      // Show grade badge for pipeline health scorecards
      if (response.data.grade) {
        const gradeColors = { A: '#10B981', B: '#3B82F6', C: '#F59E0B', D: '#F97316', F: '#EF4444' };
        const color = gradeColors[response.data.grade] || '#94A3B8';
        appendBotMessage('bot', `<div class="crm-dealdesk-grade" style="display:inline-flex;align-items:center;gap:8px;padding:8px 16px;border-radius:12px;background:${color}15;border:1px solid ${color}40;margin:4px 0;"><span style="font-size:28px;font-weight:700;color:${color};">${escHTML(response.data.grade)}</span><span style="font-size:13px;color:var(--text-secondary);">Pipeline Health Grade</span></div>`);
      }
      if (response.data.table) appendBotTable(response.data.table);
      if (response.data.bars) appendBotBars(response.data.bars);
      // Show disclaimer for analytical queries
      if (response.data.disclaimer) {
        appendBotMessage('bot', `<div style="font-size:11px;color:var(--text-secondary);font-style:italic;margin-top:4px;padding:4px 8px;border-left:2px solid var(--border-light);">${escHTML(response.data.disclaimer)}</div>`);
      }
    } else {
      appendBotMessage('bot', response.message || 'No response.');
    }
  } catch (err) {
    console.error('[CRM] Deal Desk error:', err);
    removeBotMessage(typingId);
    appendBotMessage('bot', 'Something went wrong. Please try again.');
  }
}

function appendBotMessage(role, html) {
  const container = document.getElementById('crm-dealdesk-messages');
  if (!container) return;
  const id = 'msg-' + Date.now() + Math.random().toString(36).substr(2, 4);
  const div = document.createElement('div');
  div.className = `crm-dealdesk-msg ${role}`;
  div.id = id;
  div.innerHTML = html;
  container.appendChild(div);
  container.scrollTop = container.scrollHeight;
  return id;
}

function removeBotMessage(id) {
  document.getElementById(id)?.remove();
}

function showBotGeneratedDocument(fields) {
  const container = document.getElementById('crm-dealdesk-messages');
  if (!container) return;
  const saveId = 'crm-save-doc-' + Date.now();

  const div = document.createElement('div');
  div.className = 'crm-dealdesk-msg bot';
  div.style.padding = '0';
  div.style.overflow = 'hidden';
  div.style.borderRadius = '12px';
  div.innerHTML = `
    <div style="background:linear-gradient(135deg,#F59E0B20,#D9770610);padding:8px 12px;display:flex;align-items:center;gap:6px;border-bottom:1px solid #F59E0B30;font-size:0.75rem;color:#92400E;">
      <span class="material-icons" style="font-size:14px;">warning_amber</span>
      AI-generated draft — review carefully. Not legal advice.
    </div>
    <div style="padding:10px 12px;max-height:300px;overflow-y:auto;white-space:pre-wrap;font-size:0.8rem;line-height:1.5;background:var(--bg-primary);">${escHTML(fields.content)}</div>
    <div style="padding:8px 12px;border-top:1px solid var(--border-light);display:flex;gap:6px;flex-wrap:wrap;background:var(--bg-primary);">
      <button id="${saveId}" class="btn btn-primary" style="font-size:0.75rem;padding:4px 12px;display:inline-flex;align-items:center;gap:4px;" onclick="window.CRMModule.saveBotGeneratedDoc(this)">
        <span class="material-icons" style="font-size:14px;">save</span> Save to CRM
      </button>
      <button class="btn btn-secondary" style="font-size:0.75rem;padding:4px 12px;display:inline-flex;align-items:center;gap:4px;" onclick="window.CRMModule.copyBotDoc(this)">
        <span class="material-icons" style="font-size:14px;">content_copy</span> Copy
      </button>
    </div>
  `;
  container.appendChild(div);
  container.scrollTop = container.scrollHeight;

  // Stash content on the save button
  const saveBtn = document.getElementById(saveId);
  if (saveBtn) {
    saveBtn._docTitle = fields.title || 'Generated Document';
    saveBtn._docContent = fields.content;
    saveBtn._customerId = fields.customerId || CRMState.selectedCustomer?.id || null;
    saveBtn._customerName = fields.customerName || CRMState.selectedCustomer?.name || null;
  }
}

async function saveBotGeneratedDoc(btn) {
  if (!btn || !btn._docContent) return;
  const content = btn._docContent;
  const title = btn._docTitle;
  const customerId = btn._customerId;

  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner"></span> Saving...';

  try {
    // Cap: max 25 generated docs per tenant to prevent storage abuse
    const tenantId = CRMState.tenantId;
    const genDocsSnap = await getDocs(query(collection(db, 'tenants', tenantId, 'crmDocuments'), where('generated', '==', true)));
    if (genDocsSnap.size >= 25) {
      window.showToast('Generated document limit reached (25). Delete some to save more.', 'error');
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons" style="font-size:14px;">save</span> Save to CRM';
      return;
    }

    const timestamp = Date.now();
    const safeName = title.replace(/[^a-zA-Z0-9 ._-]/g, '').replace(/\s+/g, '_').substring(0, 60);
    const fileName = `${safeName}.txt`;
    const storagePath = `tenants/${tenantId}/crm/documents/${timestamp}_${fileName}`;
    const storageRef = ref(storage, storagePath);

    const blob = new Blob([content], { type: 'text/plain' });
    await uploadBytes(storageRef, blob, { contentType: 'text/plain' });
    const url = await getDownloadURL(storageRef);

    // Save to customer documents subcollection if customer is selected, otherwise to tenant-level CRM docs
    const docData = {
      title,
      fileName,
      url,
      storagePath,
      mimeType: 'text/plain',
      sizeBytes: blob.size,
      generated: true,
      createdAt: Timestamp.now(),
      createdBy: window.AppState?.userName || 'Deal Desk'
    };

    if (customerId) {
      await addDoc(collection(db, 'tenants', tenantId, 'customers', customerId, 'documents'), docData);
    } else {
      // Tenant-level CRM documents
      await addDoc(collection(db, 'tenants', tenantId, 'crmDocuments'), docData);
    }

    btn.innerHTML = '<span class="material-icons" style="font-size:14px;">check</span> Saved';
    btn.classList.remove('btn-primary');
    btn.style.background = '#059669';
    btn.style.color = '#fff';
    window.showToast(`Document saved${customerId ? ' to customer' : ''}`, 'success');
  } catch (err) {
    console.error('[CRM] Save generated doc error:', err);
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons" style="font-size:14px;">save</span> Save to CRM';
    window.showToast('Failed to save document', 'error');
  }
}

function copyBotDoc(btn) {
  const container = btn.closest('.crm-dealdesk-msg');
  const contentDiv = container?.querySelector('div[style*="pre-wrap"]');
  if (!contentDiv) return;

  navigator.clipboard.writeText(contentDiv.textContent).then(() => {
    const origHTML = btn.innerHTML;
    btn.innerHTML = '<span class="material-icons" style="font-size:14px;">check</span> Copied';
    setTimeout(() => { btn.innerHTML = origHTML; }, 2000);
  }).catch(() => {
    window.showToast('Copy failed', 'error');
  });
}

function formatBotActionSummary(action) {
  const f = action.fields || {};
  switch (action.type) {
    case 'CREATE_TASK': {
      const icon = f.taskType === 'call' ? 'phone' : f.taskType === 'email' ? 'email' : f.taskType === 'meeting' ? 'groups' : 'task_alt';
      const due = f.dueDate ? new Date(f.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
      return `<span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">${icon}</span><strong>${escHTML(f.title || 'Task')}</strong>${f.customerName ? ` · ${escHTML(f.customerName)}` : ''}${due ? ` · Due ${due}` : ''}`;
    }
    case 'CREATE_ACTIVITY': {
      const icon = f.type === 'call' ? 'phone' : f.type === 'email' ? 'email' : f.type === 'meeting' ? 'groups' : 'edit_note';
      return `<span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">${icon}</span>Log <strong>${escHTML(f.type || 'activity')}</strong>${f.subject ? ` — ${escHTML(f.subject)}` : ''}${f.customerName ? ` · ${escHTML(f.customerName)}` : ''}`;
    }
    case 'CREATE_OPPORTUNITY':
      return `<span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">developer_board</span><strong>${escHTML(f.title || 'Opportunity')}</strong>${f.revenue ? (_botMoneyNumber(f.revenue) == null ? ' · amount unreadable' : ` · $${_botMoneyNumber(f.revenue).toLocaleString()}`) : ''}${f.customerName ? ` · ${escHTML(f.customerName)}` : ''}`;
    case 'CREATE_CUSTOMER':
      return `<span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">person_add</span>Add <strong>${escHTML(f.name || 'Customer')}</strong>${f.status ? ` (${escHTML(f.status)})` : ''}${f.notes ? ` — ${escHTML(f.notes)}` : ''}`;
    case 'UPDATE_OPPORTUNITY':
    case 'UPDATE_CUSTOMER': {
      const updates = f.fieldsToUpdate ? Object.keys(f.fieldsToUpdate).join(', ') : '';
      return `<span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">edit</span>Update <strong>${escHTML(f.customerName || f.title || 'record')}</strong>${updates ? ` — ${escHTML(updates)}` : ''}`;
    }
    case 'CREATE_CONTACT':
      return `<span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:4px;">person_add</span>Add contact <strong>${escHTML(f.name || '')}</strong>${f.customerName ? ` to ${escHTML(f.customerName)}` : ''}`;
    default: {
      const label = action.type.replace(/_/g, ' ');
      const name = f.title || f.name || f.subject || '';
      return `<strong>${escHTML(label)}</strong>${name ? ` — ${escHTML(name)}` : ''}`;
    }
  }
}

// TASK-9585 S10b — the spine's "one action per confirmation when money is
// involved; never bundle several changes into a single yes" (spine.js
// AI_GUARDRAILS_PROMPT), enforced on the Deal Desk confirm card. Writes happen
// here in the browser (executeBotAction), so this card IS the gate.
// HIGH stakes on a Deal Desk action (design §8 fix 10 + aiActionPolicy.js
// escalate keys for create_customer / save_contact):
//   money          — an opportunity revenue, billing terms, or any rate / price /
//                    amount style field in an update
//   customer_reach — a phone number or email on a new customer / contact, or a
//                    changed one on an update
// Deal Desk has no hours / delete / compliance actions.
// TASK-9585 S6c: + surcharge|detention|fsc. proposalGuard DEAL_DESK_MONEY_KEY_RE is kept equal (tested).
const BOT_MONEY_FIELD_RE = /revenue|rate|price|amount|cost|billing|terms|credit|tax|invoice|discount|fee|value|pay|deposit|markup|margin|budget|total|dollar|surcharge|detention|fsc|\$/i;
const BOT_REACH_FIELD_RE = /phone|email|mobile|cell|sms/i;
// FAIL CLOSED (COD r3): an update key NOT on this list gets its own yes even
// when the money regex misses it (a rate under `ppm`, `fsc`, a nested
// `bidLines[].rate`, an internal `_contractCount`). Only these plain record
// fields may ride in a records-only "Approve All".
const BOT_LOW_STAKES_UPDATE_KEYS = {
  UPDATE_OPPORTUNITY: ['title', 'stage', 'notes', 'estCloseDate', 'customerName'],
  UPDATE_CUSTOMER: ['name', 'status', 'type', 'address', 'notes', 'customerName']
};

function _botFieldSet(v) {
  if (v === undefined || v === null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (typeof v === 'number') return !Number.isNaN(v); // an explicit 0 is a value (COD r3)
  return true;
}

function classifyBotActionStakes(action) {
  const f = (action && action.fields) || {};
  const out = [];
  const type = action && action.type;
  const isUpdate = type === 'UPDATE_OPPORTUNITY' || type === 'UPDATE_CUSTOMER';
  let written;
  if (isUpdate) {
    written = (f.fieldsToUpdate && typeof f.fieldsToUpdate === 'object') ? f.fieldsToUpdate : {};
  } else if (type === 'CREATE_OPPORTUNITY') {
    written = { revenue: f.revenue };
  } else if (type === 'CREATE_CUSTOMER') {
    written = { billingTerms: f.billingTerms, phone: f.phone, email: f.email };
  } else if (type === 'CREATE_CONTACT') {
    written = { phone: f.phone, email: f.email };
  } else {
    written = {};
  }
  // An update that names a money / contact key counts even when it CLEARS the
  // value (clearing a rate is a money change too).
  const keys = Object.keys(written);
  if (keys.some(k => BOT_MONEY_FIELD_RE.test(k) && (isUpdate || _botFieldSet(written[k])))) out.push('money');
  if (keys.some(k => BOT_REACH_FIELD_RE.test(k) && (isUpdate || _botFieldSet(written[k])))) out.push('customer_reach');
  if (isUpdate && !out.length) {
    const low = BOT_LOW_STAKES_UPDATE_KEYS[type] || [];
    if (keys.some(k => !low.includes(k))) out.push('unlisted');
  }
  return out;
}

// One step = one yes. Any high-stakes action in the batch → every action gets
// its own step, in order (design §8 fix 10: "crm.js steps through them, one
// confirm each"; order kept so a CREATE_CUSTOMER still lands before the
// PENDING_CUSTOMER contact / task that hangs off it). A batch of only
// low-stakes record actions (activities, tasks, a customer / contact with no
// phone, email or terms) stays one confirm, with every action read back.
function planBotBatch(actions) {
  const list = Array.isArray(actions) ? actions.filter(a => a && a.type) : [];
  const anyHigh = list.some(a => classifyBotActionStakes(a).length > 0);
  if (!anyHigh) return list.length ? [{ actions: list, stakes: [] }] : [];
  return list.map(a => ({ actions: [a], stakes: classifyBotActionStakes(a) }));
}

// What the card will write, field by field, so the yes is for the exact
// values (spine: read it back). Values are escaped; long text is cut.
// An UPDATE writes its whole fieldsToUpdate (executeBotAction spreads it), so
// EVERY key is shown — no row cap, and an empty / zero value is shown as
// "(cleared)" / 0, never hidden (COD r1 Adversary: a hidden key could carry
// the money change). A create shows the fields it sets, plus the billing
// terms default executeBotAction writes for a new customer.
// The fields executeBotAction actually stores for each create (anything else
// the model sends is dropped there, so it is not shown here — COD r4).
const BOT_CREATE_WRITTEN_KEYS = {
  CREATE_OPPORTUNITY: ['title', 'stage', 'revenue', 'estCloseDate', 'notes', 'customerName'],
  CREATE_ACTIVITY: ['type', 'subject', 'notes', 'opportunityTitle'],
  CREATE_CUSTOMER: ['name', 'type', 'status', 'phone', 'email', 'address', 'billingTerms', 'notes'],
  CREATE_CONTACT: ['name', 'title', 'phone', 'email', 'notes'],
  CREATE_TASK: ['title', 'description', 'dueDate', 'priority', 'taskType', 'customerName', 'contactName', 'opportunityTitle']
};

function formatBotActionDetail(action) {
  const f = (action && action.fields) || {};
  const isUpdate = action.type === 'UPDATE_OPPORTUNITY' || action.type === 'UPDATE_CUSTOMER';
  const src = isUpdate
    ? Object.assign({}, (f.fieldsToUpdate && typeof f.fieldsToUpdate === 'object') ? f.fieldsToUpdate : {})
    : Object.assign({}, f);
  if (action.type === 'CREATE_CUSTOMER' && !_botFieldSet(f.billingTerms)) src.billingTerms = 'Due on Receipt (default)';
  // Show what executeBotAction will actually store (COD r2 Adversary).
  if (/_OPPORTUNITY$/.test(action.type) && (_botFieldSet(src.revenue) || (isUpdate && 'revenue' in src))) {
    const n = _botFieldSet(src.revenue) ? _botMoneyNumber(src.revenue) : '0 (cleared)';
    src.revenue = n == null ? `${src.revenue} (can't read this amount, so it won't be saved)` : n;
  }
  // TASK-9585 S6c — the server (crmAssistant → proposalGuard.flagDealDeskActions)
  // lists the fields the user's own text does not support in
  // `action.unsupported`. Those rows are painted amber with a short hint; the
  // value is still shown and still written on Approve (a flag, not a block).
  const flagged = Object.create(null);
  (Array.isArray(action.unsupported) ? action.unsupported : []).forEach(u => {
    if (u && typeof u.field === 'string' && u.field && !flagged[u.field]) flagged[u.field] = u;
  });
  const flagHint = (u) => u.reason === 'instruction_text'
    ? 'only in a line telling the assistant what to do — check it'
    : u.reason === 'not_in_crm' ? (/^opportunity/.test(u.field) ? 'not a deal in your CRM — check it' : 'not a customer in your CRM — check it')
    : u.reason === 'unchecked' ? 'could not be checked against your text — check every field'
    : 'not in the text — check it';
  const cut = (v) => (v.length > 140 ? v.slice(0, 140) + '…' : v);
  const row = (k, v, u) => u
    ? `<div class="crm-dealdesk-unsupported" style="display:flex;gap:6px;flex-wrap:wrap;background:#FFFBEB;border-left:3px solid #F59E0B;border-radius:4px;padding:2px 6px;margin:1px 0;"><span style="color:#92400E;min-width:90px;">${escHTML(k)}</span><span style="color:#1F2937;word-break:break-word;">${escHTML(v)}</span><span style="color:#92400E;">— ${escHTML(flagHint(u))}</span></div>`
    : `<div style="display:flex;gap:6px;"><span style="color:var(--text-secondary);min-width:96px;">${escHTML(k)}</span><span style="color:var(--text-primary);word-break:break-word;">${escHTML(v)}</span></div>`;
  const shown = Object.create(null);
  const rows = Object.keys(src)
    .filter(k => isUpdate || ((BOT_CREATE_WRITTEN_KEYS[action.type] || Object.keys(src)).includes(k) && _botFieldSet(src[k])))
    .map(k => {
      let v = src[k];
      if (v === undefined || v === null || (typeof v === 'string' && v.trim() === '')) v = '(cleared)';
      else if (typeof v === 'object') { try { v = JSON.stringify(v); } catch (_) { v = String(v); } }
      shown[k] = cut(String(v));
      return row(k, shown[k], flagged[k]);
    });
  if (action.type === 'UPDATE_OPPORTUNITY' && src.stage) {
    rows.push(`<div style="display:flex;gap:6px;"><span style="color:var(--text-secondary);min-width:96px;">probability</span><span style="color:var(--text-primary);">set from the new stage</span></div>`);
  }
  // A flag on something that is not a row of its own (the customer or
  // opportunity the action is attached to) still gets an amber row, first.
  // Only those three: a flag on a field this card does not write is not shown (COD r14).
  Object.keys(flagged).reverse().forEach(k => {
    if (!(k in shown) && (k === 'customer' || k === 'opportunity' || k === 'all fields')) rows.unshift(row(k, cut(String(flagged[k].value ?? '')), flagged[k]));
  });
  if (!rows.length) return '';
  // An amount the text does not support is called out on its own, above the
  // rows and outside their scroll area, so it is read before the yes.
  // `money: true` = the server read it as an amount (a nested rate too).
  const money = Object.keys(flagged).filter(k => flagged[k].money === true || BOT_MONEY_FIELD_RE.test(k));
  const callout = money.length
    ? `<div class="crm-dealdesk-money-unsupported" role="status" style="display:flex;gap:6px;align-items:flex-start;background:#FFFBEB;border:1px solid #FCD34D;border-left:4px solid #F59E0B;border-radius:6px;padding:6px 8px;margin:6px 0 0 20px;font-size:0.75rem;color:#1F2937;"><span class="material-icons" style="font-size:16px;color:#D97706;">error_outline</span><span><strong>Check the amount.</strong> ${money.map(k => `${escHTML(k)} ${escHTML(k in shown ? shown[k] : cut(String(flagged[k].value ?? '')))}`).join(', ')} — ${escHTML(flagHint(flagged[money[0]]))}</span></div>`
    : '';
  return `${callout}<div class="crm-dealdesk-confirm-detail" style="font-size:0.75rem;margin:4px 0 0 20px;max-height:160px;overflow-y:auto;">${rows.join('')}</div>`;
}

function showBotConfirmation(action) {
  _botConfirmGen++;
  CRMState.pendingBotAction = action;
  CRMState.pendingBotActions = null;
  const panel = document.getElementById('crm-dealdesk-confirm');
  if (!panel) return;

  const summary = formatBotActionSummary(action) + formatBotActionDetail(action);

  panel.innerHTML = `
    <div style="font-size:0.8125rem;margin-bottom:8px;">${summary}</div>
    <div style="display:flex;gap:8px;">
      <button class="btn btn-secondary btn-sm" onclick="window.CRMModule.cancelBotAction()" style="flex:1;border-radius:8px;">Cancel</button>
      <button class="btn btn-primary btn-sm" onclick="window.CRMModule.approveBotAction()" style="flex:1;border-radius:8px;">Approve</button>
    </div>
  `;
  panel.classList.remove('hidden');
}

function cancelBotAction() {
  const batch = CRMState.pendingBotActions;
  // TASK-9585 S10b — a stepped batch: "Skip" declines THIS step only. Nothing
  // is written for it; the next step still needs its own yes.
  if (batch && Array.isArray(batch.steps) && batch.steps.length > 1) {
    const step = batch.steps[batch.i];
    if (step) {
      batch.declined.push(...step.actions);
      // A PENDING_CUSTOMER after this point means THIS customer, which was not
      // added — never an earlier one (COD r2 Code Reviewer).
      if (step.actions.some(a => a.type === 'CREATE_CUSTOMER')) batch.lastCreatedCustomer = null;
      appendBotMessage('bot', `Skipped: ${escHTML(step.actions.map(_botActionLabel).join(', '))}. Nothing was saved for it.`);
    }
    batch.i++;
    _advanceBotBatch(batch);
    return;
  }
  CRMState.pendingBotAction = null;
  CRMState.pendingBotActions = null;
  const panel = document.getElementById('crm-dealdesk-confirm');
  if (panel) panel.classList.add('hidden');
  appendBotMessage('bot', 'Action cancelled.');
}

async function approveBotAction() {
  const action = CRMState.pendingBotAction;
  if (!action) return;

  const panel = document.getElementById('crm-dealdesk-confirm');
  if (panel) panel.classList.add('hidden');
  CRMState.pendingBotAction = null;

  try {
    await executeBotAction(action);
    appendBotMessage('bot', 'Done! The data has been updated.');
    CRMState.allOpportunitiesLoaded = false;
  } catch (err) {
    console.error('[CRM] Bot action error:', err);
    appendBotMessage('bot', `Error executing action: ${escHTML(err.message)}`);
  }
}

// TASK-10056 — an amount as a number ("$48,000", "48k", "$1.2M"), or null
// when the string is not JUST an amount ("TBD", "$500/bbl", "($500)"). Same
// reading as the server's proposalGuard _moneyValue, except an overflow (parity test:
// tests/task10056-dealdesk-revenue-parse.unit.cjs).
function _botMoneyNumber(v) {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;
  const m = /^\s*(-)?\s*\$?\s*(-)?\s*(\d{1,3}(?:,\d{3})+|\d+)(\.\d+)?\s*(k|mm|m|million|thousand|grand)?(?![a-z0-9])\s*$/i.exec(v.replace(/(^|[^\d])\.(\d)/g, '$10.$2'));
  if (!m) return null;
  const g = (m[5] || '').toLowerCase();
  const n = (m[1] || m[2] ? -1 : 1) * parseFloat(m[3].replace(/,/g, '') + (m[4] || '')) * (/^(k|thousand|grand)$/.test(g) ? 1e3 : g ? 1e6 : 1);
  const r = Math.round(n * 1e6) / 1e6;
  return Number.isFinite(r) ? r : null; // "9999…" overflows to Infinity — refused
}

async function executeBotAction(action) {
  const t = CRMState.tenantId;
  const fields = action.fields || {};
  // TASK-10056: a revenue that is set but is not an amount ("TBD") refuses
  // this action — it is never stored as 0. No revenue at all is 0, as before.
  const readRevenue = (r) => {
    if (r == null || (typeof r === 'string' && !r.trim())) return 0;
    const n = _botMoneyNumber(r);
    if (n == null) throw new Error("Couldn't read the amount — enter it on the opportunity");
    return n;
  };
  // TASK-9585 S10b (COD r5): the ids come from the model. A "/" would point
  // the write at a different document than the card named, so only a single
  // Firestore id segment is accepted.
  for (const k of ['customerId', 'opportunityId']) {
    const v = fields[k];
    if (v !== undefined && v !== null && v !== '' && (typeof v !== 'string' || v.includes('/') || v === '.' || v === '..')) {
      throw new Error(`Invalid ${k}`);
    }
  }

  switch (action.type) {
    case 'CREATE_OPPORTUNITY': {
      const cid = fields.customerId;
      if (!cid) throw new Error('No customer specified');
      const revenue = readRevenue(fields.revenue);
      const stage = STAGE_MAP[fields.stage || 'initiating'] || STAGE_MAP['initiating'];
      await addDoc(collection(db, `tenants/${t}/customers/${cid}/opportunities`), {
        title: fields.title || 'Untitled',
        stage: stage.key,
        probability: stage.probability,
        revenue,
        assets: [], contacts: [],
        estCloseDate: fields.estCloseDate ? Timestamp.fromDate(new Date(fields.estCloseDate)) : null,
        showOnDispatch: false,
        notes: fields.notes || '',
        customerName: fields.customerName || '',
        _activityCount: 0, _lastActivityDate: null,
        createdBy: 'deal-desk', createdAt: Timestamp.now(), updatedAt: Timestamp.now()
      });
      break;
    }
    case 'UPDATE_OPPORTUNITY': {
      const cid = fields.customerId, oid = fields.opportunityId;
      if (!cid || !oid) throw new Error('Missing customer or opportunity ID');
      const updates = { ...fields.fieldsToUpdate, updatedAt: Timestamp.now() };
      // Was stored verbatim ("$48,000" as a string) — now the number, or refused.
      if ('revenue' in updates) updates.revenue = readRevenue(updates.revenue);
      if (updates.stage) {
        const s = STAGE_MAP[updates.stage];
        if (s) updates.probability = s.probability;
      }
      await updateDoc(doc(db, `tenants/${t}/customers/${cid}/opportunities/${oid}`), updates);
      break;
    }
    case 'CREATE_ACTIVITY': {
      const cid = fields.customerId;
      if (!cid) throw new Error('No customer specified');
      await addDoc(collection(db, `tenants/${t}/customers/${cid}/activities`), {
        type: fields.type || 'note',
        subject: fields.subject || 'Note from Deal Desk',
        notes: fields.notes || '',
        opportunityId: fields.opportunityId || null,
        opportunityTitle: fields.opportunityTitle || null,
        contactId: null, contactName: null,
        duration: null, outcome: null,
        nextAction: null, nextActionDate: null,
        createdBy: 'deal-desk',
        // TASK-352 Slice 3: per-rep attribution. Deal Desk runs as the
        // signed-in user, so the activeProfile is theirs.
        profileId: CRMState.activeProfile?.id || null,
        createdAt: Timestamp.now()
      });
      break;
    }
    case 'CREATE_CUSTOMER': {
      const docRef = await addDoc(collection(db, `tenants/${t}/customers`), {
        name: fields.name || 'New Customer',
        type: fields.type || 'customer',
        status: fields.status || 'Prospect',
        phone: fields.phone || '', email: fields.email || '',
        address: fields.address || '', billingTerms: fields.billingTerms || 'Due on Receipt',
        notes: fields.notes || '', _contractCount: 0, _siteCount: 0,
        createdAt: Timestamp.now(), updatedAt: Timestamp.now()
      });
      return { createdId: docRef.id, createdName: fields.name || 'New Customer' };
    }
    case 'UPDATE_CUSTOMER': {
      const cid = fields.customerId;
      if (!cid) throw new Error('No customer specified');
      const updates = { ...fields.fieldsToUpdate, updatedAt: Timestamp.now() };
      await updateDoc(doc(db, `tenants/${t}/customers/${cid}`), updates);
      break;
    }
    case 'CREATE_CONTACT': {
      const cid = fields.customerId;
      if (!cid) throw new Error('No customer specified');
      await addDoc(collection(db, `tenants/${t}/customers/${cid}/contacts`), {
        name: fields.name || 'New Contact',
        title: fields.title || '',
        phone: fields.phone || '',
        email: fields.email || '',
        notes: fields.notes || '',
        createdAt: Timestamp.now(), updatedAt: Timestamp.now()
      });
      break;
    }
    case 'CREATE_TASK': {
      const dueDate = fields.dueDate ? new Date(fields.dueDate) : new Date(Date.now() + 86400000);
      dueDate.setHours(9, 0, 0, 0);
      await addDoc(collection(db, `tenants/${t}/crmTasks`), {
        title: fields.title || 'Untitled Task',
        description: fields.description || '',
        dueDate: Timestamp.fromDate(dueDate),
        status: 'pending',
        priority: fields.priority || 'normal',
        taskType: fields.taskType || 'followup',
        customerId: fields.customerId || null,
        customerName: fields.customerName || null,
        contactId: fields.contactId || null,
        contactName: fields.contactName || null,
        opportunityId: fields.opportunityId || null,
        opportunityTitle: fields.opportunityTitle || null,
        snoozedUntil: null,
        snoozeCount: 0,
        completedAt: null,
        createdBy: 'deal-desk',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      break;
    }
    default:
      throw new Error(`Unknown action type: ${action.type}`);
  }
}

function appendBotTable(tableData) {
  if (!tableData || !tableData.headers || !tableData.rows) return;
  const headers = tableData.headers;
  let html = '<div class="crm-dealdesk-cards">';
  tableData.rows.forEach(row => {
    html += '<div class="crm-dealdesk-card">';
    html += `<div class="crm-dealdesk-card-title">${escHTML(String(row[0] || ''))}</div>`;
    html += '<div class="crm-dealdesk-card-details">';
    for (let i = 1; i < row.length; i++) {
      const val = String(row[i] || '');
      if (!val) continue;
      html += `<div class="crm-dealdesk-card-field"><span class="crm-dealdesk-card-label">${escHTML(headers[i] || '')}</span><span class="crm-dealdesk-card-value">${escHTML(val)}</span></div>`;
    }
    html += '</div></div>';
  });
  html += '</div>';
  appendBotMessage('bot', html);
}

function appendBotBars(barsData) {
  if (!barsData || !Array.isArray(barsData)) return;
  const maxVal = Math.max(...barsData.map(b => b.value), 1);
  let html = '<div class="crm-dealdesk-bars">';
  barsData.forEach(bar => {
    const pct = Math.max(2, (bar.value / maxVal) * 100);
    html += `
      <div class="crm-dealdesk-bar-row">
        <span class="crm-dealdesk-bar-label">${escHTML(bar.label)}</span>
        <div class="crm-dealdesk-bar-track"><div class="crm-dealdesk-bar-fill" style="width:${pct}%;background:${bar.color || 'var(--brand-blue)'};"></div></div>
        <span class="crm-dealdesk-bar-value">${escHTML(String(bar.display || bar.value))}</span>
      </div>
    `;
  });
  html += '</div>';
  appendBotMessage('bot', html);
}

// Bumped whenever a new Deal Desk confirm card replaces the current one, so a
// batch step still writing when the user gets a new proposal does not take
// the card back over when it finishes (COD r2 Code Reviewer).
let _botConfirmGen = 0;

function _botActionLabel(action) {
  const label = String(action.type || '').replace(/_/g, ' ');
  const name = action.fields?.name || action.fields?.title || action.fields?.subject || action.fields?.customerName || '';
  return name ? `${label}: ${name}` : label;
}

function showBotMultiConfirmation(actions) {
  const steps = planBotBatch(actions);
  CRMState.pendingBotAction = null;
  if (!steps.length) { CRMState.pendingBotActions = null; return; }
  const batch = {
    gen: ++_botConfirmGen, tenantId: CRMState.tenantId, steps, i: 0, total: steps.reduce((n, s) => n + s.actions.length, 0),
    lastCreatedCustomer: null, done: 0, declined: [], skipped: [], errors: []
  };
  _advanceBotBatch(batch);
}

// An action that hangs off a customer created earlier in this batch, when no
// customer was created (declined, failed, or none in the batch): it would write
// under the literal id "PENDING_CUSTOMER", so it is skipped and nothing is written.
function _botActionNeedsMissingCustomer(action, batch) {
  return action.fields?.customerId === 'PENDING_CUSTOMER' && !batch.lastCreatedCustomer;
}

function _renderBotBatchStep(batch) {
  const panel = document.getElementById('crm-dealdesk-confirm');
  if (!panel) return;
  const step = batch.steps[batch.i];
  const n = batch.steps.length;
  let summary;
  let approveLabel;
  let cancelLabel;
  if (n === 1 && step.actions.length > 1) {
    summary = `<strong>Batch Action (${step.actions.length} items)</strong><br><div style="margin:6px 0;">`;
    step.actions.forEach((action, i) => {
      summary += `<div style="padding:3px 0;"><span style="background:var(--brand-blue);color:#fff;border-radius:50%;width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center;font-size:11px;margin-right:6px;">${i + 1}</span>${formatBotActionSummary(action)}${formatBotActionDetail(action)}</div>`;
    });
    summary += '</div>';
    approveLabel = 'Approve All';
    cancelLabel = 'Cancel';
  } else {
    const action = step.actions[0];
    const why = step.stakes.includes('money') ? 'This one sets money, so it gets its own yes.'
      : step.stakes.includes('customer_reach') ? 'This one saves a phone number or email, so it gets its own yes.'
      : step.stakes.includes('unlisted') ? 'This one changes a field that can affect money or the record itself, so it gets its own yes.'
      : n > 1 ? 'Each change in this batch gets its own yes.' : '';
    summary = `<strong>Change ${batch.i + 1} of ${n}</strong>`
      + (why ? `<div style="font-size:0.75rem;color:var(--text-secondary);margin:2px 0 6px;">${why}</div>` : '')
      + `<div>${formatBotActionSummary(action)}${formatBotActionDetail(action)}</div>`;
    approveLabel = 'Approve';
    cancelLabel = n > 1 ? 'Skip' : 'Cancel';
  }
  panel.innerHTML = `
    <div style="font-size:0.8125rem;margin-bottom:8px;">${summary}</div>
    <div style="display:flex;gap:8px;">
      <button class="btn btn-secondary btn-sm" onclick="window.CRMModule.cancelBotAction()" style="flex:1;border-radius:8px;">${cancelLabel}</button>
      <button class="btn btn-primary btn-sm" onclick="window.CRMModule.approveMultiBotAction()" style="flex:1;border-radius:8px;">${approveLabel}</button>
    </div>
  `;
  panel.classList.remove('hidden');
}

// Show the next step that can still be written, or finish the batch.
function _advanceBotBatch(batch) {
  while (batch.i < batch.steps.length) {
    const step = batch.steps[batch.i];
    const blocked = step.actions.filter(a => _botActionNeedsMissingCustomer(a, batch));
    if (blocked.length && blocked.length === step.actions.length && batch.steps.length > 1) {
      batch.skipped.push(...blocked);
      appendBotMessage('bot', `Skipped: ${escHTML(blocked.map(_botActionLabel).join(', '))} — it was for the new customer, which was not added. Nothing was saved for it.`);
      batch.i++;
      continue;
    }
    CRMState.pendingBotActions = batch;
    _renderBotBatchStep(batch);
    return;
  }
  CRMState.pendingBotActions = null;
  const panel = document.getElementById('crm-dealdesk-confirm');
  if (panel) panel.classList.add('hidden');
  if (batch.done === 0 && batch.errors.length === 0 && batch.skipped.length === 0 && batch.declined.length === 0) return;
  if (batch.steps.length > 1 || batch.errors.length || batch.skipped.length) {
    const parts = [`Saved ${batch.done} of ${batch.total}.`];
    if (batch.declined.length) parts.push(`Skipped by you: ${batch.declined.length}.`);
    if (batch.skipped.length) parts.push(`Not saved (no customer to attach to): ${batch.skipped.length}.`);
    if (batch.errors.length) parts.push(`Errors: ${batch.errors.join(', ')}`);
    appendBotMessage('bot', escHTML(parts.join(' ')));
  } else {
    appendBotMessage('bot', `All ${batch.done} actions completed successfully.`);
  }
  CRMState.allOpportunitiesLoaded = false;
}

async function approveMultiBotAction() {
  const batch = CRMState.pendingBotActions;
  if (!batch || !Array.isArray(batch.steps)) return;
  const step = batch.steps[batch.i];
  if (!step) return;

  const panel = document.getElementById('crm-dealdesk-confirm');
  if (panel) panel.classList.add('hidden');
  // A batch is only ever written to the company it was proposed in (COD r4:
  // the CRM can be re-initialised for another tenant while a card is open).
  if (batch.tenantId !== CRMState.tenantId) {
    CRMState.pendingBotActions = null;
    appendBotMessage('bot', 'That proposal was for a different company session. Nothing was saved — ask again.');
    return;
  }
  // Debounce: a second tap while this step writes finds no pending batch.
  CRMState.pendingBotActions = null;

  for (const action of step.actions) {
    // Re-checked before EVERY write: a newer card or a company switch while an
    // earlier write in this step was awaiting stops the rest (COD r5).
    if (batch.gen !== _botConfirmGen || batch.tenantId !== CRMState.tenantId) break;
    try {
      // Resolve PENDING_CUSTOMER from a customer created earlier in this batch
      if (action.fields?.customerId === 'PENDING_CUSTOMER') {
        if (!batch.lastCreatedCustomer) {
          batch.skipped.push(action);
          continue;
        }
        action.fields.customerId = batch.lastCreatedCustomer.id;
        if (!action.fields.customerName) action.fields.customerName = batch.lastCreatedCustomer.name;
      }
      if (action.type === 'CREATE_CUSTOMER') batch.lastCreatedCustomer = null;
      const result = await executeBotAction(action);
      if (action.type === 'CREATE_CUSTOMER' && result?.createdId) {
        batch.lastCreatedCustomer = { id: result.createdId, name: result.createdName };
      }
      batch.done++;
    } catch (err) {
      console.error('[CRM] Multi-action error:', err);
      batch.errors.push(`${action.type}: ${err.message}`);
    }
  }
  batch.i++;
  if (batch.gen !== _botConfirmGen || batch.tenantId !== CRMState.tenantId) {
    // A newer proposal owns the card now. Report this step; the rest of the
    // old batch is dropped — nothing more is written for it.
    const left = batch.total - batch.done - batch.declined.length - batch.skipped.length - batch.errors.length;
    appendBotMessage('bot', escHTML(`Saved ${batch.done} of ${batch.total} from the earlier batch.${left ? ` ${left} not saved — ask again if you still want them.` : ''}${batch.errors.length ? ` Errors: ${batch.errors.join(', ')}` : ''}`));
    CRMState.allOpportunitiesLoaded = false;
    return;
  }
  _advanceBotBatch(batch);
}

// ============================================
// UTILITIES
// ============================================
function escHTML(str) {
  if (!str) return '';
  const d = document.createElement('div');
  d.textContent = str;
  return d.innerHTML;
}

// Defense-in-depth for tenant-supplied image URLs that we drop into <img src="...">
// in print HTML. Allows http(s), Firebase Storage download URLs, and inline image
// data URIs. Rejects javascript:, file:, non-image data:, and anything malformed.
function sanitizeImageUrl(url) {
  const v = String(url || '').trim();
  if (!v) return '';
  if (/^javascript:/i.test(v)) return '';
  if (/^data:/i.test(v)) {
    return /^data:image\/(png|jpe?g|gif|webp|svg\+xml);base64,/i.test(v) ? v : '';
  }
  if (/^https?:\/\//i.test(v)) return v;
  return '';
}

function escAttr(str) {
  if (!str) return '';
  return str.replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ============================================
// CRM PROFILES (multi-rep support)
// ============================================

// TASK-1619 — client-side mirror of firestore.rules isTenantAdmin (owner or
// admin role; no member doc at all = legacy single-login/founder = admin).
// This is a UI-only convenience gate (hide the ACH/Tax ID section + skip the
// customersSecure fetch for non-admins so they don't eat a permission-denied
// console error) — the REAL boundary is the firestore.rules admin-only read
// on customersSecure. Matches hr.js:_hrIsOwnerOrAdmin exactly (same mirror,
// same collection-family precedent: peopleTax/peopleComp).
function _crmIsTenantAdmin() {
  const m = window.AppState?.memberAccess;
  if (!m) return true; // owner/legacy (no member doc)
  if (['revoked', 'inactive', 'pending'].includes(m.status)) return false;
  return m.role === 'owner' || m.role === 'admin';
}

// Scoping: rep sees only customers assigned to them + unassigned (shared pool).
// Supervisor sees everything. Owner / legacy-admin (no modules field on member
// doc) also sees everything as the unrestricted-access escape hatch.
//
// TASK-352 SECURITY (Slice 1): when activeProfile is null/undefined AND caller
// is NOT owner/legacy-admin, return [] instead of the full list. Previous
// behavior leaked the entire pipeline to TASK-319 members granted the `crm`
// module before they had a crmProfile bridged (Code Reviewer ⚠️ surfaced this
// in cod-reviews/2026-05-22T17-54_crm-team-system-task-319-multi-user-member-access-archit.md).
//
// Owner / legacy-admin detection MUST mirror app.js:checkModuleAccess —
// signed-in user without a `members/{uid}` doc, or with `role === 'owner'`,
// or admin with missing OR empty `modules` array (legacy unrestricted access)
// gets through.
//
// Slice 1 COD Code Reviewer ⚠️ catches addressed:
// (1) `status !== 'active'` is NOT a free pass — pending/revoked members fall
//     through to scoped/empty, not unrestricted.
// (2) Legacy-admin signature matches app.js:14449 — admin with `modules` field
//     missing OR length === 0 is the unrestricted condition.
function _isUnrestrictedCrmCaller() {
  const memberAccess = window.AppState?.memberAccess;
  // No member doc at all → not a TASK-319 multi-user invite → legacy single-
  // login or kiosk → unrestricted (the PIN gate handles per-rep scoping).
  if (!memberAccess) return true;
  // Pending/revoked/never-active members are NOT unrestricted. They get the
  // scoped-or-empty path (i.e. [] when no activeProfile). Other guards
  // (auth listener / auth callable rules) actually deny their requests; this
  // is the defense-in-depth client-side gate.
  if (memberAccess.status !== 'active') return false;
  if (memberAccess.role === 'owner') return true;
  // Legacy admin: `modules` missing OR empty array → full access by design.
  // Mirrors `app.js:14449` exactly so the two access policies don't drift.
  if (memberAccess.role === 'admin'
      && (!Array.isArray(memberAccess.modules) || memberAccess.modules.length === 0)) {
    return true;
  }
  return false;
}

function scopedCustomers(list) {
  const profile = CRMState.activeProfile;
  if (!profile) {
    // No active CRM profile yet. Unrestricted callers (owner / legacy-admin /
    // pre-TASK-319 logins) get the full pipeline; scoped multi-user members
    // get [] until the lazy-init bridge (Slice 2) provisions their profile.
    return _isUnrestrictedCrmCaller() ? (list || []) : [];
  }
  if (profile.role === 'supervisor') return list || [];
  return (list || []).filter(c => !c.assignedTo || c.assignedTo === profile.id);
}

function scopedCrmTasks(list) {
  const profile = CRMState.activeProfile;
  if (!profile) {
    return _isUnrestrictedCrmCaller() ? (list || []) : [];
  }
  if (profile.role === 'supervisor') return list || [];
  return (list || []).filter(t => !t.assignedTo || t.assignedTo === profile.id);
}

function applyActiveProfileScoping() {
  CRMState.customers = scopedCustomers(CRMState._allCustomers || []);
  CRMState.crmTasks = scopedCrmTasks(CRMState._allCrmTasks || []);
  // Clear cross-customer opportunity cache so it reloads against scoped customer list
  CRMState.allOpportunities = [];
  CRMState.allOpportunitiesLoaded = false;
  updateTasksBadge();
  updateComplianceBadges();
  renderActiveTab();
}

// TASK-375 — CRM PIN hashing helpers (hashCrmPin / generateCrmPinSalt) removed.
// No path writes or verifies a per-rep CRM PIN anymore. CRM access is
// identity-scoped via the member + `crm` module.

function subscribeToCrmProfiles() {
  if (!CRMState.tenantId) return;
  if (CRMState.profilesUnsubscriber) { CRMState.profilesUnsubscriber(); CRMState.profilesUnsubscriber = null; }
  const q = collection(db, 'tenants', CRMState.tenantId, 'crmProfiles');
  CRMState.profilesUnsubscriber = onSnapshot(q, (snap) => {
    CRMState.profiles = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(p => p.active !== false);
    CRMState.profilesLoaded = true;
    // Keep activeProfile fields in sync with Firestore (gmailConnected, gmailEmail, gmailSignature)
    if (CRMState.activeProfile) {
      const fresh = CRMState.profiles.find(p => p.id === CRMState.activeProfile.id);
      if (fresh) {
        CRMState.activeProfile.gmailConnected = !!fresh.gmailConnected;
        CRMState.activeProfile.gmailNeedsReconnect = !!fresh.gmailNeedsReconnect; // TASK-1412
        CRMState.activeProfile.gmailEmail = fresh.gmailEmail || null;
        CRMState.activeProfile.gmailSignature = fresh.gmailSignature || '';
        sessionStorage.setItem(`crmProfile_${CRMState.tenantId}`, JSON.stringify(CRMState.activeProfile));
      }
    }
    updateManageTeamButtonVisibility();
    // If modal is open, re-render list
    if (document.getElementById('crm-manage-profiles-modal')) {
      renderCrmProfilesList();
    }
    // Gate check: if profiles exist and no active session, prompt for PIN.
    // maybeShowCrmPinGate is async (Path B reads members/{uid}.crmProfile
    // first) — catch + log to avoid unhandled promise rejections.
    Promise.resolve(maybeShowCrmPinGate()).catch(err => {
      console.warn('[CRM] maybeShowCrmPinGate failed:', err?.message || err);
    });
  }, (err) => {
    console.warn('[CRM Profiles] subscribe error:', err?.message || err);
  });
  CRMState.unsubscribers.push(() => {
    if (CRMState.profilesUnsubscriber) { CRMState.profilesUnsubscriber(); CRMState.profilesUnsubscriber = null; }
  });
}

// Build the activeProfile shape from a crmProfile doc + cache it.
// Single source of truth so cached-session, PIN gate, and TASK-319 auto-link
// stay in sync (previously duplicated at 3 call sites — Code Reviewer catch).
function hydrateActiveProfile(p) {
  if (!p) return;
  // TASK-1412 (COD 2026-09-25) — Path B hands us members/{uid}.crmProfile, a
  // one-time COPY stamped at migration that nothing updates when a Gmail grant
  // dies. The live crmProfiles snapshot has usually landed already (and only
  // re-syncs an activeProfile that existed when it fired), so overlay the live
  // connection fields here or a dead connection reads "Connected" after reload.
  const live = (CRMState.profiles || []).find(x => x.id === p.id);
  if (live) {
    p = {
      ...p,
      gmailConnected: !!live.gmailConnected,
      gmailNeedsReconnect: !!live.gmailNeedsReconnect,
      gmailEmail: live.gmailEmail || null,
      gmailSignature: live.gmailSignature || p.gmailSignature || '',
    };
  }
  CRMState.activeProfile = {
    id: p.id,
    name: p.name,
    email: p.email,
    role: p.role,
    gmailConnected: !!p.gmailConnected,
    gmailNeedsReconnect: !!p.gmailNeedsReconnect, // TASK-1412
    gmailEmail: p.gmailEmail || null,
    gmailSignature: p.gmailSignature || ''
  };
  try {
    sessionStorage.setItem(`crmProfile_${CRMState.tenantId}`, JSON.stringify(CRMState.activeProfile));
  } catch (_) {}
}

// CRM bridge Path B: try to hydrate activeProfile from the member doc's
// crmProfile sub-field (post-migration steady state). Returns true if the
// member doc had a crmProfile and activeProfile was set; false otherwise
// (caller continues to Path A email-match fallback).
//
// Schema (written by functions/src/index.js:migrateCrmProfileToMember):
//   members/{uid}.crmProfile = {
//     name, email, role, gmailConnected, gmailEmail, gmailSignature,
//     profileId,    // breadcrumb — also used to reconstruct activeProfile.id
//     migratedAt
//   }
async function tryHydrateFromMemberDoc() {
  const uid = auth.currentUser?.uid;
  if (!uid || !CRMState.tenantId) return false;
  try {
    const memberSnap = await getDoc(doc(db, 'tenants', CRMState.tenantId, 'members', uid));
    if (!memberSnap.exists()) return false;
    const cp = memberSnap.data()?.crmProfile;
    if (!cp || !cp.profileId) return false;
    // Map members/{uid}.crmProfile → activeProfile shape (id = profileId, so
    // subscribeToCrmProfiles onSnapshot can keep gmailConnected/Email/Signature
    // fresh from the live legacy crmProfiles/{profileId} doc by id-match).
    hydrateActiveProfile({
      id: cp.profileId,
      name: cp.name,
      email: cp.email,
      role: cp.role,
      gmailConnected: !!cp.gmailConnected,
      gmailEmail: cp.gmailEmail || null,
      gmailSignature: cp.gmailSignature || ''
    });
    return true;
  } catch (err) {
    console.warn('[CRM Path B] tryHydrateFromMemberDoc failed, falling back:', err?.message || err);
    return false;
  }
}

// Fire-and-forget: call the server-side migrator to copy the legacy
// crmProfiles/{profileId} payload into the caller's members/{uid}.crmProfile.
// Idempotent server-side (returns alreadyMigrated:true if no-op), so safe to
// re-fire on every successful email-match auto-link until the member doc
// catches up.
async function maybeTriggerCrmProfileMigration(profileId) {
  try {
    const uid = auth.currentUser?.uid;
    if (!uid || !CRMState.tenantId || !profileId) return;
    const memberAccess = window.AppState?.memberAccess;
    if (!memberAccess || memberAccess.status !== 'active') return;
    const fn = httpsCallable(functions, 'migrateCrmProfileToMember');
    const res = await fn({ tenantId: CRMState.tenantId, profileId, memberUid: uid });
    if (res?.data && !res.data.alreadyMigrated) {
      console.info('[CRM Path B] migrated crmProfile -> members/{uid}.crmProfile', { profileId });
    }
  } catch (err) {
    // Non-fatal — user already has activeProfile from email-match. Next page
    // load will re-attempt via tryHydrateFromMemberDoc.
    console.warn('[CRM Path B] auto-migration failed (non-fatal):', err?.message || err);
  }
}

async function maybeShowCrmPinGate() {
  if (!CRMState.profilesLoaded) return;
  if (CRMState.activeProfile) return;

  // TASK-352 Slice 1: tenant has zero crmProfiles. Previously: bare-return,
  // dropping the user into unscoped visibility via scopedCustomers's old
  // `if !profile return list` branch. Now scopedCustomers gates on
  // _isUnrestrictedCrmCaller. We still want to bridge restricted callers into
  // a profile via the auto-link prompt so they don't sit on an empty CRM view.
  if ((CRMState.profiles || []).length === 0) {
    // Restricted multi-user member opening the CRM for the first time on a
    // tenant with zero crmProfiles → silently provision them via the lazy-init
    // bridge (TASK-352 Slice 2). Active members only — pending/revoked never
    // get auto-provisioned (Code Reviewer ⚠️ concern 3).
    const memberAccess = window.AppState?.memberAccess;
    const isActiveRestrictedMember = !!(memberAccess
      && memberAccess.status === 'active'
      && !_isUnrestrictedCrmCaller());
    if (isActiveRestrictedMember && (auth.currentUser?.email || '')) {
      if (!CRMState._lazyBridgeInFlight) {
        CRMState._lazyBridgeInFlight = true;
        // TASK-375 — paint the LOADING state immediately (renderCustomerList
        // shows the spinner while _lazyBridgeInFlight is true) so the member
        // never sees the "access not set up yet" empty state during provision.
        if (document.getElementById('crm-customer-list')) renderCustomerList();
        try {
          await autoCreateCrmProfileForMember({ silent: true });
        } finally {
          CRMState._lazyBridgeInFlight = false;
          // Re-render once the in-flight flag clears so the spinner resolves to
          // the customer list (success) or the empty state (provision failed).
          if (!CRMState.activeProfile && document.getElementById('crm-customer-list')) renderCustomerList();
        }
      }
    }
    return;
  }

  // CRM bridge Path B (PRIMARY) — try the member-doc post-migration source
  // of truth first. If the user has been migrated, their members/{uid}.crmProfile
  // is the single source — no separate-doc join, no email lookup. The legacy
  // subscribeToCrmProfiles onSnapshot keeps the in-memory activeProfile fresh
  // from the live crmProfiles/{profileId} doc via id-match (so Gmail
  // connect/disconnect / signature edits propagate within ms of page load).
  const hydratedFromMember = await tryHydrateFromMemberDoc();
  if (hydratedFromMember) {
    updateManageTeamButtonVisibility();
    updateCrmProfileIndicator();
    applyActiveProfileScoping();
    return;
  }

  // TASK-319 bridge (Path A): if the signed-in user is a per-user member (members/{uid}
  // exists, AppState.memberAccess populated by app.js:loadUserTenant), DD
  // already knows who they are. Try email-match against existing crmProfiles
  // before the PIN gate fires — they shouldn't get prompted for a CRM PIN
  // when they just typed their password 30 seconds ago.
  const memberAccess = window.AppState?.memberAccess;
  const isMember = !!(memberAccess && memberAccess.status === 'active');
  const myEmail = (auth.currentUser?.email || '').toLowerCase();

  // Check session storage for a remembered profile in this browser session.
  // Validate that the cached profile's email matches the currently-signed-in
  // user — otherwise a shared-browser session could bind the wrong rep across
  // logins (Code Reviewer catch).
  const cached = sessionStorage.getItem(`crmProfile_${CRMState.tenantId}`);
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      const match = CRMState.profiles.find(p => p.id === parsed.id);
      if (match) {
        const sameUser = !isMember || !myEmail || (match.email || '').toLowerCase() === myEmail;
        if (sameUser) {
          hydrateActiveProfile(match);
          updateManageTeamButtonVisibility();
          updateCrmProfileIndicator();
          applyActiveProfileScoping();
          // Path B auto-migrate (re-fire safe via idempotent server-side check).
          maybeTriggerCrmProfileMigration(match.id);
          return;
        }
        // Cached profile belongs to a different user (someone else's session
        // on this device) — drop it and re-evaluate.
        try { sessionStorage.removeItem(`crmProfile_${CRMState.tenantId}`); } catch (_) {}
      }
    } catch (_) {}
  }

  // TASK-319: auto-link by email if signed-in member matches an existing profile.
  if (isMember && myEmail) {
    const emailMatch = (CRMState.profiles || []).find(p => (p.email || '').toLowerCase() === myEmail);
    if (emailMatch) {
      // Trust the existing profile's role — don't override based on
      // members/{uid}.role. The CRM team supervisor designation is an
      // independent product decision (Edge Case Hunter catch).
      hydrateActiveProfile(emailMatch);
      updateManageTeamButtonVisibility();
      updateCrmProfileIndicator();
      applyActiveProfileScoping();
      // Path B auto-migrate (fire-and-forget; non-fatal on error). Server-side
      // re-checks ownership (profile.email === auth.token.email) before write,
      // and is idempotent if migration already happened in another tab.
      maybeTriggerCrmProfileMigration(emailMatch.id);
      return;
    }

    // TASK-352 Slice 2: LAZY-INIT BRIDGE.
    // No matching profile but a signed-in TASK-319 member just opened CRM.
    // Provision them via the server callable — owner/admin → supervisor,
    // staff → rep (UX Lead ✅, Architect ✅ — keeps provisioning in the
    // CRM module boundary, no auth-side coupling). Replaces the previous
    // showCrmMemberAutoLinkModal() click-to-confirm modal so invited users
    // see shared customers immediately on first CRM open, matching the
    // natural mental model of "I gave them access, so they are on the team."
    //
    // Re-entrancy guard: subscribeToCrmProfiles onSnapshot fires repeatedly
    // and maybeShowCrmPinGate is fire-and-forget. Without a flag, rapid view
    // switches can stack multiple callable invocations. The server-side
    // transaction is the authoritative race winner; this just avoids
    // unnecessary callable round-trips. Cross-tab races are defeated by the
    // transaction itself.
    if (!CRMState._lazyBridgeInFlight) {
      CRMState._lazyBridgeInFlight = true;
      // TASK-375 — paint the LOADING state immediately (see note at the
      // zero-profiles bridge above).
      if (document.getElementById('crm-customer-list')) renderCustomerList();
      try {
        await autoCreateCrmProfileForMember({ silent: true });
      } finally {
        CRMState._lazyBridgeInFlight = false;
        if (!CRMState.activeProfile && document.getElementById('crm-customer-list')) renderCustomerList();
      }
    }
    return;
  }

  // TASK-375 — CRM PIN RETIRED. CRM is a back-office sales tool; access now
  // requires a signed-in member with the `crm` module (handled by the member
  // paths above). A no-member / kiosk / shared-tablet session gets NO CRM — the
  // old showCrmPinGate() fallback is gone. scopedCustomers returns [] for such
  // callers (they are not _isUnrestrictedCrmCaller AND have no activeProfile),
  // and renderCustomerList shows the "ask an admin" empty state rather than a
  // PIN prompt. The general Settings → Access PINs (Dispatch/Maintenance) are a
  // SEPARATE system and are untouched.
}

// TASK-375 — the click-to-confirm "add yourself to the sales roster" modal
// (showCrmMemberAutoLinkModal) + its PIN fallback (fallbackToPinGate) are
// removed. Members with the `crm` module are now provisioned silently via the
// lazy-init bridge (autoCreateCrmProfileForMember({silent:true})), so there is
// no interstitial prompt and no PIN escape hatch.

// Provision a crmProfile for the signed-in TASK-319 member via the
// server-side callable (firestore.rules:833 blocks client-side create for
// non-admins). Role is mapped server-side from members/{uid}.role:
// owner/admin → supervisor, else → rep. Editable later in Manage Team.
//
// TASK-352 Slice 2: `opts.silent === true` skips modal UI updates and is
// used by the lazy-init bridge in maybeShowCrmPinGate. Modal-path callers
// (showCrmMemberAutoLinkModal Yes button) keep the original click flow.
//
// Server-side `provisionCrmProfileForMember` callable handles all
// race-safety + idempotency in a Firestore transaction (Code Reviewer ⚠️
// cross-tab race + role-mapping security concerns addressed there).
// TASK-375 — silent-only now. The interactive modal path (yes/pin buttons) was
// removed with the CRM PIN retirement; the only caller is the lazy-init bridge
// in maybeShowCrmPinGate (opts.silent === true). `opts` kept for call-site
// compatibility.
async function autoCreateCrmProfileForMember(_opts) {
  try {
    const myEmail = (auth.currentUser?.email || '').toLowerCase();
    if (!myEmail) throw new Error('No signed-in user email');
    if (!CRMState.tenantId) throw new Error('No tenant');

    // Defensive client-side dedupe (catches the case where another tab's
    // onSnapshot has already added a matching profile to CRMState.profiles
    // between the gate check and this call). The server-side transaction is
    // the authoritative race winner; this just avoids an unnecessary
    // callable round-trip.
    const existing = (CRMState.profiles || []).find(p => (p.email || '').toLowerCase() === myEmail);
    if (existing) {
      hydrateActiveProfile(existing);
      updateManageTeamButtonVisibility();
      updateCrmProfileIndicator();
      applyActiveProfileScoping();
      maybeTriggerCrmProfileMigration(existing.id);
      return;
    }

    const fn = httpsCallable(functions, 'provisionCrmProfileForMember');
    // TASK-375 SECURITY — crmRole is NOT passed from the client. The server
    // reads the admin-authored members/{uid}.crmRole field (set at invite time
    // by acceptUserInvite) so a rep can't self-promote to supervisor via a
    // crafted payload. See provisionCrmProfileForMember.
    const res = await fn({ tenantId: CRMState.tenantId });
    const result = res?.data || {};
    if (!result.ok || !result.profileId) {
      throw new Error('Provision returned no profileId');
    }

    // The new (or existing-matched) profile will land in CRMState.profiles
    // via the onSnapshot listener within ms. Set activeProfile immediately
    // from the callable's response so renderActiveTab doesn't see the
    // null-activeProfile + restricted-member empty state for the half-second
    // before onSnapshot fires.
    const name = auth.currentUser?.displayName || myEmail.split('@')[0] || 'Team Member';
    const newProfile = {
      id: result.profileId,
      name,
      email: auth.currentUser.email,
      role: result.role || 'rep',
      gmailConnected: false,
    };
    hydrateActiveProfile(newProfile);
    updateManageTeamButtonVisibility();
    updateCrmProfileIndicator();
    applyActiveProfileScoping();
  } catch (err) {
    console.error('[CRM] auto-create profile error:', err);
    // Leave activeProfile null. scopedCustomers returns [] for restricted
    // members, so the user sees the "CRM access not set up yet" empty state.
    // Surface a toast so a non-tech-savvy user knows a background failure
    // occurred (UX Lead ✅).
    if (window.showToast) {
      window.showToast("Couldn't set up your CRM access. Try refreshing or ask an admin.", 'error');
    }
  }
}

// TASK-375 — CRM PIN RETIRED. showCrmPinGate() + submitCrmPin() removed. CRM
// access is identity-scoped (signed-in member with the `crm` module) via the
// email-match / lazy-init bridge in maybeShowCrmPinGate. PIN hashing helpers
// (hashCrmPin/generateCrmPinSalt) and the per-rep PIN fields are no longer
// written by any path. The general Settings → Access PINs (Dispatch/Maintenance)
// are a SEPARATE system and are untouched.

function switchCrmProfile() {
  CRMState.activeProfile = null;
  sessionStorage.removeItem(`crmProfile_${CRMState.tenantId}`);
  updateCrmProfileIndicator();
  applyActiveProfileScoping();
  // Re-run the gate so TASK-319 members get the auto-link path again instead
  // of being forced through a PIN they don't have (Code Reviewer catch).
  Promise.resolve(maybeShowCrmPinGate()).catch(err => {
    console.warn('[CRM] maybeShowCrmPinGate failed:', err?.message || err);
  });
}

function updateCrmProfileIndicator() {
  let indicator = document.getElementById('crm-active-profile-indicator');
  const header = document.querySelector('.crm-header-actions');
  if (!header) return;
  if (!CRMState.activeProfile) {
    indicator?.remove();
    return;
  }
  if (!indicator) {
    indicator = document.createElement('div');
    indicator.id = 'crm-active-profile-indicator';
    indicator.style.cssText = 'display:flex;align-items:center;gap:6px;padding:6px 10px;background:#EFF6FF;border:1px solid #BFDBFE;border-radius:20px;font-size:12px;font-weight:600;color:var(--brand-blue);cursor:pointer;';
    indicator.title = 'Click to switch profile';
    indicator.onclick = () => window.CRMModule.switchCrmProfile();
    header.insertBefore(indicator, header.firstChild);
  }
  const p = CRMState.activeProfile;
  const badge = p.role === 'supervisor' ? 'Supervisor' : 'Rep';
  indicator.innerHTML = `<span class="material-icons" style="font-size:16px;">account_circle</span>${escapeHtml(p.name)} <span style="font-size:10px;opacity:0.7;">(${badge})</span> <span class="material-icons" style="font-size:14px;">swap_horiz</span>`;
}

function updateManageTeamButtonVisibility() {
  const btn = document.getElementById('crm-manage-team-btn');
  if (!btn) return;
  // Hide the Team button for a rep-level CRM profile. Supervisors and
  // admin/owner sessions (no rep-scoped activeProfile) can manage the team.
  // Firestore rules enforce tenant-admin-only writes as the real security boundary.
  const isRepLocked = CRMState.activeProfile && CRMState.activeProfile.role !== 'supervisor';
  btn.style.display = isRepLocked ? 'none' : '';
}

function showManageCrmProfiles() {
  const existing = document.getElementById('crm-manage-profiles-modal');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.id = 'crm-manage-profiles-modal';
  modal.className = 'modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:720px;width:95%;max-height:90vh;max-height:90dvh;overflow-y:auto;">
      <div class="modal-header">
        <h2 style="display:flex;align-items:center;gap:8px;"><span class="material-icons">group</span> CRM Team</h2>
        <button class="modal-close" onclick="window.CRMModule.closeManageCrmProfiles()">&times;</button>
      </div>
      <div class="modal-body">
        <p style="color:var(--text-secondary);margin-top:0;">Each CRM team member gets their own login. Reps see only their assigned customers; one supervisor sees the whole pipeline plus rep comparison.</p>

        <div id="crm-profiles-list" style="margin-bottom:16px;"></div>

        <div style="border-top:1px solid var(--border-color);padding-top:16px;">
          <h3 style="margin-top:0;font-size:15px;">Add a teammate</h3>
          <p style="color:var(--text-secondary);font-size:13px;margin-top:0;">Pick the person from your roster and we'll email them an invite to set up their login with CRM access.</p>
          <button class="btn btn-primary" onclick="window.CRMModule.inviteToCrmTeam()" style="margin-top:4px;">
            <span class="material-icons">person_add</span> Invite to CRM Team
          </button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  renderCrmProfilesList();
}

function closeManageCrmProfiles() {
  const modal = document.getElementById('crm-manage-profiles-modal');
  if (modal) modal.remove();
}

function renderCrmProfilesList() {
  const container = document.getElementById('crm-profiles-list');
  if (!container) return;
  const profiles = CRMState.profiles || [];
  if (profiles.length === 0) {
    container.innerHTML = `
      <div style="padding:20px;text-align:center;background:#F8FAFC;border-radius:6px;color:var(--text-secondary);">
        No team members yet. Invite your first rep below.
      </div>`;
    return;
  }
  container.innerHTML = `
    <table style="width:100%;border-collapse:collapse;">
      <thead>
        <tr style="background:#F1F5F9;">
          <th style="padding:8px;text-align:left;font-size:12px;color:var(--text-secondary);">Name</th>
          <th style="padding:8px;text-align:left;font-size:12px;color:var(--text-secondary);">Email</th>
          <th style="padding:8px;text-align:left;font-size:12px;color:var(--text-secondary);">Role</th>
          <th style="padding:8px;text-align:left;font-size:12px;color:var(--text-secondary);">Gmail</th>
          <th style="padding:8px;text-align:right;font-size:12px;color:var(--text-secondary);">Actions</th>
        </tr>
      </thead>
      <tbody>
        ${profiles.map(p => `
          <tr style="border-top:1px solid var(--border-color);">
            <td style="padding:8px;">${escapeHtml(p.name || '')}</td>
            <td style="padding:8px;color:var(--text-secondary);font-size:13px;">${escapeHtml(p.email || '')}</td>
            <td style="padding:8px;">
              <span style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;background:${p.role === 'supervisor' ? '#8B5CF6' : '#3B82F6'};color:#fff;">
                ${p.role === 'supervisor' ? 'Supervisor' : 'Rep'}
              </span>
            </td>
            <td style="padding:8px;font-size:12px;">
              ${p.gmailConnected ? `
                <span style="color:#059669;font-weight:600;">
                  <span class="material-icons" style="font-size:14px;vertical-align:middle;">check_circle</span>
                  ${escapeHtml(p.gmailEmail || 'Connected')}
                </span>
                <button class="btn btn-sm" style="margin-left:6px;font-size:11px;padding:2px 6px;" onclick="window.CRMModule.editCrmSignature('${escJsAttr(p.id)}')" title="Edit signature">
                  <span class="material-icons" style="font-size:12px;vertical-align:middle;">edit_note</span>
                  ${p.gmailSignature ? 'Signature ✓' : 'Add signature'}
                </button>
                <button class="btn btn-sm" style="margin-left:4px;font-size:11px;padding:2px 6px;" onclick="window.CRMModule.disconnectCrmGmail('${escJsAttr(p.id)}', event)" title="Disconnect">Disconnect</button>
              ` : p.gmailNeedsReconnect ? `
                <span style="color:#B45309;font-weight:600;" title="Google stopped accepting sends from this account. Emails from DispatchDeck will not go out until it is reconnected.">
                  <span class="material-icons" style="font-size:14px;vertical-align:middle;">error</span>
                  Reconnect needed${p.gmailEmail ? ` — ${escapeHtml(p.gmailEmail)}` : ''}
                </span>
                ${_crmIsTenantAdmin() ? `
                <button class="btn btn-sm btn-primary" style="margin-left:6px;font-size:11px;padding:4px 8px;" onclick="window.CRMModule.connectCrmGmail('${window.escJsAttr(p.id)}', event)">
                  <span class="material-icons" style="font-size:14px;vertical-align:middle;">refresh</span>
                  Reconnect Gmail
                </button>` : `<span style="margin-left:6px;font-size:11px;color:var(--text-secondary);">An admin can reconnect it.</span>`}
              ` : `
                <button class="btn btn-sm btn-secondary" style="font-size:11px;padding:4px 8px;" onclick="window.CRMModule.connectCrmGmail('${escJsAttr(p.id)}', event)">
                  <span class="material-icons" style="font-size:14px;vertical-align:middle;">mail</span>
                  Connect Gmail
                </button>
              `}
            </td>
            <td style="padding:8px;text-align:right;">
              <button class="btn btn-sm btn-danger" onclick="window.CRMModule.deleteCrmProfile('${escJsAttr(p.id)}')" title="Remove">
                <span class="material-icons" style="font-size:16px;">delete</span>
              </button>
            </td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
}

// TASK-375 — "Invite to CRM Team". A thin layer over the EXISTING staff-invite
// flow (Roster → Set up login) with the `crm` module pre-checked + a CRM-level
// (rep/supervisor) selector. Every CRM team member is a roster person with a
// sub-user login — the legacy PIN-based crmProfile create (saveCrmProfile) is
// retired. Delegates to RosterModule.openCrmTeamInvite, which handles the
// select-or-create-roster-person step and opens the shared invite modal.
function inviteToCrmTeam() {
  closeManageCrmProfiles();
  if (window.RosterModule?.openCrmTeamInvite) {
    window.RosterModule.openCrmTeamInvite();
  } else {
    window.showToast?.('Open Roster to invite a CRM teammate.', 'info');
  }
}

async function deleteCrmProfile(profileId) {
  const profile = (CRMState.profiles || []).find(p => p.id === profileId);
  if (!profile) return;
  const confirmed = window.showConfirm
    // Found while shipping TASK-1342: this passed an OBJECT, but showConfirm's
    // signature is (title, message, options) — so the dialog rendered
    // "[object Object]" as its title with an empty body, and the real question
    // was never asked before removing someone's CRM access.
    ? await window.showConfirm(
        'Remove team member?',
        `${profile.name} will lose CRM access. Any customers assigned to them will become unassigned.`,
        { confirmText: 'Remove', cancelText: 'Cancel' }
      )
    : confirm(`Remove ${profile.name} from CRM team?`);
  if (!confirmed) return;
  try {
    // Soft delete
    await updateDoc(doc(db, 'tenants', CRMState.tenantId, 'crmProfiles', profileId), { active: false, deletedAt: Timestamp.now() });
    if (window.showToast) window.showToast(`${profile.name} removed`, 'success');
  } catch (err) {
    console.error('[CRM Profiles] delete error:', err);
    if (window.showToast) window.showToast(`Error: ${err.message || err}`, 'error');
  }
}

// ============================================
// GMAIL CONNECTION (per-rep OAuth send-as)
// ============================================

function setGmailBtnLoading(event, label) {
  const btn = event?.currentTarget || event?.target?.closest('button');
  if (!btn) return null;
  const original = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = `<span class="material-icons" style="font-size:14px;vertical-align:middle;animation:spin 1s linear infinite;">autorenew</span> ${label}`;
  return { btn, original };
}

async function connectCrmGmail(profileId, event) {
  const restore = setGmailBtnLoading(event, 'Opening...');
  try {
    if (window.showToast) window.showToast('Opening Google sign-in...', 'info');
    const getUrl = httpsCallable(functions, 'crmGetGmailAuthUrl');
    const res = await getUrl({ tenantId: CRMState.tenantId, profileId });
    const authUrl = res.data?.url;
    if (!authUrl) throw new Error('No auth URL returned');
    // Remember which profile we're connecting so the popup result handler can
    // update the right profile's state when the popup messages us back.
    CRMState._pendingGmailProfileId = profileId;
    window.open(authUrl, '_blank', 'width=520,height=700');
  } catch (err) {
    console.error('[CRM Gmail] connect error:', err);
    if (window.showToast) window.showToast(`Error: ${err.message || err}`, 'error');
  } finally {
    if (restore) { restore.btn.disabled = false; restore.btn.innerHTML = restore.original; }
  }
}

async function disconnectCrmGmail(profileId, event) {
  const profile = (CRMState.profiles || []).find(p => p.id === profileId);
  if (!profile) return;
  if (!confirm(`Disconnect Gmail for ${profile.name}?`)) return;
  const restore = setGmailBtnLoading(event, 'Disconnecting...');
  try {
    const fn = httpsCallable(functions, 'crmDisconnectGmail');
    await fn({ tenantId: CRMState.tenantId, profileId });
    if (CRMState.activeProfile && CRMState.activeProfile.id === profileId) {
      CRMState.activeProfile.gmailConnected = false;
      CRMState.activeProfile.gmailEmail = null;
      sessionStorage.setItem(`crmProfile_${CRMState.tenantId}`, JSON.stringify(CRMState.activeProfile));
    }
    if (window.showToast) window.showToast('Gmail disconnected', 'success');
  } catch (err) {
    console.error('[CRM Gmail] disconnect error:', err);
    if (window.showToast) window.showToast(`Error: ${err.message || err}`, 'error');
    if (restore) { restore.btn.disabled = false; restore.btn.innerHTML = restore.original; }
  }
}

// Whitelist-based sanitizer for rich-text signatures (XSS defense).
// Allows only tags/attrs produced by the execCommand toolbar + image upload.
function sanitizeSignatureHtml(dirty) {
  if (!dirty || typeof dirty !== 'string') return '';
  const ALLOWED_TAGS = new Set(['A','B','I','U','STRONG','EM','P','DIV','SPAN','BR','IMG','SMALL','FONT','UL','OL','LI']);
  const ALLOWED_ATTRS = {
    A: ['href','target','rel'],
    IMG: ['src','alt','width','height','style'],
    SPAN: ['style'], DIV: ['style'], P: ['style'], FONT: ['style','color','face'],
    UL: ['style'], OL: ['style'], LI: ['style'],
  };
  const safeUrl = (val, { allowData }) => {
    const v = String(val || '').trim();
    if (!v) return '';
    if (/^javascript:/i.test(v)) return '';
    if (/^data:/i.test(v)) return allowData && /^data:image\/(png|jpe?g|gif|webp);base64,/i.test(v) ? v : '';
    if (/^(https?:|mailto:|tel:|\/|#)/i.test(v)) return v;
    return '';
  };
  const safeStyle = (val) => {
    const v = String(val || '');
    if (/expression\s*\(|javascript:|url\s*\(\s*['"]?\s*javascript:/i.test(v)) return '';
    return v;
  };
  const doc = new DOMParser().parseFromString(`<div>${dirty}</div>`, 'text/html');
  const walk = (node) => {
    for (let i = node.childNodes.length - 1; i >= 0; i--) {
      const child = node.childNodes[i];
      if (child.nodeType === 1) {
        const tag = child.tagName;
        if (!ALLOWED_TAGS.has(tag)) {
          while (child.firstChild) node.insertBefore(child.firstChild, child);
          node.removeChild(child);
          continue;
        }
        const allowed = ALLOWED_ATTRS[tag] || [];
        for (const attr of Array.from(child.attributes)) {
          const name = attr.name.toLowerCase();
          if (!allowed.includes(name)) { child.removeAttribute(attr.name); continue; }
          if (name === 'href') {
            const clean = safeUrl(attr.value, { allowData: false });
            if (!clean) child.removeAttribute(attr.name); else child.setAttribute(name, clean);
          } else if (name === 'src') {
            const clean = safeUrl(attr.value, { allowData: true });
            if (!clean) child.removeAttribute(attr.name); else child.setAttribute(name, clean);
          } else if (name === 'style') {
            const clean = safeStyle(attr.value);
            if (!clean) child.removeAttribute(attr.name); else child.setAttribute(name, clean);
          }
        }
        walk(child);
      } else if (child.nodeType === 8) {
        node.removeChild(child);
      }
    }
  };
  const root = doc.body.firstChild;
  walk(root);
  return root.innerHTML;
}

function editCrmSignature(profileId) {
  const profile = (CRMState.profiles || []).find(p => p.id === profileId);
  if (!profile) return;

  let modal = document.getElementById('crm-signature-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'crm-signature-modal';
    modal.className = 'modal';
    modal.style.zIndex = '9998';
    document.body.appendChild(modal);
  }

  const currentSig = sanitizeSignatureHtml(profile.gmailSignature || '');

  modal.innerHTML = `
    <div class="modal-content" style="max-width:640px;width:95%;">
      <div class="modal-header">
        <h3 style="margin:0;">Email Signature — ${escapeHtml(profile.name)}</h3>
        <button class="modal-close" onclick="document.getElementById('crm-signature-modal').remove()">&times;</button>
      </div>
      <div class="modal-body">
        <p style="color:var(--text-secondary);font-size:13px;margin-top:0;">
          Appended to every email sent from this Gmail account. Recipient sees only your signature — no DispatchDeck branding.
        </p>
        <div id="crm-sig-toolbar" style="display:flex;gap:6px;padding:8px;background:#F8FAFC;border:1px solid var(--border-color);border-bottom:none;border-radius:6px 6px 0 0;">
          <button type="button" class="btn btn-sm" onclick="document.execCommand('bold')" title="Bold"><b>B</b></button>
          <button type="button" class="btn btn-sm" onclick="document.execCommand('italic')" title="Italic"><i>I</i></button>
          <button type="button" class="btn btn-sm" onclick="document.execCommand('underline')" title="Underline"><u>U</u></button>
          <button type="button" class="btn btn-sm" onclick="(function(){var u=prompt('Link URL:','https://');if(u)document.execCommand('createLink',false,u);})()" title="Insert link">
            <span class="material-icons" style="font-size:14px;vertical-align:middle;">link</span>
          </button>
          <button type="button" class="btn btn-sm" onclick="document.getElementById('crm-sig-image-input').click()" title="Insert image">
            <span class="material-icons" style="font-size:14px;vertical-align:middle;">image</span>
          </button>
          <button type="button" class="btn btn-sm" onclick="document.execCommand('removeFormat')" title="Clear formatting">
            <span class="material-icons" style="font-size:14px;vertical-align:middle;">format_clear</span>
          </button>
          <input type="file" id="crm-sig-image-input" accept="image/png,image/jpeg,image/gif,image/webp" style="display:none;" onchange="window.CRMModule.uploadSignatureImage(event, '${escJsAttr(profile.id)}')">
        </div>
        <div id="crm-sig-editor" contenteditable="true" style="min-height:180px;max-height:360px;overflow-y:auto;padding:12px;border:1px solid var(--border-color);border-radius:0 0 6px 6px;font-family:Arial,sans-serif;font-size:14px;line-height:1.5;background:#fff;">${currentSig}</div>
        <div style="font-size:11px;color:var(--text-secondary);margin-top:6px;">
          Tip: keep it short. Name, title, company, phone, website. Logos render best at 60–80px tall.
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" onclick="document.getElementById('crm-signature-modal').remove()">Cancel</button>
        <button class="btn btn-primary" id="crm-sig-save-btn" onclick="window.CRMModule.saveCrmSignature('${escJsAttr(profile.id)}')">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">save</span> Save Signature
        </button>
      </div>
    </div>`;
  setTimeout(() => document.getElementById('crm-sig-editor')?.focus(), 50);
}

async function uploadSignatureImage(event, profileId) {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;
  if (file.size > 2 * 1024 * 1024) {
    if (window.showToast) window.showToast('Image must be under 2 MB', 'error');
    return;
  }
  try {
    const ts = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `tenants/${CRMState.tenantId}/email-signatures/${profileId}/${ts}_${safeName}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    const img = `<img src="${url}" alt="" style="max-height:80px;display:block;margin:6px 0;">`;
    const editor = document.getElementById('crm-sig-editor');
    if (editor) {
      document.execCommand('insertHTML', false, img);
      editor.focus();
    }
  } catch (err) {
    console.error('[CRM Signature] upload error:', err);
    if (window.showToast) window.showToast(`Upload failed: ${err.message || err}`, 'error');
  }
}

async function saveCrmSignature(profileId) {
  const editor = document.getElementById('crm-sig-editor');
  const btn = document.getElementById('crm-sig-save-btn');
  if (!editor) return;
  const html = sanitizeSignatureHtml(editor.innerHTML.trim());
  if (btn) { btn.disabled = true; btn.textContent = 'Saving...'; }
  try {
    await updateDoc(doc(db, 'tenants', CRMState.tenantId, 'crmProfiles', profileId), {
      gmailSignature: html,
      updatedAt: Timestamp.now(),
    });
    if (CRMState.activeProfile && CRMState.activeProfile.id === profileId) {
      CRMState.activeProfile.gmailSignature = html;
    }
    if (window.showToast) window.showToast('Signature saved', 'success');
    document.getElementById('crm-signature-modal')?.remove();
  } catch (err) {
    console.error('[CRM Signature] save error:', err);
    if (window.showToast) window.showToast(`Save failed: ${err.message || err}`, 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;vertical-align:middle;">save</span> Save Signature'; }
  }
}

// TASK-1412 — who can fix a dead Gmail connection. crmGetGmailAuthUrl is
// guarded tenantAdmin server-side, so only offer the button where it will work;
// everyone else is told who to ask.
function _crmGmailReconnectHint(profileId) {
  if (_crmIsTenantAdmin() && profileId) {
    return { canReconnect: true, text: 'Reconnect Gmail to send again.' };
  }
  return { canReconnect: false, text: 'Ask your admin to reconnect your Gmail in CRM → Team.' };
}

function openCrmEmailCompose(toEmail, customerId, customerName) {
  if (CRMState.activeProfile && !CRMState.activeProfile.gmailConnected && CRMState.activeProfile.gmailNeedsReconnect) {
    const hint = _crmGmailReconnectHint(CRMState.activeProfile.id);
    if (window.showToast) window.showToast(`Your Gmail connection stopped working. ${hint.text}`, 'error');
    return;
  }
  if (!CRMState.activeProfile || !CRMState.activeProfile.gmailConnected) {
    if (window.showToast) window.showToast('Connect Gmail first (CRM → Team → Connect Gmail)', 'error');
    return;
  }

  let modal = document.getElementById('crm-email-compose-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'crm-email-compose-modal';
    modal.className = 'modal modal-fullscreen hidden';
    modal.style.zIndex = '1060';
    document.body.appendChild(modal);
  }
  modal.innerHTML = `
    <div class="modal-content" style="max-width:640px;width:95%;">
      <div class="modal-header">
        <h3 style="margin:0;">Compose Email</h3>
        <button class="modal-close" onclick="document.getElementById('crm-email-compose-modal').classList.add('hidden')">&times;</button>
      </div>
      <div class="modal-body">
        <div style="background:#F1F5F9;padding:8px 12px;border-radius:6px;font-size:12px;color:var(--text-secondary);margin-bottom:12px;">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;color:#059669;">check_circle</span>
          Sending as <strong>${escapeHtml(CRMState.activeProfile.gmailEmail || CRMState.activeProfile.email || '')}</strong>
        </div>
        <div class="form-group">
          <label class="form-label">To</label>
          <input type="email" id="crm-email-to" class="form-control" value="${escapeHtml(toEmail || '')}">
        </div>
        <div class="form-group">
          <label class="form-label">Subject *</label>
          <input type="text" id="crm-email-subject" class="form-control" placeholder="Subject line">
        </div>
        <div class="form-group">
          <label class="form-label">Message *</label>
          <textarea id="crm-email-body" class="form-control" rows="8" placeholder="Write your message..."></textarea>
        </div>
        ${CRMState.activeProfile.gmailSignature ? `
        <div style="border:1px dashed var(--border-color);border-radius:6px;padding:10px;background:#F8FAFC;">
          <div style="font-size:11px;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">Signature (appended to every send)</div>
          <div style="font-family:Arial,sans-serif;font-size:13px;line-height:1.5;">${sanitizeSignatureHtml(CRMState.activeProfile.gmailSignature)}</div>
        </div>
        ` : `
        <div style="font-size:12px;color:var(--text-secondary);padding:8px;">
          No signature set. <a href="#" onclick="event.preventDefault();document.getElementById('crm-email-compose-modal').classList.add('hidden');window.CRMModule.editCrmSignature('${escJsAttr(CRMState.activeProfile.id)}')">Add one</a> in CRM → Team.
        </div>
        `}
        <div id="crm-email-send-error" role="alert" style="display:none;margin-top:12px;padding:10px 12px;border-radius:6px;border:1px solid #FCA5A5;background:#FEF2F2;color:#7F1D1D;font-size:13px;line-height:1.45;"></div>
        <input type="hidden" id="crm-email-customer-id" value="${escapeHtml(customerId || '')}">
        <input type="hidden" id="crm-email-customer-name" value="${escapeHtml(customerName || '')}">
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" onclick="document.getElementById('crm-email-compose-modal').classList.add('hidden')">Cancel</button>
        <button class="btn btn-primary" id="crm-email-send-btn" onclick="window.CRMModule.sendCrmEmail()">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">send</span> Send
        </button>
      </div>
    </div>`;
  modal.classList.remove('hidden');
  setTimeout(() => document.getElementById('crm-email-subject')?.focus(), 50);
}

async function sendCrmEmail() {
  const to = document.getElementById('crm-email-to')?.value?.trim();
  const subject = document.getElementById('crm-email-subject')?.value?.trim();
  const body = document.getElementById('crm-email-body')?.value?.trim();
  const customerId = document.getElementById('crm-email-customer-id')?.value || null;
  const customerName = document.getElementById('crm-email-customer-name')?.value || null;

  if (!to || !to.includes('@')) { if (window.showToast) window.showToast('Valid recipient email required', 'error'); return; }
  if (!subject) { if (window.showToast) window.showToast('Subject required', 'error'); return; }
  if (!body) { if (window.showToast) window.showToast('Message body required', 'error'); return; }
  // TASK-1412 follow-up — after a "can't tell whether it went" on THIS email
  // (same profile + recipient + subject), Send is not a silent blind resend.
  // A Set, not one slot: two unconfirmed emails must BOTH stay gated (COD round 2).
  if (!(CRMState._crmUnconfirmedSendKeys instanceof Set)) CRMState._crmUnconfirmedSendKeys = new Set();
  if (!(CRMState._crmInFlightSendKeys instanceof Set)) CRMState._crmInFlightSendKeys = new Set();
  const _unconfirmed = CRMState._crmUnconfirmedSendKeys;
  const _inFlight = CRMState._crmInFlightSendKeys;
  const _sendKey = [CRMState.tenantId, CRMState.activeProfile?.id, to.toLowerCase(), subject].join('\n');
  // Cancel only HIDES the modal and reopening rebuilds an enabled Send, so the
  // same email can be sent again while the first call is still out (COD round 3).
  if (_inFlight.has(_sendKey)) {
    if (window.showToast) window.showToast('This email is still sending — wait for it to finish.', 'warning');
    return;
  }
  if (_unconfirmed.has(_sendKey)
      && !confirm("Your last send of this email could not be confirmed. It may already be in their inbox. Check your Gmail Sent folder first. Send it again anyway?")) return;

  const btn = document.getElementById('crm-email-send-btn');
  if (btn) { btn.disabled = true; btn.textContent = 'Sending...'; }
  const errBox = document.getElementById('crm-email-send-error');
  if (errBox) { errBox.style.display = 'none'; errBox.innerHTML = ''; }

  _inFlight.add(_sendKey);
  try {
    const fn = httpsCallable(functions, 'crmSendEmail');
    const res = await fn({
      tenantId: CRMState.tenantId,
      profileId: CRMState.activeProfile.id,
      to, subject, body,
      customerId, customerName,
    });
    // TASK-1412 — success is Gmail's message id, not "the call resolved".
    if (!res?.data?.ok || !res?.data?.messageId) {
      throw Object.assign(new Error('Gmail did not confirm this send. Check your Gmail Sent folder before sending again.'), { details: { reason: 'gmail_unconfirmed' } });
    }
    if (customerId && res.data.activityLogged === false) {
      if (window.showToast) window.showToast(`Email sent to ${to} — but it didn't save to this customer's activity.`, 'warning');
    } else if (window.showToast) {
      window.showToast(`Email sent to ${to}`, 'success');
    }
    _unconfirmed.delete(_sendKey);
    document.getElementById('crm-email-compose-modal').classList.add('hidden');
  } catch (err) {
    console.error('[CRM Gmail] send error:', err?.code || '', err?.details?.reason || '', err?.message || err);
    const unconfirmed = _showCrmSendFailure(err) === 'gmail_unconfirmed';
    if (unconfirmed) _unconfirmed.add(_sendKey);
    const label = _unconfirmed.has(_sendKey) ? 'Resend anyway' : 'Send';
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;vertical-align:middle;">send</span> ' + label; }
  } finally {
    _inFlight.delete(_sendKey);
  }
}

// TASK-1412 follow-up (COD) — "can't tell whether it went" is the DEFAULT. A
// timeout, a dropped connection, a gateway 502 ('unknown') or an error with no
// Firebase code at all can each land after Gmail accepted the message. Only the
// server's own answer makes a failure definite: details.sent === false (every
// classified failure sets it) or a code rejected before any send is attempted.
const _CRM_SEND_DEFINITE_CODES = new Set([
  'invalid-argument', 'permission-denied', 'unauthenticated', 'failed-precondition',
  'not-found', 'already-exists', 'resource-exhausted', 'out-of-range', 'unimplemented',
]);
function _isCrmSendOutcomeUnknown(err) {
  const d = (err && typeof err === 'object' && err.details) || {};
  if (d.reason === 'gmail_unconfirmed' || d.sent === null) return true;
  if (d.sent === false) return false;
  const code = String((err && err.code) || '').replace(/^functions\//, '');
  return !_CRM_SEND_DEFINITE_CODES.has(code);
}

// TASK-1412 — a failed send stays IN the compose modal (the draft survives) and
// says plainly whether it went out ("NOT sent" only when that is certain).
// Connection state is left to the crmProfiles snapshot, never flipped here.
function _showCrmSendFailure(err) {
  let reason = err?.details?.reason || '';
  // A transport failure (timeout, dropped connection, deadline-exceeded,
  // unavailable, unknown, no code at all) never reached our error handling, so
  // we cannot know whether Gmail sent it. Never call that "NOT sent".
  if (reason !== 'gmail_unconfirmed' && _isCrmSendOutcomeUnknown(err)) {
    reason = 'gmail_unconfirmed';
    err = { message: "We lost contact before Gmail answered, so we can't tell whether this email went out. Check your Gmail Sent folder before sending again." };
  }
  const box = document.getElementById('crm-email-send-error');
  const prof = CRMState.activeProfile;
  let html;
  if (reason === 'gmail_reconnect_required') {
    // No optimistic local flip: the server wrote gmailConnected:false before it
    // answered, and the crmProfiles snapshot applies it. Flipping here could
    // overwrite a reconnect that landed after that write (COD round 3).
    const hint = _crmGmailReconnectHint(prof?.id);
    html = `<strong>Email NOT sent.</strong> Google stopped accepting sends from ${escapeHtml(prof?.gmailEmail || 'your Gmail')} — the connection was revoked or expired. ${escapeHtml(hint.text)}`
      + (hint.canReconnect
        ? ` <div style="margin-top:8px;"><button class="btn btn-sm btn-primary" onclick="window.CRMModule.connectCrmGmail('${window.escJsAttr(prof.id)}', event)"><span class="material-icons" style="font-size:14px;vertical-align:middle;">refresh</span> Reconnect Gmail</button></div>`
        : '');
  } else if (reason === 'gmail_service_unavailable') {
    html = '<strong>Email NOT sent.</strong> Sending from Gmail is down on DispatchDeck\'s side right now. We\'ve been alerted — your draft is still here.';
  } else if (reason === 'gmail_unconfirmed') {
    html = `<strong>Couldn't confirm this send.</strong> ${escapeHtml(err?.message || '')}`;
  } else {
    html = `<strong>Email NOT sent.</strong> ${escapeHtml(String(err?.message || err || 'Unknown error').replace(/^Email NOT sent:?\s*/i, ''))}`;
  }
  if (box) { box.innerHTML = html; box.style.display = 'block'; }
  if (window.showToast) window.showToast(reason === 'gmail_unconfirmed' ? 'Send not confirmed' : 'Email NOT sent', 'error');
  return reason;
}

// Apply a Gmail OAuth result to local state + UI (shared by popup-message and
// the legacy URL-param fallback path).
function applyCrmGmailOAuthResult(result) {
  if (!result) return;
  if (result.error) {
    if (window.showToast) window.showToast(`Gmail connect failed: ${result.error}`, 'error');
    return;
  }
  if (result.connected) {
    const email = result.email || '';
    const pid = CRMState._pendingGmailProfileId;
    // Optimistically reflect the connection in local state; the
    // subscribeToCrmProfiles snapshot will reconcile authoritative fields.
    if (pid && Array.isArray(CRMState.profiles)) {
      const prof = CRMState.profiles.find(p => p.id === pid);
      if (prof) { prof.gmailConnected = true; prof.gmailEmail = email; }
    }
    if (CRMState.activeProfile && pid && CRMState.activeProfile.id === pid) {
      CRMState.activeProfile.gmailConnected = true;
      CRMState.activeProfile.gmailEmail = email;
      try { sessionStorage.setItem(`crmProfile_${CRMState.tenantId}`, JSON.stringify(CRMState.activeProfile)); } catch (_) {}
    }
    CRMState._pendingGmailProfileId = null;
    try { if (typeof renderCrmProfilesList === 'function') renderCrmProfilesList(); } catch (_) {}
    if (window.showToast) window.showToast(`Gmail connected: ${email}`, 'success');
  }
}

// Primary path: OAuth popup posts the result back here, then self-closes.
// Trusted senders: the app's own origin (no-opener URL-param fallback / future
// hosting-rewrite case) AND the Cloud Functions origin that actually serves the
// OAuth callback popup page (CRM_GMAIL_REDIRECT_URI). The data.type check below
// is the second gate. (TASK-374 re-COD fix: the callback is NOT same-origin —
// it's served from cloudfunctions.net, so a same-origin-only check dropped every
// valid message and the UI never updated.)
const _CRM_OAUTH_ORIGINS = [window.location.origin, 'https://us-central1-dispatch-deck-be88c.cloudfunctions.net'];
window.addEventListener('message', (ev) => {
  if (!_CRM_OAUTH_ORIGINS.includes(ev.origin)) return;
  const data = ev.data;
  if (!data || data.type !== 'crm-gmail-oauth') return;
  applyCrmGmailOAuthResult(data);
});

// Fallback path: popup had no opener (popup-blocked / TWA) and redirected the
// top-level window back with query params. Handle them on load.
(function handleOAuthCallbackParams() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('crm-gmail-connected')) {
      const email = params.get('email') || '';
      setTimeout(() => {
        if (window.showToast) window.showToast(`Gmail connected: ${email}`, 'success');
      }, 1000);
      params.delete('crm-gmail-connected');
      params.delete('email');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? '?' + newSearch : ''));
    } else if (params.has('crm-gmail-error')) {
      const err = params.get('crm-gmail-error');
      setTimeout(() => {
        if (window.showToast) window.showToast(`Gmail connect failed: ${err}`, 'error');
      }, 1000);
      params.delete('crm-gmail-error');
      const newSearch = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (newSearch ? '?' + newSearch : ''));
    }
  } catch (e) { /* ignore */ }
})();

// ============================================
// EXPOSE TO WINDOW
// ============================================
window.CRMState = CRMState;
// TASK-9622 — a rate-sheet approval changed a price book: drop the New Job
// form's cached rates and repaint the contract if it is the one on screen.
window.addEventListener('dd:price-book-updated', (e) => {
  const { customerId, contractId } = (e && e.detail) || {};
  if (!customerId) return;
  window.DispatchModule?.invalidateCustomerPriceBooks?.(customerId);
  if (CRMState.selectedCustomer?.id === customerId && document.getElementById('crm-line-items-list')
      && CRMState.contracts.some((c) => c.id === contractId)) {
    viewContract(customerId, contractId);
  }
});

window.CRMModule = {
  decide1099Suggestion, // TASK-9792
  startRecordedCall, openCallRecording, // TASK-9823
  // TASK-9793 — AR/AP opens the vendor edit form through these (no import).
  openVendorEditor,
  removeVendorEditorHost,
  isVendorRecord,
  showAddCustomer,
  showEditCustomer,
  saveCustomer,
  // TASK-339 — vendor logo handlers
  handleVendorLogo,
  removeVendorLogo,
  deleteCustomer,
  closeCustomerModal,
  // TASK-10030 — Merge into… (+ the delete guard, shared with dispatch.js deleteCustomer)
  customerDeleteHolds: _customerDeleteHolds,
  showMergeCustomer,
  closeMergeCustomer,
  onMergeInputChange,
  previewMergeCustomer,
  onMergeAck,
  applyMergeCustomer,
  undoMergeCustomer,
  resumeMergeCustomer,
  addPocRow,
  removePocRow,
  addPhoneRow,
  removePhoneRow,
  addEmailRow,
  removeEmailRow,
  addAddressRow,
  removeAddressRow,
  openCustomerDetail,
  closeDetailModal,
  openProjectFromCrm, // TASK-9736
  startProjectFromCrm, // TASK-9739
  submitProjectFromCrm, // TASK-9739
  switchDetailTab,
  applyFilters,
  // Tabs
  switchCRMTab,
  dashboardFilterCustomers,
  renderDashboardTab,
  renderRevenueTab,
  saveForecast,
  // Quick Actions
  quickLogActivity,
  quickNewOpportunity,
  // Opportunities
  showOpportunityForm,
  saveOpportunity,
  closeOpportunityModal,
  onStageChange,
  toggleOppAsset,
  confirmWonLost: confirmWonLostDispatch,
  renderOpportunitiesKanban,
  onKanbanDrop,
  // Full opportunity form
  closeOpportunityFullForm,
  saveOpportunityFull,
  copyOpportunity,
  onFullStageChange,
  toggleOppFullAsset,
  // Bid builder
  onBidContractSelect,
  addBidLine,
  removeBidLine,
  updateBidLineQty,
  // People & roles
  addAssignedPerson,
  toggleOppPersonSteps, // TASK-4705
  removeAssignedPerson,
  addAssignedRole,
  removeAssignedRole,
  // Portal
  togglePortal,
  togglePortalTracker,
  togglePortalOrders,
  toggleOrderCategory,
  toggleOrderSetting,
  addPortalStep,
  removePortalStep,
  updatePortalStep,
  movePortalStep,
  togglePortalStepComplete,
  regeneratePortalPin,
  addPortalNote,
  deletePortalNote,
  addPortalAssignee,
  removePortalAssignee,
  // Orders
  updateOrderStatus,
  loadOppOrders,
  // Comments
  addOppComment,
  onCommentInput,
  insertMention,
  // Activities
  showActivityForm,
  selectActivityType,
  selectOutcome,
  saveActivity,
  closeActivityModal,
  onActivityCustomerChange,
  onOppCustomerChange,
  openFullOppFormFromPicker,
  // Sites
  showSiteForm,
  saveSite,
  deleteSite,
  // Contacts
  showContactForm,
  saveContact,
  deleteContact,
  // TASK-823 — cross-module contact CRUD for the dispatch edit-customer modal
  // (crmManaged customers). window.CRMModule.* only — no ?v= import (Pitfall #1).
  loadContacts,
  saveContacts,
  // Contracts
  showContractForm,
  saveContract,
  deleteContract,
  viewContract,
  printRateSheet,
  onCopySourceChange,
  togglePermPoField,
  // Export
  exportCustomerList,
  // Line Items
  showLineItemForm,
  saveLineItem,
  toggleLineItem,
  onCrmAppliesToKindChange,
  // Deal Desk
  toggleDealDesk,
  sendBotMessage,
  cancelBotAction,
  approveBotAction,
  approveMultiBotAction,
  saveBotGeneratedDoc,
  copyBotDoc,
  // Tasks
  setTaskFilter,
  showTaskForm,
  saveTask,
  completeTask,
  showSnoozeMenu,
  snoozeTask,
  deleteTask,
  renderTasksTab,
  // Documents
  uploadCustomerDocs,
  deleteCustomerDoc,
  filterByType,
  // Vendors
  applyVendorFilters,
  renderVendorList,
  // TASK-9684 — account vendors vs other payees
  setShowAllPayees,
  setPayeePin,
  setVendorChargeAccount, // TASK-10141
  togglePayees,
  togglePayeeInfo,
  // Type selector
  selectCustType,
  // Compliance
  showComplianceForm,
  saveComplianceItem,
  // CRM Profiles (multi-rep)
  showManageCrmProfiles,
  closeManageCrmProfiles,
  inviteToCrmTeam,
  deleteCrmProfile,
  switchCrmProfile,
  // TASK-319/352 bridge — silently provision a member's crmProfile on first open
  autoCreateCrmProfileForMember,
  connectCrmGmail,
  disconnectCrmGmail,
  editCrmSignature,
  uploadSignatureImage,
  saveCrmSignature,
  openCrmEmailCompose,
  sendCrmEmail,
  // TASK-9797 item 3 — contract checklist (crmContractChecklist.js does the work).
  openContractChecklist: (type, id) => {
    const c = CRMState.selectedCustomer;
    if (c) _ccLoad().then((m) => m.openEditor(c, CRMState.tenantId, type, id)).catch(() => window.showToast?.('Could not load the contract checklist — try again.', 'error'));
  },
  openContractChecklistSettings: () => _ccLoad().then((m) => m.openSettings(CRMState.tenantId)).catch(() => window.showToast?.('Could not load the contract checklist — try again.', 'error')),
  customerById: (id) => (CRMState.customers || []).find((x) => x.id === id) || null,
  refreshContractChecklist: () => {
    if (CRMState.selectedCustomer && CRMState.detailTab === 'overview' && !document.getElementById('crm-detail-modal')?.classList.contains('hidden')) renderDetailBody({ force: true });
  },
};

export default { initCRM };

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
